import { useState, useEffect, useRef } from "react";
import React from "react";
// PDF wird serverseitig erstellt (jsPDF + jsdom auf dem Server)

// ── Theme System ──────────────────────────────────────────────────────────────
const THEMES = {
  light: {
    leaf:        "#2D5A27",
    leafLight:   "#4A8C42",
    leafPale:    "var(--leafPale)",
    cream:       "#FAF7F0",
    gold:        "#C8A84B",
    text:        "#1C2B1A",
    textMuted:   "#6B7E69",
    white:       "#FFFFFF",
    danger:      "#C0392B",
    border:      "#D4E6CE",
    pageBg:      "#F4F6F4",
    cardBg:      "#FFFFFF",
    inputBg:     "#FFFFFF",
    inputBorder: "#D4E6CE",
    sidebarBg:   "#2D5A27",
    mainBg:      "#F4F6F4",
    tableStripe: "#F9FCF9",
    modalBg:     "#FFFFFF",
  },
  dark: {
    leaf:        "#5CBF52",
    leafLight:   "#6ED664",
    leafPale:    "#1a1a1a",
    cream:       "#1a1a1a",
    gold:        "#F0C030",
    text:        "#E8E8E8",
    textMuted:   "#999999",
    white:       "#111111",
    danger:      "#FF4444",
    border:      "#2a2a2a",
    pageBg:      "#000000",
    cardBg:      "#111111",
    inputBg:     "#1a1a1a",
    inputBorder: "#333333",
    sidebarBg:   "#111111",
    mainBg:      "#000000",
    tableStripe: "#0d0d0d",
    modalBg:     "#111111",
  }
};

function getTheme() {
  return localStorage.getItem("gv_theme") || "light";
}
function setTheme(t) {
  localStorage.setItem("gv_theme", t);
  applyTheme(t);
}
function applyTheme(t) {
  const theme = THEMES[t];
  const root  = document.documentElement;
  Object.entries(theme).forEach(([k, v]) => root.style.setProperty(`--${k}`, v));
  root.setAttribute("data-theme", t);
  // Body background
  document.body.style.background = theme.mainBg;
  document.body.style.color      = theme.text;
  // Globales CSS für Inputs, Selects etc.
  let styleEl = document.getElementById("gv-theme-style");
  if (!styleEl) { styleEl = document.createElement("style"); styleEl.id = "gv-theme-style"; document.head.appendChild(styleEl); }
  styleEl.textContent = `
    * { transition: background-color 0.2s, color 0.2s, border-color 0.2s; }
    input, select, textarea { background: var(--inputBg) !important; color: var(--text) !important; border-color: var(--border) !important; }
    [data-theme="dark"] ::-webkit-scrollbar { background: #1A2A1A; }
    [data-theme="dark"] ::-webkit-scrollbar-thumb { background: #2D5A27; border-radius: 4px; }
    [data-theme="dark"] select option { background: #1A2A1A; color: #E8F5E2; }
  `;
}

// Theme beim Start anwenden
if (typeof window !== "undefined") {
  applyTheme(getTheme());
}

// Globale COLORS — reaktiv auf Theme

// ── Server-Konfiguration ──────────────────────────────────────────────────────
const PDF_SERVER_URL = "https://fgr-gartenfreunde.myds.me:3443";
window._PDF_SERVER_URL = PDF_SERVER_URL;

// Prüft ob PDF auf Server vorhanden und öffnet oder fragt nach Erstellung
async function oeffneOderErstelleRechnung(r, mitglied, onErstellen, setRechnungsAnzeige) {
  const fname = normalizeRechnungName(r.name || `FGR_RE_${r.rechnungsnummer}.pdf`);
  const url   = `${PDF_SERVER_URL}/rechnungen/${r.jahr}/${fname}`;
  try {
    const check = await fetch(url, { method: "HEAD" });
    if (check.ok) {
      setRechnungsAnzeige({ pdfUrl: `/rechnungen/${r.jahr}/${fname}`, rechnungsnummer: r.rechnungsnummer });
    } else {
      setRechnungsAnzeige({ nichtVorhanden: true, onErstellen });
    }
  } catch(e) {
    setRechnungsAnzeige({ nichtVorhanden: true, onErstellen });
  }
}

// Dateiname normalisieren: Rechnung_ → FGR_RE_, trailing _ weg, Leerzeichen → _
function normalizeRechnungName(name) {
  if (!name) return "";
  return name
    .trim()
    .replace(/^Rechnung_/i, "FGR_RE_")   // altes Präfix ersetzen
    .replace(/\s+/g, "_")                 // Leerzeichen → _
    .replace(/(_+)(\.pdf)$/i, "$2");      // trailing _ vor .pdf weg
}

// ── Zentrale Datensynchronisation ─────────────────────────────────────────────
const DEMO_IDS = new Set(["m1","m2","m3","m4","m5","m6","m7","m8","m9","m10",
  "m11","m12","m13","m14","m15","m16","m17","m18","m19","m20","m21","m22","m23","m24","m25"]);

function isDemoMembers(jsonStr) {
  try {
    const arr = JSON.parse(jsonStr);
    if (!Array.isArray(arr) || arr.length === 0) return false;
    return DEMO_IDS.has(arr[0]?.id) && arr[0]?.ort === "Musterstadt";
  } catch(e) { return false; }
}

async function cleanDemoDataFromServer() {
  try {
    await fetch(`${PDF_SERVER_URL}/api/data/gv_members`, {
      method: "POST", headers: {"Content-Type":"application/json"},
      body: JSON.stringify({value:"[]"})
    });
  } catch(e) {}
}

// Einfacher fetch mit individuellem Timeout pro Request
function fetchWithTimeout(url, ms = 10000) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  // Cache-Buster gegen aggressives Caching auf mobilem Chrome
  const sep = url.includes("?") ? "&" : "?";
  const finalUrl = url + sep + "_=" + Date.now();
  return fetch(finalUrl, {
    signal: ctrl.signal,
    cache: "no-store",
    headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
  }).finally(() => clearTimeout(t));
}

// Diese Keys sind gerätespezifisch und werden NICHT auf den Server synchronisiert
const LOCAL_ONLY_KEYS = new Set([
  "gv_theme",            // Dark/Light Mode pro Gerät
  "gv_viewmode",         // Desktop/Mobile pro Gerät
  "gv_viewmode_manual",  // Manueller Override pro Gerät
  "gv_mobile_navbar",    // Mobile NavBar-Auswahl pro Gerät
  "gv_last_sync",        // Letzter Sync-Zeitstempel pro Gerät
  "gv_version",          // Versions-Marker pro Gerät
  "gv_zaehler_fotos",    // Foto-Preview-Cache (Base64!) — Originale liegen auf dem Server unter /api/foto
]);

// In-Flight-Guard: parallele Syncs (Interval + Focus + Login) teilen sich einen Aufruf
let _syncInFlight = null;
function syncFromServer() {
  if (_syncInFlight) return _syncInFlight;
  _syncInFlight = _doSyncFromServer().finally(() => { _syncInFlight = null; });
  return _syncInFlight;
}

async function _doSyncFromServer() {
  let loadedAny = false;
  let errorMsg = null;
  const changedKeys = [];
  try {
    // Keys-Liste holen — 10s Timeout
    const keysRes = await fetchWithTimeout(`${PDF_SERVER_URL}/api/data`, 10000);
    if (!keysRes.ok) throw new Error("HTTP " + keysRes.status);
    const keys = await keysRes.json();
    console.log("📋 Server-Keys:", keys);

    if (Array.isArray(keys) && keys.length > 0) {
      // Jeden Key einzeln laden — kein Gesamt-Timeout der alles abbricht
      // Parallel mit großzügigem Einzel-Timeout (15s für Handy)
      const results = await Promise.allSettled(keys.map(async key => {
        try {
          const res = await fetchWithTimeout(`${PDF_SERVER_URL}/api/data/${key}`, 15000);
          if (!res.ok) throw new Error("HTTP " + res.status);
          const data = await res.json();
          return {key, value: data.value};
        } catch(e) {
          console.warn("⚠️ Key fehlgeschlagen:", key, e.message);
          return {key, value: null};
        }
      }));

      let loaded = 0;
      for (const r of results) {
        if (r.status !== "fulfilled") continue;
        const {key, value} = r.value;
        if (value === null || value === undefined) continue;
        // Gerätespezifische Keys nicht überschreiben
        if (LOCAL_ONLY_KEYS.has(key)) continue;
        // WICHTIG: Lokale Änderungen, die noch nicht zum Server gesendet wurden
        // (Debounce läuft noch oder in Retry-Warteschlange), NICHT überschreiben.
        // Sonst würde der ältere Server-Stand die neuere lokale Änderung zurückrollen.
        if (_syncTimers[key] || _pendingWrites.has(key)) {
          console.log("⏭️ Übersprungen (lokale Änderung ausstehend):", key);
          continue;
        }
        if (key === "gv_members" && isDemoMembers(value)) {
          console.warn("🚫 Demo-Daten ignoriert");
          cleanDemoDataFromServer();
          continue;
        }
        // _deleted Einträge: bei Fest-Keys den Tombstone behalten, sonst lokal löschen
        try {
          const parsed = typeof value === "string" ? JSON.parse(value) : value;
          if (parsed && parsed._deleted) {
            if (key.startsWith("gv_fest_") && !key.startsWith("gv_festdaten")) {
              // Tombstone für Feste behalten, damit Defaults nicht neu eingespielt werden
              _gvStore[key] = value;
            } else {
              if (Object.prototype.hasOwnProperty.call(_gvStore, key)) changedKeys.push(key);
              delete _gvStore[key];
            }
            continue;
          }
        } catch(e) {}
        if (_gvStore[key] !== value) changedKeys.push(key);
        // gv_feste_list: Default-IDs (f1-f4) beim Laden vom Server direkt entfernen
        if (key === GV_FESTE_LIST_KEY) {
          try {
            const arr = JSON.parse(value);
            if (Array.isArray(arr)) {
              const bereinigt = arr.filter(f => f && f.id && !DEFAULT_FEST_IDS.has(f.id));
              _gvStore[key] = JSON.stringify(bereinigt);
              loadedAny = true; loaded++;
              continue;
            }
          } catch(e) {}
        }
        _gvStore[key] = value;
        loadedAny = true;
        loaded++;
      }
      console.log(`📦 Sync abgeschlossen: ${loaded}/${keys.length} Keys geladen, ${changedKeys.length} geändert`);
    } else {
      console.warn("⚠️ Server hat keine Daten:", keys);
    }
  } catch(e) {
    errorMsg = e.name === "AbortError" ? "Timeout beim Verbinden" : e.message;
    console.warn("⚠️ Sync fehlgeschlagen:", errorMsg);
  }
  return { loadedAny, errorMsg, changedKeys };
}

async function syncFromServerBackground(onDone) {
  const result = await syncFromServer();
  if (onDone) onDone(result);
}

// ─── Auto-Sync mit Debounce ───────────────────────────────────────────────────
const _syncTimers = {};
const _syncStatus = { pending: 0, element: null };

// Warteschlange für fehlgeschlagene Schreibvorgänge.
// Garantiert, dass JEDE Änderung den Server erreicht — auch nach Netzwerkfehlern.
const _pendingWrites = new Map(); // key → letzter Wert

async function flushPendingWrites() {
  if (_pendingWrites.size === 0) return;
  console.log(`🔁 Wiederhole ${_pendingWrites.size} ausstehende Schreibvorgänge…`);
  for (const [key, value] of Array.from(_pendingWrites.entries())) {
    const ok = await syncToServer(key, value);
    // Nur löschen wenn der Wert inzwischen nicht erneut geändert wurde
    if (ok && _pendingWrites.get(key) === value) _pendingWrites.delete(key);
  }
  showSyncIndicator(_pendingWrites.size === 0 ? "saved" : "error");
}

// Alle 10s ausstehende Writes erneut versuchen + sofort bei Netz-Rückkehr
setInterval(() => { if (_pendingWrites.size > 0) flushPendingWrites(); }, 10000);
window.addEventListener("online", () => { if (_pendingWrites.size > 0) flushPendingWrites(); });

// KRITISCH: Beim Schließen/Verlassen der Seite alle ausstehenden Änderungen
// SOFORT senden (keepalive-fetch überlebt das Entladen der Seite).
// Ohne das gehen Eingaben verloren, die noch im 500ms-Debounce stecken.
function flushAllPendingNow() {
  // 1. Laufende Debounce-Timer sofort auflösen → Werte in die Queue
  Object.keys(_syncTimers).forEach(key => {
    clearTimeout(_syncTimers[key]);
    delete _syncTimers[key];
    if (_gvStore[key] !== undefined) _pendingWrites.set(key, _gvStore[key]);
  });
  // 2. Alles per keepalive-fetch senden — wird vom Browser auch nach unload zugestellt
  _pendingWrites.forEach((value, key) => {
    try {
      fetch(`${PDF_SERVER_URL}/api/data/${key}`, {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value }),
      }).then(r => { if (r.ok) _pendingWrites.delete(key); }).catch(() => {});
    } catch(e) {}
  });
}
window.addEventListener("pagehide", flushAllPendingNow);
window.addEventListener("beforeunload", flushAllPendingNow);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") flushAllPendingNow();
});

function showSyncIndicator(state) {
  window.dispatchEvent(new CustomEvent("syncstate", { detail: state }));
}

async function syncToServer(key, value, attempt = 0) {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 15000);
    const res = await fetch(`${PDF_SERVER_URL}/api/data/${key}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ value }),
      signal: ctrl.signal,
    }).finally(() => clearTimeout(t));
    if (!res.ok) {
      console.warn(`⚠️ Sync ${key} fehlgeschlagen: HTTP ${res.status}`);
      if (attempt === 0 && res.status >= 500) {
        await new Promise(r => setTimeout(r, 1500));
        return syncToServer(key, value, 1);
      }
      return false;
    }
    return true;
  } catch(e) {
    console.warn(`⚠️ Sync ${key} Netzwerkfehler:`, e.message);
    // Einmal nach 2s erneut versuchen bei Netzwerkfehler
    if (attempt === 0) {
      await new Promise(r => setTimeout(r, 2000));
      return syncToServer(key, value, 1);
    }
    return false;
  }
}

// ─── In-Memory Store: Server ist die einzige Quelle der Wahrheit ─────────────
// Alle gv_* Daten leben ausschließlich im Arbeitsspeicher (_gvStore).
// localStorage wird nur noch für gerätespezifische Keys (Theme, ViewMode) genutzt.
// Beim Laden: syncFromServer füllt _gvStore direkt vom Server.
// Beim Speichern: _gvStore + sofort zum Server (kein alter localStorage-Umweg mehr).
const _gvStore = {};
const _origSetItem    = localStorage.setItem.bind(localStorage);
const _origGetItem    = localStorage.getItem.bind(localStorage);
const _origRemoveItem = localStorage.removeItem.bind(localStorage);

// LESEN: gv_* → aus _gvStore; gerätespezifisch/sonstige → echter localStorage
localStorage.getItem = function(key) {
  if (key.startsWith("gv_") && !LOCAL_ONLY_KEYS.has(key)) {
    return Object.prototype.hasOwnProperty.call(_gvStore, key) ? _gvStore[key] : null;
  }
  return _origGetItem(key);
};

// SCHREIBEN: gv_* → _gvStore + direkt zum Server (kurzer Debounce gegen Rapid-Saves)
localStorage.setItem = function(key, value) {
  if (LOCAL_ONLY_KEYS.has(key)) { _origSetItem(key, value); return; }
  if (!key.startsWith("gv_")) { _origSetItem(key, value); return; }
  // In-Memory Store aktualisieren
  _gvStore[key] = value;
  // Direkt zum Server — Debounce 500ms (statt alter 2000ms)
  if (_syncTimers[key]) clearTimeout(_syncTimers[key]);
  _syncStatus.pending++;
  showSyncIndicator("saving");
  _syncTimers[key] = setTimeout(async () => {
    delete _syncTimers[key];
    const ok = await syncToServer(key, value);
    if (ok) {
      // Erfolgreich → ggf. alten Eintrag aus Warteschlange entfernen
      if (_pendingWrites.get(key) === value) _pendingWrites.delete(key);
    } else {
      // Fehlgeschlagen → in Warteschlange, wird automatisch wiederholt
      _pendingWrites.set(key, value);
      console.warn(`📥 ${key} in Warteschlange — wird automatisch erneut gesendet`);
    }
    _syncStatus.pending--;
    if (_syncStatus.pending <= 0) {
      _syncStatus.pending = 0;
      showSyncIndicator(ok ? "saved" : "error");
    }
  }, 500);
};

// LÖSCHEN: gv_* → aus _gvStore entfernen + Server informieren (mit Retry-Queue)
localStorage.removeItem = function(key) {
  if (key.startsWith("gv_") && !LOCAL_ONLY_KEYS.has(key)) {
    delete _gvStore[key];
    const tombstone = JSON.stringify({ _deleted: true });
    syncToServer(key, tombstone).then(ok => {
      if (!ok) _pendingWrites.set(key, tombstone);
    }).catch(() => { _pendingWrites.set(key, tombstone); });
    return;
  }
  _origRemoveItem(key);
};

// Global helper: check Eich expiry
function checkEich(eichJJ, typ) {
  if (!eichJJ) return null;
  const eichJahr = 2000 + parseInt(eichJJ);
  const gueltigBis = typ === "wasser" ? eichJahr + 6 : eichJahr + 16;
  const currentYear = new Date().getFullYear();
  if (gueltigBis < currentYear) return "abgelaufen";
  if (gueltigBis === currentYear) return "laeuft_ab";
  if (gueltigBis === currentYear + 1) return "bald";
  return null;
}
function eichColor(status) {
  if (status === "abgelaufen") return "#C0392B";
  if (status === "laeuft_ab") return "#E67E22";
  if (status === "bald") return "#D4AC0D";
  return null;
}
function eichIcon(status) {
  if (status === "abgelaufen") return "⚠️";
  if (status === "laeuft_ab") return "⚠️";
  if (status === "bald") return "⏰";
  return null;
}

// Helper to categorize gardens by location
function categorizeGarten(gartenName) {
  if (!gartenName) return "Sonstige";
  const num = parseInt(gartenName.replace(/\D/g, "")) || 0;
  if (num >= 8 && num <= 29) return "Unterer Weg";
  if (num >= 30 && num <= 76) return "Mittlerer Weg";
  if (num >= 78 && num <= 125) return "Oberer Weg";
  return "Sonstige";
}

// Hide number input spinners globally
const styleEl = document.createElement("style");
styleEl.textContent = "@keyframes gv-pulse{0%,100%{opacity:0.4}50%{opacity:1}}input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0;}input[type=number]{-moz-appearance:textfield;}";
document.head.appendChild(styleEl);

// Bereinigt Ablesewert-Eingaben: nur Ziffern und ein Dezimalpunkt erlaubt
function sanitizeAblesewert(val) {
  // Erlaubt Komma und Punkt als Dezimaltrennzeichen
  let v = String(val);
  // Nur Ziffern, Punkt und Komma erlauben
  v = v.replace(/[^0-9.,]/g, "");
  // Maximal ein Dezimalzeichen (Komma oder Punkt)
  const m = v.match(/^([0-9]*)([.,]?)([0-9]*)$/);
  if (m) v = m[1] + m[2] + m[3];
  return v;
}

// Für Berechnungen: Komma → Punkt

function normAbl(val) {
  if (!val && val !== 0) return "";
  return String(val).replace(",", ".");
}

// ─── Auth config ──────────────────────────────────────────────────────────────
const ADMIN_USERS_DEFAULT = [
  { username: "admin",    password: "garten2024", rolle: "Admin" },
  { username: "vorstand", password: "verein123",  rolle: "Vorstand" },
];
function getAdminUsers() {
  try {
    const saved = JSON.parse(localStorage.getItem("gv_admin_users"));
    if (Array.isArray(saved) && saved.length > 0) return saved;
  } catch(e) {}
  return ADMIN_USERS_DEFAULT;
}
function saveAdminUsers(users) {
  const json = JSON.stringify(users);
  localStorage.setItem("gv_admin_users", json);
  // Passwortänderungen SOFORT zum Server (ohne Debounce) — dürfen nie verloren gehen
  const PDF_SERVER_URL = window._PDF_SERVER_URL || "https://fgr-gartenfreunde.myds.me:3443";
  fetch(`${PDF_SERVER_URL}/api/data/gv_admin_users`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ value: json }),
  }).then(r => {
    if (r.ok) console.log("🔐 Admin-Users sofort zum Server gesynct");
    else console.warn("⚠️ Admin-Sync HTTP", r.status);
  }).catch(e => console.warn("⚠️ Admin-Sofort-Sync fehlgeschlagen:", e));
}
const DEFAULT_PW = "fgr";

function getMemberAccounts() {
  try { return JSON.parse(localStorage.getItem("gv_member_accounts") || "{}"); } catch(e) { return {}; }
}
function saveMemberAccounts(data) { localStorage.setItem("gv_member_accounts", JSON.stringify(data)); }

function logPortalLogin(user) {
  // Admin-Logins NICHT protokollieren — nur Mitglieder
  if (user.rolle !== "Mitglied") return;
  try {
    // Mitgliedsdaten für Protokoll holen
    const members = JSON.parse(localStorage.getItem("gv_members") || "[]");
    const member = members.find(m => m.id === user.memberId);
    const logs = JSON.parse(localStorage.getItem("gv_portal_logs") || "[]");
    logs.unshift({
      id: "log" + Date.now() + Math.random().toString(36).slice(2,6),
      timestamp: new Date().toISOString(),
      memberId: user.memberId || null,
      nachname: member?.nachname || user.username,
      vorname: member?.vorname || "",
      garten: member?.garten || "",
      userAgent: (typeof navigator !== "undefined" ? navigator.userAgent : "").slice(0, 250),
    });
    const trimmed = logs.slice(0, 1000);
    localStorage.setItem("gv_portal_logs", JSON.stringify(trimmed));
    // Server-Sync erfolgt automatisch über patched localStorage.setItem
  } catch(e) {}
}

function logDatenAenderung(memberId, felder) {
  // Protokolliert Grunddaten-Änderungen durch Mitglieder
  try {
    const members = JSON.parse(localStorage.getItem("gv_members") || "[]");
    const member = members.find(m => m.id === memberId);
    const logs = JSON.parse(localStorage.getItem("gv_daten_aenderungen") || "[]");
    felder.forEach(f => {
      logs.unshift({
        id: "aend" + Date.now() + Math.random().toString(36).slice(2,6),
        timestamp: new Date().toISOString(),
        memberId,
        nachname: member?.nachname || "",
        vorname: member?.vorname || "",
        garten: member?.garten || "",
        feld: f.feld,
        altWert: f.alt,
        neuWert: f.neu,
      });
    });
    const trimmed = logs.slice(0, 2000);
    localStorage.setItem("gv_daten_aenderungen", JSON.stringify(trimmed));
    // Server-Sync erfolgt automatisch über patched localStorage.setItem
  } catch(e) {}
}

function loginCheck(username, password, garten) {
  // Check admin users first (kein Garten-Feld nötig)
  const admin = getAdminUsers().find(u => u.username.toLowerCase() === username.toLowerCase() && u.password === password);
  if (admin) {
    const u = { ...admin, isMember: false };
    logPortalLogin(u);
    return u;
  }

  // Check member accounts — per E-Mail oder Nachname + Gartennummer
  const members = JSON.parse(localStorage.getItem("gv_members") || "[]");
  const uname = username.toLowerCase().trim();
  const gartenInput = (garten || "").toLowerCase().trim();

  const member = members.find(m => {
    // Identität: E-Mail oder Nachname
    const emailMatch = m.email?.toLowerCase().trim() === uname || m.partner_email?.toLowerCase().trim() === uname;
    const nachnameMatch = m.nachname?.toLowerCase().trim() === uname;
    if (!emailMatch && !nachnameMatch) return false;
    // Gartennummer muss zum Hauptgarten passen
    if (!gartenInput) return false;
    const gartenName = (m.garten || "").toLowerCase().trim();
    // Vergleich: exakt oder nur die Nummer (z.B. "12" passt auf "Garten 12")
    return gartenName === gartenInput || gartenName.replace(/\D/g, "") === gartenInput.replace(/\D/g, "");
  });
  if (!member) return null;
  
  // Block archived members from logging in
  if (member.status === "Ehemaliges Mitglied") return null;

  const accounts = getMemberAccounts();
  const account = accounts[member.id];
  const storedPw = account?.password || DEFAULT_PW;
  if (storedPw !== password) return null;

  const u = {
    username: member.email || member.nachname,
    rolle: "Mitglied",
    memberId: member.id,
    isMember: true,
    mustChangePw: !account?.passwordChanged,
  };
  logPortalLogin(u);
  return u;
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
// ─── Wetter-Daten vom Server (gecacht) ───────────────────────────────────────
function wmoToIconClient(code) {
  if (code === 0) return "☀️";
  if (code <= 2) return "🌤️";
  if (code === 3) return "☁️";
  if (code <= 49) return "🌫️";
  if (code <= 59) return "🌦️";
  if (code <= 69) return "🌧️";
  if (code <= 79) return "🌨️";
  if (code <= 84) return "🌦️";
  if (code <= 99) return "⛈️";
  return "☁️";
}

async function fetchWetterAPI() {
  // Zuerst eigenen Server versuchen
  try {
    const res = await fetchWithTimeout(`${PDF_SERVER_URL}/api/wetter`, 5000);
    if (res.ok) {
      const json = await res.json();
      if (json.data) return json.data;
    }
  } catch(e) { /* Server nicht erreichbar – direkt Open-Meteo versuchen */ }

  // Fallback: direkt Open-Meteo aufrufen
  const url = "https://api.open-meteo.com/v1/forecast?latitude=51.03&longitude=14.59&daily=weathercode,temperature_2m_max,temperature_2m_min&timezone=Europe%2FBerlin&forecast_days=7";
  const res = await fetchWithTimeout(url, 8000);
  if (!res.ok) throw new Error("Open-Meteo HTTP " + res.status);
  const raw = await res.json();
  const days = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  return raw.daily.time.map((dateStr, i) => {
    const d = new Date(dateStr + "T12:00:00");
    return {
      day:  days[d.getDay()],
      icon: wmoToIconClient(raw.daily.weathercode[i]),
      high: Math.round(raw.daily.temperature_2m_max[i]),
      low:  Math.round(raw.daily.temperature_2m_min[i]),
    };
  });
}
function WetterWidget() {
  const [wetter, setWetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [rotating, setRotating]   = useState(false);

  async function laden() {
    setLoading(true); setError(false); setRotating(true);
    try {
      const data = await fetchWetterAPI();
      setWetter(data); setLastUpdate(new Date());
    } catch(e) { setError(true); }
    finally { setLoading(false); setTimeout(() => setRotating(false), 700); }
  }
  useEffect(() => { laden(); }, []);
  const timeStr = lastUpdate ? lastUpdate.toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"}) : null;

  return (
    <div style={{marginBottom:16,background:"var(--cardBg)",border:"1px solid var(--border)",borderRadius:12,padding:16,boxSizing:"border-box",width:"100%",overflow:"hidden"}}>
      <style>{`@keyframes gv-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
        <div>
          <h3 style={{margin:0,fontSize:15,fontWeight:700,color:"var(--text)"}}>Wetter Rödern (Ebersbach)</h3>
          <p style={{margin:"2px 0 0",fontSize:11,color:"var(--textMuted)"}}>{timeStr ? `Stand: ${timeStr} Uhr` : "7-Tage Vorhersage"}</p>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {wetter && !loading && <div style={{fontSize:28}}>{wetter[0]?.icon}</div>}
          <button onClick={laden} disabled={loading} title="Wetter aktualisieren"
            style={{background:"var(--tableStripe)",border:"1.5px solid var(--border)",borderRadius:8,padding:"6px 10px",cursor:loading?"not-allowed":"pointer",fontSize:15,color:"var(--textMuted)",opacity:loading?0.6:1,display:"inline-flex",alignItems:"center",lineHeight:1}}>
            <span style={{display:"inline-block",animation:rotating?"gv-spin 0.7s linear":"none"}}>🔄</span>
          </button>
        </div>
      </div>
      {loading && !wetter && <div style={{textAlign:"center",padding:"18px 0",color:"var(--textMuted)",fontSize:13}}>Wetterdaten werden geladen…</div>}
      {error && !wetter && (
        <div style={{textAlign:"center",padding:"14px 0"}}>
          <div style={{fontSize:22,marginBottom:6}}>⚠️</div>
          <div style={{color:"var(--textMuted)",fontSize:13}}>Wetterdaten nicht verfügbar</div>
        </div>
      )}
      {wetter && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(7, 1fr)",gap:4,opacity:loading?0.45:1,transition:"opacity 0.3s"}}>
          {wetter.map((w,i) => (
            <div key={i} style={{background:i===0?"var(--leafPale)":"var(--tableStripe)",borderRadius:8,padding:"6px 2px",textAlign:"center",border:i===0?"2px solid var(--gold)":"1px solid var(--border)",minWidth:0}}>
              <div style={{fontSize:10,fontWeight:600,marginBottom:2,color:"var(--textMuted)"}}>{w.day}</div>
              <div style={{fontSize:20,marginBottom:2}}>{w.icon}</div>
              <div style={{fontSize:11,fontWeight:700,color:"var(--text)"}}>{w.high}°</div>
              <div style={{fontSize:10,color:"var(--textMuted)"}}>{w.low}°</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function getGaertnerSpruch() {
  const sprueche = [
    "Ein Garten ohne Unkraut ist wie eine Party ohne Gäste – irgendwie zu ordentlich! 🌿",
    "Gärtnern: Die einzige Sucht, bei der man vom Arzt applaudiert wird. 🌻",
    "Ich habe nicht versagt – ich habe nur 10.000 Wege gefunden, wie man Tomaten nicht züchtet. 🍅",
    "Mein Garten ist wie ich: wild, ein bisschen chaotisch, aber voller Leben! 🦋",
    "Man erntet, was man sät – außer man hat Wühlmäuse im Garten. 🐭",
    "Der frühe Gärtner erwischt die Schnecke. 🐌",
    "Geduld ist eine Tugend – außer wenn der Salat schon wieder Schosser treibt. 🥬",
  ];
  const weekNum = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
  return sprueche[weekNum % sprueche.length];
}

function WeisheitWidget() {
  return (
    <div style={{ background: "var(--cardBg)", border: "1px solid var(--gold)", borderRadius: 12, padding: "16px 24px", textAlign: "center" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 20 }}>🌻</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", letterSpacing: 1.2 }}>Gärtner-Weisheit der Woche</span>
      </div>
      <div style={{ fontStyle: "italic", fontSize: 15, lineHeight: 1.6, color: "var(--text)", fontFamily: "'Georgia','Times New Roman',serif" }}>
        "{getGaertnerSpruch()}"
      </div>
    </div>
  );
}

function TermineWidget() {
  const festDaten = (() => { try { return JSON.parse(localStorage.getItem("gv_termine") || "{}"); } catch(e) { return {}; } })();
  const jahr = new Date().getFullYear();
  const feste  = FESTE.filter(f => f.id === "f2" || f.id === "f3");
  const wasser = FESTE.filter(f => f.id === "f1" || f.id === "f4");

  const FestCard = ({ fest }) => {
    const datum   = festDaten[fest.id];
    const formatD = datum ? new Date(datum + "T12:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "long" }) : null;
    return (
      <div style={{ background: fest.farbe.bg, borderLeft: `4px solid ${fest.farbe.border}`, borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 12 }}>
        {fest.svgIcon ? <WasserAbstellenIcon size={28} /> : <span style={{ fontSize: 28 }}>{fest.emoji}</span>}
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: fest.farbe.text }}>{fest.name}</div>
          <div style={{ fontSize: 12, marginTop: 2 }}>
            {datum
              ? <span style={{ color: "#2D5A27", fontWeight: 600 }}>📅 {formatD}</span>
              : <span style={{ color: "#C0392B", fontWeight: 600 }}>📅 Termin noch offen</span>
            }
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ marginTop: 16, background: "var(--cream)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, boxSizing: "border-box", width: "100%" }}>
      <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 700, color: "var(--text)" }}>Wichtige Termine {jahr}</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
        {/* Links: Feste */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {feste.map(fest => <FestCard key={fest.id} fest={fest} />)}
        </div>
        {/* Rechts: Wasser */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {wasser.map(fest => <FestCard key={fest.id} fest={fest} />)}
        </div>
      </div>
    </div>
  );
}

// ─── Theme Toggle ────────────────────────────────────────────────────────────
function doThemeTransition(isLight, callback) {
  const ov = document.createElement("div");
  ov.style.cssText = `position:fixed;inset:0;z-index:99999;background:${isLight?"#fff":"#000"};opacity:0;pointer-events:none;transition:opacity 0.3s ease;`;
  document.body.appendChild(ov);
  requestAnimationFrame(() => {
    ov.style.opacity = "1";
    setTimeout(() => { callback(); ov.style.opacity = "0"; setTimeout(() => document.body.removeChild(ov), 400); }, 300);
  });
}
function ThemeToggle({ style = {} }) {
  const [isLight, setIsLight] = useState(() => getTheme() === "light");
  const [tr, setTr] = useState(false);
  useEffect(() => {
    const h = () => setIsLight(getTheme() === "light");
    window.addEventListener("themechange", h); return () => window.removeEventListener("themechange", h);
  }, []);
  function toggle() {
    if (tr) return; setTr(true);
    doThemeTransition(!isLight, () => { setTheme(isLight?"dark":"light"); setIsLight(v=>!v); window.dispatchEvent(new Event("themechange")); setTimeout(()=>setTr(false),400); });
  }
  return (
    <div onClick={toggle} style={{display:"flex",alignItems:"center",gap:9,cursor:tr?"wait":"pointer",userSelect:"none",...style}}>
      <span style={{fontSize:"0.9rem"}}>{isLight?"☀️":"🌙"}</span>
      <div style={{width:40,height:22,borderRadius:11,background:isLight?"var(--gold,#C8A84B)":"rgba(100,100,100,0.4)",position:"relative",flexShrink:0,transition:"background 0.25s"}}>
        <div style={{position:"absolute",top:3,left:3,width:16,height:16,borderRadius:"50%",background:"#fff",boxShadow:"0 1px 4px rgba(0,0,0,0.35)",transition:"transform 0.25s",transform:isLight?"translateX(18px)":"translateX(0)"}} />
      </div>
      <span style={{fontSize:"0.63rem",fontWeight:600,color:"var(--textMuted)",textTransform:"uppercase",letterSpacing:"1px"}}>{tr?"…":isLight?"Light":"Dark"}</span>
    </div>
  );
}
function SidebarThemeToggle() {
  const [isLight, setIsLight] = useState(() => getTheme() === "light");
  const [tr, setTr] = useState(false);
  useEffect(() => {
    const h = () => setIsLight(getTheme() === "light");
    window.addEventListener("themechange", h); return () => window.removeEventListener("themechange", h);
  }, []);
  function toggle() {
    if (tr) return; setTr(true);
    doThemeTransition(!isLight, () => { setTheme(isLight?"dark":"light"); setIsLight(v=>!v); window.dispatchEvent(new Event("themechange")); setTimeout(()=>setTr(false),400); });
  }
  return (
    <div onClick={toggle} style={{display:"flex",alignItems:"center",gap:9,cursor:tr?"wait":"pointer",userSelect:"none"}}>
      <span style={{fontSize:"0.85rem"}}>{isLight?"☀️":"🌙"}</span>
      <div style={{width:40,height:22,borderRadius:11,background:isLight?"rgba(255,255,255,0.9)":"rgba(255,255,255,0.2)",position:"relative",flexShrink:0,transition:"background 0.25s",boxShadow:"inset 0 1px 3px rgba(0,0,0,0.2)"}}>
        <div style={{position:"absolute",top:3,left:3,width:16,height:16,borderRadius:"50%",background:isLight?"#2D5A27":"#fff",boxShadow:"0 1px 4px rgba(0,0,0,0.4)",transition:"transform 0.25s,background 0.25s",transform:isLight?"translateX(18px)":"translateX(0)"}} />
      </div>
      <span style={{fontSize:"0.6rem",fontWeight:700,color:"rgba(255,255,255,0.75)",textTransform:"uppercase",letterSpacing:"1px"}}>{tr?"…":isLight?"Light Mode":"Dark Mode"}</span>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [garten, setGarten]     = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("gv_viewmode") || "desktop");
  const [ansichtOpen, setAnsichtOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncedOnMount, setSyncedOnMount] = useState(false);

  // Beim Öffnen des Login-Bildschirms einmal Server-Daten holen
  // damit immer mit aktuellen Zugangsdaten geprüft wird
  React.useEffect(() => {
    (async () => {
      try {
        console.log("🔐 Login-Mount: Hole Admin-Daten vom Server...");
        const r = await syncFromServer();
        const admins = JSON.parse(localStorage.getItem("gv_admin_users") || "null");
        console.log("🔐 Admin-Users nach Sync:", admins ? admins.map(a => a.username) : "(keine)");
        // Erster Start: Wenn Server erreichbar aber noch keine Admin-Users hinterlegt,
        // Defaults einmalig zum Server schreiben. Danach ist der Server IMMER die
        // einzige Quelle — der Default-Fallback (garten2024) greift nie wieder.
        if (!r.errorMsg && !admins) {
          console.log("🔐 Seede Default-Admin-Users zum Server (Erststart)");
          saveAdminUsers(ADMIN_USERS_DEFAULT);
        }
      } catch(e) { console.warn("Login-Sync fehlgeschlagen:", e); }
      setSyncedOnMount(true);
    })();
  }, []);

  function switchMode(mode) {
    setViewMode(mode);
    localStorage.setItem("gv_viewmode", mode);
    localStorage.setItem("gv_viewmode_manual", "1"); // manueller Override – Autoerkennung deaktivieren
    applyMobileBodyClass();
  }

  async function handleLogin() {
    if (loading) return;
    setLoading(true);
    setError("");
    // Frische Daten vom Server holen — Passwörter könnten auf anderem Gerät geändert sein
    let syncOk = false;
    try {
      const r = await syncFromServer();
      syncOk = !r.errorMsg;
    } catch(e) { /* unten behandelt */ }

    // SICHERHEIT: Wenn der Server nicht erreichbar ist UND keine Admin-Daten
    // geladen wurden, würde der Default-Fallback (garten2024) greifen.
    // Das wäre ein Sicherheitsloch nach Passwortänderung → Login blockieren.
    const hatAdminDaten = localStorage.getItem("gv_admin_users") !== null;
    if (!syncOk && !hatAdminDaten) {
      setLoading(false);
      setError("Server nicht erreichbar. Bitte Internetverbindung prüfen und erneut versuchen.");
      return;
    }

    const user = loginCheck(username.trim(), password, garten.trim());
    setLoading(false);
    if (user) {
      onLogin(user);
    } else {
      setError("Zugangsdaten falsch oder Gartennummer stimmt nicht überein.");
    }
  }

  function handleKey(e) {
    if (e.key === "Enter") handleLogin();
  }

  return (
    <div style={{ ...loginStyles.bg, background: getTheme() === "dark" ? "#000000" : "linear-gradient(135deg, #1C3A18 0%, #2D5A27 50%, #4A8C42 100%)", alignItems: "flex-start", overflowY: "auto", paddingTop: 24, paddingBottom: 32 }}>
      <div style={{ width: "100%", maxWidth: 860, margin: "0 auto", padding: "0 16px", boxSizing: "border-box" }}>
        {/* Wetter + Weisheit + Termine */}
        <WetterWidget />
        <WeisheitWidget />
        <TermineWidget />

        {/* Login-Card */}
        <div style={{ ...loginStyles.card, margin: "24px auto 0", maxWidth: 420, width: "100%", boxSizing: "border-box" }}>
          <div style={loginStyles.logo}>
            <span style={{ fontSize: 48 }}>🌱</span>
            <h1 style={loginStyles.title}>Gartenfreunde</h1>
            <p style={loginStyles.sub}>Mitgliederverwaltung</p>
          </div>
          <div style={loginStyles.form}>
            <div style={loginStyles.field}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div style={{ flex: 1 }}>
                  <label style={loginStyles.label}>E-Mail / Nachname</label>
                  <input
                    style={{ ...loginStyles.input, width: "100%", boxSizing: "border-box" }}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder="E-Mail oder Nachname"
                    autoFocus
                  />
                </div>
                <div style={{ width: 72, flexShrink: 0 }}>
                  <label style={{ ...loginStyles.label, textAlign: "center", display: "block" }}>Gart.Nr.</label>
                  <input
                    style={{ ...loginStyles.input, width: "100%", boxSizing: "border-box", textAlign: "center" }}
                    value={garten}
                    onChange={(e) => setGarten(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder="Nr."
                  />
                </div>
              </div>
            </div>
            <div style={loginStyles.field}>
              <label style={loginStyles.label}>Passwort</label>
              <div style={{ position: "relative" }}>
                <input
                  style={{ ...loginStyles.input, width: "100%", boxSizing: "border-box" }}
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Passwort eingeben"
                />
                <button
                  style={loginStyles.eyeBtn}
                  onClick={() => setShowPw((v) => !v)}
                  tabIndex={-1}
                >
                  {showPw ? "🙈" : "👁️"}
                </button>
              </div>
            </div>
            {error && <div style={loginStyles.error}>{error}</div>}
            <button style={{ ...loginStyles.btn, width: "100%", boxSizing: "border-box", opacity: loading ? 0.7 : 1, cursor: loading ? "wait" : "pointer" }} onClick={handleLogin} disabled={loading}>
              {loading ? "⏳ Anmelden..." : "Anmelden"}
            </button>
          </div>
          <p style={loginStyles.hint}>Gartenfreunde Rödern · Verwaltungssystem</p>
          {/* Ansicht-Umschalter */}
          <div style={{ marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
            <button
              onClick={() => setAnsichtOpen(v => !v)}
              style={{ width: "100%", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, color: "var(--textMuted)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: "inherit", padding: "4px 0" }}>
              <span>{ansichtOpen ? "▲" : "▼"}</span>
              Ansicht
              <span style={{ fontSize: 10, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                ({viewMode === "mobile" ? "📱 Mobil" : "🖥️ Desktop"}{!localStorage.getItem("gv_viewmode_manual") ? " · Auto" : ""})
              </span>
            </button>
            {ansichtOpen && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 10, color: "var(--textMuted)", textAlign: "center", marginBottom: 8 }}>
                  {localStorage.getItem("gv_viewmode_manual") ? "Manuell gewählt" : "🔍 Automatisch erkannt"}
                  {localStorage.getItem("gv_viewmode_manual") && (
                    <button onClick={() => { localStorage.removeItem("gv_viewmode_manual"); window.location.reload(); }}
                      style={{ marginLeft: 6, background: "none", border: "none", color: COLORS.leaf, fontSize: 10, cursor: "pointer", textDecoration: "underline", padding: 0, fontFamily: "inherit" }}>
                      Auto
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <button onClick={() => switchMode("desktop")}
                    style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "1.5px solid", borderColor: viewMode === "desktop" ? "#4A8C42" : "var(--border)", background: viewMode === "desktop" ? "#4A8C42" : "transparent", color: viewMode === "desktop" ? "#fff" : "var(--textMuted)", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                    🖥️ Desktop
                  </button>
                  <button onClick={() => switchMode("mobile")}
                    style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "1.5px solid", borderColor: viewMode === "mobile" ? "#4A8C42" : "var(--border)", background: viewMode === "mobile" ? "#4A8C42" : "transparent", color: viewMode === "mobile" ? "#fff" : "var(--textMuted)", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                    📱 Mobil
                  </button>
                </div>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "center", marginTop: ansichtOpen ? 0 : 10 }}>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const loginStyles = {
  bg: {
    minHeight: "100vh",
    background: getTheme() === "dark" ? "#000" : "linear-gradient(135deg, #1C3A18 0%, #2D5A27 50%, #4A8C42 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Georgia', 'Times New Roman', serif",
  },
  card: {
    background: "var(--cardBg)",
    borderRadius: 20,
    padding: "48px 40px 32px",
    width: 380,
    boxShadow: "0 30px 80px rgba(0,0,0,0.4)",
    border: getTheme() === "dark" ? "1px solid #333" : "none",
    display: "flex",
    flexDirection: "column",
  },
  logo: { textAlign: "center", marginBottom: 32 },
  title: { margin: "10px 0 4px", fontSize: 26, color: "var(--text)", fontWeight: 700 },
  sub: { margin: 0, color: "var(--textMuted)", fontSize: 13, letterSpacing: 1, textTransform: "uppercase" },
  form: { display: "flex", flexDirection: "column", gap: 16 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, color: "var(--textMuted)" },
  input: {
    width: "100%", padding: "11px 14px", border: "1.5px solid var(--border)",
    borderRadius: 9, fontSize: 14, fontFamily: "inherit", color: "var(--text)",
    background: "var(--cardBg)", outline: "none", boxSizing: "border-box",
  },
  eyeBtn: {
    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
    background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 4,
  },
  error: {
    background: "var(--cream)", color: "#C0392B", borderRadius: 8,
    padding: "10px 14px", fontSize: 13, fontWeight: 600,
  },
  btn: {
    padding: "12px", background: "#2D5A27", color: "#fff", border: "none",
    borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer",
    fontFamily: "inherit", marginTop: 4,
  },
  hint: { textAlign: "center", color: "var(--textMuted)", fontSize: 11, marginTop: 28, marginBottom: 0 },
};

// ─── Mobile Hook ──────────────────────────────────────────────────────────────
function useMobile() {
  return localStorage.getItem("gv_viewmode") === "mobile";
}

// Globale mobile CSS-Regeln injizieren
(function injectMobileCSS() {
  if (document.getElementById("gv-mobile-css")) return;
  const style = document.createElement("style");
  style.id = "gv-mobile-css";
  style.textContent = `
    body.gv-mobile .gv-page { padding: 12px 10px 80px !important; }
    body.gv-mobile input, body.gv-mobile select, body.gv-mobile textarea { font-size: 16px !important; }
  `;
  document.head.appendChild(style);
})();

// Mobile-Klasse auf body setzen/entfernen
function applyMobileBodyClass() {
  const isMob = localStorage.getItem("gv_viewmode") === "mobile";
  document.body.classList.toggle("gv-mobile", isMob);
}

// Automatische Geräteerkennung beim ersten Laden
(function autoDetectDevice() {
  // Nur automatisch setzen wenn kein manueller Override vorhanden
  if (localStorage.getItem("gv_viewmode_manual")) return;
  const ua = navigator.userAgent || "";
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua)
    || (navigator.maxTouchPoints > 1 && window.innerWidth < 1024);
  const mode = isMobileDevice ? "mobile" : "desktop";
  localStorage.setItem("gv_viewmode", mode);
})();

applyMobileBodyClass();


const COLORS = {
  get leaf()        { return getComputedStyle(document.documentElement).getPropertyValue("--leaf").trim() || "#2D5A27"; },
  get leafLight()   { return getComputedStyle(document.documentElement).getPropertyValue("--leafLight").trim() || "#4A8C42"; },
  get leafPale()    { return getComputedStyle(document.documentElement).getPropertyValue("--leafPale").trim() || "var(--leafPale)"; },
  get cream()       { return getComputedStyle(document.documentElement).getPropertyValue("--cream").trim() || "#FAF7F0"; },
  get gold()        { return getComputedStyle(document.documentElement).getPropertyValue("--gold").trim() || "#C8A84B"; },
  get text()        { return getComputedStyle(document.documentElement).getPropertyValue("--text").trim() || "#1C2B1A"; },
  get textMuted()   { return getComputedStyle(document.documentElement).getPropertyValue("--textMuted").trim() || "#6B7E69"; },
  get white()       { return getComputedStyle(document.documentElement).getPropertyValue("--white").trim() || "#FFFFFF"; },
  get danger()      { return getComputedStyle(document.documentElement).getPropertyValue("--danger").trim() || "#C0392B"; },
  get border()      { return getComputedStyle(document.documentElement).getPropertyValue("--border").trim() || "#D4E6CE"; },
  get cardBg()      { return getComputedStyle(document.documentElement).getPropertyValue("--cardBg").trim() || "#FFFFFF"; },
  get inputBg()     { return getComputedStyle(document.documentElement).getPropertyValue("--inputBg").trim() || "#FFFFFF"; },
  get mainBg()      { return getComputedStyle(document.documentElement).getPropertyValue("--mainBg").trim() || "#F4F6F4"; },
  get tableStripe() { return getComputedStyle(document.documentElement).getPropertyValue("--tableStripe").trim() || "#F9FCF9"; },
  get modalBg()     { return getComputedStyle(document.documentElement).getPropertyValue("--modalBg").trim() || "#FFFFFF"; },
};

const S = {
  page: { padding: "24px 24px 24px 48px", maxWidth: 1400, margin: "0 auto" },
  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 },
  h1: { fontSize: 28, fontWeight: 700, color: "var(--text)", margin: 0 },
  subtitle: { fontSize: 14, color: "var(--textMuted)", margin: "4px 0 0" },
  card: { background: "var(--cardBg)", borderRadius: 12, padding: 20, border: "1px solid var(--border)" },
  cardTitle: { fontSize: 15, fontWeight: 700, color: "var(--text)", marginBottom: 16 },
  input: { width: "100%", padding: "8px 12px", border: "1px solid var(--inputBorder)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--inputBg)", color: "var(--text)" },
  fieldLabel: { display: "block", fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.5px" },
  avatar: { width: 36, height: 36, borderRadius: "50%", background: COLORS.leaf, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0 },
  backBtn: { background: "none", border: "none", color: COLORS.leaf, cursor: "pointer", fontSize: 14, fontWeight: 600, padding: "4px 8px", borderRadius: 6 },
  btnGhost: { background: "transparent", border: `1px solid ${COLORS.border}`, color: COLORS.text, padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600, fontFamily: "inherit" },
  root: { display: "flex", minHeight: "100vh", background: "var(--mainBg)", fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' },
  sidebar: { background: "var(--sidebarBg,#2D5A27)", color: "#fff", display: "flex", flexDirection: "column", flexShrink: 0, position: "fixed", left: 0, top: 0, bottom: 0, width: 260, overflowY: "auto" },
  main: { flex: 1, marginLeft: 260, minHeight: "100vh", transition: "margin-left 0.25s ease" },
  nav: { flex: 1, padding: "12px 0", display: "flex", flexDirection: "column", gap: 2 },
  navItem: { background: "none", border: "none", color: "#fff", textAlign: "left", padding: "10px 14px", cursor: "pointer", fontSize: 14, fontWeight: 500, display: "flex", alignItems: "center", gap: 10, transition: "background 0.2s", fontFamily: "inherit", margin: "0 8px 2px", borderRadius: 8, width: "calc(100% - 16px)" },
  navActive: { background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.25)", borderLeft: "3px solid #fff" },
  navSub: { fontSize: 13, paddingLeft: 32, margin: "0 8px 2px", borderRadius: 8, width: "calc(100% - 16px)" },
  navSubActive: { background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.2)", borderLeft: "3px solid rgba(255,255,255,0.7)", fontSize: 13, paddingLeft: 32, margin: "0 8px 2px", borderRadius: 8, width: "calc(100% - 16px)" },
  sidebarLogo: { padding: "24px 20px", borderBottom: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", gap: 12 },
  logoName: { fontSize: 18, fontWeight: 700 },
  logoSub: { fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: "1px" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "var(--cardBg)", borderRadius: 12, padding: 24 },
  notification: { position: "fixed", top: 20, right: 20, padding: "12px 20px", borderRadius: 8, color: "#fff", fontWeight: 600, zIndex: 2000 },
  btnPrimary: { background: COLORS.leaf, color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600, fontFamily: "inherit" },
  btnDanger: { background: COLORS.danger, color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600, fontFamily: "inherit" },
  btnSmall: { background: COLORS.leaf, color: "#fff", border: "none", padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" },
  tableWrap: { overflowX: "auto", background: "var(--cardBg)", borderRadius: 12, border: `1px solid ${COLORS.border}` },
  th: { textAlign: "left", padding: "12px 16px", fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: `1px solid ${COLORS.border}`, background: "var(--cardBg)" },
  td: { padding: "12px 16px", borderBottom: `1px solid ${COLORS.border}`, fontSize: 14 },
  badge: { padding: "4px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 },
  docRemove: { background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 16, padding: 4 },
  filterBtn: { padding: "6px 16px", borderRadius: 12, border: `1.5px solid ${COLORS.border}`, background: "var(--cardBg)", color: COLORS.text, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600 },
  filterBtnActive: { background: COLORS.leaf, color: "#fff", borderColor: COLORS.leaf },
  gartenBadge: { background: COLORS.leafPale, color: COLORS.leaf, border: `1px solid ${COLORS.leafLight}`, borderRadius: 8, padding: "2px 8px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", display: "inline-block" },
};

const STATUS_COLORS = {
  "Aktives Mitglied":  { bg: "var(--leafPale)", text: "var(--leaf)", dot: "var(--leaf)" },
  "Passives Mitglied": { bg: "var(--cream)", text: "var(--gold)", dot: "var(--gold)" },
  "Nichtmitglied":     { bg: "var(--leafPale)", text: "#2E86C1", dot: "#2E86C1" },
};

// Versionsnummer erhöhen → erzwingt Reset der gespeicherten Demo-Daten
const DATA_VERSION = "7";

function initStorage() {
  if (!localStorage.getItem("gv_members")) _gvStore["gv_members"] = "[]";
}

// Migrationen NACH dem ersten erfolgreichen Server-Sync ausführen —
// vorher ist _gvStore leer und Migrationen liefen ins Leere.
function runMigrationsAfterSync() {
  if (localStorage.getItem("gv_version") === DATA_VERSION) return;

  // Demo-Daten-Check
  try {
    const ex = JSON.parse(localStorage.getItem("gv_members") || "[]");
    if (isDemoMembers(JSON.stringify(ex))) localStorage.removeItem("gv_members");
  } catch(e) {}

  // gv_feste_list bereinigen (Default-IDs f1-f4 entfernen)
  try {
    const raw = localStorage.getItem(GV_FESTE_LIST_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) {
        const bereinigt = arr.filter(f => f && f.id && !DEFAULT_FEST_IDS.has(f.id));
        if (bereinigt.length !== arr.length) {
          localStorage.setItem(GV_FESTE_LIST_KEY, JSON.stringify(bereinigt));
        }
      }
    }
  } catch(e) {}

  localStorage.setItem("gv_version", DATA_VERSION);
}


// ─── Root ─────────────────────────────────────────────────────────────────────
// ─── Bild-Zuschnitt Komponente ────────────────────────────────────────────────
function kaufmaennischRunden(wert) {
  // Kaufmännische Rundung: ,5 aufrunden, ,4 abrunden → ganzzahlig
  const num = parseFloat(String(wert).replace(",", "."));
  if (isNaN(num)) return wert;
  return String(Math.round(num));
}

function BildZuschnitt({ imageSrc, onCrop, onCancel, onZaehlerstand }) {
  const imgRef       = React.useRef();
  const containerRef = React.useRef();
  const [box, setBox]         = useState({ x: 5, y: 5, w: 90, h: 90 });
  const [dragging, setDragging] = useState(null);
  const [start, setStart]     = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const isMobile = typeof window !== "undefined" && localStorage.getItem("gv_viewmode") === "mobile";
  const [mode, setMode]       = useState("crop"); // "crop" | "ai"
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult]   = useState(null);
  const [aiError, setAiError]     = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null); // Vorschau des KI-Ausschnitts
  const [editWert, setEditWert]   = useState(""); // Bearbeitbarer KI-Wert
  const [rotation, setRotation]   = useState(0); // 0, 90, 180, 270
  const [rotatedSrc, setRotatedSrc] = useState(imageSrc);

  function pct2px(pct, dim) { return pct / 100 * dim; }
  function px2pct(px, dim)  { return px / dim * 100; }

  function getPos(e, el) {
    const rect = el.getBoundingClientRect();
    const touch = e.touches?.[0] || e.changedTouches?.[0];
    const clientX = touch ? touch.clientX : e.clientX;
    const clientY = touch ? touch.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  function onDown(e, dir) {
    e.preventDefault(); e.stopPropagation();
    const el = containerRef.current;
    if (!el) return;
    setDragging(dir);
    setStart({ ...getPos(e, el), box: { ...box } });
  }

  function onMove(e) {
    if (!dragging || !start) return;
    e.preventDefault();
    const el = containerRef.current;
    if (!el) return;
    const pos = getPos(e, el);
    const dx = px2pct(pos.x - start.x, el.offsetWidth);
    const dy = px2pct(pos.y - start.y, el.offsetHeight);
    setBox(b => {
      if (dragging === "move") return {
        ...b,
        x: Math.max(0, Math.min(100 - b.w, start.box.x + dx)),
        y: Math.max(0, Math.min(100 - b.h, start.box.y + dy)),
      };
      let { x, y, w, h } = start.box;
      if (dragging.includes("e")) w = Math.max(15, Math.min(100 - x, w + dx));
      if (dragging.includes("s")) h = Math.max(15, Math.min(100 - y, h + dy));
      if (dragging.includes("w")) { const nw = Math.max(15, w - dx); x = x + w - nw; w = nw; }
      if (dragging.includes("n")) { const nh = Math.max(15, h - dy); y = y + h - nh; h = nh; }
      return { x: Math.max(0, x), y: Math.max(0, y), w, h };
    });
  }

  function onUp() { setDragging(null); setStart(null); }

  function rotateImage(currentSrc, deg) {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const swap = deg === 90 || deg === 270;
        canvas.width  = swap ? img.naturalHeight : img.naturalWidth;
        canvas.height = swap ? img.naturalWidth  : img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((deg * Math.PI) / 180);
        ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
        resolve(canvas.toDataURL("image/jpeg", 0.95));
      };
      img.src = currentSrc;
    });
  }

  async function doRotate(dir) {
    const newDeg = ((rotation + dir) + 360) % 360;
    const newSrc = await rotateImage(rotatedSrc, dir);
    setRotation(newDeg);
    setRotatedSrc(newSrc);
    setBox({ x: 5, y: 5, w: 90, h: 90 }); // Reset crop box after rotation
    setAiResult(null);
  }

  function getCroppedCanvas() {
    const img = imgRef.current;
    if (!img) return null;
    const canvas = document.createElement("canvas");
    const nw = img.naturalWidth, nh = img.naturalHeight;
    canvas.width  = pct2px(box.w, nw);
    canvas.height = pct2px(box.h, nh);
    canvas.getContext("2d").drawImage(img, pct2px(box.x, nw), pct2px(box.y, nh), canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
    return canvas;
  }

  function doCrop() {
    const canvas = getCroppedCanvas();
    if (!canvas) return;
    canvas.toBlob(blob => onCrop(blob), "image/jpeg", 0.9);
  }

  // Smart Auto-Zuschnitt: KI findet Zähler-Position + Drehung automatisch
  async function doAiErkennung() {
    setAiLoading(true); setAiError(null); setAiResult(null); setEditWert("");
    const previewCanvas = getCroppedCanvas();
    if (previewCanvas) setPreviewSrc(previewCanvas.toDataURL("image/jpeg", 0.85));
    try {
      const canvas = getCroppedCanvas() || (() => {
        const c = document.createElement("canvas");
        const img = imgRef.current;
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img, 0, 0);
        return c;
      })();
      const base64 = canvas.toDataURL("image/jpeg", 0.92).split(",")[1];
      console.log("📸 KI-Erkennung: Bild gesendet,", Math.round(base64.length / 1024), "KB");

      // KI-Anfrage geht über eigenen Server-Proxy (API-Key sicher im Backend)
      const res = await fetch(`${PDF_SERVER_URL}/api/ki-zaehler`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image: base64 })
      });

      if (!res.ok) {
        const errText = await res.text();
        console.error("❌ API-Fehler:", res.status, errText);
        throw new Error("API " + res.status + ": " + errText.slice(0, 150));
      }

      const data = await res.json();
      console.log("✅ API-Antwort:", data);

      // Verschiedene Antwort-Formate abfangen
      const raw = data?.content?.[0]?.text || data?.completion || "";
      if (!raw) {
        console.error("Leere Antwort:", data);
        throw new Error("Leere Antwort vom Modell");
      }
      console.log("📝 Roh-Antwort:", raw);

      // Versuche JSON aus der Antwort zu extrahieren — flexibel
      let parsed = null;
      try {
        // 1. Versuch: direktes JSON
        parsed = JSON.parse(raw.replace(/```json|```/g, "").trim());
      } catch(e1) {
        // 2. Versuch: LETZTES {...} Objekt in der Antwort finden (bei Chain-of-Thought steht JSON am Ende)
        const matches = [...raw.matchAll(/\{[^{}]*"wert"[^{}]*\}/g)];
        if (matches.length > 0) {
          // Nimm das letzte gefundene JSON (das ist das Endergebnis)
          try { parsed = JSON.parse(matches[matches.length - 1][0]); } catch(e2) {}
        }
        // 3. Fallback: irgendein {...}
        if (!parsed) {
          const m = raw.match(/\{[^}]+\}/);
          if (m) {
            try { parsed = JSON.parse(m[0]); } catch(e3) {}
          }
        }
      }

      if (!parsed) {
        // Letzter Versuch: Zahl direkt aus dem Text rausziehen
        const numMatch = raw.match(/(\d+(?:[.,]\d+)?)/);
        if (numMatch) {
          parsed = { wert: numMatch[1], einheit: raw.toLowerCase().includes("kwh") ? "kWh" : "m3" };
        }
      }

      if (!parsed) throw new Error("Antwort konnte nicht ausgewertet werden: " + raw.slice(0, 100));

      if (parsed.wert) parsed.gerundet = kaufmaennischRunden(parsed.wert);
      console.log("🎯 Erkannt:", parsed);
      setAiResult(parsed);
      if (parsed.wert) setEditWert(String(parsed.wert));
    } catch(e) {
      console.error("KI-Erkennung Fehler:", e);
      setAiError("Erkennung fehlgeschlagen: " + e.message);
    } finally {
      setAiLoading(false);
    }
  }

  const handles = [
    { s: "n",  style: { top: -14, left: "50%", transform: "translateX(-50%)", cursor: "n-resize", width: 48, height: 28 } },
    { s: "s",  style: { bottom: -14, left: "50%", transform: "translateX(-50%)", cursor: "s-resize", width: 48, height: 28 } },
    { s: "e",  style: { right: -14, top: "50%", transform: "translateY(-50%)", cursor: "e-resize", width: 28, height: 48 } },
    { s: "w",  style: { left: -14, top: "50%", transform: "translateY(-50%)", cursor: "w-resize", width: 28, height: 48 } },
    { s: "ne", style: { top: -16, right: -16, cursor: "ne-resize", width: 32, height: 32 } },
    { s: "nw", style: { top: -16, left: -16, cursor: "nw-resize", width: 32, height: 32 } },
    { s: "se", style: { bottom: -16, right: -16, cursor: "se-resize", width: 32, height: 32 } },
    { s: "sw", style: { bottom: -16, left: -16, cursor: "sw-resize", width: 32, height: 32 } },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000", zIndex: 10000, display: "flex", flexDirection: "column", touchAction: "none" }}>
      {/* Header */}
      <div style={{ padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.06)", flexShrink: 0 }}>
        <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
          {mode === "ai" ? "🤖 KI-Zählerstand" : "✂️ Ausschnitt wählen"}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          {!isMobile && <button onClick={() => { setMode(m => m === "crop" ? "ai" : "crop"); setAiResult(null); setAiError(null); }}
            style={{ background: mode === "ai" ? "#4A8C42" : "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
            {mode === "ai" ? "✂️ Zuschnitt" : "🤖 KI-Erkennung"}
          </button>}
          <button onClick={() => doRotate(-90)} title="Links drehen"
            style={{ background: "rgba(255,255,255,0.12)", border: "none", color: "#fff", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>↺</button>
          <button onClick={() => doRotate(90)} title="Rechts drehen"
            style={{ background: "rgba(255,255,255,0.12)", border: "none", color: "#fff", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>↻</button>
          <button onClick={onCancel}
            style={{ background: "rgba(255,255,255,0.12)", border: "none", color: "#fff", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 16 }}>✕</button>
        </div>
      </div>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", textAlign: "center", padding: "4px 0", flexShrink: 0 }}>
        {mode === "crop" ? "Rahmen verschieben · Ecken/Kanten ziehen" : "Zähler einrahmen → KI liest den Wert ab"}
      </div>

      {/* Bild */}
      <div ref={containerRef} style={{ flex: 1, position: "relative", overflow: "hidden", margin: "6px 8px", touchAction: "none" }}
        onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        onTouchMove={onMove} onTouchEnd={onUp} onTouchCancel={onUp}>
        <img ref={imgRef} src={rotatedSrc} onLoad={() => setImgLoaded(true)}
          style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", userSelect: "none", touchAction: "none" }}
          draggable={false} />
        {imgLoaded && (<>
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
            <svg width="100%" height="100%" style={{ position: "absolute", inset: 0 }}>
              <defs><mask id="crop-mask">
                <rect width="100%" height="100%" fill="white" />
                <rect x={`${box.x}%`} y={`${box.y}%`} width={`${box.w}%`} height={`${box.h}%`} fill="black" />
              </mask></defs>
              <rect width="100%" height="100%" fill="rgba(0,0,0,0.62)" mask="url(#crop-mask)" />
            </svg>
          </div>
          <div style={{ position: "absolute", left: `${box.x}%`, top: `${box.y}%`, width: `${box.w}%`, height: `${box.h}%`, border: "2px solid #4A8C42", boxSizing: "border-box", touchAction: "none" }}
            onMouseDown={e => onDown(e, "move")} onTouchStart={e => onDown(e, "move")}>
            <div style={{ position: "absolute", inset: 0, pointerEvents: "none",
              backgroundImage: "linear-gradient(rgba(255,255,255,0.15) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.15) 1px,transparent 1px)",
              backgroundSize: "33.3% 33.3%" }} />
            {handles.map(h => (
              <div key={h.s} style={{ position: "absolute", ...h.style, background: h.s.length === 2 ? "#4A8C42" : "rgba(74,140,66,0.7)", borderRadius: h.s.length === 2 ? 6 : 4, touchAction: "none" }}
                onMouseDown={e => { e.stopPropagation(); onDown(e, h.s); }}
                onTouchStart={e => { e.stopPropagation(); onDown(e, h.s); }} />
            ))}
          </div>
        </>)}
      </div>

      {/* KI-Ergebnis */}
      {mode === "ai" && (aiResult || aiError || aiLoading || previewSrc) && (
        <div style={{ margin: "0 8px 6px", background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px 14px", flexShrink: 0 }}>

          {/* Vorschau + Ergebnis nebeneinander */}
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>

            {/* Vorschau des gesendeten Ausschnitts */}
            {previewSrc && (
              <div style={{ flexShrink: 0 }}>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Gesendeter Ausschnitt</div>
                <img src={previewSrc} alt="Ausschnitt"
                  style={{ width: 90, height: 70, objectFit: "contain", borderRadius: 6, border: "1px solid rgba(255,255,255,0.2)", background: "#000" }} />
              </div>
            )}

            {/* KI-Ergebnis */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {aiLoading && (
                <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, paddingTop: 20 }}>🤖 KI liest Zählerstand ab…</div>
              )}
              {aiError && <div style={{ color: "#ff6b6b", fontSize: 12 }}>{aiError}</div>}
              {aiResult && (<>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>
                  {aiResult.wert ? "Erkannter Wert — bitte prüfen & ggf. korrigieren:" : "Nicht erkannt"}
                </div>
                {aiResult.wert ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="text" inputMode="decimal"
                      value={editWert}
                      onChange={e => setEditWert(sanitizeAblesewert(e.target.value))}
                      style={{ fontFamily: "monospace", fontSize: 22, fontWeight: 800, color: "#4A8C42", background: "rgba(74,140,66,0.15)", border: "2px solid #4A8C42", borderRadius: 8, padding: "6px 10px", width: "100%", outline: "none", minWidth: 0 }}
                    />
                    <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", flexShrink: 0 }}>{aiResult.einheit}</span>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ fontSize: 12, color: "#ff9f43", marginBottom: 4 }}>⚠️ Nicht lesbar — manuell eingeben:</div>
                    <input
                      type="text" inputMode="decimal"
                      value={editWert}
                      onChange={e => setEditWert(sanitizeAblesewert(e.target.value))}
                      placeholder="Wert eingeben…"
                      style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 700, color: "#fff", background: "rgba(255,255,255,0.1)", border: "1.5px solid rgba(255,255,255,0.3)", borderRadius: 8, padding: "6px 10px", width: "100%", outline: "none" }}
                    />
                  </div>
                )}
              </>)}
            </div>
          </div>

          {/* Übernehmen-Button */}
          {aiResult && onZaehlerstand && (editWert || !aiResult.wert) && (
            <button
              onClick={() => { if (editWert) { onZaehlerstand(editWert); } }}
              disabled={!editWert}
              style={{ width: "100%", marginTop: 10, background: editWert ? "#4A8C42" : "rgba(255,255,255,0.1)", border: "none", color: "#fff", borderRadius: 10, padding: "12px", fontWeight: 800, fontSize: 15, cursor: editWert ? "pointer" : "not-allowed", opacity: editWert ? 1 : 0.5 }}>
              ✓ {editWert} {aiResult.einheit} übernehmen
            </button>
          )}
        </div>
      )}

      {/* Buttons */}
      <div style={{ padding: "10px 12px 16px", display: "flex", gap: 10, flexShrink: 0 }}>
        {mode === "crop" ? (<>
          <button onClick={onCancel} style={{ flex: 1, padding: 16, borderRadius: 14, border: "1.5px solid rgba(255,255,255,0.25)", background: "transparent", color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
            Ohne Zuschnitt
          </button>
          <button onClick={doCrop} style={{ flex: 2, padding: 16, borderRadius: 14, border: "none", background: "#4A8C42", color: "#fff", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>
            ✂️ Speichern
          </button>
        </>) : (<>
          <button onClick={doCrop} style={{ flex: 1, padding: 16, borderRadius: 14, border: "1.5px solid rgba(255,255,255,0.25)", background: "transparent", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            💾 Foto speichern
          </button>
          <button onClick={doAiErkennung} disabled={aiLoading}
            style={{ flex: 2, padding: 16, borderRadius: 14, border: "none", background: aiLoading ? "rgba(74,140,66,0.5)" : "#4A8C42", color: "#fff", fontWeight: 800, fontSize: 16, cursor: aiLoading ? "wait" : "pointer" }}>
            {aiLoading ? "⏳ Wird erkannt…" : "🤖 Zählerstand erkennen"}
          </button>
        </>)}
      </div>
    </div>
  );
}

// ─── Mobile Datenaufnahme ────────────────────────────────────────────────────
function MobileDatenaufnahme({ members, onBack }) {
  const currentYear = new Date().getFullYear();
  const gaertenList = getGaerten();
  const [ablesungen, setAblesungen] = useState(() => {
    try { return JSON.parse(localStorage.getItem("gv_ablesungen") || "{}"); } catch(e) { return {}; }
  });
  const [fotos, setFotos] = useState(() => {
    try { return JSON.parse(localStorage.getItem("gv_zaehler_fotos") || "{}"); } catch(e) { return {}; }
  });
  const [selectedGartenId, setSelectedGartenId] = useState(null);
  const [typ, setTyp] = useState("wasser");
  const [eingabe, setEingabe] = useState("");
  const [gespeichert, setGespeichert] = useState(false);
  const [fotoPreview, setFotoPreview] = useState(null);
  const kameraRef = React.useRef();
  const galerieRef = React.useRef();

  const garten = gaertenList.find(g => g.id === selectedGartenId);
  const member = garten ? members.find(m => m.garten === garten.name || (m.zusatzgaerten||[]).includes(garten.name)) : null;
  const vorjahr = garten ? (ablesungen[`${garten.id}__${typ}__${currentYear-1}__EndeB`] || ablesungen[`${garten.id}__${typ}__${currentYear-1}__Ende`] || "–") : "–";
  const hasWechsel = garten ? !!ablesungen[`${garten.id}__${typ}__${currentYear}__ZaehlernummerB`] : false;
  const saveKey = hasWechsel ? "EndeB" : "Ende";
  const fotoKey = garten ? `${garten.id}__${typ}__${currentYear}` : null;
  const aktuellesFoto = fotoKey ? fotos[fotoKey] : null;

  // Fotos vom Server nachladen wenn Garten gewechselt wird
  React.useEffect(() => {
    if (!garten) return;
    const safeGarten = garten.name.replace(/\s+/g, "_");
    ["wasser","strom"].forEach(async t => {
      const key = `${garten.id}__${t}__${currentYear}`;
      if (fotos[key]) return; // schon geladen
      try {
        const res  = await fetch(`${PDF_SERVER_URL}/api/foto/${currentYear}/${safeGarten}/${t}`);
        const data = await res.json();
        if (data.url) {
          setFotos(prev => ({ ...prev, [key]: `${PDF_SERVER_URL}${data.url}?t=${Date.now()}` }));
        }
      } catch(e) {}
    });
  }, [selectedGartenId]);

  function numpad(val) {
    if (val === "⌫") { setEingabe(e => e.slice(0, -1)); return; }
    if (val === "." && eingabe.includes(".")) return;
    setEingabe(e => (e + val).slice(0, 10));
    setGespeichert(false);
  }

  const [fotoZuschnitt, setFotoZuschnitt] = useState(null); // { src, originalFile }

  function handleFoto(e) {
    const file = e.target.files?.[0];
    if (!file || !fotoKey || !garten) return;
    const reader = new FileReader();
    reader.onload = ev => {
      // Öffne Zuschnitt-Dialog
      setFotoZuschnitt({ src: ev.target.result });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  async function uploadFotoBlob(blob) {
    const localUrl = URL.createObjectURL(blob);
    setFotoPreview(localUrl);
    setFotoZuschnitt(null);
    try {
      const safeGartenName = garten.name.replace(/\s+/g, "_");
      const formData = new FormData();
      formData.append("foto", blob, `${typ}.jpg`);
      formData.append("jahr", String(currentYear));
      formData.append("garten", safeGartenName);
      formData.append("typ", typ);
      const res = await fetch(`${PDF_SERVER_URL}/api/foto/upload`, { method: "POST", body: formData });
      if (res.ok) {
        const data = await res.json();
        const updated = { ...fotos, [fotoKey]: `${PDF_SERVER_URL}${data.url}` };
        setFotos(updated);
        localStorage.setItem("gv_zaehler_fotos", JSON.stringify(updated));
      }
    } catch(err) {
      console.error("Foto-Upload fehlgeschlagen:", err);
    }
  }

  function saveAndNext() {
    if (!garten || !eingabe) return;
    const key = `${garten.id}__${typ}__${currentYear}__${saveKey}`;
    const updated = { ...ablesungen, [key]: eingabe };
    setAblesungen(updated);
    localStorage.setItem("gv_ablesungen", JSON.stringify(updated));
    setGespeichert(true);
    setTimeout(() => {
      if (typ === "wasser") {
        setTyp("strom");
        setEingabe(ablesungen[`${garten.id}__strom__${currentYear}__${hasWechsel?"EndeB":"Ende"}`] || "");
        setFotoPreview(fotos[`${garten.id}__strom__${currentYear}`] || null);
        setGespeichert(false);
      } else {
        const idx = gaertenList.findIndex(g => g.id === selectedGartenId);
        const next = gaertenList[idx + 1];
        if (next) {
          setSelectedGartenId(next.id);
          setTyp("wasser");
          setEingabe(ablesungen[`${next.id}__wasser__${currentYear}__Ende`] || "");
          setFotoPreview(fotos[`${next.id}__wasser__${currentYear}`] || null);
          setGespeichert(false);
        }
      }
    }, 800);
  }

  function Odometer({ value }) {
    const digits = (value || "").toString().padStart(5, " ").split("");
    return (
      <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 12 }}>
        {digits.map((d, i) => (
          <div key={i} style={{ width: 44, height: 60, background: d.trim() ? "var(--leafPale)" : "#f5f5f5", borderRadius: 8, border: `2px solid ${d.trim() ? COLORS.leaf : "#ddd"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800, color: COLORS.leaf, fontFamily: "monospace" }}>{d.trim()}</div>
        ))}
      </div>
    );
  }

  // Zuschnitt-Dialog anzeigen wenn Foto ausgewählt
  if (fotoZuschnitt) {
    return (
      <BildZuschnitt
        imageSrc={fotoZuschnitt.src}
        onCrop={uploadFotoBlob}
        onZaehlerstand={(wert) => {
          setEingabe(kaufmaennischRunden(wert));
          setGespeichert(false);
          setFotoZuschnitt(null);
        }}
        onCancel={() => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const MAX = 800;
            let w = img.width, h = img.height;
            if (w > MAX || h > MAX) { if(w>h){h=Math.round(h*MAX/w);w=MAX;}else{w=Math.round(w*MAX/h);h=MAX;} }
            canvas.width=w; canvas.height=h;
            canvas.getContext("2d").drawImage(img,0,0,w,h);
            canvas.toBlob(blob => uploadFotoBlob(blob), "image/jpeg", 0.82);
          };
          img.src = fotoZuschnitt.src;
        }}
      />
    );
  }

  // Gartenauswahl
  if (!selectedGartenId) {
    return (
      <div style={{ padding: 16, paddingBottom: 80 }}>
        <h2 style={{ margin: "0 0 16px", fontSize: 20, fontWeight: 700, color: COLORS.leaf }}>💧⚡ Zähler ablesen {currentYear}</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {gaertenList.map(g => {
            const m = members.find(x => x.garten === g.name || (x.zusatzgaerten||[]).includes(g.name));
            const wDone = !!ablesungen[`${g.id}__wasser__${currentYear}__Ende`] || !!ablesungen[`${g.id}__wasser__${currentYear}__EndeB`];
            const sDone = !!ablesungen[`${g.id}__strom__${currentYear}__Ende`] || !!ablesungen[`${g.id}__strom__${currentYear}__EndeB`];
            const wFoto = !!fotos[`${g.id}__wasser__${currentYear}`];
            const sFoto = !!fotos[`${g.id}__strom__${currentYear}`];
            return (
              <button key={g.id} onClick={() => {
                setSelectedGartenId(g.id); setTyp("wasser");
                setEingabe(ablesungen[`${g.id}__wasser__${currentYear}__Ende`] || ablesungen[`${g.id}__wasser__${currentYear}__EndeB`] || "");
                setFotoPreview(fotos[`${g.id}__wasser__${currentYear}`] || null);
              }} style={{ background: "var(--cardBg)", border: `2px solid ${wDone && sDone ? COLORS.leaf : COLORS.border}`, borderRadius: 12, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.text }}>{g.name}</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>{m ? `${m.vorname} ${m.nachname}` : "Kein Mitglied"}</div>
                </div>
                <div style={{ display: "flex", gap: 6, fontSize: 14, alignItems: "center" }}>
                  <span>{wDone ? "💧✓" : "💧"}</span>
                  <span style={{ fontSize: 11, color: wFoto ? "#27AE60" : "#ccc" }}>📷</span>
                  <span>{sDone ? "⚡✓" : "⚡"}</span>
                  <span style={{ fontSize: 11, color: sFoto ? "#27AE60" : "#ccc" }}>📷</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const isWasser = typ === "wasser";
  const color = isWasser ? "#2E86C1" : "#D4AC0D";
  const unit  = isWasser ? "m³" : "kWh";

  return (
    <div style={{ padding: 16, paddingBottom: 80, maxWidth: 440, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <button onClick={() => { setSelectedGartenId(null); setEingabe(""); setFotoPreview(null); }} style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: COLORS.leaf }}>←</button>
        <div>
          <div style={{ fontWeight: 700, fontSize: 18, color: COLORS.leaf }}>{garten?.name}</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>{member ? `${member.vorname} ${member.nachname}` : "Kein Mitglied"}</div>
        </div>
      </div>

      {/* Typ-Umschalter */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {["wasser","strom"].map(t => (
          <button key={t} onClick={() => {
            setTyp(t);
            setEingabe(ablesungen[`${garten.id}__${t}__${currentYear}__${hasWechsel?"EndeB":"Ende"}`] || "");
            setFotoPreview(fotos[`${garten.id}__${t}__${currentYear}`] || null);
            setGespeichert(false);
          }} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "2px solid", borderColor: typ===t ? color : COLORS.border, background: typ===t ? (t==="wasser"?"var(--leafPale)":"var(--cream)") : "var(--cardBg)", fontWeight: 700, fontSize: 14, cursor: "pointer", color: typ===t ? color : COLORS.textMuted }}>
            {t === "wasser" ? "💧 Wasser" : "⚡ Strom"}
          </button>
        ))}
      </div>

      {/* Vorjahr */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: COLORS.textMuted, fontWeight: 600 }}>
          {isWasser ? "Wasserzähler" : "Stromzähler"}
          {hasWechsel && <span style={{ marginLeft: 6, fontSize: 11, background: color, color: "#fff", borderRadius: 4, padding: "1px 6px" }}>Neuer Zähler</span>}
        </span>
        <span style={{ fontSize: 12, color: COLORS.textMuted }}>Vorjahr: <strong>{vorjahr} {unit}</strong></span>
      </div>

      {/* Odometer + Eingabe */}
      <div style={{ background: "var(--cardBg)", border: `2px solid ${color}44`, borderRadius: 16, padding: 16, marginBottom: 12 }}>
        <Odometer value={eingabe} />
        <input type="text" inputMode="decimal" value={eingabe} onChange={e => { setEingabe(sanitizeAblesewert(e.target.value).slice(0,10)); setGespeichert(false); }}
          placeholder={`Ablesewert (${unit})`}
          style={{ width: "100%", height: 56, border: `2px solid ${color}`, borderRadius: 10, textAlign: "center", fontSize: 22, fontWeight: 700, color: COLORS.leaf, fontFamily: "monospace", background: "var(--inputBg)", boxSizing: "border-box", outline: "none" }} />
        {gespeichert && <div style={{ textAlign: "center", color: "#27AE60", fontWeight: 700, marginTop: 8 }}>✅ Gespeichert!</div>}
      </div>

      {/* Numpad */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, background: "var(--tableStripe)", padding: 10, borderRadius: 14, marginBottom: 12 }}>
        {["1","2","3","4","5","6","7","8","9",".","0","⌫"].map(k => (
          <button key={k} onClick={() => numpad(k)}
            style={{ height: 56, borderRadius: 10, border: "none", background: k==="⌫"?"var(--cardBg)":k==="."?"#f5f5f5":"#fff", fontSize: k==="⌫"?20:24, fontWeight: 700, color: k==="⌫"?"#C0392B":COLORS.leaf, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.1)", fontFamily: "monospace" }}>
            {k}
          </button>
        ))}
      </div>

      {/* Foto-Bereich */}
      <div style={{ background: (aktuellesFoto || fotoPreview) ? "var(--cardBg)" : "var(--tableStripe)", border: `1.5px dashed ${(aktuellesFoto || fotoPreview) ? color : COLORS.border}`, borderRadius: 12, padding: 12, marginBottom: 12 }}>
        {(aktuellesFoto || fotoPreview) ? (
          <div>
            <img src={fotoPreview || aktuellesFoto} alt="Zähler" style={{ width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 8 }} />
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <button onClick={() => kameraRef.current?.click()}
                style={{ flex: 1, padding: "6px 0", borderRadius: 7, border: `1px solid ${color}`, background: "transparent", color, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                📸 Neu aufnehmen
              </button>
              <button onClick={async () => {
                try {
                  const safeGartenName = garten.name.replace(/\s+/g, "_");
                  await fetch(`${PDF_SERVER_URL}/api/foto/${currentYear}/${safeGartenName}/${typ}`, { method: "DELETE" });
                } catch(e) {}
                const updated = { ...fotos };
                delete updated[fotoKey];
                setFotos(updated);
                setFotoPreview(null);
                localStorage.setItem("gv_zaehler_fotos", JSON.stringify(updated));
              }} style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid #C0392B", background: "transparent", color: "#C0392B", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                🗑️
              </button>
            </div>
            <div style={{ fontSize: 10, color: "#27AE60", fontWeight: 600, marginTop: 4, textAlign: "center" }}>✅ Foto vorhanden</div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: COLORS.text, marginBottom: 2 }}>📷 Zähler fotografieren</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted }}>Foto als Nachweis hinterlegen</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => kameraRef.current?.click()} style={{ padding: "8px 12px", borderRadius: 8, border: `1.5px solid ${color}`, background: "var(--cardBg)", color, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                📸 Kamera
              </button>
              <button onClick={() => galerieRef.current?.click()} style={{ padding: "8px 12px", borderRadius: 8, border: `1.5px solid ${COLORS.border}`, background: "var(--cardBg)", color: COLORS.textMuted, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                🖼️ Galerie
              </button>
            </div>
          </div>
        )}
        {/* Kamera-Input mit capture, Galerie ohne */}
        <input ref={kameraRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleFoto} />
        <input ref={galerieRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFoto} />
      </div>

      {/* Aktions-Buttons */}
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={() => {
          if(typ==="wasser"){setTyp("strom");setEingabe(ablesungen[`${garten.id}__strom__${currentYear}__${saveKey}`]||"");setFotoPreview(fotos[`${garten.id}__strom__${currentYear}`]||null);}
          else{const idx=gaertenList.findIndex(g=>g.id===selectedGartenId);const next=gaertenList[idx+1];if(next){setSelectedGartenId(next.id);setTyp("wasser");setEingabe("");setFotoPreview(null);}}
        }} style={{ flex: 1, padding: "14px 0", borderRadius: 12, border: `1.5px solid ${COLORS.border}`, background: "var(--cardBg)", fontWeight: 700, fontSize: 14, color: COLORS.textMuted, cursor: "pointer" }}>
          Überspringen
        </button>
        <button onClick={saveAndNext} style={{ flex: 2, padding: "14px 0", borderRadius: 12, border: "none", background: eingabe ? COLORS.leaf : "var(--border)", color: "#fff", fontWeight: 700, fontSize: 15, cursor: eingabe?"pointer":"not-allowed" }}>
          Speichern & Weiter →
        </button>
      </div>
    </div>
  );
}


function ServerDebug({ syncFromServer, load, PDF_SERVER_URL }) {
  const [log, setLog] = useState([]);
  const [running, setRunning] = useState(false);

  async function runTest() {
    setRunning(true);
    const lines = [];
    const add = (msg) => { lines.push(msg); setLog([...lines]); };

    add("🔍 Starte Verbindungstest...");
    add(`📡 Basis-URL: ${PDF_SERVER_URL}`);

    // Teste verschiedene mögliche Endpunkte
    const candidates = [
      `${PDF_SERVER_URL}/api/data`,
      PDF_SERVER_URL.replace("myds.me", "myds.me:3443") + "/api/data",
      PDF_SERVER_URL + ":3443/api/data",
      PDF_SERVER_URL + "/data",
    ];
    add("🔎 Teste mögliche Endpunkte...");
    for (const url of candidates) {
      try {
        const r = await fetch(url, { signal: AbortSignal.timeout(5000) });
        add(`  ${url} → ${r.status} ${r.ok ? "✅" : "❌"}`);
        if (r.ok) { add("  ☝️ Diese URL funktioniert!"); break; }
      } catch(e) {
        add(`  ${url} → ❌ ${e.name === "AbortError" ? "Timeout" : e.message}`);
      }
    }
    add(`📡 URL: ${PDF_SERVER_URL}`);

    // Test 1: Keys abrufen
    try {
      add("1️⃣ Keys-Liste abrufen...");
      const ctrl = new AbortController();
      setTimeout(() => ctrl.abort(), 10000);
      const res = await fetch(`${PDF_SERVER_URL}/api/data`, { signal: ctrl.signal });
      add(`   Status: ${res.status} ${res.ok ? "✅" : "❌"}`);
      if (res.ok) {
        const keys = await res.json();
        add(`   Keys (${keys.length}): ${keys.join(", ")}`);

        // Test 2: Jeden Key einzeln
        add("2️⃣ Keys laden...");
        for (const key of keys) {
          try {
            const ctrl2 = new AbortController();
            setTimeout(() => ctrl2.abort(), 15000);
            const r = await fetch(`${PDF_SERVER_URL}/api/data/${key}`, { signal: ctrl2.signal });
            const d = await r.json();
            const preview = d.value ? String(d.value).slice(0, 40) : "null";
            add(`   ${key}: ${r.ok ? "✅" : "❌"} ${preview}`);
          } catch(e) {
            add(`   ${key}: ❌ ${e.message}`);
          }
        }
      }
    } catch(e) {
      add(`❌ Fehler: ${e.name === "AbortError" ? "Timeout (>10s)" : e.message}`);
    }

    add("✅ Test abgeschlossen");
    setRunning(false);
  }

  async function runSync() {
    setRunning(true);
    setLog(["🔄 Starte Sync..."]);
    const { loadedAny, errorMsg } = await syncFromServer();
    load();
    setLog(prev => [
      ...prev,
      loadedAny ? "✅ Daten geladen + State aktualisiert" : "⚠️ Keine Daten geladen",
      errorMsg ? `❌ Fehler: ${errorMsg}` : "Kein Fehler"
    ]);
    setRunning(false);
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      <div style={{display:"flex",gap:6}}>
        <button onClick={runTest} disabled={running}
          style={{flex:1,background:"var(--tableStripe)",border:"1px solid var(--border)",color:"var(--text)",borderRadius:8,padding:"8px 6px",cursor:running?"wait":"pointer",fontSize:11,fontFamily:"inherit",fontWeight:600}}>
          🔍 Verbindung testen
        </button>
        <button onClick={runSync} disabled={running}
          style={{flex:1,background:"var(--tableStripe)",border:"1px solid var(--border)",color:"var(--text)",borderRadius:8,padding:"8px 6px",cursor:running?"wait":"pointer",fontSize:11,fontFamily:"inherit",fontWeight:600}}>
          🔄 Sync + laden
        </button>
      </div>
      {log.length > 0 && (
        <div style={{background:"#111",borderRadius:8,padding:"10px 12px",maxHeight:200,overflowY:"auto"}}>
          {log.map((l, i) => (
            <div key={i} style={{fontFamily:"monospace",fontSize:10,color:"#0f0",lineHeight:1.6,whiteSpace:"pre-wrap",wordBreak:"break-all"}}>{l}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function BackupSection({ exportData, importRef, importData, syncFromServer, load, PDF_SERVER_URL, sectionLabel, btn }) {
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
      <div style={{ background: "var(--tableStripe)", padding: "11px 14px", borderBottom: "1px solid var(--border)" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: 8 }}>
          🛠 Backup, Sync &amp; Erweitert
        </span>
      </div>
      <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>

                    {sectionLabel("Backup")}
                    <button style={{width:"100%",background:"var(--tableStripe)",border:"1px solid var(--border)",color:"var(--text)",borderRadius:10,padding:"10px 14px",cursor:"pointer",fontSize:13,fontFamily:"inherit",fontWeight:600,textAlign:"left",display:"flex",alignItems:"center",gap:10}}
                      onClick={async (e) => { const b=e.currentTarget; const orig=b.innerHTML; b.disabled=true; b.innerHTML="<span>⏳</span> Wird geladen…"; await exportData(); b.disabled=false; b.innerHTML=orig; }}>
                      <span style={{fontSize:17}}>💾</span>
                      <div><div>Backup vom Server herunterladen</div><div style={{fontSize:11,color:"var(--textMuted)",fontWeight:400}}>Alle Daten direkt vom Server — vollständigstes Backup</div></div>
                    </button>
                    {btn("Backup importieren & wiederherstellen", "📂", () => importRef.current?.click())}
                    <input ref={importRef} type="file" accept=".json" style={{display:"none"}}
                      onChange={e=>{const f=e.target.files?.[0];if(f)importData(f);e.target.value="";}} />

                    {sectionLabel("Server-Synchronisation")}
                    <button style={{width:"100%",background:"var(--tableStripe)",border:"1px solid var(--border)",color:"var(--text)",borderRadius:10,padding:"10px 14px",cursor:"pointer",fontSize:13,fontFamily:"inherit",fontWeight:600,textAlign:"left",display:"flex",alignItems:"center",gap:10}}
                      onClick={async (e) => {
                        const b = e.currentTarget; const orig = b.innerHTML;
                        b.disabled = true; b.innerHTML = "<span>⏳</span> Wird gesendet…";
                        const keys = Object.keys(_gvStore).filter(k => k !== "gv_zaehler_fotos");
                        let ok = 0;
                        for (const key of keys) {
                          const val = _gvStore[key];
                          if (val) try { await fetch(`${PDF_SERVER_URL}/api/data/${key}`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({value:val})}); ok++; } catch(e) {}
                        }
                        b.innerHTML = `<span>✅</span> ${ok} Datensätze gesendet`;
                        setTimeout(() => { b.disabled = false; b.innerHTML = orig; }, 2500);
                      }}>
                      <span style={{fontSize:17}}>⬆️</span>
                      <div><div>Browser → Server senden</div><div style={{fontSize:11,color:"var(--textMuted)",fontWeight:400}}>Nur bei Problemen nötig — normalerweise automatisch</div></div>
                    </button>
                    <button style={{width:"100%",background:"var(--tableStripe)",border:"1px solid var(--border)",color:"var(--text)",borderRadius:10,padding:"10px 14px",cursor:"pointer",fontSize:13,fontFamily:"inherit",fontWeight:600,textAlign:"left",display:"flex",alignItems:"center",gap:10}}
                      onClick={async (e) => {
                        const b = e.currentTarget; const orig = b.innerHTML;
                        b.disabled = true; b.innerHTML = "<span>⏳</span> Wird geladen…";
                        const { loadedAny, errorMsg } = await syncFromServer();
                        load();
                        if (errorMsg) { b.innerHTML = `<span>⚠️</span> ${errorMsg}`; }
                        else if (loadedAny) { b.innerHTML = "<span>✅</span> Daten geladen!"; }
                        else { b.innerHTML = "<span>⚠️</span> Keine Daten auf Server"; }
                        setTimeout(() => { b.disabled = false; b.innerHTML = orig; }, 2500);
                      }}>
                      <span style={{fontSize:17}}>⬇️</span>
                      <div><div>Server → Browser laden</div><div style={{fontSize:11,color:"var(--textMuted)",fontWeight:400}}>Aktuelle Server-Daten in diesen Browser holen</div></div>
                    </button>

                    {sectionLabel("Browser")}
                    <button style={{width:"100%",background:"rgba(231,76,60,0.08)",border:"1px solid rgba(231,76,60,0.3)",color:"var(--danger,#C0392B)",borderRadius:10,padding:"10px 14px",cursor:"pointer",fontSize:13,fontFamily:"inherit",fontWeight:600,textAlign:"left",display:"flex",alignItems:"center",gap:10}}
                      onClick={()=>{
                        if(!window.confirm("Lokalen Browser-Cache löschen? Die Daten bleiben auf dem Server erhalten.")) return;
                        // _gvStore leeren (ohne Server-Tombstones zu senden)
                        Object.keys(_gvStore).forEach(k => delete _gvStore[k]);
                        window.location.reload();
                      }}>
                      <span style={{fontSize:17}}>🗑️</span>
                      <div><div>Browser-Cache löschen</div><div style={{fontSize:11,opacity:0.7,fontWeight:400}}>Server-Daten bleiben erhalten</div></div>
                    </button>

                    {sectionLabel("Debug")}
                    <ServerDebug syncFromServer={syncFromServer} load={load} PDF_SERVER_URL={PDF_SERVER_URL} />

      </div>
    </div>
  );
}

function AdminLoginEditor() {
  const [users, setUsers] = useState(() => getAdminUsers());
  const [editing, setEditing] = useState(null); // index
  const [form, setForm] = useState({ username: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [saved, setSaved] = useState(false);

  function startEdit(i) {
    setEditing(i);
    setForm({ username: users[i].username, password: users[i].password });
    setShowPw(false);
    setSaved(false);
  }

  function save() {
    if (!form.username.trim() || !form.password.trim()) return;
    const updated = users.map((u, i) => i === editing ? { ...u, username: form.username.trim(), password: form.password.trim() } : u);
    setUsers(updated);
    saveAdminUsers(updated);
    setEditing(null);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {users.map((u, i) => (
        <div key={i} style={{ background: "var(--cardBg)", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
          {/* Kopfzeile */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>👤 {u.username}</div>
              <div style={{ fontSize: 11, color: "var(--textMuted)" }}>{u.rolle}</div>
            </div>
            <button onClick={() => editing === i ? setEditing(null) : startEdit(i)}
              style={{ background: editing === i ? "rgba(231,76,60,0.1)" : "var(--tableStripe)", border: `1px solid ${editing === i ? "rgba(231,76,60,0.3)" : "var(--border)"}`, color: editing === i ? "#C0392B" : "var(--text)", borderRadius: 8, padding: "5px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              {editing === i ? "Abbrechen" : "✏️ Ändern"}
            </button>
          </div>
          {/* Edit-Form */}
          {editing === i && (
            <div style={{ padding: "0 12px 12px", borderTop: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--textMuted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Benutzername</div>
                <input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 8, border: "1.5px solid var(--border)", fontSize: 13, fontFamily: "inherit", background: "var(--mainBg)", color: "var(--text)" }} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--textMuted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Passwort</div>
                <div style={{ position: "relative" }}>
                  <input type={showPw ? "text" : "password"} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    style={{ width: "100%", boxSizing: "border-box", padding: "8px 36px 8px 10px", borderRadius: 8, border: "1.5px solid var(--border)", fontSize: 13, fontFamily: "inherit", background: "var(--mainBg)", color: "var(--text)" }} />
                  <button onClick={() => setShowPw(v => !v)}
                    style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 15, color: "var(--textMuted)", padding: 0, lineHeight: 1 }}>
                    {showPw ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>
              <button onClick={save}
                style={{ width: "100%", padding: "9px", background: "#2D5A27", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginTop: 2 }}>
                💾 Speichern
              </button>
            </div>
          )}
        </div>
      ))}
      {saved && <div style={{ textAlign: "center", fontSize: 12, color: "#2D5A27", fontWeight: 600 }}>✓ Gespeichert</div>}
    </div>
  );
}

function EinstellungenModal({ onClose, exportData, importRef, importData, syncFromServer, load, PDF_SERVER_URL }) {
  const [pos, setPos]       = useState({ x: 20, y: 80 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const modalRef = React.useRef();

  function onDragStart(e) {
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragging(true);
    setDragStart({ mx: clientX, my: clientY, px: pos.x, py: pos.y });
  }

  useEffect(() => {
    if (!dragging) return;
    function onMove(e) {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = clientX - dragStart.mx;
      const dy = clientY - dragStart.my;
      const el = modalRef.current;
      const maxX = window.innerWidth  - (el ? el.offsetWidth  : 300);
      const maxY = window.innerHeight - (el ? el.offsetHeight : 400);
      setPos({ x: Math.max(0, Math.min(maxX, dragStart.px + dx)), y: Math.max(0, Math.min(maxY, dragStart.py + dy)) });
    }
    function onUp() { setDragging(false); }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend",  onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend",  onUp);
    };
  }, [dragging, dragStart]);

  const btn = (label, icon, onClick, danger = false) => (
    <button style={{width:"100%",background:danger?"rgba(231,76,60,0.08)":"var(--tableStripe)",border:`1px solid ${danger?"rgba(231,76,60,0.3)":"var(--border)"}`,color:danger?"var(--danger,#C0392B)":"var(--text)",borderRadius:10,padding:"10px 14px",cursor:"pointer",fontSize:13,fontFamily:"inherit",fontWeight:600,textAlign:"left",display:"flex",alignItems:"center",gap:10}}
      onClick={onClick}>
      <span style={{fontSize:17}}>{icon}</span>{label}
    </button>
  );

  const sectionLabel = (text) => (
    <div style={{fontSize:10,fontWeight:700,color:"var(--textMuted)",textTransform:"uppercase",letterSpacing:1,marginTop:4,paddingTop:4,borderTop:"1px solid var(--border)"}}>{text}</div>
  );

  return (
    <div style={{position:"fixed",inset:0,zIndex:4000}}>
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.35)",pointerEvents:"auto",zIndex:4001}} onClick={onClose} />
      <div ref={modalRef} style={{position:"fixed",left:pos.x,top:pos.y,zIndex:4002,background:"var(--cardBg)",borderRadius:16,width:880,maxWidth:"calc(100vw - 24px)",maxHeight:"calc(100vh - 40px)",boxShadow:"0 8px 40px rgba(0,0,0,0.35)",display:"flex",flexDirection:"column",pointerEvents:"auto",userSelect:"none"}}>

        {/* Drag-Handle / Header */}
        <div onMouseDown={onDragStart} onTouchStart={onDragStart}
          style={{padding:"12px 16px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:dragging?"grabbing":"grab",borderRadius:"16px 16px 0 0",background:"var(--tableStripe)",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{display:"flex",flexDirection:"column",gap:3}}>
              {[0,1,2].map(i=>(
                <div key={i} style={{display:"flex",gap:3}}>
                  {[0,1].map(j=><div key={j} style={{width:3,height:3,borderRadius:"50%",background:"var(--textMuted)",opacity:0.5}} />)}
                </div>
              ))}
            </div>
            <span style={{fontWeight:700,fontSize:14,color:"var(--text)"}}>⚙️ Einstellungen</span>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"var(--textMuted)",padding:4,lineHeight:1}}>✕</button>
        </div>

        {/* 3-Spalten-Inhalt */}
        <div style={{overflowY:"auto",padding:"14px 16px 18px"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1.4fr",gap:16,alignItems:"start"}}>

            {/* Spalte 1: Admin-Login + Server-Daten */}
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {sectionLabel("Admin-Login")}
              <AdminLoginEditor />
              {sectionLabel("Server-Daten")}
              <ServerDataSummary />
            </div>

            {/* Spalte 2: Rücklage + Vorstand */}
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {sectionLabel("Rücklage")}
              <RuecklageKorrektur />
              {sectionLabel("Vorstand")}
              <VorstandNamen />
            </div>

            {/* Spalte 3: Backup, Sync & Erweitert */}
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <BackupSection exportData={exportData} importRef={importRef} importData={importData} syncFromServer={syncFromServer} load={load} PDF_SERVER_URL={PDF_SERVER_URL} sectionLabel={sectionLabel} btn={btn} />
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

function RuecklageKorrektur() {
  const [grundbetrag, setGrundbetrag] = useState(() => {
    const v = localStorage.getItem("gv_ruecklage_grundbetrag");
    return v !== null ? v : "";
  });

  function handleChange(val) {
    setGrundbetrag(val);
    localStorage.setItem("gv_ruecklage_grundbetrag", val);
  }

  return (
    <div style={{ background: "var(--tableStripe)", border: "1px solid var(--border)", borderRadius: 10, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 12, color: "var(--textMuted)", lineHeight: 1.5 }}>
        Vorhandener Betrag in der Rücklage vor Nutzung dieser App. Wird zum berechneten Saldo addiert.
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="number" step="0.01" placeholder="0,00"
          value={grundbetrag}
          onChange={e => handleChange(e.target.value)}
          style={{ flex: 1, padding: "7px 10px", borderRadius: 7, border: "1px solid var(--border)", fontSize: 14, fontWeight: 700, background: "var(--cardBg)", color: "var(--text)", fontFamily: "inherit" }}
        />
        <span style={{ fontSize: 13, color: "var(--textMuted)", fontWeight: 600 }}>€</span>
      </div>
      {grundbetrag && parseFloat(grundbetrag) > 0 && (
        <div style={{ fontSize: 11, color: "#2D5A27", background: "#f0f8ed", borderRadius: 6, padding: "4px 8px" }}>
          Grundbetrag: {parseFloat(grundbetrag).toFixed(2).replace(".",",")} € wird zur Rücklage addiert
        </div>
      )}
    </div>
  );
}

// Vorstand-Namen für Einstellungen (werden z.B. in PDFs verwendet)
const VORSTAND_ROLLEN = [
  { key: "vorsitzender",   label: "Vorstandsvorsitzender"              },
  { key: "stv1",           label: "1. stellv. Vorstandsvorsitzender"   },
  { key: "stv2",           label: "2. stellv. Vorstandsvorsitzender"   },
];
const GV_VORSTAND_NAMEN_KEY = "gv_vorstand_namen";

function loadVorstandNamen() {
  try { return JSON.parse(localStorage.getItem(GV_VORSTAND_NAMEN_KEY) || "{}"); } catch(e) { return {}; }
}

function VorstandNamen() {
  const [namen, setNamen] = useState(loadVorstandNamen);

  function handleChange(key, val) {
    const updated = { ...namen, [key]: val };
    setNamen(updated);
    localStorage.setItem(GV_VORSTAND_NAMEN_KEY, JSON.stringify(updated));
  }

  return (
    <div style={{ background: "var(--tableStripe)", border: "1px solid var(--border)", borderRadius: 10, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
      {VORSTAND_ROLLEN.map(({ key, label }) => (
        <div key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: "var(--textMuted)", textTransform: "uppercase", letterSpacing: 0.7 }}>
            {label}
          </label>
          <input
            type="text"
            placeholder={`Name eintragen…`}
            value={namen[key] || ""}
            onChange={e => handleChange(key, e.target.value)}
            style={{ padding: "7px 10px", borderRadius: 7, border: "1px solid var(--border)", fontSize: 13, background: "var(--cardBg)", color: "var(--text)", fontFamily: "inherit" }}
          />
        </div>
      ))}
    </div>
  );
}

function ServerDataSummary() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  function laden() {
    setLoading(true);
    // Daten direkt aus localStorage lesen — bereits beim App-Start vom Server synchronisiert
    const parse = (k, fallback) => { try { return JSON.parse(localStorage.getItem(k) || JSON.stringify(fallback)); } catch(e) { return fallback; } };
    const members  = parse("gv_members", []);
    const abl      = parse("gv_ablesungen", {});
    const finanzen = parse("gv_finanzen", []);
    const termine  = !!localStorage.getItem("gv_termine");
    const vorstand = parse("gv_vorstand", []);
    const mgv      = parse("gv_mgv", []);
    const aktiv    = members.filter(m => m.status !== "Ehemaliges Mitglied");
    setInfo({
      mitglieder: aktiv.length,
      gaerten:    new Set(aktiv.flatMap(m => [m.garten, ...(m.zusatzgaerten||[])]).filter(Boolean)).size,
      ablesungen: Object.keys(abl).length,
      finanzen:   finanzen.length,
      termine,
      vorstand:   vorstand.length,
      mgv:        mgv.length,
      letzteSync: localStorage.getItem("gv_last_sync") || null,
    });
    setLoading(false);
  }

  useEffect(() => { laden(); }, []);

  const row = (label, value, icon) => (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:"1px solid var(--border)"}}>
      <span style={{fontSize:12,color:"var(--textMuted)",display:"flex",alignItems:"center",gap:6}}><span>{icon}</span>{label}</span>
      <span style={{fontSize:12,fontWeight:700,color:"var(--text)"}}>{value}</span>
    </div>
  );

  if (loading) return (
    <div style={{background:"var(--tableStripe)",borderRadius:10,padding:"12px 14px",fontSize:12,color:"var(--textMuted)",textAlign:"center"}}>
      ⏳ Server wird abgefragt…
    </div>
  );

  if (info === false) return (
    <div style={{background:"rgba(231,76,60,0.08)",border:"1px solid rgba(231,76,60,0.2)",borderRadius:10,padding:"12px 14px",fontSize:12,color:"var(--danger,#C0392B)",textAlign:"center",display:"flex",flexDirection:"column",gap:6}}>
      <span>⚠️ Server nicht erreichbar</span>
      <button onClick={laden} style={{background:"none",border:"1px solid rgba(231,76,60,0.3)",borderRadius:6,padding:"4px 10px",cursor:"pointer",fontSize:11,color:"var(--danger,#C0392B)",fontFamily:"inherit"}}>Erneut versuchen</button>
    </div>
  );

  return (
    <div style={{background:"var(--tableStripe)",border:"1px solid var(--border)",borderRadius:10,padding:"12px 14px",display:"flex",flexDirection:"column",gap:0}}>
      {row("Aktive Mitglieder", info.mitglieder, "👥")}
      {row("Belegte Gärten", info.gaerten, "🌱")}
      {row("Zählereinträge", info.ablesungen, "💧")}
      {row("Finanzbuchungen", info.finanzen, "💶")}
      {row("Termine gespeichert", info.termine ? "Ja" : "Nein", "📅")}
      {row("Vorstandsversammlungen", info.vorstand, "👔")}
      {row("Mitgliederversammlungen", info.mgv, "🏛️")}
      <button onClick={laden} style={{marginTop:8,background:"none",border:"1px solid var(--border)",borderRadius:6,padding:"5px 0",cursor:"pointer",fontSize:11,color:"var(--textMuted)",fontFamily:"inherit",fontWeight:600}}>
        🔄 Aktualisieren
      </button>
    </div>
  );
}

export default function App() {
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("gv_user")); } catch(e) { return null; }
  });

  function handleLogin(user) {
    sessionStorage.setItem("gv_user", JSON.stringify(user));
    // Hard Refresh nach Login: lädt aktuelle Daten vom Server
    window.location.reload(true);
  }

  function handleLogout() {
    sessionStorage.removeItem("gv_user");
    setCurrentUser(null);
  }

  function handlePwChanged(user) {
    const updated = { ...user, mustChangePw: false };
    sessionStorage.setItem("gv_user", JSON.stringify(updated));
    setCurrentUser(updated);
  }

  if (!currentUser) return <LoginScreen onLogin={handleLogin} />;
  if (currentUser.mustChangePw) return <ChangePasswordScreen user={currentUser} onChanged={handlePwChanged} onLogout={handleLogout} />;
  if (currentUser.isMember) return <MemberPortal currentUser={currentUser} onLogout={handleLogout} />;
  return <AppMain currentUser={currentUser} onLogout={handleLogout} />;
}

// ─── Alle verfügbaren Nav-Seiten ──────────────────────────────────────────────
const ALL_NAV_ITEMS = [
  { icon: "🏠", label: "Übersicht",       v: "dashboard" },
  { icon: "👥", label: "Mitglieder",      v: "list" },
  { icon: "🏡", label: "Gärten",          v: "gaerten" },
  { icon: "💶", label: "Finanzen",        v: "finanzen" },
  { icon: "💧", label: "Ablesungen",      v: "ablesungen" },
  { icon: "📅", label: "Kalender",        v: "kalender" },
  { icon: "🎉", label: "Feste",           v: "feste" },
  { icon: "🛒", label: "Kasse",           v: "kasse" },
  { icon: "📝", label: "Datenaufnahme",   v: "datenaufnahme" },
  { icon: "🧾", label: "Jahresrechnungen",v: "jahresrechnung" },
  { icon: "📦", label: "Archiv",          v: "archiv" },
  { icon: "🗂️", label: "Kontoauszüge",   v: "kontoauszuege" },
  { icon: "📊", label: "Bilanzen",        v: "bilanzen" },
  { icon: "👔", label: "Vorstand",        v: "vorstand" },
  { icon: "🏛️", label: "Mitgliedervers.",v: "mitgliederversammlung" },
  { icon: "💬", label: "Nachrichten",     v: "nachrichten" },
  { icon: "📋", label: "Login-Protokoll", v: "portallog" },
];

const NAV_BAR_KEY = "gv_mobile_navbar";
const NAV_BAR_DEFAULT = ["dashboard", "list", "gaerten", "finanzen", "ablesungen"];
const NAV_BAR_MAX = 5;

function getNavBarItems() {
  try {
    const saved = JSON.parse(localStorage.getItem(NAV_BAR_KEY));
    if (Array.isArray(saved) && saved.length > 0) return saved;
  } catch(e) {}
  return NAV_BAR_DEFAULT;
}

function NavBarEditor({ onClose }) {
  const [selected, setSelected] = useState(() => getNavBarItems());

  function toggle(v) {
    setSelected(prev => {
      if (prev.includes(v)) return prev.filter(x => x !== v);
      if (prev.length >= NAV_BAR_MAX) return prev; // max 5
      return [...prev, v];
    });
  }

  function save() {
    localStorage.setItem(NAV_BAR_KEY, JSON.stringify(selected));
    onClose();
  }

  function reset() {
    setSelected([...NAV_BAR_DEFAULT]);
  }

  const bg = getTheme() === "dark" ? "#1a1a1a" : "#fff";
  const textColor = getTheme() === "dark" ? "#fff" : "#1C2833";
  const mutedColor = getTheme() === "dark" ? "rgba(255,255,255,0.5)" : "#666";
  const leafColor = "#2D5A27";

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100 }} />
      {/* Sheet */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: bg, borderRadius: "18px 18px 0 0", zIndex: 1101, boxShadow: "0 -8px 40px rgba(0,0,0,0.3)", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ padding: "16px 20px 12px", borderBottom: `1px solid ${getTheme() === "dark" ? "rgba(255,255,255,0.1)" : "#eee"}` }}>
          <div style={{ width: 36, height: 4, background: getTheme() === "dark" ? "rgba(255,255,255,0.2)" : "#ddd", borderRadius: 2, margin: "0 auto 14px" }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: textColor }}>Navigationsleiste anpassen</div>
              <div style={{ fontSize: 12, color: mutedColor, marginTop: 2 }}>
                {selected.length}/{NAV_BAR_MAX} ausgewählt
                {selected.length >= NAV_BAR_MAX && <span style={{ color: "#E74C3C", marginLeft: 6 }}>· Maximum erreicht</span>}
              </div>
            </div>
            <button onClick={reset} style={{ background: "none", border: `1px solid ${mutedColor}`, color: mutedColor, borderRadius: 8, padding: "5px 10px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
              Zurücksetzen
            </button>
          </div>
        </div>

        {/* Vorschau der aktuellen Bar */}
        <div style={{ padding: "10px 20px", background: leafColor, display: "flex", justifyContent: "space-around" }}>
          {selected.map(v => {
            const item = ALL_NAV_ITEMS.find(x => x.v === v);
            if (!item) return null;
            return (
              <div key={v} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <span style={{ fontSize: 18 }}>{item.icon}</span>
                <span style={{ fontSize: 9, color: "#fff", fontWeight: 600 }}>{item.label}</span>
              </div>
            );
          })}
          {/* Mehr-Platzhalter */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <span style={{ fontSize: 18 }}>☰</span>
            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.6)", fontWeight: 600 }}>Mehr</span>
          </div>
        </div>

        {/* Liste aller Seiten */}
        <div style={{ overflowY: "auto", flex: 1, padding: "8px 0 20px" }}>
          {ALL_NAV_ITEMS.map(item => {
            const active = selected.includes(item.v);
            const disabled = !active && selected.length >= NAV_BAR_MAX;
            return (
              <button key={item.v} onClick={() => !disabled && toggle(item.v)}
                style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "13px 20px", background: "none", border: "none", cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: disabled ? 0.35 : 1 }}>
                <span style={{ fontSize: 22, width: 28, textAlign: "center" }}>{item.icon}</span>
                <span style={{ flex: 1, fontSize: 15, color: textColor, fontWeight: active ? 600 : 400, textAlign: "left" }}>{item.label}</span>
                {/* Toggle */}
                <div style={{ width: 44, height: 26, borderRadius: 13, background: active ? leafColor : (getTheme() === "dark" ? "#444" : "#ddd"), position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                  <div style={{ position: "absolute", top: 3, left: active ? 21 : 3, width: 20, height: 20, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.3)", transition: "left 0.2s" }} />
                </div>
              </button>
            );
          })}
        </div>

        {/* Speichern */}
        <div style={{ padding: "12px 20px 28px", borderTop: `1px solid ${getTheme() === "dark" ? "rgba(255,255,255,0.1)" : "#eee"}` }}>
          <button onClick={save}
            style={{ width: "100%", padding: "14px", background: leafColor, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            ✓ Speichern
          </button>
        </div>
      </div>
    </>
  );
}


function MobileMoreMenu({ view, setView, onLogout, onEditNav }) {
  const [open, setOpen] = useState(false);
  const navBarItems = getNavBarItems();
  const moreItems = ALL_NAV_ITEMS.filter(x => !navBarItems.includes(x.v));
  const moreViews = moreItems.map(x => x.v);
  const isMoreActive = moreViews.includes(view);
  return (
    <>
      {open && (
        <div style={{ position: "fixed", inset: 0, zIndex: 999 }} onClick={() => setOpen(false)} />
      )}
      {open && (
        <div style={{ position: "fixed", bottom: 68, right: 8, background: getTheme() === "dark" ? "#1a1a1a" : "#2D5A27", borderRadius: 14, padding: "8px 0", zIndex: 1001, boxShadow: "0 8px 32px rgba(0,0,0,0.5)", minWidth: 210 }}>
          {moreItems.map(item => (
            <button key={item.v} onClick={() => { setView(item.v); setOpen(false); }}
              style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "11px 18px", background: view === item.v ? "rgba(255,255,255,0.15)" : "none", border: "none", color: "#fff", fontSize: 14, fontWeight: view === item.v ? 700 : 500, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
              <span style={{ fontSize: 18 }}>{item.icon}</span>{item.label}
            </button>
          ))}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.15)", margin: "6px 0" }} />
          <button onClick={() => { setOpen(false); onEditNav(); }}
            style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "11px 18px", background: "none", border: "none", color: "rgba(255,255,255,0.75)", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>
            <span style={{ fontSize: 18 }}>⚙️</span> Navigationsleiste anpassen
          </button>
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.15)", margin: "6px 0" }} />
          <button onClick={() => { setOpen(false); onLogout(); }}
            style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "11px 18px", background: "none", border: "none", color: "rgba(255,100,100,0.9)", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            <span style={{ fontSize: 18 }}>⏻</span> Abmelden
          </button>
        </div>
      )}
      <button onClick={() => setOpen(v => !v)}
        style={{ background: isMoreActive ? "rgba(255,255,255,0.15)" : "none", border: "none", color: isMoreActive ? "#fff" : "rgba(255,255,255,0.55)", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "4px 8px", borderRadius: 8, minWidth: 44 }}>
        <span style={{ fontSize: 20 }}>☰</span>
        <span style={{ fontSize: 9, fontWeight: 600, fontFamily: "inherit" }}>Mehr</span>
      </button>
    </>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
function AppMain({ currentUser, onLogout }) {
  const isMobile = localStorage.getItem("gv_viewmode") === "mobile";
  const [navBarEditor, setNavBarEditor] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(() => getTheme());

  // Theme-Wechsel beobachten
  React.useEffect(() => {
    const handler = () => setCurrentTheme(getTheme());
    window.addEventListener("themechange", handler);
    return () => window.removeEventListener("themechange", handler);
  }, []);

  const isDark = currentTheme === "dark";
  const sidebarBg = isDark ? "#111" : COLORS.leaf;
  const [members, setMembers] = useState([]);
  const [view, setViewRaw] = useState("dashboard");

  function setView(newView) {
    window.__setViewGlobal = setView;
    setViewRaw(newView);
  }
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("Alle");
  const [notification, setNotification] = useState(null);
  const [docUploadOpen, setDocUploadOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [portalPreview, setPortalPreview] = useState(null);
  const [wechselModal, setWechselModal] = useState(null);
  const [previousView, setPreviousView] = useState("list");
  const [selectedGarten, setSelectedGarten] = useState(null);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [rechnungsPreviewMember, setRechnungsPreviewMember] = useState(null);
  const [syncStatus, setSyncStatus] = useState("idle");
  const [einstellungenOpen, setEinstellungenOpen] = useState(false);
  const [syncState, setSyncState] = useState("idle"); // "idle"|"saving"|"saved"|"error"
  const importRef = React.useRef();

  const [initialLoading, setInitialLoading] = useState(true);
  // dataEpoch: wird bei jeder vom Server erkannten Änderung erhöht.
  // Als key auf <main> erzwingt es einen Remount aller Ansichten,
  // sodass jede Komponente ihre Daten frisch aus dem Store liest.
  const [dataEpoch, setDataEpoch] = useState(0);

  useEffect(() => {
    initStorage();
    setSyncStatus("syncing");
    // ZUERST Server-Sync abwarten, dann Migration auf echten Daten, dann State laden
    (async () => {
      try {
        const result = await syncFromServer();
        if (result.errorMsg) console.warn("Initial-Sync-Fehler:", result.errorMsg);
        else runMigrationsAfterSync();
      } catch(e) { console.warn("Initial-Sync-Exception:", e); }
      load();
      setSyncStatus("done");
      setInitialLoading(false);
    })();
  }, []);

  // Auto-Refresh: bei Tab-Focus und alle 60 Sekunden vom Server laden
  useEffect(() => {
    let isActive = true;
    let lastRefresh = Date.now();

    async function refreshFromServer(reason) {
      if (!isActive) return;
      // Bei Tab-Focus nur wenn länger als 10s nicht refreshed wurde
      if (reason === "focus" && Date.now() - lastRefresh < 10000) return;
      lastRefresh = Date.now();
      console.log("🔄 Auto-Refresh (" + reason + ")");
      const result = await syncFromServer();
      if (!isActive) return;
      // Nur bei tatsächlichen Änderungen: Members neu laden + alle Ansichten remounten
      if (result.changedKeys && result.changedKeys.length > 0) {
        console.log("📥 Server-Änderungen erkannt:", result.changedKeys.join(", "));
        load();
        setDataEpoch(e => e + 1);
      }
    }

    // Bei Tab-Focus
    const onFocus = () => refreshFromServer("focus");
    const onVisibility = () => {
      if (document.visibilityState === "visible") refreshFromServer("visibility");
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    // Alle 20 Sekunden im Hintergrund — hält alle Geräte synchron
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") refreshFromServer("interval");
    }, 20000);

    return () => {
      isActive = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(interval);
    };
  }, []);

  async function exportData() {
    const date = new Date().toISOString().slice(0, 10);
    const filename = `gartenverein_backup_${date}.json`;
    try {
      // Direkt vom Server laden — vollständigstes Backup
      const keysRes = await fetch(`${PDF_SERVER_URL}/api/data`);
      if (!keysRes.ok) throw new Error("Server nicht erreichbar");
      const keys = await keysRes.json();
      const data = {};
      await Promise.allSettled(keys.map(async key => {
        const res = await fetch(`${PDF_SERVER_URL}/api/data/${key}`);
        const d   = await res.json();
        if (d.value) data[key] = d.value;
      }));
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: "JSON Backup", accept: { "application/json": [".json"] } }],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch(e) { if (e.name === "AbortError") return; }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch(e) {
      alert("❌ Backup fehlgeschlagen: " + e.message);
    }
  }

  function importData(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        Object.entries(data).forEach(([k, v]) => localStorage.setItem(k, v));
        alert("✅ Import erfolgreich! Seite wird neu geladen...");
        window.location.reload();
      } catch(e) {
        alert("❌ Fehler beim Import – ungültige Datei.");
      }
    };
    reader.readAsText(file);
  }

  function load() {
    setMembers(JSON.parse(localStorage.getItem("gv_members") || "[]"));
    // Signal an alle Sub-Komponenten dass sich Daten geändert haben können
    window.dispatchEvent(new Event("gv-data-refreshed"));
  }

  function save(updated) {
    localStorage.setItem("gv_members", JSON.stringify(updated));
    setMembers(updated);
    window.dispatchEvent(new Event("storage"));
  }

  function notify(msg, type = "success") {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  }

  function saveMember(data) {
    // Ensure partner has status if partner_aktiv
    if (data.partner_aktiv && !data.partner_status) {
      data.partner_status = "Aktives Mitglied";
    }
    // When archiving member, also archive partner
    if (data.status === "Ehemaliges Mitglied" && data.partner_aktiv) {
      data.partner_status = "Ehemaliges Mitglied";
    }
    let updated;
    if (data.id) {
      updated = members.map((m) => m.id === data.id ? { ...data } : m);
      notify("Mitglied gespeichert ✓");
    } else {
      const newM = { ...data, id: "m" + Date.now(), dokumente: [] };
      updated = [...members, newM];
      notify("Neues Mitglied angelegt ✓");
      // Auto-create login account for new member
      const accounts = getMemberAccounts();
      accounts[newM.id] = { password: DEFAULT_PW, mustChange: true };
      saveMemberAccounts(accounts);
    }
    save(updated);
    const fresh = updated.find((m) => m.id === (data.id || updated[updated.length - 1].id));
    setSelected({ ...fresh });
    setView("detail");
  }

  function deleteMember(id) {
    save(members.filter((m) => m.id !== id));
    // Remove member account
    const accounts = getMemberAccounts();
    delete accounts[id];
    saveMemberAccounts(accounts);
    setView(previousView); // Return to archiv or list depending on where we came from
    setSelected(null);
    setConfirmDelete(null);
    notify("Mitglied gelöscht", "error");
  }

  function addDocument(memberId, doc) {
    const updated = members.map((m) =>
      m.id === memberId ? { ...m, dokumente: [...(m.dokumente || []), doc] } : m
    );
    save(updated);
    setSelected({ ...updated.find((m) => m.id === memberId) });
    notify("Dokument hinzugefügt ✓");
    setDocUploadOpen(false);
  }

  function removeDocument(memberId, docId) {
    const updated = members.map((m) =>
      m.id === memberId ? { ...m, dokumente: m.dokumente.filter((d) => d.id !== docId) } : m
    );
    save(updated);
    setSelected({ ...updated.find((m) => m.id === memberId) });
    notify("Dokument entfernt", "error");
  }

  const filtered = members.filter((m) => {
    // Exclude archived members from list view
    if (m.status === "Ehemaliges Mitglied") return false;
    const q = search.toLowerCase();
    return (
      (!q || m.vorname?.toLowerCase().includes(q) || m.nachname?.toLowerCase().includes(q) ||
       m.garten?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)) &&
      (filterStatus === "Alle" || m.status === filterStatus)
    );
  });

  // Beim Erststart: warten bis Server-Sync fertig ist, damit keine alten Daten gezeigt werden
  if (initialLoading) {
    return (
      <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #1C3A18 0%, #2D5A27 50%, #4A8C42 100%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'Georgia','Times New Roman',serif" }}>
        <style>{`@keyframes gv-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
        <div style={{ fontSize: 64, marginBottom: 20 }}>🌱</div>
        <div style={{ color: "#fff", fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Gartenfreunde Rödern</div>
        <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, marginBottom: 28, letterSpacing: 1 }}>MITGLIEDERVERWALTUNG</div>
        <div style={{ display: "inline-block", width: 32, height: 32, border: "3px solid rgba(255,255,255,0.2)", borderTopColor: "#C8A84B", borderRadius: "50%", animation: "gv-spin 0.9s linear infinite" }} />
        <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, marginTop: 16 }}>Daten werden geladen…</div>
      </div>
    );
  }

  return (
    <div style={S.root}>
      <DocModal />
      {notification && (
        <div style={{ ...S.notification, background: notification.type === "error" ? COLORS.danger : COLORS.leaf }}>
          {notification.msg}
        </div>
      )}
      {confirmDelete && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <h3 style={{ color: COLORS.danger, marginBottom: 8 }}>Mitglied löschen</h3>
            <p style={{ color: COLORS.textMuted, marginBottom: 24 }}>
              Soll <strong>{confirmDelete.vorname} {confirmDelete.nachname}</strong> wirklich gelöscht werden?
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button style={S.btnGhost} onClick={() => setConfirmDelete(null)}>Abbrechen</button>
              <button style={S.btnDanger} onClick={() => deleteMember(confirmDelete.id)}>Löschen</button>
            </div>
          </div>
        </div>
      )}

      {/* Portal Preview for Admin */}
      {portalPreview && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 2000, overflowY: "auto" }}>
          <div style={{ position: "sticky", top: 0, zIndex: 10, display: "flex", justifyContent: "flex-end", padding: "12px 16px", background: "rgba(0,0,0,0.7)" }}>
            <button style={{ background: COLORS.danger, color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 14 }} onClick={() => setPortalPreview(null)}>✕ Vorschau schließen</button>
          </div>
          <MemberPortal currentUser={{ memberId: portalPreview.id, id: portalPreview.id, nachname: portalPreview.nachname, isMember: true, adminPreview: true }} onLogout={() => setPortalPreview(null)} />
        </div>
      )}


      {/* Gartenwechsel Modal */}
      {wechselModal && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, maxWidth: 500 }}>
            <h3 style={{ color: "#E67E22", marginBottom: 8 }}>🔄 Gartenwechsel</h3>
            <div style={{ marginBottom: 16, padding: 12, background: "var(--cream)", borderRadius: 8, border: "1px solid #E67E2244" }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Aktuelles Mitglied wird archiviert:</div>
              <div style={{ fontSize: 14 }}>{wechselModal.vorname} {wechselModal.nachname}</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>{wechselModal.garten}</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={S.fieldLabel}>Wechseldatum</label>
              <input type="date" id="wechselDatum" style={S.input} defaultValue={new Date().toISOString().slice(0,10)} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: COLORS.leaf }}>Neues Mitglied anlegen:</div>
              <div style={{ display: "grid", gap: 12 }}>
                <div><label style={S.fieldLabel}>Vorname</label><input id="newVorname" style={S.input} /></div>
                <div><label style={S.fieldLabel}>Nachname</label><input id="newNachname" style={S.input} /></div>
                <div><label style={S.fieldLabel}>Garten (fest)</label><input style={{ ...S.input, background: "var(--tableStripe)" }} value={wechselModal.garten} disabled /></div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button style={S.btnGhost} onClick={() => setWechselModal(null)}>Abbrechen</button>
              <button style={{ ...S.btnPrimary, background: "#E67E22" }} onClick={() => {
                const datum = document.getElementById("wechselDatum").value;
                const vorname = document.getElementById("newVorname").value.trim();
                const nachname = document.getElementById("newNachname").value.trim();
                if (!vorname || !nachname) { alert("Bitte Vor- und Nachname eingeben"); return; }
                // Archive old member and their partner
                const updated = members.map(m => {
                  if (m.id === wechselModal.id) return { ...m, status: "Ehemaliges Mitglied", austrittsdatum: datum };
                  if (m.id === wechselModal.partner_id) return { ...m, status: "Ehemaliges Mitglied", austrittsdatum: datum };
                  return m;
                });
                // Create new member (always Aktives Mitglied)
                const newMember = { id: "m" + Date.now(), vorname, nachname, garten: wechselModal.garten, status: "Aktives Mitglied", eintrittsdatum: datum, dokumente: [] };
                updated.push(newMember);
                save(updated);
                setMembers(updated);
                // Create account for new member
                const accounts = getMemberAccounts();
                accounts[newMember.id] = { password: DEFAULT_PW, mustChange: true };
                saveMemberAccounts(accounts);
                setWechselModal(null);
                notify(`Gartenwechsel durchgeführt: ${wechselModal.garten} → ${vorname} ${nachname}`);
                setView("archiv");
              }}>Wechsel durchführen</button>
            </div>
          </div>
        </div>
      )}

      {rechnungsPreviewMember && (
        <div style={{ ...S.overlay, zIndex: 1100 }}>
          <div style={{ position: "relative", width: "min(96vw, 620px)", maxHeight: "96vh", overflowY: "auto", background: "var(--cardBg)", borderRadius: 12, padding: 0 }}>
            <button style={{ position: "sticky", top: 10, float: "right", margin: "10px 10px 0 0", ...S.btnGhost, zIndex: 10 }}
              onClick={() => setRechnungsPreviewMember(null)}>✕ Schließen</button>
            <Jahresrechnungen members={members} initialPreviewMember={rechnungsPreviewMember} onClosePreview={() => setRechnungsPreviewMember(null)} />
          </div>
        </div>
      )}

      {/* Sidebar - nur Desktop */}
      <aside style={{ ...S.sidebar, background: sidebarBg, width: isMobile ? 0 : (navCollapsed ? 0 : 260), overflow: isMobile ? "hidden" : "auto", transition: "width 0.25s ease" }}>
        <div style={S.sidebarLogo}>
          <span style={{ fontSize: 32 }}>🌱</span>
          <div>
            <div style={S.logoName}>Gartenfreunde</div>
            <div style={S.logoSub}>Verwaltung</div>
          </div>
        </div>
        <nav style={S.nav}>
          <button style={{ ...S.navItem, ...(view === "dashboard" ? S.navActive : {}) }} onClick={() => setView("dashboard")}>
            <span>🏠</span> Übersicht
          </button>
          <button style={{ ...S.navItem, ...(["gaerten","garten_detail","garten_form","list","detail","form","ablesungen","datenaufnahme","jahresrechnung","archiv"].includes(view) ? S.navActive : {}) }} onClick={() => setView("gaerten")}>
            <span>🏡</span> Gärten
          </button>
          {["gaerten","garten_detail","garten_form","list","detail","form","ablesungen","datenaufnahme","jahresrechnung","archiv"].includes(view) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 0, marginTop: 2 }}>
              <button style={{ ...S.navItem, ...(["gaerten","garten_detail","garten_form"].includes(view) ? S.navSubActive : S.navSub) }} onClick={() => setView("gaerten")}>
                <span>📋</span> Übersicht
              </button>
              <button style={{ ...S.navItem, ...(["list","detail","form"].includes(view) && selected?.status !== "Ehemaliges Mitglied" ? S.navSubActive : S.navSub) }} onClick={() => { setSelected(null); setPreviousView("list"); setView("list"); }}>
                <span>👥</span> Mitglieder
              </button>
              <button style={{ ...S.navItem, ...(["ablesungen","datenaufnahme"].includes(view) ? S.navSubActive : S.navSub) }} onClick={() => setView("ablesungen")}>
                <span>💧</span> Ablesungen
              </button>
              {["ablesungen","datenaufnahme"].includes(view) && (
                <div style={{ paddingLeft: 16, display: "flex", flexDirection: "column", gap: 2 }}>
                  <button style={{ ...S.navItem, ...(view === "datenaufnahme" ? S.navSubActive : S.navSub), fontSize: 12 }} onClick={() => setView("datenaufnahme")}>
                    <span>✏️</span> Datenaufnahme
                  </button>
                </div>
              )}
              <button style={{ ...S.navItem, ...(view === "jahresrechnung" ? S.navSubActive : S.navSub) }} onClick={() => setView("jahresrechnung")}>
                <span>🧾</span> Jahresrechnungen
              </button>
              <button style={{ ...S.navItem, ...((view === "archiv" || (view === "detail" && selected?.status === "Ehemaliges Mitglied")) ? S.navSubActive : S.navSub) }} onClick={() => { setSelected(null); setView("archiv"); }}>
                <span>📦</span> Mitglieder-Archiv
              </button>
            </div>
          )}
          <div>
            <button style={{ ...S.navItem, ...(["kalender","feste"].includes(view) ? S.navActive : {}) }} onClick={() => setView("kalender")}>
              <span>📅</span> Kalender
            </button>
            {["kalender","feste"].includes(view) && (
              <div style={{ marginLeft: 8 }}>
                <button style={{ ...S.navItem, ...(view === "feste" ? S.navSubActive : S.navSub) }} onClick={() => setView("feste")}>
                  <span>🎉</span> Feste
                </button>
              </div>
            )}
          </div>


          <button style={{ ...S.navItem, ...(["finanzen","kontoauszuege","erstellte-rechnungen","bilanzen"].includes(view) ? S.navActive : {}) }} onClick={() => setView("finanzen")}><span>💶</span> Finanzen</button>
          {["finanzen","kontoauszuege","erstellte-rechnungen","bilanzen"].includes(view) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 0, marginTop: 2 }}>
              <button style={{ ...S.navItem, ...(view === "finanzen" ? S.navSubActive : S.navSub) }} onClick={() => setView("finanzen")}>
                <span>💶</span> Budget
              </button>
              <button style={{ ...S.navItem, ...(view === "erstellte-rechnungen" ? S.navSubActive : S.navSub) }} onClick={() => setView("erstellte-rechnungen")}>
                <span>🧾</span> Erstellte Rechnungen
              </button>
              <button style={{ ...S.navItem, ...(view === "kontoauszuege" ? S.navSubActive : S.navSub) }} onClick={() => setView("kontoauszuege")}>
                <span>🗂️</span> Kontoauszüge
              </button>
              <button style={{ ...S.navItem, ...(view === "bilanzen" ? S.navSubActive : S.navSub) }} onClick={() => setView("bilanzen")}>
                <span>📊</span> Bilanzen
              </button>
            </div>
          )}
          <button style={{ ...S.navItem, ...(view === "vorstand" ? S.navActive : {}) }} onClick={() => setView("vorstand")}><span>👔</span> Vorstandsversammlungen</button>

          <button style={{ ...S.navItem, ...(view === "mitgliederversammlung" ? S.navActive : {}) }} onClick={() => setView("mitgliederversammlung")}><span>🏛️</span> Mitgliederversammlung</button>
        </nav>
        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "8px 16px", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            <StatRow label="Mitglieder gesamt" val={members.length} />
            <StatRow label="Aktiv" val={members.filter(m => m.status === "Aktives Mitglied").length} color="#A8D4A0" />
            <StatRow label="Gärten belegt" val={new Set(members.filter(m => m.status !== "Ehemaliges Mitglied").flatMap(m => [m.garten, ...(m.zusatzgaerten||[])]).filter(Boolean)).size} />
            {syncStatus === "syncing" && (
              <div style={{ marginTop: 6, fontSize: 10, color: "rgba(255,255,255,0.45)", display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#A8D4A0", display: "inline-block", animation: "gv-pulse 1.2s ease-in-out infinite" }} />
                Sync läuft…
              </div>
            )}
          </div>
          {/* Nachrichtenzentrale Button */}
          <button style={{ ...S.navItem, ...(view === "nachrichten" ? S.navActive : {}) }} onClick={() => setView("nachrichten")}>
            <span>💬</span> Nachrichtenzentrale
          </button>
          {/* Login-Protokoll (nur Admin/Vorstand) */}
          {currentUser?.rolle !== "Mitglied" && (
            <div>
              <button style={{ ...S.navItem, ...(["portallog","datenaenderungen"].includes(view) ? S.navActive : {}) }} onClick={() => setView("portallog")}>
                <span>📋</span> Login-Protokoll
              </button>
              {["portallog","datenaenderungen"].includes(view) && (
                <div style={{ marginLeft: 8 }}>
                  <button style={{ ...S.navItem, ...(view === "portallog" ? S.navSubActive : S.navSub) }} onClick={() => setView("portallog")}>
                    <span>🔑</span> Login-Protokoll
                  </button>
                  <button style={{ ...S.navItem, ...(view === "datenaenderungen" ? S.navSubActive : S.navSub) }} onClick={() => setView("datenaenderungen")}>
                    <span>✏️</span> Mitglieder-Änderungen
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Theme Toggle + Zahnrad nebeneinander */}
          <div style={{padding:"10px 16px",borderTop:"1px solid rgba(255,255,255,0.1)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <SidebarThemeToggle />
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              {/* Manueller Sync-Button */}
              <button
                title="Alle Daten jetzt auf Server synchronisieren"
                onClick={async () => {
                  setSyncState("saving");
                  try {
                    // Nur wichtige Daten-Keys synchronisieren (keine Theme/UI-Einstellungen)
                    const importantKeys = ["gv_members","gv_gaerten","gv_ablesungen","gv_gebuehren",
                      "gv_finanzen","gv_kontoauszuege","gv_termine","gv_mgv","gv_bargeld",
                      "gv_kontostand","gv_ruecklage_grundbetrag","gv_vorstand","gv_hauptzaehler",
                      "gv_kasse_artikel","gv_kasse_buchungen","gv_nachrichten","gv_bargeld_eintraege","gv_portal_logs","gv_daten_aenderungen"];
                    let ok = true;
                    for (const key of importantKeys) {
                      const val = localStorage.getItem(key);
                      if (val) {
                        const success = await syncToServer(key, val);
                        if (!success) { ok = false; break; }
                      }
                    }
                    setSyncState(ok ? "saved" : "error");
                  } catch(e) {
                    setSyncState("error");
                  }
                  setTimeout(() => setSyncState("idle"), 3000);
                }}
                style={{background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:8,width:32,height:32,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,padding:0,position:"relative"}}>
                {syncState === "saving"
                  ? <span style={{fontSize:16,color:"#E67E22",animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</span>
                  : syncState === "saved"
                  ? <span style={{fontSize:14,color:"#2D7A4F"}}>✓</span>
                  : syncState === "error"
                  ? <span style={{fontSize:14,color:"#C0392B"}}>⚠</span>
                  : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="1 4 1 10 7 10"/><polyline points="23 20 23 14 17 14"/>
                      <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                    </svg>
                }
              </button>
              {syncState !== "idle" && (
                <div style={{
                  fontSize:10, fontWeight:700, padding:"3px 8px", borderRadius:10,
                  background: syncState==="saving"?"#E67E22":syncState==="saved"?"#2D7A4F":"#C0392B",
                  color:"#fff", whiteSpace:"nowrap"
                }}>
                  {syncState==="saving" ? "Speichern…" : syncState==="saved" ? "✓ Gespeichert" : "⚠ Fehler"}
                </div>
              )}
            <button
              onClick={() => setEinstellungenOpen(true)}
              title="Einstellungen"
              style={{background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:8,width:32,height:32,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,padding:0}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>
            </div>
          </div>

          {/* Einstellungen-Modal */}
          {einstellungenOpen && <EinstellungenModal
            onClose={() => setEinstellungenOpen(false)}
            exportData={exportData}
            importRef={importRef}
            importData={importData}
            syncFromServer={syncFromServer}
            load={load}
            PDF_SERVER_URL={PDF_SERVER_URL}
          />}

          <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700 }}>{currentUser.username[0].toUpperCase()}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{currentUser.username}</div>
                <div style={{ fontSize: 11, opacity: 0.7 }}>{currentUser.rolle}</div>
              </div>
            </div>
            <button style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: 18, padding: 4, opacity: 0.7, transition: "opacity 0.2s" }} onClick={onLogout} title="Abmelden" onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0.7"}>⏻</button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      {/* Collapse toggle button */}
      {!isMobile && !einstellungenOpen && <button onClick={() => setNavCollapsed(v => !v)} style={{ position: "fixed", left: navCollapsed ? 8 : 260, top: "50%", transform: "translateY(-50%)", zIndex: 100, width: 22, height: 48, borderRadius: "0 8px 8px 0", background: sidebarBg, border: "none", color: "#fff", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", transition: "left 0.25s ease", boxShadow: "2px 0 8px rgba(0,0,0,0.3)" }}>
        {navCollapsed ? "▶" : "◀"}
      </button>}
      <main key={dataEpoch} style={{ ...S.main, marginLeft: isMobile ? 0 : (navCollapsed ? 0 : 260), paddingBottom: isMobile ? 70 : 0, background: "var(--mainBg)", minHeight: "100vh", boxSizing: "border-box" }}>
        {view === "list" && (
          <MemberList members={filtered} search={search} setSearch={setSearch}
            filterStatus={filterStatus} setFilterStatus={setFilterStatus}
            onSelect={(m) => { setPreviousView("list"); setSelected({...m}); setView("detail"); }}
            onNew={() => { setSelected(null); setView("form"); }} />
        )}
        {view === "detail" && selected && (
          <MemberDetail member={selected}
            onEdit={() => { setSelected({...selected}); setView("form"); }}
            onBack={() => setView(previousView)}
            onDelete={() => setConfirmDelete(selected)}
            onAddDoc={(doc) => addDocument(selected.id, doc)}
            onRemoveDoc={(docId) => removeDocument(selected.id, docId)}
            onPortal={() => setPortalPreview(selected)}
            onWechsel={() => setWechselModal(selected)}
            onRechnungsPreview={() => setRechnungsPreviewMember(selected)}
            docUploadOpen={docUploadOpen} setDocUploadOpen={setDocUploadOpen} />
        )}
        {view === "form" && (
          <MemberForm member={selected} onSave={saveMember}
            onCancel={() => setView(selected ? "detail" : "list")} />
        )}
        {view === "dashboard" && <Dashboard members={members} onNav={setView} />}
        {view === "ablesungen" && <Ablesungen members={members} />}
        {view === "datenaufnahme" && <Ablesungen members={members} isDatenaufnahme={true} />}
        {view === "jahresrechnung" && <Jahresrechnungen members={members} />}
        {view === "archiv" && <MitgliederArchiv members={members} onRestore={id => { const m = members.find(x => x.id === id); if(m) { setPreviousView("archiv"); setSelected(m); setView("detail"); } }} />}
        {view === "gaerten" && <GartenList onSelect={(g) => { setSelectedGarten(g); setView("garten_detail"); }} />}
        {view === "garten_detail" && selectedGarten && <GartenDetail garten={selectedGarten} members={members} onBack={() => setView("gaerten")} onEdit={() => setView("garten_form")} />}
        
        {view === "kalender" && <Kalender />}
        {(view === "finanzen" || view === "kontoauszuege" || view === "erstellte-rechnungen" || view === "bilanzen") && <FinanzenWrapper activeTab={view} onTabChange={setView} members={members} />}
        {view === "feste"   && <Feste members={members} />}
        {view === "kasse" && <KassenApp members={members} />}
        {view === "vorstand" && <Vorstandsversammlungen />}
        {view === "mitgliederversammlung" && <Mitgliederversammlung members={members} />}
        {view === "nachrichten" && <Nachrichtencenter members={members} />}
        {view === "portallog" && <PortalProtokolle members={members} />}
        {view === "datenaenderungen" && <DatenAenderungsProtokoll members={members} />}
      </main>

      {/* Mobile Bottom-Navigation */}
      {isMobile && (
        <>
          {navBarEditor && <NavBarEditor onClose={() => { setNavBarEditor(false); }} />}
          <nav style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: getTheme() === "dark" ? "#111" : COLORS.leaf, display: "flex", justifyContent: "space-around", alignItems: "center", height: 60, zIndex: 1000, boxShadow: "0 -2px 12px rgba(0,0,0,0.4)" }}>
            {ALL_NAV_ITEMS.filter(x => getNavBarItems().includes(x.v)).map(item => (
              <button key={item.v}
                onClick={() => setView(item.v)}
                style={{ background: view === item.v ? "rgba(255,255,255,0.15)" : "none", border: "none", color: view === item.v ? "#fff" : "rgba(255,255,255,0.55)", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "4px 8px", borderRadius: 8, minWidth: 44 }}>
                <span style={{ fontSize: 20 }}>{item.icon}</span>
                <span style={{ fontSize: 9, fontWeight: 600, fontFamily: "inherit" }}>{item.label}</span>
              </button>
            ))}
            <MobileMoreMenu view={view} setView={setView} onLogout={onLogout} onEditNav={() => setNavBarEditor(true)} />
          </nav>
        </>
      )}
    </div>
  );
}

function StatRow({ label, val, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
      <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>{label}</span>
      <span style={{ color: color || COLORS.white, fontWeight: 700, fontSize: 15 }}>{val}</span>
    </div>
  );
}

// ─── Member List ──────────────────────────────────────────────────────────────
function MemberList({ members, search, setSearch, filterStatus, setFilterStatus, onSelect, onNew }) {
  const [sortCol, setSortCol] = useState("garten");
  const [sortDir, setSortDir] = useState("asc");

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  const colMap = { "Garten": "garten", "Name": "nachname", "Status": "status" };

  const sorted = [...members].sort((a, b) => {
    if (sortCol === "garten") {
      // Extract numeric part: "Garten 3" -> 3
      const na = parseInt((a.garten || "").replace(/\D/g, "")) || 0;
      const nb = parseInt((b.garten || "").replace(/\D/g, "")) || 0;
      return sortDir === "asc" ? na - nb : nb - na;
    }
    const va = (a[sortCol] || "").toString().toLowerCase();
    const vb = (b[sortCol] || "").toString().toLowerCase();
    return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
  });

  function SortTh({ col, sortable = true }) {
    const key = colMap[col];
    const active = sortable && sortCol === key;
    return (
      <th style={{ ...S.th, cursor: sortable ? "pointer" : "default", userSelect: "none", whiteSpace: "nowrap" }}
        onClick={() => sortable && toggleSort(key)}>
        {col} {sortable && <span style={{ opacity: active ? 1 : 0.3, fontSize: 10 }}>{active ? (sortDir === "asc" ? "▲" : "▼") : "▲▼"}</span>}
      </th>
    );
  }

  return (
    <div className="gv-page" style={S.page}>
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>Mitglieder</h1>
          <p style={S.subtitle}>{members.length} Mitglied{members.length !== 1 ? "er" : ""} gefunden</p>
        </div>
        <button style={S.btnPrimary} onClick={onNew}>+ Neues Mitglied</button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {["Alle","Aktives Mitglied","Passives Mitglied","Nichtmitglied"].map((s) => (
          <button key={s}
            style={{ ...S.filterBtn, ...(filterStatus === s ? S.filterBtnActive : {}), whiteSpace: "nowrap" }}
            onClick={() => setFilterStatus(s)}>{s}</button>
        ))}
        <div style={{ ...S.searchBox, marginLeft: "auto", minWidth: 220, maxWidth: 320 }}>
          <span>🔍</span>
          <input style={S.searchInput} placeholder="Suche nach Name, Garten, E-Mail…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
      <div style={S.tableWrap}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <SortTh col="Garten" />
              <SortTh col="Name" />
              <SortTh col="Status" />
              <SortTh col="E-Mail" sortable={false} />
              <SortTh col="Telefon" sortable={false} />
              <SortTh col="Beitritt" sortable={false} />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: "center", padding: "48px", color: COLORS.textMuted }}>Keine Mitglieder gefunden</td></tr>
            ) : sorted.map((m) => {
              const sc = STATUS_COLORS[m.status] || STATUS_COLORS["Aktives Mitglied"];
              const partnerSc = m.partner_aktiv && m.partner_status ? (STATUS_COLORS[m.partner_status] || STATUS_COLORS["Aktives Mitglied"]) : null;
              return (
                <tr key={m.id} style={S.tr} onClick={() => onSelect(m)}>
                  <td style={S.td}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span style={S.gartenBadge}>{m.garten || "–"}</span>
                      {(m.zusatzgaerten || []).map(g => (
                        <span key={g} style={{ ...S.gartenBadge, background: "#F0F0F0", color: COLORS.textMuted, borderColor: "#CCC", fontSize: 10 }}>{g}</span>
                      ))}
                    </div>
                  </td>
                  <td style={S.td}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {/* Hauptmitglied */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={S.avatar}>{m.vorname?.[0]}{m.nachname?.[0]}</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{m.vorname} {m.nachname}</div>
                          {m.ort && <div style={{ fontSize: 12, color: COLORS.textMuted }}>{m.ort}</div>}
                        </div>
                      </div>
                      {/* Partner */}
                      {m.partner_aktiv && (m.partner_vorname || m.partner_nachname) && (
                        <div style={{ display: "flex", alignItems: "center", gap: 10, paddingLeft: 4, borderLeft: `2px solid ${partnerSc ? partnerSc.dot : COLORS.gold}` }}>
                          <div style={{ ...S.avatar, width: 26, height: 26, fontSize: 10, background: partnerSc ? partnerSc.dot : COLORS.gold, color: "#fff" }}>
                            {m.partner_vorname?.[0]}{m.partner_nachname?.[0]}
                          </div>
                          <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                            {m.partner_vorname} {m.partner_nachname}
                            {partnerSc && <span style={{ ...S.badge, background: partnerSc.bg, color: partnerSc.text, fontSize: 10, padding: "1px 6px", marginLeft: 6 }}>{m.partner_status}</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={S.td}>
                    <span style={{ ...S.badge, background: sc.bg, color: sc.text }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: sc.dot, flexShrink: 0 }} />
                      {m.status}
                    </span>
                  </td>
                  <td style={{ ...S.td, color: COLORS.textMuted }}>{m.email || "–"}</td>
                  <td style={{ ...S.td, color: COLORS.textMuted }}>{m.telefon || "–"}</td>
                  <td style={{ ...S.td, color: COLORS.textMuted }}>
                    {m.eintrittsdatum ? new Date(m.eintrittsdatum).toLocaleDateString("de-DE") : "–"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Member Detail ────────────────────────────────────────────────────────────
function MemberDetail({ member: m, onEdit, onBack, onDelete, onAddDoc, onRemoveDoc, docUploadOpen, setDocUploadOpen, onPortal, onWechsel, onRechnungsPreview }) {
  const sc = STATUS_COLORS[m.status] || STATUS_COLORS["Aktives Mitglied"];
  return (
    <div className="gv-page" style={S.page}>
      <div style={S.pageHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button style={S.backBtn} onClick={onBack}>← Zurück</button>
          <div>
            <h1 style={S.h1}>{m.vorname} {m.nachname}</h1>
            <p style={S.subtitle}>{m.garten}</p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {m.status !== "Ehemaliges Mitglied" && <button style={{ ...S.btnGhost, fontSize: 13 }} onClick={onPortal}>👤 Mitgliederportal</button>}
          {m.garten && m.status !== "Ehemaliges Mitglied" && <button style={{ ...S.btnGhost, fontSize: 13, color: "#E67E22", borderColor: "#E67E22" }} onClick={onWechsel}>🔄 Gartenwechsel</button>}
          <button style={S.btnGhost} onClick={onDelete}>Löschen</button>
          {m.status !== "Ehemaliges Mitglied" && <button style={S.btnPrimary} onClick={onEdit}>✏️ Bearbeiten</button>}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={S.card}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
              <div style={S.avatarLg}>{m.vorname?.[0]}{m.nachname?.[0]}</div>
              <div>
                <h2 style={{ margin: 0, fontSize: 22 }}>{m.vorname} {m.nachname}</h2>
                <span style={{ ...S.badge, background: sc.bg, color: sc.text, marginTop: 6, display: "inline-flex" }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: sc.dot }} />{m.status}
                </span>
              </div>
            </div>
            <div style={{ height: 1, background: COLORS.border, margin: "0 0 20px" }} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
              <div>
                <Field label="Hauptgarten" value={m.garten} />
                {(m.zusatzgaerten || []).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 6 }}>Weitere Gärten</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {m.zusatzgaerten.map(g => (
                        <span key={g} style={{ background: COLORS.leafPale, border: `1px solid ${COLORS.leafLight}`, borderRadius: 7, padding: "3px 10px", fontSize: 12, fontWeight: 600, color: COLORS.leaf }}>🏡 {g}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <Field label="Geburtsdatum" value={m.geburtsdatum ? new Date(m.geburtsdatum).toLocaleDateString("de-DE") : undefined} />
              <Field label="Eintrittsdatum" value={m.eintrittsdatum ? new Date(m.eintrittsdatum).toLocaleDateString("de-DE") : undefined} />
              <Field label="Mitglied seit" value={m.eintrittsdatum ? `${Math.floor((Date.now() - new Date(m.eintrittsdatum)) / (365.25*24*3600*1000))} Jahre` : undefined} />
            </div>
          </div>
          <div style={S.card}>
            <h3 style={S.cardTitle}>Kontakt & Adresse</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
              <Field label="Straße" value={m.strasse} span={2} />
              <Field label="PLZ" value={m.plz} />
              <Field label="Ort" value={m.ort} />
              <Field label="Telefon" value={m.telefon} />
              <Field label="E-Mail" value={m.email} />
            </div>
          </div>

          <div style={S.card}>
            <h3 style={S.cardTitle}>Notizen</h3>
            <p style={{ color: COLORS.textMuted, margin: 0, lineHeight: 1.6 }}>{m.notizen || <span style={{ color: "#ccc" }}>–</span>}</p>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Partner */}
          {m.partner_aktiv && (
            <div style={{ ...S.card, border: `1.5px solid ${COLORS.gold}` }}>
              <h3 style={{ ...S.cardTitle, color: COLORS.earthLight }}>👫 Partner</h3>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <div style={S.avatar}>{m.partner_vorname?.[0]}{m.partner_nachname?.[0]}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{m.partner_vorname} {m.partner_nachname}</div>
                  {m.partner_status && (
                    <span style={{ ...S.badge, background: (STATUS_COLORS[m.partner_status]||STATUS_COLORS["Aktives Mitglied"]).bg, color: (STATUS_COLORS[m.partner_status]||STATUS_COLORS["Aktives Mitglied"]).text, marginTop: 4, display: "inline-flex" }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: (STATUS_COLORS[m.partner_status]||STATUS_COLORS["Aktives Mitglied"]).dot }} />{m.partner_status}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px" }}>
                <Field label="Geburtsdatum" value={m.partner_geburtsdatum ? new Date(m.partner_geburtsdatum).toLocaleDateString("de-DE") : undefined} />
                <Field label="Beitrittsdatum" value={m.partner_eintrittsdatum ? new Date(m.partner_eintrittsdatum).toLocaleDateString("de-DE") : undefined} />
                <Field label="Telefon" value={m.partner_telefon} />
                <Field label="E-Mail" value={m.partner_email} />
                {m.partner_andere_adresse && <>
                  <Field label="Straße" value={m.partner_strasse} span={2} />
                  <Field label="PLZ" value={m.partner_plz} />
                  <Field label="Ort" value={m.partner_ort} />
                </>}
              </div>
              {(m.partner_dokumente||[]).length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: COLORS.textMuted, marginBottom: 8, letterSpacing: 0.7 }}>Dokumente</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {m.partner_dokumente.map(doc => (
                      <div key={doc.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: COLORS.leafPale, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
                        <span>{doc.name?.endsWith(".pdf") ? "📕" : "📄"}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600 }}>{doc.name}</div>
                          <div style={{ fontSize: 11, color: COLORS.textMuted }}>{doc.typ}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ ...S.cardTitle, margin: 0 }}>Dokumente</h3>
              {m.status !== "Ehemaliges Mitglied" && <button style={S.btnSmall} onClick={() => setDocUploadOpen(true)}>+ Hinzufügen</button>}
            </div>
            {docUploadOpen && <DocUploadForm onAdd={onAddDoc} onCancel={() => setDocUploadOpen(false)} />}
            {(!m.dokumente || m.dokumente.length === 0) && !docUploadOpen ? (
              <div style={{ textAlign: "center", padding: "40px 20px", color: COLORS.textMuted }}>
                <span style={{ fontSize: 40 }}>📂</span>
                <p style={{ margin: "8px 0 0" }}>Noch keine Dokumente</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {m.dokumente?.map((doc) => (
                  <div key={doc.id} style={S.docRow}>
                    <span style={{ fontSize: 22, flexShrink: 0, cursor: doc.data ? "pointer" : "default" }} onClick={() => doc.data && openDoc(doc)}>{docIcon(doc.name)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        cursor: doc.data ? "pointer" : "default", color: doc.data ? COLORS.leaf : COLORS.text }}
                        onClick={() => doc.data && openDoc(doc)}>{doc.name}</div>
                      <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{doc.typ} · {doc.datum ? new Date(doc.datum).toLocaleDateString("de-DE") : ""} · {doc.size}</div>
                    </div>
                    {m.status !== "Ehemaliges Mitglied" && <button style={S.docRemove} onClick={() => onRemoveDoc(doc.id)}>✕</button>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={S.card}>
            <h3 style={S.cardTitle}>Abrechnung</h3>
            {(() => {
              // Show eich warnings for this member
              const abl = (() => { try { return JSON.parse(localStorage.getItem("gv_ablesungen") || "{}"); } catch(e) { return {}; } })();
              const year = new Date().getFullYear();
              const warns = [];
              ["wasser","strom"].forEach(typ => {
                ["Eichdatum","EichdatumB"].forEach(p => {
                  const val = abl[`${m.id}__${typ}__${year}__${p}`] || abl[`${m.id}__${typ}__${year-1}__${p}`] || "";
                  const status = checkEich(val, typ);
                  if (status) warns.push({ typ, val, status });
                });
              });
              if (warns.length === 0) return null;
              return (
                <div style={{ marginBottom: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                  {warns.map((w, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 7, background: "#FEF9F9", border: `1px solid ${eichColor(w.status)}66`, fontSize: 12 }}>
                      <span>{w.typ === "wasser" ? "💧" : "⚡"}</span>
                      <span style={{ fontWeight: 700, color: eichColor(w.status) }}>{eichIcon(w.status)} {w.typ === "wasser" ? "Wasserzähler" : "Stromzähler"} M {w.val} – {w.status === "abgelaufen" ? "Eichung abgelaufen!" : w.status === "laeuft_ab" ? "Läuft dieses Jahr ab" : "Läuft nächstes Jahr ab"}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
            {(() => {
              const year = new Date().getFullYear();
              const gebuehren = (() => { try { return JSON.parse(localStorage.getItem("gv_gebuehren") || "{}")[year] || {}; } catch(e) { return {}; } })();
              const BETRAG_MAP = {
                abr_wasseranschluss:       { gkey: "grundgeb_wasser",          def: "10,85" },
                abr_stromanschluss:        { gkey: "grundgeb_elektro",         def: "3,64" },
                abr_ruecklage_medien:      { gkey: "ruecklage_medien",         def: "10,00" },
                abr_vereinsbeitrag:        { gkey: "beitrag_person1",          def: "20,00" },
                abr_gebuehr_verwaltung:    { gkey: "verwaltung_mitglied",      def: "5,00" },
                abr_gebuehr_nichtmitglied: { gkey: "verwaltung_nichtmitglied", def: "25,00" },
              };
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    { key: "abr_wasseranschluss",       label: "Wasseranschluss" },
                    { key: "abr_stromanschluss",         label: "Stromanschluss" },
                    { key: "abr_ruecklage_medien",       label: "Rücklage Medien-Versorgung" },
                    { key: "abr_vereinsbeitrag",         label: "Vereinsbeitrag" },
                    { key: "abr_gebuehr_verwaltung",     label: "Gebühr Verwaltung" },
                    { key: "abr_gebuehr_nichtmitglied",  label: "Gebühr Verwaltung als Nichtmitglied" },
                  ].map(({ key, label }) => {
                    const bm = BETRAG_MAP[key];
                    const betrag = bm ? (gebuehren[bm.gkey] || bm.def) : "–";
                    return (
                      <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, background: m[key] ? "var(--leafPale)" : "var(--tableStripe)", border: `1px solid ${m[key] ? COLORS.border : "var(--border)"}` }}>
                        <div style={{ width: 18, height: 18, borderRadius: 4, background: m[key] ? COLORS.leaf : "#CCC", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {m[key] && <span style={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>✓</span>}
                        </div>
                        <span style={{ fontSize: 13, color: m[key] ? COLORS.text : COLORS.textMuted, fontWeight: m[key] ? 600 : 400, flex: 1 }}>{label}</span>
                        {m[key] && <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.leaf, whiteSpace: "nowrap" }}>{betrag} €</span>}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

function docIcon(name) {
  if (!name) return "📄";
  if (name.endsWith(".pdf")) return "📕";
  if (name.endsWith(".docx") || name.endsWith(".doc")) return "📘";
  if (name.endsWith(".jpg") || name.endsWith(".png")) return "🖼️";
  return "📄";
}

function Field({ label, value, span }) {
  return (
    <div style={span === 2 ? { gridColumn: "span 2" } : {}}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, color: COLORS.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14 }}>{value || <span style={{ color: "#ccc" }}>–</span>}</div>
    </div>
  );
}

// ─── Doc Upload ───────────────────────────────────────────────────────────────
function DocUploadForm({ onAdd, onCancel }) {
  const [name, setName] = useState("");
  const [typ, setTyp] = useState("Vertrag");
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [size, setSize] = useState("");
  const [fileData, setFileData] = useState(null);
  const [fileType, setFileType] = useState("");
  const fileRef = useRef();

  function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setName(f.name);
    setSize((f.size / 1024).toFixed(0) + " KB");
    setFileType(f.type);
    const reader = new FileReader();
    reader.onload = ev => setFileData(ev.target.result);
    reader.readAsDataURL(f);
  }

  return (
    <div style={{ background: COLORS.leafPale, borderRadius: 10, border: `1.5px dashed ${COLORS.leafLight}`, padding: 16, marginBottom: 16 }}>
      <div style={{ padding: "14px 20px", background: "var(--cardBg)", borderRadius: 8, border: `1.5px dashed ${COLORS.border}`, textAlign: "center", fontSize: 13, cursor: "pointer" }}
        onClick={() => fileRef.current?.click()}>
        <input ref={fileRef} type="file" style={{ display: "none" }} onChange={handleFile} />
        {name ? <span>📎 {name}</span> : <span style={{ color: COLORS.textMuted }}>Datei auswählen (oder hier klicken)</span>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Name</label>
          <input style={S.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Dateiname…" />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Typ</label>
          <select style={S.input} value={typ} onChange={(e) => setTyp(e.target.value)}>
            {["Vertrag","Antrag","Kündigung","Rechnung","Protokoll","Sommerfest"].map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Datum</label>
          <input type="date" style={S.input} value={datum} onChange={(e) => setDatum(e.target.value)} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button style={S.btnPrimary} onClick={() => { if (name) onAdd({ id: "d" + Date.now(), name, typ, datum, size: size || "–", data: fileData, mimeType: fileType }); }}>Speichern</button>
        <button style={S.btnGhost} onClick={onCancel}>Abbrechen</button>
      </div>
    </div>
  );
}

// Opens a document in an inline modal
function openDoc(doc) {
  if (!doc.data) {
    alert("Keine Datei hinterlegt – bitte Dokument erneut hochladen.");
    return;
  }
  // Dispatch custom event to show doc modal
  window.dispatchEvent(new CustomEvent("showDoc", { detail: doc }));
}

function DocModal() {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    const handler = e => setDoc(e.detail);
    window.addEventListener("showDoc", handler);
    return () => window.removeEventListener("showDoc", handler);
  }, []);
  if (!doc) return null;
  const isPdf = doc.mimeType?.includes("pdf") || doc.name?.endsWith(".pdf");
  const isImg = doc.mimeType?.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.name||"");
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={() => setDoc(null)}>
      <div style={{ background: "var(--cardBg)", borderRadius: 14, width: "90vw", maxWidth: 900, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid #D4E6CE", flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text)" }}>{doc.name}</div>
            <div style={{ fontSize: 12, color: "var(--textMuted)" }}>{doc.typ} · {doc.size}</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <a href={doc.data} download={doc.name}
              style={{ background: "#2D5A27", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, textDecoration: "none" }}>
              ⬇️ Download
            </a>
            <button style={{ background: "transparent", border: "1.5px solid var(--border)", borderRadius: 8, padding: "7px 12px", cursor: "pointer", fontSize: 16 }}
              onClick={() => setDoc(null)}>✕</button>
          </div>
        </div>
        {/* Content */}
        <div style={{ flex: 1, overflow: "auto", padding: isPdf ? 0 : 20, background: "var(--tableStripe)" }}>
          {isPdf && <iframe src={doc.data} style={{ width: "100%", height: "75vh", border: "none" }} title={doc.name} />}
          {isImg && <img src={doc.data} alt={doc.name} style={{ maxWidth: "100%", display: "block", margin: "0 auto", borderRadius: 8 }} />}
          {!isPdf && !isImg && (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--textMuted)" }}>
              <div style={{ fontSize: 60, marginBottom: 16 }}>📄</div>
              <p style={{ fontSize: 15 }}>Diese Datei kann nicht direkt angezeigt werden.</p>
              <a href={doc.data} download={doc.name}
                style={{ display: "inline-block", marginTop: 12, background: "#2D5A27", color: "#fff", borderRadius: 9, padding: "10px 20px", textDecoration: "none", fontWeight: 600 }}>
                ⬇️ Herunterladen
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Member Form ──────────────────────────────────────────────────────────────
function MemberForm({ member, onSave, onCancel }) {
  const isNew = !member;
  const [form, setForm] = useState(member ? { ...member } : {
    garten: "", vorname: "", nachname: "", geburtsdatum: "",
    eintrittsdatum: new Date().toISOString().slice(0, 10),
    status: "", strasse: "", plz: "", ort: "", telefon: "", email: "", notizen: "",
    dokumente: [],
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fileRef = useRef();
  const [docForm, setDocForm] = useState({ name: "", typ: "Vertrag", datum: new Date().toISOString().slice(0,10) });
  const [docFormOpen, setDocFormOpen] = useState(false);

  function addDoc() {
    if (!docForm.name) return;
    const doc = { id: "d" + Date.now(), ...docForm, size: "–" };
    set("dokumente", [...(form.dokumente || []), doc]);
    setDocForm({ name: "", typ: "Vertrag", datum: new Date().toISOString().slice(0,10) });
    setDocFormOpen(false);
  }

  function removeDoc(id) {
    set("dokumente", (form.dokumente || []).filter(d => d.id !== id));
  }

  function handleFile(e) {
    const f = e.target.files?.[0];
    if (f) setDocForm(d => ({ ...d, name: f.name, size: (f.size/1024).toFixed(0) + " KB" }));
  }

  return (
    <div className="gv-page" style={S.page}>
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>{isNew ? "Neues Mitglied" : "Mitglied bearbeiten"}</h1>
          <p style={S.subtitle}>{isNew ? "Mitgliedsdaten eingeben" : `${member.vorname} ${member.nachname}`}</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button style={S.btnGhost} onClick={onCancel}>Abbrechen</button>
          <button style={S.btnPrimary} onClick={() => onSave(form)}>💾 Speichern</button>
        </div>
      </div>
      <div style={{ maxWidth: 860, display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={S.card}>
          <h3 style={S.cardTitle}>Basisdaten</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            <FF label="Hauptgarten" required>
              <select style={S.input} value={form.garten} onChange={(e) => set("garten", e.target.value)}>
                <option value="">– Bitte wählen –</option>
                {Object.entries(ALLE_GAERTEN).map(([weg, nummern]) => (
                  <optgroup key={weg} label={weg}>
                    {nummern.map(n => <option key={n} value={`Garten ${n}`}>Garten {n}</option>)}
                  </optgroup>
                ))}
              </select>
            </FF>
            <FF label="Status" required>
              <select style={S.input} value={form.status} onChange={(e) => {
                const s = e.target.value;
                set("status", s);
                if (s === "Aktives Mitglied" || s === "Passives Mitglied") {
                  setForm(f => ({ ...f, status: s,
                    abr_vereinsbeitrag: true,
                    abr_gebuehr_verwaltung: true,
                    abr_gebuehr_nichtmitglied: false,
                  }));
                } else if (s === "Nichtmitglied") {
                  setForm(f => ({ ...f, status: s,
                    abr_vereinsbeitrag: false,
                    abr_gebuehr_verwaltung: false,
                    abr_gebuehr_nichtmitglied: true,
                  }));
                }
              }}>
                <option value="">– Bitte wählen –</option>
                {["Aktives Mitglied","Passives Mitglied","Nichtmitglied"].map(s => <option key={s}>{s}</option>)}
              </select>
            </FF>
            <FF label="Eintrittsdatum">
              <input type="date" style={S.input} value={form.eintrittsdatum} onChange={(e) => set("eintrittsdatum", e.target.value)} />
            </FF>
          </div>
          {/* Zusatzgärten */}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textMuted, marginBottom: 8 }}>
              Weitere Gärten ({(form.zusatzgaerten || []).length})
              <span style={{ fontWeight: 400, marginLeft: 8 }}>— alle Gärten erscheinen auf einer gemeinsamen Rechnung</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
              {(form.zusatzgaerten || []).map(g => (
                <div key={g} style={{ display: "flex", alignItems: "center", gap: 6, background: COLORS.leafPale, border: `1px solid ${COLORS.leafLight}`, borderRadius: 8, padding: "4px 10px", fontSize: 13, fontWeight: 600, color: COLORS.leaf }}>
                  🏡 {g}
                  <button onClick={() => set("zusatzgaerten", (form.zusatzgaerten || []).filter(x => x !== g))}
                    style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.danger, fontSize: 16, lineHeight: 1, padding: "0 0 0 2px" }}>×</button>
                </div>
              ))}
            </div>
            <select
              style={{ ...S.input, maxWidth: 260 }}
              value=""
              onChange={e => {
                const val = e.target.value;
                if (!val) return;
                if (val === form.garten) return;
                if ((form.zusatzgaerten || []).includes(val)) return;
                set("zusatzgaerten", [...(form.zusatzgaerten || []), val]);
              }}
            >
              <option value="">+ Weiteren Garten hinzufügen</option>
              {Object.entries(ALLE_GAERTEN).map(([weg, nummern]) => (
                <optgroup key={weg} label={weg}>
                  {nummern
                    .map(n => `Garten ${n}`)
                    .filter(g => g !== form.garten && !(form.zusatzgaerten || []).includes(g))
                    .map(g => <option key={g} value={g}>{g}</option>)
                  }
                </optgroup>
              ))}
            </select>
          </div>
        </div>
        <div style={S.card}>
          <h3 style={S.cardTitle}>Persönliche Daten</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <FF label="Vorname" required><input style={S.input} value={form.vorname} onChange={(e) => set("vorname", e.target.value)} placeholder="Vorname" /></FF>
            <FF label="Nachname" required><input style={S.input} value={form.nachname} onChange={(e) => set("nachname", e.target.value)} placeholder="Nachname" /></FF>
            <FF label="Geburtsdatum"><input type="date" style={S.input} value={form.geburtsdatum} onChange={(e) => set("geburtsdatum", e.target.value)} /></FF>
          </div>
        </div>
        <div style={S.card}>
          <h3 style={S.cardTitle}>Kontakt & Adresse</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <FF label="Straße" span={2}><input style={S.input} value={form.strasse} onChange={(e) => set("strasse", e.target.value)} placeholder="Straße und Hausnummer" /></FF>
            <FF label="PLZ"><input style={S.input} value={form.plz} onChange={(e) => set("plz", e.target.value)} placeholder="PLZ" /></FF>
            <FF label="Ort"><input style={S.input} value={form.ort} onChange={(e) => set("ort", e.target.value)} placeholder="Ort" /></FF>
            <FF label="Telefon"><input style={S.input} value={form.telefon} onChange={(e) => set("telefon", e.target.value)} placeholder="0123 456789" /></FF>
            <FF label="E-Mail"><input type="email" style={S.input} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="name@email.de" /></FF>
          </div>
        </div>

        {/* Partner */}
        <div style={{ ...S.card, border: `1.5px solid ${COLORS.gold}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ ...S.cardTitle, margin: 0, color: COLORS.earthLight }}>👫 Partner</h3>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <div
                onClick={() => set("partner_aktiv", !form.partner_aktiv)}
                style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${form.partner_aktiv ? COLORS.leaf : COLORS.border}`, background: form.partner_aktiv ? COLORS.leaf : COLORS.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: COLORS.white, flexShrink: 0, transition: "all 0.15s" }}
              >{form.partner_aktiv ? "✓" : ""}</div>
              <span style={{ fontSize: 13, color: COLORS.textMuted }} onClick={() => set("partner_aktiv", !form.partner_aktiv)}>Partner eintragen</span>
            </label>
          </div>

          {form.partner_aktiv && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                <FF label="Vorname"><input style={S.input} value={form.partner_vorname||""} onChange={e => set("partner_vorname", e.target.value)} placeholder="Vorname" /></FF>
                <FF label="Nachname"><input style={S.input} value={form.partner_nachname||""} onChange={e => set("partner_nachname", e.target.value)} placeholder="Nachname" /></FF>
                <FF label="Geburtsdatum"><input type="date" style={S.input} value={form.partner_geburtsdatum||""} onChange={e => set("partner_geburtsdatum", e.target.value)} /></FF>
                <FF label="Status">
                  <select style={S.input} value={form.partner_status||"Aktives Mitglied"} onChange={e => set("partner_status", e.target.value)}>
                    {["Aktives Mitglied","Passives Mitglied","Nichtmitglied"].map(s => <option key={s}>{s}</option>)}
                  </select>
                </FF>
                <FF label="Beitrittsdatum"><input type="date" style={S.input} value={form.partner_eintrittsdatum||""} onChange={e => set("partner_eintrittsdatum", e.target.value)} /></FF>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <FF label="Telefon"><input style={S.input} value={form.partner_telefon||""} onChange={e => set("partner_telefon", e.target.value)} placeholder="0123 456789" /></FF>
                <FF label="E-Mail"><input type="email" style={S.input} value={form.partner_email||""} onChange={e => set("partner_email", e.target.value)} placeholder="name@email.de" /></FF>
              </div>

              {/* Abweichende Adresse */}
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "10px 14px", borderRadius: 9, border: `1.5px solid ${form.partner_andere_adresse ? COLORS.leafLight : COLORS.border}`, background: form.partner_andere_adresse ? COLORS.leafPale : "#FAFAFA" }}>
                <div
                  onClick={() => set("partner_andere_adresse", !form.partner_andere_adresse)}
                  style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${form.partner_andere_adresse ? COLORS.leaf : COLORS.border}`, background: form.partner_andere_adresse ? COLORS.leaf : COLORS.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: COLORS.white, flexShrink: 0 }}
                >{form.partner_andere_adresse ? "✓" : ""}</div>
                <span style={{ fontSize: 14, color: COLORS.text, fontWeight: 500 }} onClick={() => set("partner_andere_adresse", !form.partner_andere_adresse)}>
                  Abweichende Adresse (lebt nicht am selben Ort)
                </span>
              </label>

              {form.partner_andere_adresse && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, padding: "16px", background: COLORS.leafPale, borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
                  <FF label="Straße" span={2}><input style={S.input} value={form.partner_strasse||""} onChange={e => set("partner_strasse", e.target.value)} placeholder="Straße und Hausnummer" /></FF>
                  <FF label="PLZ"><input style={S.input} value={form.partner_plz||""} onChange={e => set("partner_plz", e.target.value)} placeholder="PLZ" /></FF>
                  <FF label="Ort"><input style={S.input} value={form.partner_ort||""} onChange={e => set("partner_ort", e.target.value)} placeholder="Ort" /></FF>
                </div>
              )}

            </div>
          )}
        </div>


        {/* Abrechnung */}
        <div style={S.card}>
          <h3 style={S.cardTitle}>Abrechnung</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { key: "abr_wasseranschluss",        label: "Wasseranschluss",                       controlled: false },
              { key: "abr_stromanschluss",          label: "Stromanschluss",                         controlled: false },
              { key: "abr_ruecklage_medien",        label: "Rücklage Medien-Versorgung",             controlled: false },
              { key: "abr_vereinsbeitrag",          label: "Vereinsbeitrag",                         controlled: true },
              { key: "abr_gebuehr_verwaltung",      label: "Gebühr Verwaltung",                      controlled: true },
              { key: "abr_gebuehr_nichtmitglied",   label: "Gebühr Verwaltung als Nichtmitglied",    controlled: true },
            ].map(({ key, label, controlled }) => {
              const isActive = !!form[key];
              const isDisabled = controlled; // status-controlled = not manually editable
              return (
                <div key={key} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 14px", borderRadius: 9,
                  border: `1.5px solid ${isActive ? COLORS.leafLight : COLORS.border}`,
                  background: isActive ? COLORS.leafPale : (isDisabled ? "var(--tableStripe)" : COLORS.white),
                  opacity: isDisabled && !isActive ? 0.45 : 1,
                  cursor: isDisabled ? "default" : "pointer",
                  transition: "all 0.15s",
                }}
                  onClick={() => !isDisabled && set(key, !form[key])}
                >
                  <div style={{
                    width: 22, height: 22, borderRadius: 6,
                    border: `2px solid ${isActive ? COLORS.leaf : COLORS.border}`,
                    background: isActive ? COLORS.leaf : COLORS.white,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 700, color: COLORS.white, flexShrink: 0,
                  }}>
                    {isActive ? "✓" : ""}
                  </div>
                  <span style={{ fontSize: 14, color: isActive ? COLORS.text : COLORS.textMuted, fontWeight: isActive ? 600 : 400, userSelect: "none" }}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={S.card}>
          <h3 style={S.cardTitle}>Notizen</h3>
          <textarea style={{ ...S.input, height: 100, resize: "vertical" }} value={form.notizen} onChange={(e) => set("notizen", e.target.value)} placeholder="Interne Notizen zum Mitglied…" />
        </div>

        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ ...S.cardTitle, margin: 0 }}>Dokumente</h3>
            <button style={S.btnSmall} onClick={() => setDocFormOpen(v => !v)}>+ Hinzufügen</button>
          </div>

          {docFormOpen && (
            <div style={{ background: COLORS.leafPale, borderRadius: 10, border: `1.5px dashed ${COLORS.leafLight}`, padding: 16, marginBottom: 16 }}>
              <div style={{ padding: "12px 16px", background: "var(--cardBg)", borderRadius: 8, border: `1.5px dashed ${COLORS.border}`, textAlign: "center", fontSize: 13, cursor: "pointer", marginBottom: 10 }}
                onClick={() => fileRef.current?.click()}>
                <input ref={fileRef} type="file" style={{ display: "none" }} onChange={handleFile} />
                {docForm.name ? <span>📎 {docForm.name}</span> : <span style={{ color: COLORS.textMuted }}>Datei auswählen (oder hier klicken)</span>}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Name</label>
                  <input style={S.input} value={docForm.name} onChange={e => setDocForm(d => ({...d, name: e.target.value}))} placeholder="Dateiname…" />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Typ</label>
                  <select style={S.input} value={docForm.typ} onChange={e => setDocForm(d => ({...d, typ: e.target.value}))}>
                    {["Vertrag","Antrag","Kündigung","Rechnung","Protokoll","Sonstiges"].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Datum</label>
                  <input type="date" style={S.input} value={docForm.datum} onChange={e => setDocForm(d => ({...d, datum: e.target.value}))} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button style={S.btnPrimary} onClick={addDoc}>Speichern</button>
                <button style={S.btnGhost} onClick={() => setDocFormOpen(false)}>Abbrechen</button>
              </div>
            </div>
          )}

          {(!form.dokumente || form.dokumente.length === 0) && !docFormOpen ? (
            <div style={{ textAlign: "center", padding: "28px 0", color: COLORS.textMuted }}>
              <span style={{ fontSize: 36 }}>📂</span>
              <p style={{ margin: "8px 0 0", fontSize: 13 }}>Noch keine Dokumente</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(form.dokumente || []).map(doc => (
                <div key={doc.id} style={S.docRow}>
                  <span style={{ fontSize: 22, flexShrink: 0 }}>{doc.name?.endsWith(".pdf") ? "📕" : doc.name?.endsWith(".docx") ? "📘" : "📄"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.name}</div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{doc.typ} · {doc.datum ? new Date(doc.datum + "T12:00:00").toLocaleDateString("de-DE") : ""}</div>
                  </div>
                  <button style={S.docRemove} onClick={() => removeDoc(doc.id)}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

function FF({ label, required, children, span }) {
  return (
    <div style={span === 2 ? { gridColumn: "span 2" } : {}}>
      <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, color: COLORS.textMuted, marginBottom: 4, display: "block" }}>
        {label} {required && <span style={{ color: COLORS.leafLight }}>*</span>}
      </label>
      {children}
    </div>
  );
}


// ─── Kalender ─────────────────────────────────────────────────────────────────
const TERMIN_TYPEN = {
  "Versammlung":  { bg: "var(--leafPale)", text: "#2D5A27", border: "#4A8C42" },
  "Arbeitseinsatz": { bg: "var(--cream)", text: "#7D6608", border: "#C8A84B" },
  "Frist":        { bg: "var(--cardBg)", text: "#C0392B", border: "#E74C3C" },
  "Sommerfest":   { bg: "var(--cream)", text: "var(--gold)", border: "#E67E22" },
  "Herbstfest":   { bg: "var(--cream)", text: "var(--gold)", border: "#CA6F1E" },
};

const MONATE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
const WOCHENTAGE = ["Mo","Di","Mi","Do","Fr","Sa","So"];

function loadTermine() {
  try { return JSON.parse(localStorage.getItem("gv_termine") || "[]"); } catch(e) { return []; }
}
function saveTermine(t) { localStorage.setItem("gv_termine", JSON.stringify(t)); }

function Kalender() {
  const today = new Date();
  const [anzeige, setAnzeige] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [termine, setTermine] = useState(loadTermine);
  const [modal, setModal] = useState(null); // null | { datum, termin? }
  const [form, setForm] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);

  const { year, month } = anzeige;

  // Build calendar grid
  const firstDay = new Date(year, month, 1);
  const lastDay  = new Date(year, month + 1, 0);
  // Monday-first: 0=Mo … 6=So
  const startDow = (firstDay.getDay() + 6) % 7;
  const totalDays = lastDay.getDate();

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  function dateKey(d) { return `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`; }
  function termineForDay(d) { return termine.filter(t => t.datum === dateKey(d)); }
  function isToday(d) { return d === today.getDate() && month === today.getMonth() && year === today.getFullYear(); }

  function openNew(d) {
    setForm({ datum: dateKey(d), titel: "", typ: "Versammlung", uhrzeit: "10:00", beschreibung: "" });
    setModal({ mode: "new" });
  }
  function openEdit(termin) {
    setForm({ ...termin });
    setModal({ mode: "edit", id: termin.id });
  }
  function saveForm() {
    if (!form.titel) return;
    let updated;
    if (modal.mode === "edit") {
      updated = termine.map(t => t.id === modal.id ? { ...form, id: modal.id } : t);
    } else {
      updated = [...termine, { ...form, id: "t" + Date.now() }];
    }
    saveTermine(updated);
    setTermine(updated);
    setModal(null);
  }
  function deleteTermin(id) {
    const updated = termine.filter(t => t.id !== id);
    saveTermine(updated);
    setTermine(updated);
  }

  // Upcoming termine (next 60 days)
  const upcoming = termine
    .filter(t => t.datum >= today.toISOString().slice(0,10))
    .sort((a,b) => a.datum.localeCompare(b.datum))
    .slice(0, 8);

  return (
    <div className="gv-page" style={S.page}>
      {/* Modal */}
      {modal && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width: 460 }}>
            <h3 style={{ margin: "0 0 20px", color: COLORS.leaf, fontSize: 18 }}>
              {modal.mode === "edit" ? "Termin bearbeiten" : "Neuer Termin"}
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={KS.label}>Titel *</label>
                <input style={S.input} value={form.titel||""} onChange={e => setForm(f=>({...f,titel:e.target.value}))} placeholder="z.B. Jahreshauptversammlung" autoFocus />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={KS.label}>Datum</label>
                  <input type="date" style={S.input} value={form.datum||""} onChange={e => setForm(f=>({...f,datum:e.target.value}))} />
                </div>
                <div>
                  <label style={KS.label}>Uhrzeit</label>
                  <input type="time" style={S.input} value={form.uhrzeit||""} onChange={e => setForm(f=>({...f,uhrzeit:e.target.value}))} />
                </div>
              </div>
              <div>
                <label style={KS.label}>Typ</label>
                <select style={S.input} value={form.typ||"Versammlung"} onChange={e => setForm(f=>({...f,typ:e.target.value}))}>
                  {Object.keys(TERMIN_TYPEN).map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={KS.label}>Beschreibung</label>
                <textarea style={{ ...S.input, height: 80, resize: "vertical" }} value={form.beschreibung||""} onChange={e => setForm(f=>({...f,beschreibung:e.target.value}))} placeholder="Weitere Informationen…" />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
              {modal.mode === "edit" && (
                <button style={{ ...S.btnDanger, marginRight: "auto", padding: "7px 12px", fontSize: 13 }} onClick={() => { deleteTermin(modal.id); setModal(null); }}>Löschen</button>
              )}
              <button style={S.btnGhost} onClick={() => setModal(null)}>Abbrechen</button>
              <button style={S.btnPrimary} onClick={saveForm}>💾 Speichern</button>
            </div>
          </div>
        </div>
      )}

      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>Kalender</h1>
          <p style={S.subtitle}>{termine.length} Termin{termine.length !== 1 ? "e" : ""} gespeichert</p>
        </div>
        <button style={S.btnPrimary} onClick={() => { setForm({ datum: today.toISOString().slice(0,10), titel:"", typ:"Versammlung", uhrzeit:"10:00", beschreibung:"" }); setModal({mode:"new"}); }}>
          + Neuer Termin
        </button>
      </div>

      <div style={KS.layout}>
        {/* Calendar grid */}
        <div style={KS.calCard}>
          {/* Month navigation */}
          <div style={KS.monthHeader}>
            <button style={KS.navBtn} onClick={() => setAnzeige(a => {
              const d = new Date(a.year, a.month - 1);
              return { year: d.getFullYear(), month: d.getMonth() };
            })}>‹</button>
            <span style={KS.monthTitle}>{MONATE[month]} {year}</span>
            <button style={KS.navBtn} onClick={() => setAnzeige(a => {
              const d = new Date(a.year, a.month + 1);
              return { year: d.getFullYear(), month: d.getMonth() };
            })}>›</button>
          </div>

          {/* Weekday headers */}
          <div style={KS.weekRow}>
            {WOCHENTAGE.map(d => <div key={d} style={KS.weekLabel}>{d}</div>)}
          </div>

          {/* Day cells */}
          <div style={KS.grid}>
            {cells.map((d, i) => {
              if (!d) return <div key={i} style={KS.emptyCell} />;
              const dayTermine = termineForDay(d);
              const todayStyle = isToday(d) ? KS.todayCell : {};
              return (
                <div key={i} style={{ ...KS.dayCell, ...todayStyle }}
                  onClick={() => openNew(d)}>
                  <div style={{ ...KS.dayNum, ...(isToday(d) ? KS.todayNum : {}) }}>{d}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2 }}>
                    {dayTermine.slice(0, 2).map(t => {
                      const typ = TERMIN_TYPEN[t.typ] || TERMIN_TYPEN.Sonstiges;
                      return (
                        <div key={t.id}
                          style={{ ...KS.terminChip, background: typ.bg, color: typ.text, borderLeft: `2px solid ${typ.border}` }}
                          onClick={e => { e.stopPropagation(); openEdit(t); }}>
                          {t.uhrzeit && <span style={{ opacity: 0.7 }}>{t.uhrzeit} </span>}{t.titel}
                        </div>
                      );
                    })}
                    {dayTermine.length > 2 && (
                      <div style={{ fontSize: 9, color: COLORS.textMuted, paddingLeft: 3 }}>+{dayTermine.length - 2} weitere</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p style={{ fontSize: 11, color: COLORS.textMuted, textAlign: "center", margin: "10px 0 0" }}>
            Auf einen Tag klicken um einen Termin hinzuzufügen
          </p>
        </div>

        {/* Upcoming events */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={S.card}>
            <h3 style={S.cardTitle}>Nächste Termine</h3>
            {upcoming.length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px 0", color: COLORS.textMuted }}>
                <span style={{ fontSize: 32 }}>📅</span>
                <p style={{ margin: "8px 0 0", fontSize: 13 }}>Keine bevorstehenden Termine</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {upcoming.map(t => {
                  const typ = TERMIN_TYPEN[t.typ] || TERMIN_TYPEN.Sonstiges;
                  const d = new Date(t.datum + "T12:00:00");
                  return (
                    <div key={t.id} style={{ ...KS.upcomingRow, borderLeft: `3px solid ${typ.border}` }}
                      onClick={() => openEdit(t)}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 42, background: typ.bg, borderRadius: 8, padding: "6px 4px" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: typ.text }}>{MONATE[d.getMonth()].slice(0,3).toUpperCase()}</span>
                        <span style={{ fontSize: 20, fontWeight: 800, color: typ.text, lineHeight: 1 }}>{d.getDate()}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.titel}</div>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                          {t.uhrzeit && <span>🕐 {t.uhrzeit} · </span>}
                          <span style={{ ...KS.typPill, background: typ.bg, color: typ.text }}>{t.typ}</span>
                        </div>
                        {t.beschreibung && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.beschreibung}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Legend */}
          <div style={S.card}>
            <h3 style={S.cardTitle}>Termintypen</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {Object.entries(TERMIN_TYPEN).map(([name, c]) => (
                <div key={name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: c.bg, border: `2px solid ${c.border}`, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: COLORS.text }}>{name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const KS = {
  layout: { display: "grid", gridTemplateColumns: "1fr 280px", gap: 20, alignItems: "start" },
  calCard: { background: "var(--cardBg)", borderRadius: 14, border: `1.5px solid ${COLORS.border}`, padding: 20 },
  monthHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  monthTitle: { fontSize: 18, fontWeight: 700, color: COLORS.text },
  navBtn: { background: COLORS.leafPale, border: `1px solid ${COLORS.border}`, color: COLORS.leaf, borderRadius: 8, width: 34, height: 34, cursor: "pointer", fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" },
  weekRow: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 6 },
  weekLabel: { textAlign: "center", fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.5, padding: "4px 0" },
  grid: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 },
  emptyCell: { minHeight: 70, borderRadius: 6 },
  dayCell: {
    minHeight: 70, borderRadius: 6, padding: "6px 5px", cursor: "pointer",
    border: `1px solid transparent`, transition: "background 0.1s",
    background: "var(--tableStripe)",
  },
  todayCell: { background: COLORS.leafPale, border: `1.5px solid ${COLORS.leafLight}` },
  dayNum: { fontSize: 13, fontWeight: 600, color: COLORS.textMuted, marginBottom: 2 },
  todayNum: { color: COLORS.leaf, fontWeight: 800 },
  terminChip: {
    fontSize: 9, padding: "1px 4px", borderRadius: 3, overflow: "hidden",
    textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer",
  },
  upcomingRow: {
    display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 12px",
    background: "var(--tableStripe)", borderRadius: 10, cursor: "pointer",
    border: `1px solid ${COLORS.border}`,
  },
  typPill: { borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 600 },
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, color: COLORS.textMuted, marginBottom: 4, display: "block" },
};


// ─── BerichtEditor: contenteditable mit B / I / U + Schriftfarbe ─────────────
function BerichtEditor({ value, onChange }) {
  const ref = useRef();
  const [colorOpen, setColorOpen] = useState(false);
  const [fmt, setFmt] = useState({ bold: false, italic: false, underline: false });
  const init = useRef(false);

  useEffect(() => {
    if (!init.current && ref.current) {
      ref.current.innerHTML = value || "";
      init.current = true;
    }
  }, []);

  function exec(cmd, arg) {
    ref.current.focus();
    document.execCommand(cmd, false, arg ?? null);
    onChange(ref.current.innerHTML);
    setFmt({
      bold:      document.queryCommandState("bold"),
      italic:    document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
    });
  }

  const COLORS = [
    { c: "#1a1a1a", l: "Schwarz"    },
    { c: "#2D5A27", l: "Grün"       },
    { c: "#1A5276", l: "Dunkelblau" },
    { c: "#2980B9", l: "Blau"       },
    { c: "#C0392B", l: "Rot"        },
    { c: "#884EA0", l: "Lila"       },
    { c: "#D35400", l: "Orange"     },
    { c: "#7D6608", l: "Goldbraun"  },
    { c: "#777777", l: "Grau"       },
  ];

  const btn = (active) => ({
    background: active ? "#e8f5e1" : "var(--tableStripe)",
    border: `1px solid ${active ? "#2D5A27" : "var(--border)"}`,
    color: active ? "#2D5A27" : "var(--text)",
    borderRadius: 5, padding: "3px 9px", cursor: "pointer",
    fontSize: 13, fontFamily: "inherit", lineHeight: 1.5,
  });

  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "visible" }}>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 4, padding: "5px 8px", background: "var(--tableStripe)", borderBottom: "1px solid var(--border)", borderRadius: "8px 8px 0 0", alignItems: "center" }}>
        <button style={{ ...btn(fmt.bold),      fontWeight: 700   }} title="Fett (Strg+B)"       onMouseDown={e=>{e.preventDefault();exec("bold");}}>B</button>
        <button style={{ ...btn(fmt.italic),    fontStyle:"italic"}} title="Kursiv (Strg+I)"     onMouseDown={e=>{e.preventDefault();exec("italic");}}>I</button>
        <button style={{ ...btn(fmt.underline), textDecoration:"underline"}} title="Unterstrichen (Strg+U)" onMouseDown={e=>{e.preventDefault();exec("underline");}}>U</button>

        <div style={{ width: 1, height: 18, background: "var(--border)", margin: "0 2px" }} />

        {/* Schriftfarbe */}
        <div style={{ position: "relative" }}>
          <button title="Schriftfarbe" style={{ ...btn(false), display:"flex", alignItems:"center", gap:4 }}
            onMouseDown={e=>{e.preventDefault(); setColorOpen(v=>!v);}}>
            <span style={{ fontWeight:700 }}>A</span>
            <span style={{ width:14, height:3, borderRadius:2, background:"#2D5A27", display:"block" }}/>
            <span style={{ fontSize:9 }}>▾</span>
          </button>
          {colorOpen && (
            <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, zIndex:9999,
              background:"var(--cardBg)", border:"1px solid var(--border)", borderRadius:8,
              padding:8, boxShadow:"0 4px 16px rgba(0,0,0,0.18)", display:"flex", flexWrap:"wrap", gap:5, width:160 }}>
              {COLORS.map(({c,l}) => (
                <button key={c} title={l}
                  onMouseDown={e=>{e.preventDefault(); exec("foreColor", c); setColorOpen(false);}}
                  style={{ width:26, height:26, borderRadius:5, background:c, border:"2px solid transparent", cursor:"pointer" }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="#fff"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="transparent"} />
              ))}
              <button onMouseDown={e=>{e.preventDefault(); exec("removeFormat"); setColorOpen(false);}}
                style={{ ...btn(false), fontSize:10, width:"100%", textAlign:"center", marginTop:2 }}>
                Zurücksetzen
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Editierbereich */}
      <div ref={ref} contentEditable suppressContentEditableWarning
        onInput={()=>{ onChange(ref.current.innerHTML); }}
        onKeyUp={()=>setFmt({ bold: document.queryCommandState("bold"), italic: document.queryCommandState("italic"), underline: document.queryCommandState("underline") })}
        onMouseUp={()=>setFmt({ bold: document.queryCommandState("bold"), italic: document.queryCommandState("italic"), underline: document.queryCommandState("underline") })}
        onClick={()=>setColorOpen(false)}
        data-placeholder="Hier den Bericht oder das Protokoll der Versammlung eintragen…"
        style={{ minHeight:180, padding:"12px 14px", outline:"none", lineHeight:1.7, fontSize:14, color:"var(--text)", fontFamily:"inherit", borderRadius:"0 0 8px 8px", background:"var(--cardBg)" }}
      />
      <style>{`[contenteditable]:empty:before{content:attr(data-placeholder);color:#aaa;pointer-events:none}`}</style>
    </div>
  );
}

// ─── Vorstandsversammlungen ───────────────────────────────────────────────────
// Vorstandsnamen dynamisch aus Einstellungen laden (gv_vorstand_namen)
function getVorstandNamen() {
  try {
    const namen = JSON.parse(localStorage.getItem(GV_VORSTAND_NAMEN_KEY) || "{}");
    return [
      namen.vorsitzender || "",
      namen.stv1 || "",
      namen.stv2 || "",
    ].filter(n => n.trim() !== "");
  } catch(e) { return []; }
}
// Fallback-Konstante nur wenn noch keine Namen eingetragen
const VORSTAND_FALLBACK = ["Christian Marx", "Ronny Junghanns", "René Walther"];

function loadVV() {
  try { return JSON.parse(localStorage.getItem("gv_vorstand") || "[]"); } catch(e) { return []; }
}
function saveVV(data) { localStorage.setItem("gv_vorstand", JSON.stringify(data)); }

function Vorstandsversammlungen() {
  const [versammlungen, setVersammlungen] = useState(loadVV);
  const [expanded, setExpanded] = useState(null);
  const [newDatum, setNewDatum] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);

  // Vorstandsnamen aus Einstellungen (gv_vorstand_namen), Fallback auf Konstante
  const VORSTAND = getVorstandNamen().length > 0 ? getVorstandNamen() : VORSTAND_FALLBACK;

  function addVersammlung() {
    const now = new Date();
    const datum = now.toISOString().slice(0, 10);
    const uhrzeit = now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    const neu = {
      id: "vv" + Date.now(),
      datum,
      uhrzeit,
      anwesend: {},
      bericht: "",
    };
    const updated = [neu, ...versammlungen].sort((a, b) => b.datum.localeCompare(a.datum) || b.id.localeCompare(a.id));
    saveVV(updated);
    setVersammlungen(updated);
    setExpanded(neu.id);
    setNewDatum("");
  }

  function updateField(id, field, value) {
    const updated = versammlungen.map(v =>
      v.id === id ? { ...v, [field]: value } : v
    );
    saveVV(updated);
    setVersammlungen(updated);
  }

  function toggleAnwesend(id, person) {
    const vv = versammlungen.find(v => v.id === id);
    const updated = versammlungen.map(v =>
      v.id === id ? { ...v, anwesend: { ...v.anwesend, [person]: !v.anwesend[person] } } : v
    );
    saveVV(updated);
    setVersammlungen(updated);
  }

  function deleteVV(id) {
    const updated = versammlungen.filter(v => v.id !== id);
    saveVV(updated);
    setVersammlungen(updated);
    if (expanded === id) setExpanded(null);
    setConfirmDel(null);
  }

  // ── Protokoll-HTML erzeugen (geteilt von Export + Server-Speicherung) ────────
  function buildVVHtml(vv) {
    const PERSONEN = getVorstandNamen().length > 0 ? getVorstandNamen() : VORSTAND_FALLBACK;
    const anwesendListe = PERSONEN.filter(p => vv.anwesend?.[p]).map(p => `<li>${p}</li>`).join("");
    const fehlendListe  = PERSONEN.filter(p => !vv.anwesend?.[p]).map(p => `<li style="color:#999">${p}</li>`).join("");
    const datum = new Date(vv.datum + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Vorstandsversammlung ${vv.datum}</title>
    <style>
      body { font-family: Georgia, serif; max-width: 750px; margin: 40px auto; color: #1C2B1A; }
      h1, h2 { color: #2D5A27; }
      .header { background: #2D5A27; color: white; padding: 20px 24px; border-radius: 8px; margin-bottom: 24px; }
      .header h2 { color: white; margin: 0; }
      .header p { margin: 4px 0 0; opacity: 0.8; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px; }
      .card { background: #f5f5f5; border-radius: 8px; padding: 16px; }
      .card h3 { margin: 0 0 10px; font-size: 14px; text-transform: uppercase; color: #666; letter-spacing: 0.5px; }
      ul { margin: 0; padding-left: 18px; }
      li { margin-bottom: 4px; }
      .bericht { background: #f9f9f9; border-left: 4px solid #2D5A27; padding: 16px; border-radius: 0 8px 8px 0; line-height: 1.6; }
      .footer { margin-top: 40px; border-top: 1px solid #ccc; padding-top: 16px; font-size: 12px; color: #999; }
    </style></head><body>
    <div class="header">
      <h2>Gartenfreunde Rödern e.V.</h2>
      <p>Vorstandsversammlung · Protokoll</p>
    </div>
    <div class="grid">
      <div class="card">
        <h3>Sitzungsdaten</h3>
        <p><strong>Datum:</strong> ${datum}</p>
        <p><strong>Uhrzeit:</strong> ${vv.uhrzeit || "–"} Uhr</p>
      </div>
      <div class="card">
        <h3>Anwesenheit</h3>
        <ul>${anwesendListe || "<li style='color:#999'>Keine Angaben</li>"}</ul>
        ${fehlendListe ? `<p style="margin-top:8px;font-size:12px;color:#999;">Abwesend:</p><ul>${fehlendListe}</ul>` : ""}
      </div>
    </div>
    <h2>Protokoll / Bericht</h2>
    <div class="bericht">${vv.bericht || "Kein Bericht eingetragen."}</div>
    <div class="footer">
      <p>Gartenfreunde Rödern e.V. · Erstellt am ${new Date().toLocaleDateString("de-DE")}</p>
    </div>
    </body></html>`;
  }

  // ── Button: Daten speichern + Protokoll als PDF auf Server ablegen ──────────
  function SpeichernProtokolButton({ vv }) {
    const [status, setStatus] = useState(null);
    const [url, setUrl]       = useState(null);
    const [errMsg, setErrMsg] = useState("");

    async function speichern() {
      if (status === "saving") return;
      setStatus("saving");
      setUrl(null);
      try {
        // Nur die AKTUELLEN Vorstandsnamen senden — alte Keys aus Umbenennungen
        // (z.B. "René Walter" vor Korrektur zu "René Walther") nicht mitschicken
        const personen = getVorstandNamen().length > 0 ? getVorstandNamen() : VORSTAND_FALLBACK;
        const anwesendGefiltert = {};
        personen.forEach(p => { anwesendGefiltert[p] = !!(vv.anwesend || {})[p]; });

        const res = await fetch(`${PDF_SERVER_URL}/api/vv/protokoll`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({
            datum:    vv.datum,
            uhrzeit:  vv.uhrzeit,
            personen,
            anwesend: anwesendGefiltert,
            bericht:  vv.bericht  || "",
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "HTTP " + res.status);
        setUrl(data.url);
        setStatus("ok");
        setTimeout(() => setStatus(null), 5000);
      } catch(e) {
        console.error("Protokoll-Speicherung fehlgeschlagen:", e);
        setErrMsg(e.message);
        setStatus("error");
        setTimeout(() => setStatus(null), 8000);
      }
    }

    if (status === "ok") return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 11, color: "#2D5A27" }}>✅ PDF gespeichert</span>
        {url && (
          <a href={`${PDF_SERVER_URL}${url}`} target="_blank" rel="noreferrer"
            style={{ fontSize: 11, color: COLORS.leaf, textDecoration: "underline" }}>
            📄 PDF öffnen
          </a>
        )}
      </div>
    );

    if (status === "error") return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
        <span style={{ fontSize: 11, color: COLORS.danger }}>❌ Fehler beim Speichern</span>
        {errMsg && <span style={{ fontSize: 10, color: COLORS.danger, maxWidth: 360, textAlign: "right", wordBreak: "break-word" }}>{errMsg}</span>}
      </div>
    );

    return (
      <button
        onClick={speichern}
        disabled={status === "saving"}
        style={{ ...S.btnPrimary, fontSize: 13, padding: "8px 18px", opacity: status === "saving" ? 0.7 : 1 }}>
        {status === "saving" ? "⏳ PDF wird erstellt…" : "💾 Speichern & als PDF ablegen"}
      </button>
    );
  }

  function exportVVPDF(vv) {
    const html = buildVVHtml(vv);
    const printWindow = window.open("", "_blank");
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => { printWindow.focus(); printWindow.print(); };
  }

  const formatDatum = (d) => d ? new Date(d + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : "";

  return (
    <div className="gv-page" style={S.page}>
      {/* Delete confirm */}
      {confirmDel && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <h3 style={{ color: COLORS.danger, marginBottom: 8 }}>Versammlung löschen</h3>
            <p style={{ color: COLORS.textMuted, marginBottom: 24 }}>
              Soll die Versammlung vom <strong>{formatDatum(confirmDel.datum)}</strong> wirklich gelöscht werden?
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button style={S.btnGhost} onClick={() => setConfirmDel(null)}>Abbrechen</button>
              <button style={S.btnDanger} onClick={() => deleteVV(confirmDel.id)}>Löschen</button>
            </div>
          </div>
        </div>
      )}

      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>Vorstandsversammlungen</h1>
          <p style={S.subtitle}>{versammlungen.length} Versammlung{versammlungen.length !== 1 ? "en" : ""} gespeichert</p>
        </div>
        <button style={S.btnPrimary} onClick={addVersammlung}>+ Neue Versammlung</button>
      </div>

      {/* List */}
      {versammlungen.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: COLORS.textMuted }}>
          <span style={{ fontSize: 48 }}>📋</span>
          <p style={{ margin: "12px 0 0", fontSize: 14 }}>Noch keine Versammlungen eingetragen</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {versammlungen.map(vv => {
            const isOpen = expanded === vv.id;
            const anwesendCount = VORSTAND.filter(p => vv.anwesend[p]).length;
            return (
              <div key={vv.id} style={{ ...VVS.accordion, ...(isOpen ? VVS.accordionOpen : {}) }}>
                {/* Header row */}
                <div style={VVS.accordionHeader} onClick={() => setExpanded(isOpen ? null : vv.id)}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={VVS.dateBox}>
                      <span style={VVS.dateDay}>{new Date(vv.datum + "T12:00:00").getDate()}</span>
                      <span style={VVS.dateMon}>{["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"][new Date(vv.datum + "T12:00:00").getMonth()]}</span>
                      <span style={VVS.dateYear}>{new Date(vv.datum + "T12:00:00").getFullYear()}</span>
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.text }}>{formatDatum(vv.datum)}{vv.uhrzeit && <span style={{ fontSize: 13, color: COLORS.textMuted, fontWeight: 400, marginLeft: 10 }}>🕐 {vv.uhrzeit} Uhr</span>}</div>
                      <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 3 }}>
                        <span style={VVS.pill}>{anwesendCount}/{VORSTAND.length} anwesend</span>
                        {vv.bericht && <span style={{ marginLeft: 8 }}>📝 Bericht vorhanden</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <button style={{ ...VVS.deleteBtn, color: COLORS.leaf }} onClick={e => { e.stopPropagation(); exportVVPDF(vv); }} title="Als PDF exportieren">📄</button>
                    <button style={VVS.deleteBtn} onClick={e => { e.stopPropagation(); setConfirmDel(vv); }} title="Löschen">🗑️</button>
                    <span style={{ color: COLORS.textMuted, fontSize: 20, transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>⌄</span>
                  </div>
                </div>

                {/* Expanded content */}
                {isOpen && (
                  <div style={VVS.accordionBody}>
                    {/* Anwesenheit */}
                    <div style={VVS.section}>
                      <h4 style={VVS.sectionTitle}>👤 Anwesenheit Vorstand</h4>
                      <div style={VVS.checkGrid}>
                        {VORSTAND.map(person => (
                          <label key={person} style={VVS.checkLabel}>
                            <input
                              type="checkbox"
                              checked={!!vv.anwesend[person]}
                              onChange={() => toggleAnwesend(vv.id, person)}
                              style={{ display: "none" }}
                            />
                            <div style={{ ...VVS.checkBox, ...(vv.anwesend[person] ? VVS.checkBoxChecked : {}) }}>
                              {vv.anwesend[person] ? "✓" : ""}
                            </div>
                            <span style={{ fontSize: 14, color: COLORS.text, fontWeight: vv.anwesend[person] ? 600 : 400 }}>
                              {person}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Bericht */}
                    <div style={VVS.section}>
                      <h4 style={VVS.sectionTitle}>📝 Bericht / Protokoll</h4>
                      <BerichtEditor value={vv.bericht} onChange={html => updateField(vv.id, "bericht", html)} />
                    </div>

                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: 4 }}>
                      <SpeichernProtokolButton vv={vv} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const VVS = {
  addCard: { padding: "0 0 8px 0" },
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, color: COLORS.textMuted, marginBottom: 6, display: "block" },
  accordion: {
    background: "var(--cardBg)", borderRadius: 12,
    border: `1.5px solid ${COLORS.border}`, overflow: "hidden",
    transition: "box-shadow 0.2s",
  },
  accordionOpen: { border: `1.5px solid ${COLORS.leafLight}`, boxShadow: "0 4px 20px rgba(45,90,39,0.1)" },
  accordionHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "16px 20px", cursor: "pointer",
    userSelect: "none",
  },
  dateBox: {
    display: "flex", flexDirection: "column", alignItems: "center",
    background: COLORS.leafPale, borderRadius: 8, padding: "6px 10px",
    minWidth: 44, border: `1px solid ${COLORS.border}`,
  },
  dateDay: { fontSize: 20, fontWeight: 800, color: COLORS.leaf, lineHeight: 1 },
  dateMon: { fontSize: 10, fontWeight: 700, color: COLORS.leafLight, textTransform: "uppercase", letterSpacing: 0.5 },
  dateYear: { fontSize: 10, color: COLORS.textMuted },
  pill: { background: COLORS.leafPale, color: COLORS.leaf, borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 600 },
  deleteBtn: { background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: "4px 6px", borderRadius: 6, color: COLORS.textMuted },
  accordionBody: { borderTop: `1px solid ${COLORS.border}`, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 },
  section: { display: "flex", flexDirection: "column", gap: 10 },
  sectionTitle: { margin: 0, fontSize: 13, fontWeight: 700, color: COLORS.text, letterSpacing: 0.3 },
  checkGrid: { display: "flex", flexDirection: "column", gap: 10 },
  checkLabel: { display: "flex", alignItems: "center", gap: 12, cursor: "pointer", padding: "10px 14px", borderRadius: 9, border: `1.5px solid ${COLORS.border}`, background: "var(--tableStripe)" },
  checkBox: {
    width: 24, height: 24, borderRadius: 6, border: `2px solid ${COLORS.border}`,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 14, fontWeight: 700, flexShrink: 0, color: COLORS.white,
    background: "var(--cardBg)", transition: "all 0.15s",
  },
  checkBoxChecked: { background: COLORS.leaf, borderColor: COLORS.leaf },
  anlageBtn: { padding: "8px 24px", background: COLORS.leaf, color: COLORS.white, border: "none", borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", alignSelf: "flex-start", whiteSpace: "nowrap" },
};


// ─── Dashboard ────────────────────────────────────────────────────────────────
function WasserAbstellenIcon({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ display: "inline-block", verticalAlign: "middle" }}>
      {/* Tropfen - identische Form und Farben wie 💧 */}
      <path d="M16 2 Q16 2 7 14 Q4 19 4 23 A12 12 0 0 0 28 23 Q28 19 25 14 Z" fill="#1E88E5"/>
      {/* Glanzfleck oben links wie beim 💧 Emoji */}
      <ellipse cx="10.5" cy="13" rx="3.5" ry="5.5" fill="#90CAF9" opacity="0.6" transform="rotate(-25,10.5,13)"/>
      {/* Rotes X mittig über dem Tropfen */}
      <line x1="6" y1="6" x2="26" y2="26" stroke="#EE2222" strokeWidth="4" strokeLinecap="round"/>
      <line x1="26" y1="6" x2="6" y2="26" stroke="#EE2222" strokeWidth="4" strokeLinecap="round"/>
    </svg>
  );
}

const FESTE = [
  {
    id: "f1",
    name: "Wasser anstellen",
    emoji: "💧",
    monat: "April / Mai",
    farbe: { bg: "var(--leafPale)", border: "#2E86C1", text: "#2E86C1" },
    beschreibung: "Wasser anstellen – Start der Bewässerungssaison.",
  },
  {
    id: "f2",
    name: "Sommerfest",
    emoji: "☀️",
    monat: "Juli / August",
    farbe: { bg: "var(--cream)", border: "#E67E22", text: "var(--gold)" },
    beschreibung: "Großes Gartenfest mit Grillen, Musik und gemeinsamen Aktivitäten.",
  },
  {
    id: "f3",
    name: "Herbstfest",
    emoji: "🍂",
    monat: "September / Oktober",
    farbe: { bg: "var(--cream)", border: "#CA6F1E", text: "var(--gold)" },
    beschreibung: "Ernte- und Abschlussfest der Gartensaison.",
  },
  {
    id: "f4",
    name: "Wasser abstellen",
    emoji: "💧",
    svgIcon: true,
    monat: "Oktober / November",
    farbe: { bg: "var(--leafPale)", border: "#5B9BD5", text: "#2E86C1" },
    beschreibung: "Wasser abstellen – Ende der Bewässerungssaison.",
  },
];

function Dashboard({ members, onNav }) {
  const heute = new Date().toISOString().slice(0, 10);

  const [termine, setTermine] = useState(loadTermine);
  const [datePicker, setDatePicker] = useState(null); // festId being edited

  // Wetter wird direkt von WetterWidget gerendert (echte API)

  // Weekly gardener quotes (changes every week)
  const getGaertnerSpruch = () => {
    const sprueche = [
      "Ein Garten ohne Unkraut ist wie eine Party ohne Gäste – irgendwie zu ordentlich! 🌿",
      "Gärtnern: Die einzige Sucht, bei der man vom Arzt applaudiert wird. 🌻",
      "Ich habe nicht versagt – ich habe nur 10.000 Wege gefunden, wie man Tomaten nicht züchtet. 🍅",
      "Mein Garten ist wie ich: wild, ein bisschen chaotisch, aber voller Leben! 🦋",
      "Früher hatte ich einen grünen Daumen. Jetzt habe ich zehn grüne Finger! 👍",
      "Wer sagt, Geld wächst nicht auf Bäumen, hat noch nie eine Ernte verkauft. 💰",
      "Gießen vergessen? Das nennt man natürliche Selektion! 💦",
      "Meine Pflanzen und ich haben eine Beziehung: Ich rede mit ihnen, sie ignorieren mich. 🗣️",
      "Gartenarbeit: Wo man für teures Geld Gemüse züchtet, das im Supermarkt billiger wäre! 🥒",
      "Schnecken im Garten? Das sind kostenlose Haustiere! 🐌",
      "Ich bin nicht faul – ich lasse der Natur einfach ihren Lauf! 🍃",
      "Ein Tag ohne Garten ist möglich, aber sinnlos. ☀️",
      "Unkraut jäten ist wie Meditation – nur mit mehr Fluchen. 😤",
      "Mein Kompost ist wie mein Leben: Es braucht Zeit, bis was Gutes draus wird. ♻️",
      "Gärtnern: Die Kunst, optimistisch in die Zukunft zu pflanzen. 🌱",
      "Wer Schmetterlinge beobachten will, muss Raupen ertragen können. 🦋",
      "Garten ist dort, wo die Seele baumelt und die Tomaten wachsen! 🍅",
      "Man erntet, was man sät – außer man hat Wühlmäuse im Garten. 🐭",
      "Gärtnern ist billig – wenn man die Zeit nicht mitrechnet! ⏰",
      "In meinem Garten wächst alles... besonders das Unkraut! 🌾",
      "Der frühe Gärtner fängt die dickste Tomate – oder verschläft sie! 😴",
      "Mein Rasen ist nicht ungepflegt – das ist biodiverser Lebensraum! 🦗",
      "Kompost passiert. Und das ist gut so! 🌿",
      "Gießen, jäten, ernten, wiederholen. Das Gärtnerleben in vier Worten. 🔄",
      "Ein Garten ohne Schnecken ist wie ein Himmel ohne Wolken – theoretisch möglich! ☁️",
      "Ich arbeite nicht im Garten. Ich vollbringe grüne Wunder! ✨",
      "Pflanzen sind wie Kinder – nur billiger und sie ziehen irgendwann aus! 👶",
      "Erde unter den Fingernägeln ist das neue Schwarz! 💅",
      "Meine Pflanzen wachsen... die Erwartungen auch! 📈",
      "Wer gräbt, der findet – meistens Schnecken und alte Wurzeln. ⛏️",
      "Gärtnern: Wo man im Dreck wühlt und sich königlich fühlt! 👑",
      "Ein Garten ist ein Freund, den man täglich besuchen kann. 🤝",
      "Unkraut nennt man die Pflanzen, deren Vorzüge noch nicht entdeckt wurden! 🌿",
      "Im Garten der Zeit wächst die Blume der Geduld. Oder Unkraut. Meist Unkraut. 🌸",
      "Gießen ist wie Kaffee trinken – ohne geht's nicht! ☕",
      "Wer einen Garten hat, braucht kein Fitnessstudio. Und keine Psychotherapie. 💪",
      "Mein Garten mag mich – er hält mich auf dem Boden der Tatsachen! 🌍",
      "Radieschen ziehen ist einfach. Man nimmt zwei Hände voll Erde... 🥗",
      "Gartenzwerge: Die einzigen Mitarbeiter, die nie Urlaub nehmen! 🧙",
      "Säen, warten, staunen – das Gärtnerleben in drei Akten. 🎭",
      "Mein Rücken sagt Garten, mein Kopf sagt Entspannung. Beides stimmt! 🧘",
      "Ein Garten ohne Überraschungen? Das wäre ja langweilig! 🎉",
      "Gärtnern: Wo Hoffnung stirbt und immer wieder aufkeimt! 🌱",
      "Wer im Garten arbeitet, ist nie allein – die Nachbarn schauen zu! 👀",
      "Meine Tomaten wachsen besser als meine Aktien! 📊",
      "Im Garten vergisst man die Zeit – und das Mittagessen. 🍽️",
      "Gießkanne in der Hand, Sorgen aus dem Land! 🪣",
      "Ein Garten ist Liebe, die man sehen kann – oder Unkraut. Je nach Tag. 💚",
      "Wer Äpfel liebt, muss Würmer mögen lernen! 🍎🐛",
      "Mein Garten: Wo aus Saat Salat wird. Manchmal. 🥬",
      "Grüner Daumen? Meiner ist eher olivgrün mit braunen Stellen! 👍",
      "Gärtnern ist die langsamste Form des Zauberns – aber die schönste! 🪄"
    ];
    
    // Calculate week number to rotate quotes weekly
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const diff = now - start;
    const oneWeek = 1000 * 60 * 60 * 24 * 7;
    const weekNum = Math.floor(diff / oneWeek);
    
    return sprueche[weekNum % sprueche.length];
  };

  const vorstand = (() => { try { return JSON.parse(localStorage.getItem("gv_vorstand") || "[]"); } catch(e) { return []; } })();

  // Fest-Termine: gespeichert als { festId: "2026-05-10", ... }
  const [festDaten, setFestDaten] = useState(() => {
    try { return JSON.parse(localStorage.getItem("gv_festdaten") || "{}"); } catch(e) { return {}; }
  });

  function setFestDatum(festId, datum, festName, festTyp) {
    // Save fest date
    const updated = { ...festDaten, [festId]: datum };
    setFestDaten(updated);
    localStorage.setItem("gv_festdaten", JSON.stringify(updated));

    // Add or update in Kalender termine
    const allTermine = loadTermine();
    const existingIdx = allTermine.findIndex(t => t.festId === festId);
    const neuerTermin = {
      id: existingIdx >= 0 ? allTermine[existingIdx].id : "t" + Date.now(),
      festId,
      datum,
      titel: festName,
      typ: festTyp,
      uhrzeit: "",
      beschreibung: "Wichtiger Vereinstermin",
    };
    let newTermine;
    if (existingIdx >= 0) {
      newTermine = allTermine.map((t, i) => i === existingIdx ? neuerTermin : t);
    } else {
      newTermine = [...allTermine, neuerTermin];
    }
    saveTermine(newTermine);
    setTermine(newTermine);
    setDatePicker(null);
  }

  const naechsteTermine = termine
    .filter(t => t.datum >= heute)
    .sort((a, b) => a.datum.localeCompare(b.datum))
    .slice(0, 4);

  const aktivMitglieder = members.filter(m => m.status === "Aktives Mitglied").length;
  const docsGesamt = members.reduce((sum, m) => sum + (m.dokumente?.length || 0), 0);

  // Termin-Typ mapping for Feste
  const FEST_TYP = { f1: "Versammlung", f2: "Sommerfest", f3: "Herbstfest", f4: "Versammlung" };

  return (
    <div className="gv-page" style={S.page}>
      {/* Date picker modal */}
      {datePicker && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width: 360 }}>
            <h3 style={{ margin: "0 0 16px", color: COLORS.leaf, fontSize: 17 }}>
              {FESTE.find(f => f.id === datePicker)?.emoji} Termin festlegen
            </h3>
            <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 16 }}>
              {FESTE.find(f => f.id === datePicker)?.name} – bitte Datum auswählen:
            </p>
            <input
              type="date"
              style={{ ...S.input, marginBottom: 20 }}
              defaultValue={festDaten[datePicker] || ""}
              id="festDateInput"
              autoFocus
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              {festDaten[datePicker] && (
                <button style={{ ...S.btnDanger, marginRight: "auto", padding: "7px 12px", fontSize: 13 }} onClick={() => {
                  const updated = { ...festDaten };
                  delete updated[datePicker];
                  setFestDaten(updated);
                  localStorage.setItem("gv_festdaten", JSON.stringify(updated));
                  const allTermine = loadTermine();
                  const newTermine = allTermine.filter(t => t.festId !== datePicker);
                  saveTermine(newTermine);
                  setTermine(newTermine);
                  setDatePicker(null);
                }}>🗑️ Termin löschen</button>
              )}
              <button style={{ ...S.btnGhost, padding: "7px 12px", fontSize: 13 }} onClick={() => setDatePicker(null)}>Abbrechen</button>
              <button style={{ ...S.btnPrimary, padding: "7px 12px", fontSize: 13 }} onClick={() => {
                const val = document.getElementById("festDateInput").value;
                if (val) {
                  const fest = FESTE.find(f => f.id === datePicker);
                  setFestDatum(datePicker, val, fest.name, FEST_TYP[datePicker] || "Versammlung");
                }
              }}>✓ Speichern &amp; in Kalender</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <h1 style={S.h1}>Willkommen 🌱</h1>
        <p style={S.subtitle}>Gartenfreunde Rödern – Übersicht</p>
      </div>

      {/* Wetter Widget — echte API mit Aktualisierungs-Button */}
      <WetterWidget />

      {/* Gärtner-Spruch der Woche */}
      <div style={{ 
        marginBottom: 24, 
        background: "var(--cardBg)", 
        border: "1px solid var(--gold)", 
        borderRadius: 12, 
        padding: "20px 24px",
        textAlign: "center"
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 8 }}>
          <span style={{ fontSize: 24 }}>🌻</span>
          <div style={{ 
            fontSize: 11, 
            fontWeight: 700, 
            color: COLORS.gold, 
            textTransform: "uppercase", 
            letterSpacing: 1.2 
          }}>
            Gärtner-Weisheit der Woche
          </div>
        </div>
        <div style={{ 
          fontStyle: "italic", 
          fontSize: 16, 
          lineHeight: 1.6, 
          color: COLORS.text,
          fontFamily: "'Georgia', 'Times New Roman', serif"
        }}>
          "{getGaertnerSpruch()}"
        </div>
      </div>

      {/* Eich-Warnungen */}
      {(() => {
        const abl = (() => { try { return JSON.parse(localStorage.getItem("gv_ablesungen") || "{}"); } catch(e) { return {}; } })();
        const currentYear = new Date().getFullYear();
        const warnings = [];
        members.forEach(m => {
          ["wasser","strom"].forEach(typ => {
            // Check current year, also check B
            ["Eichdatum","EichdatumB"].forEach(period => {
              const key = `${m.id}__${typ}__${currentYear}__${period}`;
              // Also check previous year carry-over
              const val = abl[key] || abl[`${m.id}__${typ}__${currentYear-1}__${period}`] || "";
              if (!val) return;
              const status = checkEich(val, typ);
              if (status) {
                warnings.push({ member: m, typ, period, val, status });
              }
            });
          });
        });
        if (warnings.length === 0) return null;
        return (
          <div style={{ ...S.card, border: `1.5px solid ${COLORS.danger}`, background: "#FEF9F9", marginBottom: 20 }}>
            <h3 style={{ ...S.cardTitle, color: COLORS.danger, margin: "0 0 12px" }}>⚠️ Eichung – Handlungsbedarf</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {warnings.map((w, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", borderRadius: 8, background: "var(--cardBg)", border: `1px solid ${eichColor(w.status)}44` }}>
                  <span style={{ fontSize: 18 }}>{w.typ === "wasser" ? "💧" : "⚡"}</span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>{w.member.vorname} {w.member.nachname}</span>
                    <span style={{ color: COLORS.textMuted, fontSize: 13 }}> · {w.member.garten}</span>
                    <span style={{ color: COLORS.textMuted, fontSize: 13 }}> · {w.typ === "wasser" ? "Wasserzähler" : "Stromzähler"}</span>
                  </div>
                  <span style={{ fontWeight: 700, color: eichColor(w.status), fontSize: 13 }}>
                    {eichIcon(w.status)} M {w.val} – {w.status === "abgelaufen" ? "Eichung abgelaufen!" : w.status === "laeuft_ab" ? "Läuft dieses Jahr ab" : "Läuft nächstes Jahr ab"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Wichtige Termine - Full Width */}
      <div style={{ ...S.card, marginBottom: 24 }}>
        <h3 style={S.cardTitle}>Wichtige Termine {new Date().getFullYear()}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gap: 20 }}>
          {/* Grid layout: 2x2 for equal heights */}
          {FESTE.filter(f => f.id === "f2").map(fest => {
            const datum = festDaten[fest.id];
            const formatD = datum ? new Date(datum + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : null;
            return (
              <div key={fest.id} style={{ ...DS.festCard, background: fest.farbe.bg, borderLeft: `4px solid ${fest.farbe.border}`, display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1 }}>
                  {fest.svgIcon ? <WasserAbstellenIcon size={32} /> : <span style={{ fontSize: 32 }}>{fest.emoji}</span>}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: fest.farbe.text, marginBottom: 4 }}>{fest.name}</div>
                    <div style={{ fontSize: 14, marginTop: 4 }}>
                      {datum ? (
                        <span style={{ color: COLORS.leaf, fontWeight: 600, cursor: "pointer" }} onClick={() => setDatePicker(fest.id)}>📅 {formatD} <span style={{ color: '#27AE60', fontSize: 16 }}>✔</span></span>
                      ) : (
                        <span style={{ color: COLORS.danger, fontWeight: 600, cursor: "pointer", textDecoration: "underline dotted" }} onClick={() => setDatePicker(fest.id)}>📅 Termin noch offen</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 6, lineHeight: 1.5 }}>{fest.beschreibung}</div>
                  </div>
                </div>
              </div>
            );
          })}
          
          {FESTE.filter(f => f.id === "f1").map(fest => {
            const datum = festDaten[fest.id];
            const formatD = datum ? new Date(datum + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : null;
            return (
              <div key={fest.id} style={{ ...DS.festCard, background: fest.farbe.bg, borderLeft: `4px solid ${fest.farbe.border}`, display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1 }}>
                  {fest.svgIcon ? <WasserAbstellenIcon size={32} /> : <span style={{ fontSize: 32 }}>{fest.emoji}</span>}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: fest.farbe.text, marginBottom: 4 }}>{fest.name}</div>
                    <div style={{ fontSize: 14, marginTop: 4 }}>
                      {datum ? (
                        <span style={{ color: COLORS.leaf, fontWeight: 600, cursor: "pointer" }} onClick={() => setDatePicker(fest.id)}>📅 {formatD} <span style={{ color: '#27AE60', fontSize: 16 }}>✔</span></span>
                      ) : (
                        <span style={{ color: COLORS.danger, fontWeight: 600, cursor: "pointer", textDecoration: "underline dotted" }} onClick={() => setDatePicker(fest.id)}>📅 Termin noch offen</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 6, lineHeight: 1.5 }}>{fest.beschreibung}</div>
                  </div>
                </div>
              </div>
            );
          })}
          
          {FESTE.filter(f => f.id === "f3").map(fest => {
            const datum = festDaten[fest.id];
            const formatD = datum ? new Date(datum + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : null;
            return (
              <div key={fest.id} style={{ ...DS.festCard, background: fest.farbe.bg, borderLeft: `4px solid ${fest.farbe.border}`, display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1 }}>
                  {fest.svgIcon ? <WasserAbstellenIcon size={32} /> : <span style={{ fontSize: 32 }}>{fest.emoji}</span>}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: fest.farbe.text, marginBottom: 4 }}>{fest.name}</div>
                    <div style={{ fontSize: 14, marginTop: 4 }}>
                      {datum ? (
                        <span style={{ color: COLORS.leaf, fontWeight: 600, cursor: "pointer" }} onClick={() => setDatePicker(fest.id)}>📅 {formatD} <span style={{ color: '#27AE60', fontSize: 16 }}>✔</span></span>
                      ) : (
                        <span style={{ color: COLORS.danger, fontWeight: 600, cursor: "pointer", textDecoration: "underline dotted" }} onClick={() => setDatePicker(fest.id)}>📅 Termin noch offen</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 6, lineHeight: 1.5 }}>{fest.beschreibung}</div>
                  </div>
                </div>
              </div>
            );
          })}
          
          {FESTE.filter(f => f.id === "f4").map(fest => {
            const datum = festDaten[fest.id];
            const formatD = datum ? new Date(datum + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : null;
            return (
              <div key={fest.id} style={{ ...DS.festCard, background: fest.farbe.bg, borderLeft: `4px solid ${fest.farbe.border}`, display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1 }}>
                  {fest.svgIcon ? <WasserAbstellenIcon size={32} /> : <span style={{ fontSize: 32 }}>{fest.emoji}</span>}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: fest.farbe.text, marginBottom: 4 }}>{fest.name}</div>
                    <div style={{ fontSize: 14, marginTop: 4 }}>
                      {datum ? (
                        <span style={{ color: COLORS.leaf, fontWeight: 600, cursor: "pointer" }} onClick={() => setDatePicker(fest.id)}>📅 {formatD} <span style={{ color: '#27AE60', fontSize: 16 }}>✔</span></span>
                      ) : (
                        <span style={{ color: COLORS.danger, fontWeight: 600, cursor: "pointer", textDecoration: "underline dotted" }} onClick={() => setDatePicker(fest.id)}>📅 Termin noch offen</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 6, lineHeight: 1.5 }}>{fest.beschreibung}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Nächste Termine */}
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ ...S.cardTitle, margin: 0 }}>Nächste Termine</h3>
            <button style={DS.linkBtn} onClick={() => onNav("kalender")}>Alle →</button>
          </div>
          {naechsteTermine.length === 0 ? (
            <p style={{ color: COLORS.textMuted, fontSize: 13, textAlign: "center", padding: "16px 0" }}>Keine bevorstehenden Termine</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {naechsteTermine.map(t => {
                const d = new Date(t.datum + "T12:00:00");
                const TERMIN_FARBEN = { "Versammlung": "#2D5A27", "Arbeitseinsatz": "#7D6608", "Frist": "#C0392B", "Sommerfest": "#784212", "Herbstfest": "#6E2F0A" };
                return (
                  <div key={t.id} style={DS.terminRow}>
                    <div style={{ ...DS.terminDot, background: TERMIN_FARBEN[t.typ] || "#2D5A27" }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{t.titel}</div>
                      <div style={{ fontSize: 11, color: COLORS.textMuted }}>{d.toLocaleDateString("de-DE")} {t.uhrzeit && `· ${t.uhrzeit}`}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Letzte Vorstandsversammlung */}
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <h3 style={{ ...S.cardTitle, margin: 0 }}>Letzte Vorstandssitzung</h3>
            <button style={DS.linkBtn} onClick={() => onNav("vorstand")}>Alle →</button>
          </div>
          {vorstand.length === 0 ? (
            <p style={{ color: COLORS.textMuted, fontSize: 13, textAlign: "center", padding: "12px 0" }}>Noch keine Versammlungen</p>
          ) : (
            <div>{(() => {
              const letzte = vorstand[0];
              const d = new Date(letzte.datum + "T12:00:00");
              const vorstandNamen = getVorstandNamen().length > 0 ? getVorstandNamen() : VORSTAND_FALLBACK;
              const anwesend = vorstandNamen.filter(p => letzte.anwesend?.[p]);
              return (
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{d.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
                  {letzte.uhrzeit && <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>🕐 {letzte.uhrzeit} Uhr</div>}
                  <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {vorstandNamen.map(p => (
                      <span key={p} style={{ ...DS.personPill, background: anwesend.includes(p) ? "var(--leafPale)" : "var(--tableStripe)", color: anwesend.includes(p) ? COLORS.leaf : COLORS.textMuted, border: `1px solid ${anwesend.includes(p) ? COLORS.border : "var(--border)"}` }}>
                        {anwesend.includes(p) ? "✓" : "–"} {p.split(" ")[0]}
                      </span>
                    ))}
                  </div>
                  {letzte.bericht && <div style={{ marginTop: 10, fontSize: 12, color: COLORS.textMuted, lineHeight: 1.6, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }} dangerouslySetInnerHTML={{ __html: letzte.bericht }} />}
                </div>
              );
            })()}</div>
          )}
        </div>
      </div>
    </div>
  );
}

const DS = {
  statsRow: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 },
  statCard: { background: "var(--cardBg)", borderRadius: 12, border: `1.5px solid ${COLORS.border}`, padding: "20px 16px", cursor: "pointer", textAlign: "center", transition: "box-shadow 0.15s" },
  statEmoji: { fontSize: 28, marginBottom: 8 },
  statVal: { fontSize: 28, fontWeight: 800, color: COLORS.leaf },
  statLabel: { fontSize: 13, fontWeight: 600, color: COLORS.text, marginTop: 2 },
  statSub: { fontSize: 11, color: COLORS.textMuted, marginTop: 2 },
  twoCol: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(480px, 1fr))", gap: 20, alignItems: "start" },
  festCard: { borderRadius: 10, padding: "14px 16px", border: `1px solid transparent` },
  terminRow: { display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: COLORS.leafPale, borderRadius: 8 },
  terminDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  linkBtn: { background: "none", border: "none", color: COLORS.leaf, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" },
  personPill: { fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 12 },
  terminSetBtn: { background: COLORS.leafPale, border: "1.5px solid " + COLORS.border, color: COLORS.leaf, borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 },
};


// ─── Passwort ändern (Pflicht beim ersten Login) ──────────────────────────────
function ChangePasswordScreen({ user, onChanged, onLogout }) {
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [show, setShow] = useState(false);

  function handleSave() {
    if (pw1.length < 4) { setError("Passwort muss mindestens 4 Zeichen lang sein."); return; }
    if (pw1 !== pw2) { setError("Die Passwörter stimmen nicht überein."); return; }
    if (pw1 === "fgr") { setError("Bitte wähle ein anderes Passwort als das Standardpasswort."); return; }
    const accounts = getMemberAccounts();
    accounts[user.memberId] = { password: pw1, passwordChanged: true };
    saveMemberAccounts(accounts);
    onChanged(user);
  }

  return (
    <div style={loginStyles.bg}>
      <div style={loginStyles.card}>
        <div style={loginStyles.logo}>
          <span style={{ fontSize: 48 }}>🔐</span>
          <h1 style={loginStyles.title}>Passwort ändern</h1>
          <p style={loginStyles.sub}>Bitte lege ein neues Passwort fest</p>
        </div>
        <div style={{ background: "var(--cream)", border: "1px solid #F4D03F", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#7D6608" }}>
          👋 Willkommen, Gartenfreund! Beim ersten Login musst du dein Passwort ändern.
        </div>
        <div style={loginStyles.form}>
          <div style={loginStyles.field}>
            <label style={loginStyles.label}>Neues Passwort</label>
            <div style={{ position: "relative" }}>
              <input style={loginStyles.input} type={show ? "text" : "password"} value={pw1}
                onChange={e => setPw1(e.target.value)} placeholder="Neues Passwort" autoFocus />
              <button style={loginStyles.eyeBtn} onClick={() => setShow(v => !v)} tabIndex={-1}>{show ? "🙈" : "👁️"}</button>
            </div>
          </div>
          <div style={loginStyles.field}>
            <label style={loginStyles.label}>Passwort wiederholen</label>
            <input style={loginStyles.input} type={show ? "text" : "password"} value={pw2}
              onChange={e => setPw2(e.target.value)} placeholder="Passwort bestätigen"
              onKeyDown={e => e.key === "Enter" && handleSave()} />
          </div>
          {error && <div style={loginStyles.error}>{error}</div>}
          <button style={loginStyles.btn} onClick={handleSave}>Passwort speichern</button>
          <button style={{ ...loginStyles.btn, background: "transparent", color: "var(--textMuted)", border: "1.5px solid var(--border)", marginTop: 0 }} onClick={onLogout}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
}

// ─── Mitglieder-Portal ────────────────────────────────────────────────────────
function MemberPortal({ currentUser, onLogout, onRechnungsPreview }) {
  const members = JSON.parse(localStorage.getItem("gv_members") || "[]");
  const member = members.find(m => m.id === (currentUser.id || currentUser.memberId));
  const [pwModal, setPwModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ strasse: "", plz: "", ort: "", telefon: "", email: "" });
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState(false);
  const [rechnungsVorschau, setRechnungsVorschau] = useState(false);
  const [rechnungsAnzeige, setRechnungsAnzeige] = useState(null);

  function saveNewPw() {
    if (pw1.length < 4) { setPwError("Mindestens 4 Zeichen."); return; }
    if (pw1 !== pw2) { setPwError("Passwörter stimmen nicht überein."); return; }
    if (pw1 === "fgr") { setPwError("Bitte wähle ein anderes Passwort."); return; }
    const accounts = getMemberAccounts();
    accounts[currentUser.memberId] = { password: pw1, passwordChanged: true };
    saveMemberAccounts(accounts);
    setPwError("");
    setPwSuccess(true);
    setTimeout(() => { setPwModal(false); setPwSuccess(false); setPw1(""); setPw2(""); }, 1500);
  }

  if (!member) return (
    <div style={loginStyles.bg}>
      <div style={loginStyles.card}>
        <p style={{ color: "#C0392B", textAlign: "center" }}>Mitglied nicht gefunden.</p>
        <button style={loginStyles.btn} onClick={onLogout}>Zurück</button>
      </div>
    </div>
  );

  const sc = { Aktiv: { bg: "var(--leafPale)", text: "var(--leaf)", dot: "var(--leaf)" }, Inaktiv: { bg: "var(--tableStripe)", text: "var(--textMuted)", dot: "var(--textMuted)" }, Ausgetreten: { bg: "var(--cardBg)", text: "var(--danger)", dot: "var(--danger)" } };
  const statusC = sc[member.status] || sc.Aktiv;

  const isMob = window.innerWidth < 600;

  return (
    <React.Fragment>
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #1C3A18 0%, #2D5A27 50%, #4A8C42 100%)", fontFamily: "'Georgia','Times New Roman',serif", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ background: "rgba(0,0,0,0.2)", padding: isMob ? "12px 16px" : "16px 32px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: isMob ? 22 : 28 }}>🌱</span>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: isMob ? 14 : 16 }}>Gartenfreunde Rödern</div>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 10, letterSpacing: 1, textTransform: "uppercase" }}>Mitgliederportal</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 8, padding: isMob ? "7px 10px" : "8px 16px", fontSize: isMob ? 12 : 13, cursor: "pointer", fontFamily: "inherit" }}
            onClick={() => setPwModal(true)}>{isMob ? "🔑" : "🔑 Passwort ändern"}</button>
          <button style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 8, padding: isMob ? "7px 10px" : "8px 16px", fontSize: isMob ? 12 : 13, cursor: "pointer", fontFamily: "inherit" }}
            onClick={onLogout}>{isMob ? "↩" : "Abmelden"}</button>
        </div>
      </div>

      {/* Password change modal */}
      {pwModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 900, display: "flex", alignItems: isMob ? "flex-end" : "center", justifyContent: "center" }}>
          <div style={{ background: "var(--cream)", borderRadius: isMob ? "16px 16px 0 0" : 16, padding: isMob ? "24px 20px" : 32, width: isMob ? "100%" : 360, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", boxSizing: "border-box" }}>
            <h3 style={{ margin: "0 0 20px", color: "#2D5A27" }}>🔑 Passwort ändern</h3>
            {pwSuccess ? (
              <div style={{ textAlign: "center", padding: "20px 0", color: "#2D5A27", fontWeight: 700, fontSize: 16 }}>✓ Passwort gespeichert!</div>
            ) : (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div><label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--textMuted)", display: "block", marginBottom: 4 }}>Neues Passwort</label>
                    <input style={{ width: "100%", padding: "10px 12px", border: "1.5px solid var(--border)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                      type="password" value={pw1} onChange={e => setPw1(e.target.value)} /></div>
                  <div><label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--textMuted)", display: "block", marginBottom: 4 }}>Wiederholen</label>
                    <input style={{ width: "100%", padding: "10px 12px", border: "1.5px solid var(--border)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                      type="password" value={pw2} onChange={e => setPw2(e.target.value)} onKeyDown={e => e.key === "Enter" && saveNewPw()} /></div>
                </div>
                {pwError && <div style={{ background: "var(--cream)", color: "#C0392B", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginTop: 10 }}>{pwError}</div>}
                <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
                  <button style={{ padding: "9px 16px", background: "transparent", color: "var(--textMuted)", border: "1.5px solid var(--border)", borderRadius: 8, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}
                    onClick={() => { setPwModal(false); setPwError(""); setPw1(""); setPw2(""); }}>Abbrechen</button>
                  <button style={{ padding: "9px 16px", background: "#2D5A27", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                    onClick={saveNewPw}>Speichern</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, padding: isMob ? "16px 12px" : "32px", maxWidth: 800, margin: "0 auto", width: "100%", display: "flex", flexDirection: "column", gap: isMob ? 14 : 20, boxSizing: "border-box" }}>
        {/* Profile card */}
        <div style={{ background: "var(--cream)", borderRadius: 16, padding: isMob ? "18px 16px" : 28, marginBottom: isMob ? 0 : 20, border: "1.5px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ width: isMob ? 48 : 60, height: isMob ? 48 : 60, borderRadius: "50%", background: "#4A8C42", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: isMob ? 18 : 22, fontWeight: 700, flexShrink: 0 }}>
              {member.vorname?.[0]}{member.nachname?.[0]}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: isMob ? 17 : 22, color: "var(--text)" }}>{member.vorname} {member.nachname}</h2>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: statusC.bg, color: statusC.text, marginTop: 4 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusC.dot }} />{member.status}
              </span>
            </div>
          </div>
          <div style={{ height: 1, background: "#D4E6CE", margin: "0 0 16px" }} />
          {!editMode ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr", gap: isMob ? "10px 0" : "14px 28px" }}>
                {[
                  ["Garten", member.garten, false],
                  ["Eintrittsdatum", member.eintrittsdatum ? new Date(member.eintrittsdatum).toLocaleDateString("de-DE") : "–", false],
                  ["Straße", member.strasse, true],
                  ["PLZ", member.plz, true],
                  ["Ort", member.ort, true],
                  ["Telefon", member.telefon, true],
                  ["E-Mail", member.email, true],
                ].map(([label, val]) => (
                  <div key={label}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, color: "var(--textMuted)", marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 14, color: "var(--text)", wordBreak: "break-word" }}>{val || "–"}</div>
                  </div>
                ))}
              </div>
              {!currentUser.adminPreview && (
                <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
                  <button onClick={() => { setEditForm({ strasse: member.strasse||"", plz: member.plz||"", ort: member.ort||"", telefon: member.telefon||"", email: member.email||"" }); setEditMode(true); }}
                    style={{ padding: isMob ? "10px 18px" : "8px 16px", background: "#4A8C42", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", width: isMob ? "100%" : "auto" }}>
                    ✏ Daten bearbeiten
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr", gap: "12px" }}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, color: "var(--textMuted)", marginBottom: 4 }}>Straße & Hausnummer</div>
                  <input type="text" value={editForm.strasse} onChange={e => setEditForm({...editForm, strasse: e.target.value})}
                    style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 8, border: "1.5px solid var(--border)", fontSize: 14, fontFamily: "inherit", background: "var(--cardBg)", color: "var(--text)" }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, color: "var(--textMuted)", marginBottom: 4 }}>PLZ</div>
                  <input type="text" value={editForm.plz} onChange={e => setEditForm({...editForm, plz: e.target.value})}
                    style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 8, border: "1.5px solid var(--border)", fontSize: 14, fontFamily: "inherit", background: "var(--cardBg)", color: "var(--text)" }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, color: "var(--textMuted)", marginBottom: 4 }}>Ort</div>
                  <input type="text" value={editForm.ort} onChange={e => setEditForm({...editForm, ort: e.target.value})}
                    style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 8, border: "1.5px solid var(--border)", fontSize: 14, fontFamily: "inherit", background: "var(--cardBg)", color: "var(--text)" }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, color: "var(--textMuted)", marginBottom: 4 }}>Telefon</div>
                  <input type="tel" value={editForm.telefon} onChange={e => setEditForm({...editForm, telefon: e.target.value})}
                    style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 8, border: "1.5px solid var(--border)", fontSize: 14, fontFamily: "inherit", background: "var(--cardBg)", color: "var(--text)" }} />
                </div>
                <div style={{ gridColumn: isMob ? "1" : "auto" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, color: "var(--textMuted)", marginBottom: 4 }}>E-Mail</div>
                  <input type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})}
                    style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 8, border: "1.5px solid var(--border)", fontSize: 14, fontFamily: "inherit", background: "var(--cardBg)", color: "var(--text)" }} />
                </div>
              </div>
              <div style={{ marginTop: 16, display: "flex", gap: 10, flexDirection: isMob ? "column-reverse" : "row", justifyContent: "flex-end" }}>
                <button onClick={() => setEditMode(false)}
                  style={{ padding: "10px 16px", background: "transparent", color: "var(--text)", border: "1.5px solid var(--border)", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                  Abbrechen
                </button>
                <button onClick={() => {
                    const feldNamen = { strasse: "Straße", plz: "PLZ", ort: "Ort", telefon: "Telefon", email: "E-Mail" };
                    const geaendert = Object.keys(editForm).filter(k => (editForm[k]||"") !== (member[k]||"")).map(k => ({
                      feld: feldNamen[k] || k,
                      alt: member[k] || "–",
                      neu: editForm[k] || "–",
                    }));
                    if (geaendert.length > 0) { logDatenAenderung(currentUser.memberId, geaendert); }
                    const all = JSON.parse(localStorage.getItem("gv_members") || "[]");
                    const updated = all.map(x => x.id === member.id ? { ...x, ...editForm } : x);
                    localStorage.setItem("gv_members", JSON.stringify(updated));
                    // Server-Sync erfolgt automatisch über patched localStorage.setItem
                    setEditMode(false);
                    window.location.reload();
                  }}
                  style={{ padding: "10px 16px", background: "#4A8C42", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                  💾 Speichern
                </button>
              </div>
            </>
          )}
        </div>

        <div style={S.card}>
          <h3 style={S.cardTitle}>Notizen</h3>
          <p style={{ color: "var(--textMuted)", margin: 0, lineHeight: 1.6, fontSize: 14 }}>{member.notizen || "–"}</p>
        </div>

        {/* Dokumente */}
        <div style={{ background: "var(--cream)", borderRadius: 16, padding: isMob ? "16px 14px" : 28, border: "1.5px solid var(--border)" }}>
          <h3 style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 700, color: "var(--textMuted)", textTransform: "uppercase", letterSpacing: 0.8 }}>Meine Dokumente</h3>
          {(!member.dokumente || member.dokumente.length === 0) ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "var(--textMuted)" }}>
              <span style={{ fontSize: 36 }}>📂</span>
              <p style={{ margin: "8px 0 0", fontSize: 13 }}>Keine Dokumente vorhanden</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {member.dokumente.map(doc => (
                <div key={doc.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "var(--leafPale)", borderRadius: 10, border: "1px solid var(--border)" }}>
                  <span style={{ fontSize: 22, cursor: doc.data ? "pointer" : "default" }} onClick={() => doc.data && openDoc(doc)}>{doc.name?.endsWith(".pdf") ? "📕" : doc.name?.endsWith(".docx") ? "📘" : "📄"}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, cursor: doc.data ? "pointer" : "default", color: doc.data ? "#2D5A27" : "#1C2B1A" }}
                      onClick={() => doc.data && openDoc(doc)}>{doc.name}</div>
                    <div style={{ fontSize: 11, color: "var(--textMuted)", marginTop: 2 }}>{doc.typ} · {doc.datum ? new Date(doc.datum + "T12:00:00").toLocaleDateString("de-DE") : ""}</div>
                  </div>

                </div>
              ))}
            </div>
          )}
        </div>

        {/* Verbrauch */}
        <div style={{ background: "var(--cream)", borderRadius: 16, padding: isMob ? "16px 14px" : 28, border: "1.5px solid var(--border)" }}>
          <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr", gap: isMob ? 12 : 16 }}>
            {[
              { label: "Wasserverbrauch Vorjahr", year: new Date().getFullYear() - 1, unit: "m³", key: "wasser", color: "#2E86C1", bg: "var(--leafPale)" },
              { label: "Stromverbrauch Vorjahr", year: new Date().getFullYear() - 1, unit: "kWh", key: "strom", color: "#D4AC0D", bg: "var(--cream)" },
            ].map(f => (
              <div key={f.key} style={{ background: f.bg, borderRadius: 12, padding: "18px 20px", border: "1.5px solid var(--border)" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--textMuted)", textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 4 }}>{f.label}</div>
                <div style={{ fontSize: 11, color: "var(--textMuted)", marginBottom: 12 }}>Jahr {f.year}</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: f.color }}>{member[f.key + "_vorjahr"] || "–"}</div>
                  <div style={{ fontSize: 14, color: f.color, fontWeight: 600 }}>{f.unit}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Rechnungen */}
        <div style={{ background: "var(--cream)", borderRadius: 16, padding: isMob ? "16px 14px" : 28, border: "1.5px solid var(--border)" }}>
          <h3 style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 700, color: "var(--textMuted)", textTransform: "uppercase", letterSpacing: 0.8 }}>Rechnungen</h3>
          {(onRechnungsPreview || member) && (
            <button style={{ ...S.btnSmall, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}
              onClick={() => onRechnungsPreview ? onRechnungsPreview() : setRechnungsVorschau(true)}>
              🧾 Jahresrechnung Vorschau
            </button>
          )}
          {(!member.rechnungen || member.rechnungen.length === 0) ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "var(--textMuted)" }}>
              <span style={{ fontSize: 36 }}>🧾</span>
              <p style={{ margin: "8px 0 0", fontSize: 13 }}>Keine Rechnungen vorhanden</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ background: "var(--cream)", border: "2px solid #C0392B", borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: "#C0392B", letterSpacing: 0.3 }}>
                  🔴 Die Rechnungsnummer ist immer der Verwendungszweck!
                </span>
              </div>
              {member.rechnungen.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: isMob ? "flex-start" : "center", gap: 12, padding: isMob ? "12px 10px" : "10px 14px", background: "var(--cream)", borderRadius: 10, border: "1px solid var(--border)", flexDirection: isMob ? "column" : "row" }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "center", width: "100%" }}>
                    <span style={{ fontSize: 22 }}>🧾</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{r.titel}</div>
                      <div style={{ fontSize: 11, color: "var(--textMuted)", marginTop: 2 }}>{r.datum ? (r.datum.includes("-") ? r.datum.split("-").reverse().join(".") : r.datum) : ""}{r.betrag ? " · " + r.betrag + " €" : ""}</div>
                      {r.rechnungsnummer && (
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#C0392B", marginTop: 3, fontFamily: "monospace" }}>
                          RechnungsNr.: {r.rechnungsnummer}
                        </div>
                      )}
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 12, background: r.bezahlt ? "var(--leafPale)" : "var(--cardBg)", color: r.bezahlt ? "#2D5A27" : "#C0392B", flexShrink: 0 }}>
                      {r.bezahlt ? "✓ Bezahlt" : "Offen"}
                    </span>
                  </div>
                  {r.data && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", width: isMob ? "100%" : "auto" }}>
                      <button style={{ ...S.btnSmall, fontSize: 11, padding: "6px 12px", flex: isMob ? 1 : "none" }}
                        onClick={() => oeffneOderErstelleRechnung(r, member, () => setRechnungsVorschau(true), setRechnungsAnzeige)}>
                        📄 Öffnen</button>
                      <button style={{ ...S.btnSmall, fontSize: 11, padding: "6px 12px", background: "#1a6fb3", color: "#fff", border: "none", flex: isMob ? 1 : "none" }}
                        onClick={() => setRechnungsVorschau(true)}>
                        🖨️ Erstellen
                      </button>
                      {r.name && (
                        <a
                          href={`${PDF_SERVER_URL}/rechnungen/${r.jahr}/${normalizeRechnungName(r.name)}`}
                          download={normalizeRechnungName(r.name)}
                          target="_blank"
                          rel="noreferrer"
                          title="PDF herunterladen"
                          style={{ ...S.btnSmall, fontSize: 15, padding: "6px 12px", background: COLORS.leaf, color: "#fff", border: "none", textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                        >⬇</a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {/* Email preference */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #D4E6CE" }}>
            <EmailCheckbox memberId={member.id} />
          </div>
        </div>

        {/* Passwort & Abmelden */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
          <button
            style={{ width: "100%", padding: "13px 16px", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            onClick={() => setPwModal(true)}>
            🔑 Passwort ändern
          </button>
          <button
            style={{ width: "100%", padding: "13px 16px", background: "rgba(192,57,43,0.25)", border: "1px solid rgba(192,57,43,0.5)", color: "#fff", borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            onClick={onLogout}>
            ↩ Abmelden
          </button>
        </div>

      </div>
    </div>
    {rechnungsVorschau && member && (
      <div style={{ ...S.overlay, zIndex: 1100 }}>
        <div style={{ position: "relative", width: "min(96vw, 620px)", maxHeight: "96vh", overflowY: "auto", background: "var(--cardBg)", borderRadius: 12 }}>
          <button style={{ position: "sticky", top: 10, float: "right", margin: "10px 10px 0 0", ...S.btnGhost, zIndex: 10 }}
            onClick={() => setRechnungsVorschau(false)}>✕ Schließen</button>
          <Jahresrechnungen members={members} initialPreviewMember={member} onClosePreview={() => setRechnungsVorschau(false)} />
        </div>
      </div>
    )}
    <RechnungsAnzeigeModal rechnungData={rechnungsAnzeige} onClose={() => setRechnungsAnzeige(null)} />
  </React.Fragment>
  );
}

function EmailCheckbox({ memberId }) {
  const key = "gv_email_pref_" + memberId;
  const [checked, setChecked] = useState(() => localStorage.getItem(key) === "true");

  function toggle() {
    const newVal = !checked;
    setChecked(newVal);
    localStorage.setItem(key, String(newVal));
  }

  return (
    <label style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
      <div onClick={toggle} style={{
        width: 22, height: 22, borderRadius: 5, border: "2px solid " + (checked ? "#2D5A27" : "#D4E6CE"),
        background: checked ? "#2D5A27" : "#fff", display: "flex", alignItems: "center",
        justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff",
        flexShrink: 0, transition: "all 0.15s",
      }}>
        {checked ? "✓" : ""}
      </div>
      <span style={{ fontSize: 14, color: "var(--text)", userSelect: "none" }}>Rechnungen per E-Mail bekommen</span>
    </label>
  );
}



// ─── Finanzen Wrapper (mit Unterreitern) ──────────────────────────────────────
// ─── Bilanzen ─────────────────────────────────────────────────────────────────
function Bilanzen({ members }) {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [filterKat, setFilterKat] = useState("Alle");
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  // Finanzen-Einträge aus gv_finanzen (manuell erfasst)
  const finanzEintraege = React.useMemo(() => {
    return (() => { try { return JSON.parse(localStorage.getItem("gv_finanzen") || "[]"); } catch(e) { return []; } })();
  }, []);

  // Jahresrechnungen als Block-Zusammenfassung
  const jahresrechnungsBlock = React.useMemo(() => {
    const stored = (() => { try { return JSON.parse(localStorage.getItem("gv_members") || "[]"); } catch(e) { return []; } })();
    const byYear = {};
    stored.forEach(m => {
      (m.rechnungen||[]).filter(r => r.name && r.rechnungsnummer).forEach(r => {
        const yr = String(r.jahr || "");
        if (!byYear[yr]) byYear[yr] = { anzahl: 0, bezahlt: 0, summe: 0, summeBezahlt: 0 };
        byYear[yr].anzahl++;
        const b = parseFloat((r.betrag||"0").replace(",",".")) || 0;
        byYear[yr].summe += b;
        if (r.bezahlt) { byYear[yr].bezahlt++; byYear[yr].summeBezahlt += b; }
      });
    });
    return byYear;
  }, [members]);

  // Finanzen nach Jahr filtern
  const jahrFinanzen = finanzEintraege.filter(e => {
    const yr = e.datum ? new Date(e.datum).getFullYear() : null;
    return yr === selectedYear;
  });

  // Kategorien aus Finanzen
  const alleKats = ["Alle", ...new Set(jahrFinanzen.map(e => e.kategorie).filter(Boolean))];
  const gefiltert = filterKat === "Alle" ? jahrFinanzen : jahrFinanzen.filter(e => e.kategorie === filterKat);

  // Zusammenfassung nach Kategorie
  const katSummen = {};
  jahrFinanzen.forEach(e => {
    const k = e.kategorie || "Sonstiges";
    const b = parseFloat((e.betrag||"0").replace(",",".")) || 0;
    if (!katSummen[k]) katSummen[k] = { summe: 0, anzahl: 0, einnahmen: 0, ausgaben: 0 };
    katSummen[k].anzahl++;
    katSummen[k].summe += b;
    if (e.typ === "Einnahme") katSummen[k].einnahmen += b;
    else katSummen[k].ausgaben += b;
  });

  const sumEinnahmen = jahrFinanzen.filter(e=>e.typ==="Einnahme").reduce((s,e)=>s+(parseFloat((e.betrag||"0").replace(",","."))||0),0);
  const sumAusgaben  = jahrFinanzen.filter(e=>e.typ!=="Einnahme").reduce((s,e)=>s+(parseFloat((e.betrag||"0").replace(",","."))||0),0);
  const saldo        = sumEinnahmen - sumAusgaben;
  const jahrRech     = jahresrechnungsBlock[String(selectedYear)] || null;

  const KAT_FARBEN = {
    "Mitgliedsbeitrag": { bg: "#f0f8ed", color: "#2D5A27" },
    "Wasser":           { bg: "#EBF5FB", color: "#2E86C1" },
    "Strom":            { bg: "#FEFBD8", color: "#D4AC0D" },
    "Versicherung":     { bg: "#F4ECF7", color: "#8E44AD" },
    "Instandhaltung":   { bg: "#FEF9E7", color: "#CA6F1E" },
    "Pacht":            { bg: "#EAFAF1", color: "#1E8449" },
    "Veranstaltung":    { bg: "#EBF5FB", color: "#1A5276" },
    "Spende":           { bg: "#FDEDEC", color: "#C0392B" },
    "Sonstiges":        { bg: "#F2F3F4", color: "#717D7E" },
  };
  const getFarbe = k => KAT_FARBEN[k] || { bg: "var(--leafPale)", color: COLORS.leaf };

  return (
    <div className="gv-page" style={S.page}>
      <div style={S.pageHeader}>
        <div><h1 style={S.h1}>Bilanzen</h1><p style={S.subtitle}>Zusammenfassung der Finanzen nach Kategorie</p></div>
      </div>

      {/* Jahr-Auswahl */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {years.map(y => <button key={y} style={{ ...S.filterBtn, ...(selectedYear===y?S.filterBtnActive:{}), fontSize: 15, padding: "8px 20px" }} onClick={() => setSelectedYear(y)}>{y}</button>)}
      </div>

      {/* Saldo-Karten */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
        {[
          { label: "Einnahmen",  value: sumEinnahmen, color: "#2D5A27", bg: "#f0f8ed", icon: "📈" },
          { label: "Ausgaben",   value: sumAusgaben,  color: "#C0392B", bg: "#FDEDEC", icon: "📉" },
          { label: "Saldo",      value: saldo,        color: saldo>=0?"#2D5A27":"#C0392B", bg: saldo>=0?"#f0f8ed":"#FDEDEC", icon: "⚖️" },
          { label: "Buchungen",  value: jahrFinanzen.length, color: COLORS.leaf, bg: "var(--leafPale)", icon: "📋", isCount: true },
        ].map(item => (
          <div key={item.label} style={{ background: item.bg, borderRadius: 12, padding: "16px 20px", border: `1.5px solid ${item.color}33` }}>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 4 }}>{item.icon} {item.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: item.color }}>
              {item.isCount ? item.value : `${item.value.toFixed(2).replace(".",",")} €`}
            </div>
          </div>
        ))}
      </div>

      {/* Jahresrechnungs-Block */}
      {jahrRech && (
        <div style={{ ...S.card, marginBottom: 24, background: "#f5fbf3", border: "1.5px solid #b8ddb0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 24 }}>🧾</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.leaf }}>Jahresrechnungen {selectedYear}</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
                  {jahrRech.anzahl} Rechnungen erstellt · {jahrRech.bezahlt} bezahlt · {jahrRech.anzahl - jahrRech.bezahlt} offen
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 24 }}>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, color: COLORS.textMuted }}>Rechnungssumme</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.leaf }}>{jahrRech.summe.toFixed(2).replace(".",",")} €</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, color: COLORS.textMuted }}>Eingegangen</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#2D7A4F" }}>{jahrRech.summeBezahlt.toFixed(2).replace(".",",")} €</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, color: COLORS.textMuted }}>Ausstehend</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#C0392B" }}>{(jahrRech.summe - jahrRech.summeBezahlt).toFixed(2).replace(".",",")} €</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kategorien-Zusammenfassung */}
      {Object.keys(katSummen).length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.textMuted, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Zusammenfassung nach Kategorie</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {Object.entries(katSummen).sort((a,b)=>b[1].summe-a[1].summe).map(([kat, data]) => {
              const f = getFarbe(kat);
              return (
                <div key={kat} style={{ background: f.bg, borderRadius: 10, padding: "14px 16px", border: `1.5px solid ${f.color}33`, cursor: "pointer", outline: filterKat===kat?`2px solid ${f.color}`:"none" }}
                  onClick={() => setFilterKat(filterKat===kat?"Alle":kat)}>
                  <div style={{ fontSize: 12, color: f.color, fontWeight: 700, marginBottom: 4 }}>{kat}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: f.color }}>{data.summe.toFixed(2).replace(".",",")} €</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
                    {data.anzahl} Buchung{data.anzahl!==1?"en":""}
                    {data.einnahmen>0 && data.ausgaben>0 && ` · +${data.einnahmen.toFixed(2).replace(".",",")} / -${data.ausgaben.toFixed(2).replace(".",",")} €`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Detailliste */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.textMuted }}>Buchungen:</span>
        {alleKats.map(k => <button key={k} style={{ ...S.filterBtn, ...(filterKat===k?S.filterBtnActive:{}) }} onClick={() => setFilterKat(k)}>{k}</button>)}
        <span style={{ marginLeft: "auto", fontSize: 12, color: COLORS.textMuted }}>{gefiltert.length} Einträge</span>
      </div>
      <div style={S.tableWrap}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--cardBg)" }}>
              <th style={S.th}>Datum</th>
              <th style={S.th}>Typ</th>
              <th style={S.th}>Kategorie</th>
              <th style={S.th}>Beschreibung</th>
              <th style={{ ...S.th, textAlign: "right" }}>Betrag</th>
            </tr>
          </thead>
          <tbody>
            {gefiltert.length === 0 ? (
              <tr><td colSpan={5} style={{ ...S.td, textAlign: "center", color: COLORS.textMuted, padding: "40px 0" }}>
                Keine Finanzbuchungen für {selectedYear}{filterKat!=="Alle"?" · "+filterKat:""}.<br/>
                <span style={{ fontSize: 12 }}>Buchungen unter „Finanzen" erfassen.</span>
              </td></tr>
            ) : gefiltert.sort((a,b)=>(b.datum||"").localeCompare(a.datum||"")).map((e,i) => {
              const b = parseFloat((e.betrag||"0").replace(",",".")) || 0;
              const f = getFarbe(e.kategorie||"Sonstiges");
              return (
                <tr key={e.id||i} style={S.tr}>
                  <td style={{ ...S.td, whiteSpace: "nowrap", fontSize: 12 }}>{e.datum ? e.datum.split("-").reverse().join(".") : "–"}</td>
                  <td style={S.td}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: e.typ==="Einnahme"?"#f0f8ed":"#FDEDEC", color: e.typ==="Einnahme"?"#2D5A27":"#C0392B" }}>{e.typ||"–"}</span>
                  </td>
                  <td style={S.td}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: f.bg, color: f.color }}>{e.kategorie||"–"}</span>
                  </td>
                  <td style={{ ...S.td, color: COLORS.textMuted, fontSize: 12 }}>{e.beschreibung||e.name||"–"}</td>
                  <td style={{ ...S.td, textAlign: "right", fontWeight: 700, color: e.typ==="Einnahme"?COLORS.leaf:"#C0392B" }}>{b.toFixed(2).replace(".",",")} €</td>
                </tr>
              );
            })}
          </tbody>
          {gefiltert.length > 0 && (
            <tfoot>
              <tr style={{ background: COLORS.leafPale }}>
                <td colSpan={4} style={{ ...S.td, fontWeight: 700 }}>Gesamt {filterKat!=="Alle"?filterKat:""}</td>
                <td style={{ ...S.td, textAlign: "right", fontWeight: 800, color: COLORS.leaf, fontSize: 15 }}>
                  {gefiltert.reduce((s,e)=>s+(parseFloat((e.betrag||"0").replace(",","."))||0),0).toFixed(2).replace(".",",")} €
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}


// ─── FinanzenWrapper ──────────────────────────────────────────────────────────
// ─── Ablesungen ──────────────────────────────────────────────────────────────
function loadAblesungen() {
  try { return JSON.parse(localStorage.getItem("gv_ablesungen") || "{}"); } catch(e) { return {}; }
}
function saveAblesungen(d) { localStorage.setItem("gv_ablesungen", JSON.stringify(d)); }

function Ablesungen({ members, isDatenaufnahme = false }) {
  const currentYear = new Date().getFullYear();
  const [ablesungen, setAblesungen] = useState(loadAblesungen);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [editCell, setEditCell] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [gartenSearch, setGartenSearch] = useState("");
  const [selectedGartenModal, setSelectedGartenModal] = useState(null); // For popup

  const years = [currentYear - 1, currentYear, currentYear + 1];
  
  // In Datenaufnahme mode: use all gardens, otherwise use active members
  const gaerten = getGaerten();
  const allGartenItems = isDatenaufnahme 
    ? gaerten.map(g => {
        const mitglied = members.find(m => m.garten === g.name && m.status !== "Ehemaliges Mitglied");
        return {
          id: g.id,
          garten: g.name,
          vorname: mitglied?.vorname || "",
          nachname: mitglied?.nachname || "",
          isMember: !!mitglied,
          category: categorizeGarten(g.name)
        };
      })
    : (() => {
        // For Ablesungen: expand members with zusatzgaerten into separate rows
        const rows = [];
        members
          .filter(m => m.status === "Aktives Mitglied" || m.status === "Passives Mitglied")
          .forEach(m => {
            // Main garden — use garden ID (same as Datenaufnahme uses)
            const hauptG = gaerten.find(x => x.name === m.garten);
            rows.push({ ...m, _rowGarten: m.garten, _gartenId: hauptG ? hauptG.id : m.id });
            // Additional gardens
            (m.zusatzgaerten || []).forEach(zg => {
              const g = gaerten.find(x => x.name === zg);
              rows.push({ ...m, _rowGarten: zg, _gartenId: g ? g.id : zg, id: g ? g.id : zg });
            });
          });
        return rows;
      })();

  // Filter by garten number in Datenaufnahme mode
  const filteredMembers = isDatenaufnahme
    ? (gartenSearch ? allGartenItems.filter(m => (m.garten || "").toLowerCase().includes(gartenSearch.toLowerCase())) : allGartenItems)
    : allGartenItems;

  function getKey(itemId, typ, period) {
    return `${itemId}__${typ}__${selectedYear}__${period}`;
  }

  function getVal(itemId, typ, period) {
    const current = ablesungen[getKey(itemId, typ, period)];
    if (current) return current;
    // Anfang = previous year's EndeB (if Zählerwechsel) else Ende
    if (period === "Anfang") {
      const prevEndeB = ablesungen[`${itemId}__${typ}__${selectedYear - 1}__EndeB`];
      if (prevEndeB) return prevEndeB;
      return ablesungen[`${itemId}__${typ}__${selectedYear - 1}__Ende`] || "";
    }
    // Zählernummer: prefer B from previous year if Zählerwechsel happened
    if (period === "Zaehlernummer") {
      const prevB = ablesungen[`${itemId}__${typ}__${selectedYear - 1}__ZaehlernummerB`];
      if (prevB && prevB.trim()) return prevB;
      return ablesungen[`${itemId}__${typ}__${selectedYear - 1}__Zaehlernummer`] || "";
    }
    // Eichdatum: prefer B from previous year if Zählerwechsel happened
    if (period === "Eichdatum") {
      const prevB = ablesungen[`${itemId}__${typ}__${selectedYear - 1}__EichdatumB`];
      if (prevB && prevB.trim()) return prevB;
      return ablesungen[`${itemId}__${typ}__${selectedYear - 1}__Eichdatum`] || "";
    }
    return "";
  }

  function saveVal(itemId, typ, period, val) {
    const updated = { ...ablesungen, [getKey(itemId, typ, period)]: val };
    // If Zählernummer or Eichdatum: also copy to current year explicitly so it's saved
    saveAblesungen(updated);
    setAblesungen(updated);
    setEditCell(null);
  }

  function EditField({ itemId, typ, period, type = "number", label: labelOverride }) {
    const val = getVal(itemId, typ, period);
    const isEditing = editCell?.itemId === itemId && editCell?.typ === typ && editCell?.period === period;
    const labelAuto = period === "Zaehlernummer" ? "Zählernummer" : period === "ZaehlernummerB" ? "Neue Zählernummer" : period === "Eichdatum" ? "Eichdatum" : period === "Anfang" ? "Vorjahr" : period === "Ende" ? "Ablesewert" : period === "EndeB" ? "Ablesewert Ende Jahr" : period;
    const label = labelOverride || labelAuto;
    const isEich = period === "Eichdatum";
    const isEnde = period === "Ende";
    const isAnfang = period === "Anfang";

    // Check if Eichung is expired or expiring soon
    let eichWarning = null;
    if (isEich && val) {
      const eichJahr = 2000 + parseInt(val);
      const gueltigBis = typ === "wasser" ? eichJahr + 6 : eichJahr + 16;
      const cy = new Date().getFullYear();
      if (gueltigBis < cy) eichWarning = "abgelaufen";
      else if (gueltigBis === cy) eichWarning = "laeuft_ab";
      else if (gueltigBis === cy + 1) eichWarning = "bald";
    }

    const eichColor = eichWarning === "abgelaufen" ? COLORS.danger : eichWarning === "laeuft_ab" ? "#E67E22" : eichWarning === "bald" ? "#D4AC0D" : null;

    return (
      <div style={isEich ? { maxWidth: "70%" } : {}}>
        <div style={{ fontSize: 10, fontWeight: 700, color: eichColor || COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
          {label}
          {eichWarning === "abgelaufen" && <span title="Eichung abgelaufen!">⚠️</span>}
          {eichWarning === "laeuft_ab" && <span title="Eichung läuft dieses Jahr ab!">⚠️</span>}
          {eichWarning === "bald" && <span title="Eichung läuft nächstes Jahr ab">⏰</span>}
        </div>
        {isAnfang ? (
          <div style={{ padding: "6px 10px", borderRadius: 7, background: "rgba(0,0,0,0.04)", border: `1px solid rgba(0,0,0,0.08)`, fontSize: 13, fontWeight: val ? 600 : 400, color: val ? "#1C2B1A" : COLORS.textMuted, minHeight: 34, display: "flex", alignItems: "center" }}>
            {val || <span style={{ fontSize: 11 }}>Vorjahreswert</span>}
          </div>
        ) : isEich ? (
          <div style={{ display: "flex", alignItems: "center", gap: 0, background: eichColor ? `${eichColor}18` : "rgba(255,255,255,0.7)", border: `1.5px solid ${eichColor || "rgba(0,0,0,0.1)"}`, borderRadius: 7, overflow: "hidden", minHeight: 34 }}>
            <span style={{ padding: "6px 8px", fontSize: 13, fontWeight: 700, color: eichColor || "#1C2B1A", background: eichColor ? `${eichColor}22` : "rgba(0,0,0,0.05)", borderRight: `1px solid ${eichColor || "rgba(0,0,0,0.08)"}`, whiteSpace: "nowrap" }}>M</span>
            {isEditing ? (
              <input
                style={{ fontSize: 13, padding: "6px 8px", border: "none", borderRadius: 0, width: 50, minWidth: 0, height: 34, boxSizing: "border-box", fontFamily: "inherit", background: "transparent", color: "var(--text)", outline: "none" }}
                type="number" min="0" max="99" maxLength={2} autoFocus
                value={editVal}
                onChange={e => setEditVal(e.target.value.slice(0, 2))}
                onBlur={() => saveVal(itemId, typ, period, editVal)}
                onKeyDown={e => { if (e.key === "Enter") saveVal(itemId, typ, period, editVal); if (e.key === "Escape") setEditCell(null); }}
                placeholder="JJ"
              />
            ) : (
              <div
                style={{ padding: "6px 8px", fontSize: 13, fontWeight: val ? 700 : 400, color: eichColor || (val ? "#1C2B1A" : COLORS.textMuted), cursor: "pointer", flex: 1 }}
                onClick={() => { setEditCell({ itemId, typ, period }); setEditVal(val); }}
              >
                {val || <span style={{ fontSize: 11 }}>JJ</span>}
              </div>
            )}
          </div>
        ) : isEditing ? (
          <input
            style={{ width: "100%", boxSizing: "border-box", minHeight: 34, height: 34, padding: "6px 10px", borderRadius: 7, border: `1px solid ${COLORS.leaf}`, fontSize: 13, fontFamily: "inherit", background: "var(--inputBg)", color: "var(--text)", outline: "none" }}
            type={type === "number" ? "text" : type} inputMode={type === "number" ? "decimal" : undefined} autoFocus
            value={editVal}
            onChange={e => setEditVal(type === "number" ? sanitizeAblesewert(e.target.value) : e.target.value)}
            onBlur={() => saveVal(itemId, typ, period, editVal)}
            onKeyDown={e => { if (e.key === "Enter") saveVal(itemId, typ, period, editVal); if (e.key === "Escape") setEditCell(null); }}
          />
        ) : (
          <div
            style={{ padding: "6px 10px", borderRadius: 7, background: isEnde && val ? "rgba(45,90,39,0.12)" : "rgba(255,255,255,0.7)", border: `1px solid ${isEnde && val ? COLORS.leaf : "rgba(0,0,0,0.1)"}`, cursor: "pointer", fontSize: 13, fontWeight: isEnde && val ? 800 : (val ? 600 : 400), color: isEnde && val ? COLORS.leaf : (val ? "#1C2B1A" : COLORS.textMuted), minHeight: 34, display: "flex", alignItems: "center" }}
            onClick={() => { setEditCell({ itemId, typ, period }); setEditVal(val); }}
          >
            {val || <span style={{ fontSize: 11 }}>eintragen</span>}
          </div>
        )}
      </div>
    );
  }


  function checkEich(val, typ) {
    if (!val || val === "–") return null;
    const eichJahr = 2000 + parseInt(val);
    const gueltig = typ === "wasser" ? eichJahr + 6 : eichJahr + 16;
    const cur = new Date().getFullYear();
    if (gueltig < cur) return "abgelaufen";
    if (gueltig === cur) return "laeuft_ab";
    if (gueltig === cur + 1) return "bald";
    return null;
  }
  function eichColor(w) {
    if (w === "abgelaufen") return COLORS.danger;
    if (w === "laeuft_ab") return "#E67E22";
    if (w === "bald") return "#D4AC0D";
    return null;
  }
  function eichIcon(w) {
    if (w === "abgelaufen") return "⚠️";
    if (w === "laeuft_ab") return "⚠️";
    if (w === "bald") return "⏰";
    return "";
  }

  return (
    <div className="gv-page" style={S.page}>
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>{isDatenaufnahme ? "Datenaufnahme" : "Ablesungen"}</h1>
          <p style={S.subtitle}>Wasser- und Stromablesungen {selectedYear}</p>
        </div>
      </div>

      {/* Year tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: isDatenaufnahme ? 24 : 24 }}>
        {years.map(y => {
          const isCurrent = y === new Date().getFullYear();
          const isSelected = selectedYear === y;
          return (
            <button key={y} style={{
              ...S.filterBtn,
              ...(isSelected ? S.filterBtnActive : {}),
              fontSize: 15, padding: "8px 20px",
              ...(isCurrent && !isSelected ? { border: `2px solid ${COLORS.leaf}`, fontWeight: 700, color: COLORS.leaf } : {}),
              ...(isCurrent && isSelected ? { background: COLORS.leaf, color: "#fff", border: `2px solid ${COLORS.leaf}`, fontWeight: 700, boxShadow: `0 0 0 3px ${COLORS.leaf}44`, borderRadius: 12 } : {}),
            }} onClick={() => setSelectedYear(y)}>
              {y}
            </button>
          );
        })}
      </div>

      {/* Datenaufnahme: Garten buttons */}
      {isDatenaufnahme && (
        <>
          {/* Modal for selected garden */}
          {selectedGartenModal && (
            <div style={S.overlay} onClick={() => setSelectedGartenModal(null)}>
              <div style={{ ...S.modal, width: 800, maxWidth: "calc(100vw - 32px)", boxSizing: "border-box", maxHeight: "calc(100vh - 60px)", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: COLORS.text }}>{selectedGartenModal.garten}</h3>
                    {selectedGartenModal.isMember ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                        <div style={S.avatar}>{selectedGartenModal.vorname?.[0]}{selectedGartenModal.nachname?.[0]}</div>
                        <span style={{ fontSize: 13, color: COLORS.textMuted, fontWeight: 600 }}>{selectedGartenModal.vorname} {selectedGartenModal.nachname}</span>
                      </div>
                    ) : (
                      <p style={{ margin: "4px 0 0", fontSize: 13, color: COLORS.textMuted }}>Kein Mitglied zugeordnet</p>
                    )}
                  </div>
                  <button style={{ ...S.btnGhost, fontSize: 20, padding: "4px 12px" }} onClick={() => setSelectedGartenModal(null)}>✕</button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  {["wasser", "strom"].map(typ => {
                    const isWasser = typ === "wasser";
                    const color    = isWasser ? "#2E86C1" : "#D4AC0D";
                    const bg       = isWasser ? "var(--leafPale)" : "var(--cream)";
                    const border   = isWasser ? "#AED6F1" : "#F9E79F";
                    const unit     = isWasser ? "m³" : "kWh";
                    const inaktiv  = ablesungen[`${selectedGartenModal.id}__${typ}__${selectedYear}__inaktiv`] === "1";
                    const hasWechsel = !!ablesungen[`${selectedGartenModal.id}__${typ}__${selectedYear}__ZaehlernummerB`];
                    const altNr    = ablesungen[`${selectedGartenModal.id}__${typ}__${selectedYear}__Zaehlernummer`] || "–";
                    const neuNr    = ablesungen[`${selectedGartenModal.id}__${typ}__${selectedYear}__ZaehlernummerB`] || "–";
                    const wechselAt = ablesungen[`${selectedGartenModal.id}__${typ}__${selectedYear}__WechseldatumB`] || "";

                    return (
                      <div key={typ} style={{ background: inaktiv ? "#F0F0F0" : bg, border: `1.5px solid ${inaktiv ? "#CCC" : border}`, borderRadius: 12, padding: 16, opacity: inaktiv ? 0.7 : 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, textTransform: "uppercase", color: inaktiv ? COLORS.textMuted : color }}>
                            {isWasser ? "💧 Wasserzähler" : "⚡ Stromzähler"}
                          </h4>
                          {inaktiv && <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: "#C0392B", borderRadius: 5, padding: "2px 7px" }}>INAKTIV</span>}
                          {hasWechsel && <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: color, borderRadius: 5, padding: "2px 7px" }}>🔄 Zählerwechsel</span>}
                        </div>

                        {hasWechsel ? (
                          /* Zählerwechsel: Alter Zähler als Info, neuer Zähler für Eingabe */
                          <div style={{ display: "flex", flexDirection: "column", gap: 10, ...(inaktiv ? { pointerEvents: "none" } : {}) }}>
                            {/* Alter Zähler - nur Info */}
                            <div style={{ background: "rgba(0,0,0,0.05)", borderRadius: 8, padding: "10px 12px", border: `1px dashed ${border}` }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, marginBottom: 6, textTransform: "uppercase" }}>Alter Zähler (bis {wechselAt ? new Date(wechselAt+"T12:00:00").toLocaleDateString("de-DE") : "Wechsel"})</div>
                              <div style={{ display: "flex", gap: 16, fontSize: 12, color: COLORS.textMuted }}>
                                <span>Nr.: <strong>{altNr}</strong></span>
                                <span>Ablesewert bei Wechsel: <strong>{ablesungen[`${selectedGartenModal.id}__${typ}__${selectedYear}__Ende`] || "–"} {unit}</strong></span>
                              </div>
                            </div>
                            {/* Neuer Zähler - Eingabe */}
                            <div style={{ fontSize: 11, fontWeight: 700, color: color, textTransform: "uppercase", marginTop: 4 }}>Neuer Zähler (Nr. {neuNr})</div>
                            <EditField itemId={selectedGartenModal.id} typ={typ} period="Zaehlernummer" type="text" label="Neue Zählernummer" />
                            <EditField itemId={selectedGartenModal.id} typ={typ} period="Eichdatum" type="text" label="Eichdatum (M YY)" />
                            <EditField itemId={selectedGartenModal.id} typ={typ} period="EndeB" label={`Ablesewert Ende Jahr (${unit})`} />
                            <FotoAnzeige gartenName={selectedGartenModal.garten} gartenId={selectedGartenModal.id} jahr={selectedYear} nur={typ} />
                          </div>
                        ) : (
                          /* Normaler Zähler */
                          <div style={{ display: "flex", flexDirection: "column", gap: 12, ...(inaktiv ? { pointerEvents: "none" } : {}) }}>
                            <EditField itemId={selectedGartenModal.id} typ={typ} period="Zaehlernummer" type="text" />
                            <EditField itemId={selectedGartenModal.id} typ={typ} period="Eichdatum" type="text" />
                            <EditField itemId={selectedGartenModal.id} typ={typ} period="Anfang" />
                            <EditField itemId={selectedGartenModal.id} typ={typ} period="Ende" />
                            <FotoAnzeige gartenName={selectedGartenModal.garten} gartenId={selectedGartenModal.id} jahr={selectedYear} nur={typ} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
                  <button style={S.btnPrimary} onClick={() => setSelectedGartenModal(null)}>Fertig</button>
                </div>
              </div>
            </div>
          )}

          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 32 }}>
              GARTEN AUSWÄHLEN ({allGartenItems.length} GÄRTEN)
            </div>
            
            {/* Group gardens by category */}
            {["Unterer Weg", "Mittlerer Weg", "Oberer Weg"].map((categoryName, catIdx) => {
              const categoryColors = [
                { bg: "var(--leafPale)", border: "#4A8C42", text: "#2D5A27" }, // Unterer Weg - grün
                { bg: "var(--leafPale)", border: "#4A8C42", text: "#2D5A27" }, // Mittlerer Weg - grün
                { bg: "var(--leafPale)", border: "#4A8C42", text: "#2D5A27" }, // Oberer Weg - grün
              ];
              const colors = categoryColors[catIdx];
              
              const gardens = allGartenItems
                .filter(g => g.category === categoryName)
                .sort((a, b) => (parseInt((a.garten||"").replace(/\D/g,""))||0) - (parseInt((b.garten||"").replace(/\D/g,""))||0));
              
              if (gardens.length === 0) return null;
              
              return (
                <div key={categoryName} style={{ marginBottom: 30, padding: "24px 12px 12px 12px", border: `2px solid ${colors.border}`, borderRadius: 12, position: "relative" }}>
                  <div style={{ 
                    position: "absolute",
                    top: -14,
                    left: 12,
                    padding: "4px 14px",
                    background: "var(--cardBg)",
                    borderRadius: 8,
                    border: `2px solid ${colors.border}`
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, letterSpacing: 0.3 }}>
                      {categoryName} ({gardens.length})
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: "grid", 
                    gridTemplateColumns: "repeat(auto-fill, minmax(85px, 1fr))", 
                    gap: 8 
                  }}>
                    {gardens.map(m => {
                      const hasWasser    = !!getVal(m.id, "wasser", "Ende");
                      const hasStrom     = !!getVal(m.id, "strom",  "Ende");
                      const wasserInaktiv = ablesungen[`${m.id}__wasser__${selectedYear}__inaktiv`] === "1";
                      const stromInaktiv  = ablesungen[`${m.id}__strom__${selectedYear}__inaktiv`]  === "1";

                      const beideInaktiv  = wasserInaktiv && stromInaktiv;
                      const beideErledigt = (hasWasser || wasserInaktiv) && (hasStrom || stromInaktiv);

                      const btnBg = beideInaktiv  ? "var(--border)"
                                  : beideErledigt ? colors.bg
                                  : "var(--cardBg)";
                      const btnBorder = beideErledigt && !beideInaktiv ? colors.border : COLORS.border;

                      return (
                        <button key={m.id}
                          style={{ 
                            padding: "10px 6px 6px 6px",
                            borderRadius: 12,
                            border: `2px solid ${btnBorder}`,
                            background: btnBg,
                            color: COLORS.text,
                            fontWeight: 600,
                            fontSize: 14,
                            cursor: "pointer",
                            fontFamily: "inherit",
                            transition: "all 0.15s",
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 3
                          }}
                          onClick={() => setSelectedGartenModal(m)}
                        >
                          <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "center" }}>
                            <span style={{ fontSize: 14 }}>🏡</span>
                            <span>{(m.garten||"").replace(/Garten /i, "") || m.garten}</span>
                          </div>
                          {(hasWasser || wasserInaktiv || hasStrom || stromInaktiv) && (
                            <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "center" }}>
                              {(hasWasser || wasserInaktiv) && (
                                <div style={{ width: 11, height: 11, borderRadius: "50%", background: wasserInaktiv ? "#bbb" : "#2E86C1", border: wasserInaktiv ? "1.5px solid #C0392B" : "1.5px solid #AED6F1" }} />
                              )}
                              {(hasStrom || stromInaktiv) && (
                                <div style={{ width: 11, height: 11, borderRadius: "50%", background: stromInaktiv ? "#bbb" : "#E67E22", border: stromInaktiv ? "1.5px solid #C0392B" : "1.5px solid #F0A500" }} />
                              )}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Hauptzähler */}
          {(() => {
            const hauptAbl = (() => { try { return JSON.parse(localStorage.getItem("gv_hauptzaehler") || "{}"); } catch(e) { return {}; } })();
            
            const getHz = (key) => hauptAbl[`${key}__${selectedYear}`] || "";
            const setHz = (key, val) => {
              const updated = { ...hauptAbl, [`${key}__${selectedYear}`]: val };
              localStorage.setItem("gv_hauptzaehler", JSON.stringify(updated));
            };

            const hauptzaehler = [
              { id: "wasser_haupt",        label: "Hauptwasserzähler",         icon: "💧", color: "#2E86C1", bg: "var(--leafPale)", border: "#AED6F1", global: true },
              { id: "strom_unterer_weg",   label: "Stromzähler Unterer Weg",   icon: "⚡", color: "#D4AC0D", bg: "var(--cream)", border: "#F9E79F" },
              { id: "strom_mittlerer_weg", label: "Stromzähler Mittlerer Weg", icon: "⚡", color: "#D4AC0D", bg: "var(--cream)", border: "#F9E79F" },
              { id: "strom_oberer_weg",    label: "Stromzähler Oberer Weg",    icon: "⚡", color: "#D4AC0D", bg: "var(--cream)", border: "#F9E79F" },
            ];

            return (
              <div style={{ marginTop: 8, padding: "32px 16px 16px 16px", border: `2px solid ${COLORS.leaf}`, borderRadius: 12, position: "relative" }}>
                <div style={{ position: "absolute", top: -13, left: 12, padding: "4px 14px", background: "var(--cardBg)", borderRadius: 8, border: `2px solid ${COLORS.leaf}` }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.leaf, letterSpacing: 0.3 }}>Hauptzähler</div>
                </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 28, marginTop: 8 }}>

                {/* Haupt-Wasserzähler */}
                <div style={{ border: `2px solid #AED6F1`, borderRadius: 12, background: "var(--leafPale)", overflow: "hidden" }}>
                  <div style={{ background: "#AED6F1", padding: "8px 16px" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1A5276" }}>💧 Haupt-Wasserzähler</div>
                  </div>
                  <div style={{ padding: "16px 20px" }}>
                  {(() => {
                    const hz = hauptzaehler[0];
                    const nr = getHz(`${hz.id}_nr`);
                    const ende = getHz(`${hz.id}_ende`);
                    const vorjahr = hauptAbl[`${hz.id}_ende__${selectedYear - 1}`] || "";
                    const verbrauch = vorjahr && ende ? Math.max(0, parseFloat(normAbl(ende)) - parseFloat(normAbl(vorjahr))).toFixed(2) + " m³" : "—";
                    return (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", columnGap: 24, rowGap: 16 }}>
                        <div>
                          <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 4 }}>Zählernummer</label>
                          <input style={{ ...S.input, fontSize: 13, width: "90%" }} value={nr} onChange={e => setHz(`${hz.id}_nr`, e.target.value)} placeholder="—" />
                        </div>
                        <div>
                          <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 4 }}>Vorjahr</label>
                          <input style={{ ...S.input, fontSize: 13, width: "90%", background: "var(--tableStripe)", color: COLORS.textMuted }} value={vorjahr || "—"} readOnly />
                        </div>
                        <div>
                          <label style={{ fontSize: 10, fontWeight: 700, color: "#2E86C1", textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 4 }}>Ablesewert</label>
                          <input type="text" inputMode="decimal" style={{ ...S.input, fontSize: 13, width: "90%" }} value={ende} onChange={e => setHz(`${hz.id}_ende`, sanitizeAblesewert(e.target.value))} placeholder="Wert eingeben" />
                        </div>
                        <div>
                          <label style={{ fontSize: 10, fontWeight: 700, color: "#2E86C1", textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 4 }}>Verbrauch</label>
                          <input style={{ ...S.input, fontSize: 13, width: "90%", background: vorjahr && ende ? "#2E86C1" : "#f0f0f0", color: vorjahr && ende ? "#fff" : COLORS.textMuted, fontWeight: 700 }} value={verbrauch} readOnly />
                        </div>
                      </div>
                    );
                  })()}
                  </div>
                </div>

                {/* Haupt-Stromzähler - jeder Weg einzeln wie Wasserzähler */}
                {hauptzaehler.slice(1).map(hz => {
                  const nr = getHz(`${hz.id}_nr`);
                  const ende = getHz(`${hz.id}_ende`);
                  const vorjahr = hauptAbl[`${hz.id}_ende__${selectedYear - 1}`] || "";
                  const verbrauch = vorjahr && ende ? Math.max(0, parseFloat(normAbl(ende)) - parseFloat(normAbl(vorjahr))).toFixed(2) + " kWh" : "—";
                  return (
                    <div key={hz.id} style={{ border: `2px solid #F9E79F`, borderRadius: 12, background: "var(--cream)", overflow: "hidden" }}>
                      <div style={{ background: "#F9E79F", padding: "8px 16px" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#7D6608" }}>⚡ {hz.label}</div>
                      </div>
                      <div style={{ padding: "16px 20px" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", columnGap: 24, rowGap: 16 }}>
                        <div>
                          <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 4 }}>Zählernummer</label>
                          <input style={{ ...S.input, fontSize: 13, width: "90%" }} value={nr} onChange={e => setHz(`${hz.id}_nr`, e.target.value)} placeholder="—" />
                        </div>
                        <div>
                          <label style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 4 }}>Vorjahr</label>
                          <input style={{ ...S.input, fontSize: 13, width: "90%", background: "var(--tableStripe)", color: COLORS.textMuted }} value={vorjahr || "—"} readOnly />
                        </div>
                        <div>
                          <label style={{ fontSize: 10, fontWeight: 700, color: "#D4AC0D", textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 4 }}>Ablesewert</label>
                          <input type="text" inputMode="decimal" style={{ ...S.input, fontSize: 13, width: "90%" }} value={ende} onChange={e => setHz(`${hz.id}_ende`, sanitizeAblesewert(e.target.value))} placeholder="Wert eingeben" />
                        </div>
                        <div>
                          <label style={{ fontSize: 10, fontWeight: 700, color: "#D4AC0D", textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 4 }}>Verbrauch</label>
                          <input style={{ ...S.input, fontSize: 13, width: "90%", background: vorjahr && ende ? "#D4AC0D" : "#f0f0f0", color: vorjahr && ende ? "#fff" : COLORS.textMuted, fontWeight: 700 }} value={verbrauch} readOnly />
                        </div>
                      </div>
                      </div>
                    </div>
                  );
                })}

              </div>
              </div>
            );
          })()}
        </>
      )}


      {!isDatenaufnahme && (
        filteredMembers.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px", color: COLORS.textMuted }}>
            <span style={{ fontSize: 40 }}>📋</span>
            <p>Keine Mitglieder vorhanden</p>
          </div>
        ) : (
          <AblesungenTabelle
            filteredMembers={filteredMembers}
            ablesungen={ablesungen}
            selectedYear={selectedYear}
            gaerten={gaerten}
            members={members}
            onGartenClick={setSelectedGartenModal}
          />
        )
      )}
    </div>
  );
}


// ─── Finanzen (Budget) ────────────────────────────────────────────────────────
const FINANZ_TYPEN = ["Einnahme", "Ausgabe"];
const FINANZ_KAT = ["Mitgliedsbeitrag", "Pacht", "Spende", "Veranstaltung", "Wasser", "Strom", "Versicherung", "Instandhaltung", "Rücklage", "Sonstiges"];

function loadFinanzen() {
  try { return JSON.parse(localStorage.getItem("gv_finanzen") || "[]"); } catch(e) { return []; }
}
function saveFinanzen(d) { localStorage.setItem("gv_finanzen", JSON.stringify(d)); }
function loadImportHistory() { try { return JSON.parse(localStorage.getItem("gv_csv_history") || "[]"); } catch(e) { return []; } }
function saveImportHistory(d) { localStorage.setItem("gv_csv_history", JSON.stringify(d)); }
const MONATE_NAMEN = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

function loadKontoauszuege() {
  try { return JSON.parse(localStorage.getItem("gv_kontoauszuege") || "{}"); } catch(e) { return {}; }
}
function saveKontoauszuege(d) { localStorage.setItem("gv_kontoauszuege", JSON.stringify(d)); }

function Finanzen() {
  const [eintraege, setEintraege] = useState(loadFinanzen);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ typ: "Einnahme", betrag: "", kategorie: "Mitgliedsbeitrag", beschreibung: "", datum: new Date().toISOString().slice(0,10) });
  const [editId, setEditId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [filter, setFilter] = useState("Alle");
  const [csvModal, setCsvModal] = useState(false);
  const [importHistory, setImportHistory] = useState(loadImportHistory);
  const [showHistory, setShowHistory] = useState(false);
  const [confirmClearHistory, setConfirmClearHistory] = useState(false);
  const [csvInfo, setCsvInfo] = useState(null);
  const csvFileRef = useRef();
  const [kontostand, setKontostand] = useState(() => localStorage.getItem("gv_kontostand") || "");
  const [bargeld, setBargeld]     = useState(() => localStorage.getItem("gv_bargeld") || "");
  const [rücklageModal, setRücklageModal] = useState(false);
  const [bargeldModal, setBargeldModal] = useState(false);
  const [bargeldEintraege, setBargeldEintraege] = useState(() => {
    try { return JSON.parse(localStorage.getItem("gv_bargeld_eintraege") || "[]"); } catch(e) { return []; }
  });
  const [bargeldForm, setBargeldForm] = useState({ typ: "Einnahme", betrag: "", beschreibung: "", datum: new Date().toISOString().slice(0,10) });

  function saveBargeldEintraege(data) {
    setBargeldEintraege(data);
    localStorage.setItem("gv_bargeld_eintraege", JSON.stringify(data));
    // Saldo neu berechnen
    const saldo = data.reduce((s, e) => s + (e.typ === "Einnahme" ? 1 : -1) * (parseFloat(e.betrag) || 0), 0);
    saveBargeld(saldo.toFixed(2));
  }

  function saveKonto(val) { setKontostand(val); localStorage.setItem("gv_kontostand", val); }
  function saveBargeld(val) { setBargeld(val); localStorage.setItem("gv_bargeld", val); }

  // Rücklage Medien-Versorgung berechnen
  // Einnahmen: alle Jahresrechnungs-Positionen "Rücklage Medien-Versorgung" (aus gv_members)
  // Ausgaben: alle Finanzbuchungen mit Kategorie "Rücklage"
  const ruecklageStats = React.useMemo(() => {
    const stored = (() => { try { return JSON.parse(localStorage.getItem("gv_members") || "[]"); } catch(e) { return []; } })();
    const geb    = (() => { try { return JSON.parse(localStorage.getItem("gv_gebuehren") || "{}"); } catch(e) { return {}; } })();
    const fin    = (() => { try { return JSON.parse(localStorage.getItem("gv_finanzen") || "[]"); } catch(e) { return []; } })();

    // Einnahmen pro Jahr aus Jahresrechnungen
    const einByYear = {};
    stored.forEach(m => {
      if (!m.abr_ruecklage_medien) return;
      (m.rechnungen||[]).filter(r => r.name && r.rechnungsnummer).forEach(r => {
        const yr = String(r.jahr||"");
        const gebJ = geb[r.jahr] || {};
        const betrag = parseFloat((gebJ.ruecklage_medien || "10.00").replace(",",".")) || 10.00;
        if (!einByYear[yr]) einByYear[yr] = { summe: 0, anzahl: 0, bezahlt: 0, summeBezahlt: 0 };
        einByYear[yr].anzahl++;
        einByYear[yr].summe += betrag;
        if (r.bezahlt) { einByYear[yr].bezahlt++; einByYear[yr].summeBezahlt += betrag; }
      });
    });

    // Ausgaben aus Finanzen (Kategorie "Rücklage")
    const ausgaben = fin.filter(e => (e.kategorie||"").toLowerCase().includes("rücklage") || (e.kategorie||"").toLowerCase().includes("rucklage"));
    const ausgabenSumme = ausgaben.reduce((s,e) => s + (parseFloat((e.betrag||"0").replace(",","."))||0), 0);
    const ausgabenByYear = {};
    ausgaben.forEach(e => {
      const yr = e.datum ? String(new Date(e.datum).getFullYear()) : "–";
      if (!ausgabenByYear[yr]) ausgabenByYear[yr] = [];
      ausgabenByYear[yr].push(e);
    });

    const gesamtEin  = Object.values(einByYear).reduce((s,v) => s + v.summeBezahlt, 0);
    const grundbetrag = parseFloat(localStorage.getItem("gv_ruecklage_grundbetrag") || "0") || 0;
    const saldo = gesamtEin + grundbetrag - ausgabenSumme;

    return { einByYear, ausgaben, ausgabenSumme, ausgabenByYear, gesamtEin, grundbetrag, saldo };
  }, [eintraege]);

  function set(k, v) { setForm(f => ({...f, [k]: v})); }

  function openNew() {
    setForm({ typ: "Einnahme", betrag: "", kategorie: "Mitgliedsbeitrag", beschreibung: "", datum: new Date().toISOString().slice(0,10) });
    setEditId(null);
    setModal(true);
  }

  function openEdit(e) {
    setForm({...e});
    setEditId(e.id);
    setModal(true);
  }

  function save() {
    if (!form.betrag) return;
    let updated;
    if (editId) {
      updated = eintraege.map(e => e.id === editId ? {...form, id: editId} : e);
    } else {
      updated = [{...form, id: "f" + Date.now()}, ...eintraege];
    }
    updated.sort((a,b) => b.datum.localeCompare(a.datum));
    saveFinanzen(updated);
    setEintraege(updated);
    setModal(false);
  }

  function del(id) {
    const updated = eintraege.filter(e => e.id !== id);
    saveFinanzen(updated);
    setEintraege(updated);
    setConfirmDel(null);
  }

  const filtered = filter === "Alle" ? eintraege
    : filter === "Einnahmen" ? eintraege.filter(e => parseFloat(e.betrag||0) >= 0)
    : eintraege.filter(e => parseFloat(e.betrag||0) < 0);
  const einnahmen = eintraege.filter(e => parseFloat(e.betrag||0) >= 0).reduce((s, e) => s + parseFloat(e.betrag || 0), 0);
  const ausgaben  = eintraege.filter(e => parseFloat(e.betrag||0) < 0).reduce((s, e) => s + Math.abs(parseFloat(e.betrag || 0)), 0);
  // Use the most recent entry's "Saldo nach Buchung" as the current balance
  const letzterSaldo = eintraege.length > 0 && eintraege[0].saldo ? parseFloat(eintraege[0].saldo) : null;
  const saldo = letzterSaldo !== null ? letzterSaldo : einnahmen - ausgaben;

  return (
    <div className="gv-page" style={S.page}>
      {confirmDel && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <h3 style={{ color: COLORS.danger, marginBottom: 8 }}>Eintrag löschen</h3>
            <p style={{ color: COLORS.textMuted, marginBottom: 24 }}>Soll dieser Eintrag wirklich gelöscht werden?</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button style={S.btnGhost} onClick={() => setConfirmDel(null)}>Abbrechen</button>
              <button style={S.btnDanger} onClick={() => del(confirmDel)}>Löschen</button>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <div style={S.overlay}>
          <div style={{...S.modal, width: 460}}>
            <h3 style={{ margin: "0 0 20px", color: COLORS.leaf, fontSize: 18 }}>{editId ? "Eintrag bearbeiten" : "Neuer Eintrag"}</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={FS.label}>Typ</label>
                  <select style={S.input} value={form.typ} onChange={e => set("typ", e.target.value)}>
                    {FINANZ_TYPEN.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={FS.label}>Betrag (€)</label>
                  <input style={S.input} type="number" min="0" step="0.01" value={form.betrag} onChange={e => set("betrag", e.target.value)} placeholder="0,00" autoFocus />
                </div>
              </div>
              <div>
                <label style={FS.label}>Kategorie</label>
                <select style={S.input} value={form.kategorie} onChange={e => set("kategorie", e.target.value)}>
                  {FINANZ_KAT.map(k => <option key={k}>{k}</option>)}
                </select>
              </div>
              <div>
                <label style={FS.label}>Datum</label>
                <input type="date" style={S.input} value={form.datum} onChange={e => set("datum", e.target.value)} />
              </div>
              <div>
                <label style={FS.label}>Beschreibung</label>
                <input style={S.input} value={form.beschreibung} onChange={e => set("beschreibung", e.target.value)} placeholder="Optionale Beschreibung…" />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
              {editId && <button style={{ ...S.btnDanger, marginRight: "auto" }} onClick={() => { setConfirmDel(editId); setModal(false); }}>Löschen</button>}
              <button style={S.btnGhost} onClick={() => setModal(false)}>Abbrechen</button>
              <button style={S.btnPrimary} onClick={save}>💾 Speichern</button>
            </div>
          </div>
        </div>
      )}

      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>Finanzen</h1>
          <p style={S.subtitle}>{eintraege.length} Einträge gesamt</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button style={S.btnGhost} onClick={() => setShowHistory(true)}>📋 Import-Verlauf {importHistory.length > 0 && <span style={{ background: COLORS.leaf, color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 11, marginLeft: 4 }}>{importHistory.length}</span>}</button>
          <button style={S.btnGhost} onClick={() => setCsvModal(true)}>📥 CSV importieren</button>
          <button style={S.btnPrimary} onClick={openNew}>+ Neuer Eintrag</button>
        </div>
      </div>

      {/* Import-Verlauf Modal */}
      {showHistory && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width: 580 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: COLORS.leaf, fontSize: 18 }}>📋 Import-Verlauf</h3>
              <button style={S.btnGhost} onClick={() => setShowHistory(false)}>✕</button>
            </div>
            {importHistory.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: COLORS.textMuted }}>
                <span style={{ fontSize: 40 }}>📂</span>
                <p style={{ margin: "10px 0 0" }}>Noch keine CSV-Dateien importiert</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 460, overflowY: "auto" }}>
                {importHistory.map(h => (
                  <div key={h.id} style={{ background: COLORS.leafPale, borderRadius: 10, padding: "14px 16px", border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 14 }}>
                    <span style={{ fontSize: 28, flexShrink: 0 }}>📊</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.fileName}</div>
                      <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 3 }}>
                        {new Date(h.datum).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} Uhr
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                      <span style={{ background: COLORS.leafPale, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "3px 10px", fontSize: 12, fontWeight: 600, color: COLORS.text }}>
                        {h.anzahl} importiert
                      </span>
                      {h.duplikate > 0 && (
                        <span style={{ background: "var(--cream)", borderRadius: 8, padding: "3px 8px", fontSize: 11, color: "#7D6608", fontWeight: 600 }}>
                          {h.duplikate} Duplikat{h.duplikate !== 1 ? "e" : ""}
                        </span>
                      )}
                      <span style={{ background: "var(--leafPale)", borderRadius: 8, padding: "3px 8px", fontSize: 11, color: COLORS.leaf, fontWeight: 600 }}>
                        +{h.einnahmen}
                      </span>
                      <span style={{ background: "var(--cream)", borderRadius: 8, padding: "3px 8px", fontSize: 11, color: COLORS.danger, fontWeight: 600 }}>
                        -{h.ausgaben}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {confirmClearHistory && (
              <div style={{ ...S.overlay, zIndex: 1100 }}>
                <div style={S.modal}>
                  <h3 style={{ color: COLORS.danger, marginBottom: 8 }}>Verlauf löschen</h3>
                  <p style={{ color: COLORS.textMuted, marginBottom: 24 }}>Soll der gesamte Import-Verlauf wirklich gelöscht werden?</p>
                  <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                    <button style={S.btnGhost} onClick={() => setConfirmClearHistory(false)}>Abbrechen</button>
                    <button style={S.btnDanger} onClick={() => { saveImportHistory([]); setImportHistory([]); setConfirmClearHistory(false); }}>Löschen</button>
                  </div>
                </div>
              </div>
            )}
            {importHistory.length > 0 && (
              <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, color: COLORS.textMuted }}>{importHistory.length} Import{importHistory.length !== 1 ? "e" : ""} gesamt</span>
                <button style={{ ...S.btnGhost, fontSize: 12, color: COLORS.danger, borderColor: COLORS.danger }}
                  onClick={() => setConfirmClearHistory(true)}>
                  🗑️ Verlauf löschen
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {csvModal && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width: 520 }}>
            <h3 style={{ margin: "0 0 8px", color: COLORS.leaf, fontSize: 18 }}>📥 CSV-Buchungen importieren</h3>
            <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
              Lade im Online-Banking der Skat Bank deine Umsätze als CSV-Datei herunter und importiere sie hier.
              Die Datei wird automatisch erkannt.
            </p>

            {!csvInfo ? (
              <div>
                <div style={{ border: `2px dashed ${COLORS.border}`, borderRadius: 12, padding: "32px 20px", textAlign: "center", cursor: "pointer", background: COLORS.leafPale }}
                  onClick={() => csvFileRef.current?.click()}>
                  <input ref={csvFileRef} type="file" accept=".csv,.txt" style={{ display: "none" }}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = ev => {
                        const text = ev.target.result;
                        const rows = text.split("\n").filter(r => r.trim());
                        const sep = ";";
                        // Remove BOM if present
                        const firstRow = rows[0].replace(/^\uFEFF/, "");
                        const headers = firstRow.split(sep).map(h => h.replace(/"/g, "").trim().toLowerCase());
                        
                        // Skatbank column mapping
                        const colIdx = name => headers.findIndex(h => h.includes(name));
                        const buchungstag = colIdx("buchungstag");
                        const valuta      = colIdx("valutadatum");
                        const name        = colIdx("name zahlungsbeteiligter");
                        const zweck       = colIdx("verwendungszweck");
                        const betrag      = colIdx("betrag");
                        const saldo       = colIdx("saldo nach buchung");

                        const parseDatum = s => {
                          if (!s) return "";
                          const p = s.trim().split(".");
                          if (p.length === 3) return p[2] + "-" + p[1] + "-" + p[0];
                          return s;
                        };

                        const dataRows = rows.slice(1).map(r => r.split(sep).map(v => v.replace(/"/g, "").trim()));
                        
                        const parsed = dataRows.filter(r => r.length > 3).map(r => {
                          const rawAmt = betrag >= 0 ? r[betrag] || "" : "";
                          const amt = parseFloat(rawAmt.replace(/[.]/g, "").replace(",", ".")) || 0;
                          const rawSaldo = saldo >= 0 ? r[saldo] || "" : "";
                          return {
                            datum:        parseDatum(buchungstag >= 0 ? r[buchungstag] : ""),
                            valuta:       parseDatum(valuta >= 0 ? r[valuta] : ""),
                            name:         name >= 0 ? r[name] : "",
                            beschreibung: zweck >= 0 ? r[zweck] : "",
                            betrag:       amt.toFixed(2),
                            saldo:        parseFloat(rawSaldo.replace(/[.]/g, "").replace(",", ".")).toFixed(2),
                            typ:          amt >= 0 ? "Einnahme" : "Ausgabe",
                            kategorie:    detectKategorie(name >= 0 ? r[name] : "", zweck >= 0 ? r[zweck] : ""),
                          };
                        }).filter(r => r.datum);

                        setCsvInfo({ rows: parsed, headers, sep, fileName: file.name, total: parsed.length });
                      };
                      reader.readAsText(file, "ISO-8859-1");
                    }}
                  />
                  <div style={{ fontSize: 40, marginBottom: 8 }}>📂</div>
                  <div style={{ fontWeight: 600, color: COLORS.leaf }}>CSV-Datei auswählen</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>oder hier hineinziehen</div>
                </div>
                <div style={{ marginTop: 16, padding: "12px 16px", background: "var(--cream)", borderRadius: 8, fontSize: 12, color: "#7D6608", lineHeight: 1.7 }}>
                  <strong>So exportierst du bei der Skat Bank:</strong><br/>
                  Online-Banking → Umsätze / Kontobewegungen → Export / Download → CSV oder Excel wählen → Datei speichern
                </div>
              </div>
            ) : (
              <div>
                <div style={{ padding: "12px 16px", background: COLORS.leafPale, borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                  <strong>✓ {csvInfo.fileName}</strong> — {csvInfo.total} Buchungen erkannt
                </div>
                <div style={{ maxHeight: 240, overflowY: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: COLORS.leafPale }}>
                        {["Buchungstag","Name","Verwendungszweck","Betrag","Saldo"].map(h => <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: COLORS.textMuted }}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {csvInfo.rows.slice(0, 20).map((r, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                          <td style={{ padding: "6px 12px", whiteSpace: "nowrap" }}>{r.datum ? new Date(r.datum+"T12:00:00").toLocaleDateString("de-DE") : r.datum}</td>
                          <td style={{ padding: "6px 12px", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</td>
                          <td style={{ padding: "6px 12px", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.beschreibung}</td>
                          <td style={{ padding: "6px 12px", fontWeight: 600, color: parseFloat(r.betrag) >= 0 ? COLORS.leaf : COLORS.danger, whiteSpace: "nowrap" }}>{parseFloat(r.betrag) >= 0 ? "+" : ""}{parseFloat(r.betrag).toFixed(2)} €</td>
                          <td style={{ padding: "6px 12px", whiteSpace: "nowrap", color: COLORS.textMuted }}>{r.saldo ? parseFloat(r.saldo).toFixed(2) + " €" : "–"}</td>
                        </tr>
                      ))}
                      {csvInfo.total > 20 && <tr><td colSpan={4} style={{ padding: "8px 12px", color: COLORS.textMuted, textAlign: "center" }}>… und {csvInfo.total - 20} weitere</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 16, fontSize: 12, color: COLORS.textMuted }}>
                  Alle Buchungen werden als neue Einträge importiert. Duplikate müssen manuell entfernt werden.
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
              {csvInfo && <button style={S.btnGhost} onClick={() => setCsvInfo(null)}>← Zurück</button>}
              <button style={S.btnGhost} onClick={() => { setCsvModal(false); setCsvInfo(null); }}>Abbrechen</button>
              {csvInfo && (
                <button style={S.btnPrimary} onClick={() => {
                  // Deduplicate: skip entries that already exist (same datum + betrag + name)
                  const existingKeys = new Set(eintraege.map(e => `${e.datum}|${e.betrag}|${e.name}`));
                  const uniqueEntries = csvInfo.rows.filter(r => !existingKeys.has(`${r.datum}|${r.betrag}|${r.name}`));
                  const skipped = csvInfo.rows.length - uniqueEntries.length;
                  const newEntries = uniqueEntries.map(r => ({ ...r, id: "csv" + Date.now() + Math.random() }));
                  const updated = [...newEntries, ...eintraege].sort((a,b) => b.datum.localeCompare(a.datum));
                  saveFinanzen(updated);
                  setEintraege(updated);
                  // Save to import history
                  const histEntry = { id: "h" + Date.now(), fileName: csvInfo.fileName, anzahl: newEntries.length, gesamt: csvInfo.total, duplikate: skipped, datum: new Date().toISOString(), einnahmen: uniqueEntries.filter(r => parseFloat(r.betrag||0) >= 0).length, ausgaben: uniqueEntries.filter(r => parseFloat(r.betrag||0) < 0).length };
                  const newHistory = [histEntry, ...importHistory];
                  saveImportHistory(newHistory);
                  setImportHistory(newHistory);
                  setCsvModal(false);
                  setCsvInfo(null);
                }}>✓ {(() => { const keys = new Set(eintraege.map(e => `${e.datum}|${e.betrag}|${e.name}`)); const dups = csvInfo.rows.filter(r => keys.has(`${r.datum}|${r.betrag}|${r.name}`)).length; const neu = csvInfo.total - dups; return dups > 0 ? `${neu} neue importieren (${dups} Duplikat${dups !== 1 ? "e" : ""} übersprungen)` : `${neu} Buchungen importieren`; })()}</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bargeldstand + Rücklage — zusammen so breit wie Einnahmen+Ausgaben+Saldo */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div style={{ background: parseFloat(bargeld||0) >= 0 ? "#f0f8ed" : "#FDEDEC", borderRadius: 12, padding: "12px 20px", border: `1.5px solid ${parseFloat(bargeld||0) >= 0 ? "#b8ddb0" : "#f5c6cb"}`, cursor: "pointer", textAlign: "center" }}
          onClick={() => setBargeldModal(true)}>
          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 4 }}>💵 Bargeldstand</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: parseFloat(bargeld||0) >= 0 ? COLORS.leaf : "#C0392B" }}>
            {parseFloat(bargeld || 0).toFixed(2).replace(".",",")} €
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 3, lineHeight: 1.5 }}>
            {bargeldEintraege.filter(e => e.typ === "Einnahme").reduce((s,e) => s+(parseFloat(e.betrag)||0), 0).toFixed(2).replace(".",",")} € Ein. ·{" "}
            {bargeldEintraege.filter(e => e.typ === "Ausgabe").reduce((s,e) => s+(parseFloat(e.betrag)||0), 0).toFixed(2).replace(".",",")} € Aus.
          </div>
          <div style={{ fontSize: 11, color: COLORS.leaf, marginTop: 1 }}>🔍 Details anzeigen</div>
        </div>

        {/* Rücklage Medien-Versorgung */}
        <div style={{ background: ruecklageStats.saldo >= 0 ? "#f0f8ed" : "#FDEDEC", borderRadius: 12, padding: "12px 20px", border: `1.5px solid ${ruecklageStats.saldo >= 0 ? "#b8ddb0" : "#f5c6cb"}`, cursor: "pointer", textAlign: "center" }}
          onClick={() => setRücklageModal(true)}>
          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 4 }}>📦 Rücklage Medien-Versorgung</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: ruecklageStats.saldo >= 0 ? "#2D5A27" : "#C0392B" }}>
            {ruecklageStats.saldo.toFixed(2).replace(".",",")} €
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 3, lineHeight: 1.6 }}>
            Eingegangen: {ruecklageStats.gesamtEin.toFixed(2).replace(".",",")} € · Ausgaben: {ruecklageStats.ausgabenSumme.toFixed(2).replace(".",",")} €
          </div>
          <div style={{ fontSize: 11, color: COLORS.leaf, marginTop: 2 }}>🔍 Details anzeigen</div>
        </div>
      </div>

      {/* Bargeld-Detail-Modal */}
      {bargeldModal && (
        <div style={{ ...S.overlay, zIndex: 2200 }}>
          <div style={{ ...S.modal, width: "min(96vw, 680px)", maxHeight: "90vh", overflow: "auto", padding: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 17, color: COLORS.leaf }}>💵 Bargeldkasse</h3>
              <button style={S.btnGhost} onClick={() => setBargeldModal(false)}>✕ Schließen</button>
            </div>

            {/* Saldo */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
              {[
                { label: "Einnahmen", value: bargeldEintraege.filter(e=>e.typ==="Einnahme").reduce((s,e)=>s+(parseFloat(e.betrag)||0),0), color:"#2D5A27", bg:"#f0f8ed" },
                { label: "Ausgaben",  value: bargeldEintraege.filter(e=>e.typ==="Ausgabe").reduce((s,e)=>s+(parseFloat(e.betrag)||0),0),  color:"#C0392B", bg:"#FDEDEC" },
                { label: "Kassenstand", value: parseFloat(bargeld||0), color: parseFloat(bargeld||0)>=0?"#2D5A27":"#C0392B", bg: parseFloat(bargeld||0)>=0?"#f0f8ed":"#FDEDEC" },
              ].map(k => (
                <div key={k.label} style={{ background: k.bg, borderRadius: 8, padding: "12px 16px", border: `1px solid ${k.color}33` }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>{k.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value.toFixed(2).replace(".",",")} €</div>
                </div>
              ))}
            </div>

            {/* Neue Buchung */}
            <div style={{ background: "var(--tableStripe)", borderRadius: 10, padding: "16px", marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>Neue Buchung</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={{ ...S.label }}>Typ</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    {["Einnahme","Ausgabe"].map(t => (
                      <button key={t} onClick={() => setBargeldForm(f => ({...f, typ: t}))}
                        style={{ flex: 1, padding: "7px", borderRadius: 7, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12, fontFamily: "inherit",
                          background: bargeldForm.typ === t ? (t==="Einnahme"?"#2D5A27":"#C0392B") : "var(--cardBg)",
                          color: bargeldForm.typ === t ? "#fff" : COLORS.textMuted }}>
                        {t === "Einnahme" ? "📈 Einnahme" : "📉 Ausgabe"}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ ...S.label }}>Datum</label>
                  <input type="date" style={{ ...S.input }} value={bargeldForm.datum} onChange={e => setBargeldForm(f=>({...f,datum:e.target.value}))} />
                </div>
                <div>
                  <label style={{ ...S.label }}>Betrag (€)</label>
                  <input type="number" step="0.01" placeholder="0,00" style={{ ...S.input }}
                    value={bargeldForm.betrag} onChange={e => setBargeldForm(f=>({...f,betrag:e.target.value}))} />
                </div>
                <div>
                  <label style={{ ...S.label }}>Beschreibung</label>
                  <input style={{ ...S.input }} placeholder="z.B. Vereinsfest" value={bargeldForm.beschreibung} onChange={e => setBargeldForm(f=>({...f,beschreibung:e.target.value}))} />
                </div>
              </div>
              <button style={{ ...S.btnPrimary, fontSize: 13 }}
                onClick={() => {
                  if (!bargeldForm.betrag) return;
                  const neu = [{ id: "b"+Date.now(), ...bargeldForm }, ...bargeldEintraege];
                  saveBargeldEintraege(neu);
                  setBargeldForm({ typ: "Einnahme", betrag: "", beschreibung: "", datum: new Date().toISOString().slice(0,10) });
                }}>
                ✅ Buchen
              </button>
            </div>

            {/* Liste */}
            {bargeldEintraege.length === 0 ? (
              <p style={{ textAlign:"center", color: COLORS.textMuted, fontSize: 13 }}>Noch keine Buchungen</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--cardBg)" }}>
                    <th style={S.th}>Datum</th>
                    <th style={S.th}>Typ</th>
                    <th style={S.th}>Beschreibung</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Betrag</th>
                    <th style={S.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {bargeldEintraege.sort((a,b)=>(b.datum||"").localeCompare(a.datum||"")).map(e => (
                    <tr key={e.id} style={S.tr}>
                      <td style={{ ...S.td, fontSize:12 }}>{e.datum ? e.datum.split("-").reverse().join(".") : "–"}</td>
                      <td style={S.td}>
                        <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:10,
                          background: e.typ==="Einnahme"?"#f0f8ed":"#FDEDEC", color: e.typ==="Einnahme"?"#2D5A27":"#C0392B" }}>
                          {e.typ}
                        </span>
                      </td>
                      <td style={{ ...S.td, fontSize:12, color: COLORS.textMuted }}>{e.beschreibung||"–"}</td>
                      <td style={{ ...S.td, textAlign:"right", fontWeight:700, color: e.typ==="Einnahme"?COLORS.leaf:"#C0392B" }}>
                        {e.typ==="Ausgabe"?"-":""}{(parseFloat(e.betrag)||0).toFixed(2).replace(".",",")} €
                      </td>
                      <td style={S.td}>
                        <button style={{ ...S.btnSmall, color:"#C0392B", fontSize:13 }}
                          onClick={() => saveBargeldEintraege(bargeldEintraege.filter(x => x.id !== e.id))}>🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Rücklage-Detail-Modal */}
      {rücklageModal && (
        <div style={{ ...S.overlay, zIndex: 2200 }}>
          <div style={{ ...S.modal, width: "min(96vw, 700px)", maxHeight: "90vh", overflow: "auto", padding: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 17, color: COLORS.leaf }}>📦 Rücklage Medien-Versorgung</h3>
              <button style={S.btnGhost} onClick={() => setRücklageModal(false)}>✕ Schließen</button>
            </div>

            {/* Saldo-Übersicht */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 24 }}>
              {[
                { label: "Gesamt eingegangen", value: ruecklageStats.gesamtEin, color: "#2D5A27", bg: "#f0f8ed" },
                ...(ruecklageStats.grundbetrag > 0 ? [{ label: "Grundbetrag (Korrektur)", value: ruecklageStats.grundbetrag, color: "#1A5276", bg: "#EBF5FB" }] : []),
                { label: "Ausgaben (Rücklage)", value: ruecklageStats.ausgabenSumme, color: "#C0392B", bg: "#FDEDEC" },
                { label: "Verfügbares Guthaben", value: ruecklageStats.saldo, color: ruecklageStats.saldo>=0?"#2D5A27":"#C0392B", bg: ruecklageStats.saldo>=0?"#f0f8ed":"#FDEDEC" },
              ].map(k => (
                <div key={k.label} style={{ background: k.bg, borderRadius: 8, padding: "12px 16px", border: `1px solid ${k.color}33` }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>{k.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value.toFixed(2).replace(".",",")} €</div>
                </div>
              ))}
            </div>

            {/* Jahresübersicht Einnahmen */}
            <h4 style={{ fontSize: 13, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>Einnahmen aus Jahresrechnungen</h4>
            {Object.entries(ruecklageStats.einByYear).length === 0 ? (
              <p style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 16 }}>Noch keine Rechnungen mit Rücklage-Position erstellt.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                {Object.entries(ruecklageStats.einByYear).sort((a,b)=>b[0]-a[0]).map(([yr, data]) => (
                  <div key={yr} style={{ background: "var(--cardBg)", borderRadius: 8, padding: "12px 16px", border: "1px solid var(--border)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: 14, color: COLORS.leaf }}>Jahr {yr}</span>
                        <span style={{ fontSize: 12, color: COLORS.textMuted, marginLeft: 12 }}>
                          Im Jahr {yr} wurden über {data.anzahl} Rechnung{data.anzahl!==1?"en":""} insgesamt <strong>{data.summe.toFixed(2).replace(".",",")} €</strong> als Rücklage verbucht
                          {data.bezahlt < data.anzahl ? ` (davon ${data.bezahlt} bezahlt: ${data.summeBezahlt.toFixed(2).replace(".",",")} €)` : " (alle bezahlt)"}
                        </span>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: "#2D5A27" }}>{data.summeBezahlt.toFixed(2).replace(".",",")} €</div>
                        {data.summe !== data.summeBezahlt && <div style={{ fontSize: 11, color: "#C0392B" }}>ausstehend: {(data.summe-data.summeBezahlt).toFixed(2).replace(".",",")} €</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Ausgaben */}
            <h4 style={{ fontSize: 13, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>Ausgaben (Kategorie "Rücklage")</h4>
            {ruecklageStats.ausgaben.length === 0 ? (
              <p style={{ fontSize: 13, color: COLORS.textMuted }}>Noch keine Ausgaben mit Kategorie "Rücklage" vorhanden.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--cardBg)" }}>
                    <th style={S.th}>Datum</th>
                    <th style={S.th}>Beschreibung</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  {ruecklageStats.ausgaben.sort((a,b)=>(b.datum||"").localeCompare(a.datum||"")).map((e,i) => (
                    <tr key={e.id||i} style={S.tr}>
                      <td style={{ ...S.td, fontSize: 12, whiteSpace: "nowrap" }}>{e.datum ? e.datum.split("-").reverse().join(".") : "–"}</td>
                      <td style={{ ...S.td, fontSize: 12 }}>{e.beschreibung||e.name||"–"}</td>
                      <td style={{ ...S.td, textAlign: "right", fontWeight: 700, color: "#C0392B" }}>{parseFloat((e.betrag||"0").replace(",","."))||0..toFixed(2).replace(".",",")} €</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#FDEDEC" }}>
                    <td colSpan={2} style={{ ...S.td, fontWeight: 700 }}>Gesamt Ausgaben</td>
                    <td style={{ ...S.td, textAlign: "right", fontWeight: 800, color: "#C0392B" }}>{ruecklageStats.ausgabenSumme.toFixed(2).replace(".",",")} €</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Übersicht */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
        {[
          { label: "Einnahmen", val: einnahmen, color: COLORS.leaf, bg: "var(--cardBg)" },
          { label: "Ausgaben",  val: ausgaben,  color: COLORS.danger, bg: "var(--cardBg)" },
          { label: "Saldo",     val: saldo,     color: saldo >= 0 ? COLORS.leaf : COLORS.danger, bg: "var(--cardBg)" },
        ].map(k => (
          <div key={k.label} style={{ background: k.bg, borderRadius: 12, padding: "12px 20px", border: `1.5px solid ${COLORS.border}`, textAlign: "center" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.val.toFixed(2)} €</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {["Alle", "Einnahmen", "Ausgaben"].map(f => (
          <button key={f} style={{ ...S.filterBtn, ...(filter === f ? S.filterBtnActive : {}) }} onClick={() => setFilter(f)}>{f}</button>
        ))}
      </div>

      {/* Tabelle */}
      <div style={{ ...S.tableWrap, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
          <thead>
            <tr>{["Buchungstag","Valutadatum","Name Zahlungsbeteiligter","Verwendungszweck","Betrag","Kategorie","Saldo nach Buchung",""].map(h => <th key={h} style={{ ...S.th, ...(h === "Saldo nach Buchung" ? { textAlign: "right" } : {}) }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: "48px", color: COLORS.textMuted }}>Keine Einträge vorhanden</td></tr>
            ) : filtered.map(e => (
              <tr key={e.id} style={S.tr} onClick={() => openEdit(e)}>
                <td style={{ ...S.td, whiteSpace: "nowrap" }}>{e.datum ? new Date(e.datum+"T12:00:00").toLocaleDateString("de-DE") : "–"}</td>
                <td style={{ ...S.td, whiteSpace: "nowrap", color: COLORS.textMuted }}>{e.valuta ? new Date(e.valuta+"T12:00:00").toLocaleDateString("de-DE") : "–"}</td>
                <td style={{ ...S.td, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.name || e.kategorie || "–"}</td>
                <td style={{ ...S.td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: COLORS.textMuted }}>{e.beschreibung || "–"}</td>
                <td style={{ ...S.td, fontWeight: 700, whiteSpace: "nowrap", color: parseFloat(e.betrag||0) >= 0 ? COLORS.leaf : COLORS.danger }}>
                  {parseFloat(e.betrag||0) >= 0 ? "+" : ""}{parseFloat(e.betrag||0).toFixed(2)} €
                </td>
                <td style={{ ...S.td, color: COLORS.textMuted }}>{e.kategorie || "–"}</td>
                <td style={{ ...S.td, whiteSpace: "nowrap", color: COLORS.textMuted, textAlign: "right" }}>
                  {e.saldo ? parseFloat(e.saldo).toFixed(2) + " €" : "–"}
                </td>
                <td style={S.td}>
                  <button style={{ background: "none", border: "none", color: COLORS.textMuted, cursor: "pointer", fontSize: 14 }} onClick={ev => { ev.stopPropagation(); setConfirmDel(e.id); }}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const FS = {
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, color: COLORS.textMuted, marginBottom: 4, display: "block" },
};


// ─── Kontoauszüge ────────────────────────────────────────────────────────────
function Kontoauszuege() {
  const currentYear = new Date().getFullYear();
  const [auszuege, setAuszuege] = useState(loadKontoauszuege);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [uploadModal, setUploadModal] = useState(null); // { year, month }
  const [viewModal, setViewModal] = useState(null); // { year, month, file }
  const [confirmDel, setConfirmDel] = useState(null);
  const fileRef = useRef();
  const [dragOver, setDragOver] = useState(null);

  // Years to show: current year ± 2
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  function getKey(year, month) {
    if (month === "jahresabschluss") return `${year}-jahresabschluss`;
    return `${year}-${String(month+1).padStart(2,"0")}`;
  }

  function getAuszug(year, month) {
    return auszuege[getKey(year, month)] || null;
  }

  function handleFile(file, year, month) {
    if (!file || !file.type.includes("pdf")) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const updated = { ...auszuege, [getKey(year, month)]: {
        name: file.name,
        size: (file.size / 1024).toFixed(0) + " KB",
        datum: new Date().toISOString().slice(0,10),
        data: ev.target.result, // base64 data URL
      }};
      saveKontoauszuege(updated);
      setAuszuege(updated);
      setUploadModal(null);
    };
    reader.readAsDataURL(file);
  }

  function deleteAuszug(year, month) {
    const updated = { ...auszuege };
    delete updated[getKey(year, month)];
    saveKontoauszuege(updated);
    setAuszuege(updated);
    setConfirmDel(null);
    setViewModal(null);
  }

  const yearCount = Object.keys(auszuege).filter(k => k.startsWith(selectedYear + "-")).length;

  return (
    <div className="gv-page" style={S.page}>
      {/* Upload modal */}
      {uploadModal && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width: 440 }}>
            <h3 style={{ margin: "0 0 6px", color: COLORS.leaf, fontSize: 18 }}>📄 Kontoauszug hinterlegen</h3>
            <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 20 }}>
              {uploadModal.month === "jahresabschluss" ? `Jahresabschluss ${uploadModal.year}` : `${MONATE_NAMEN[uploadModal.month]} ${uploadModal.year}`}
            </p>
            <div
              style={{ border: `2px dashed ${dragOver === "modal" ? COLORS.leaf : COLORS.border}`, borderRadius: 12, padding: "36px 20px", textAlign: "center", cursor: "pointer", background: dragOver === "modal" ? COLORS.leafPale : "#FAFAFA", transition: "all 0.15s" }}
              onClick={() => fileRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragOver("modal"); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={e => { e.preventDefault(); setDragOver(null); const f = e.dataTransfer.files?.[0]; if(f) handleFile(f, uploadModal.year, uploadModal.month); }}
            >
              <input ref={fileRef} type="file" accept=".pdf" style={{ display: "none" }}
                onChange={e => { const f = e.target.files?.[0]; if(f) handleFile(f, uploadModal.year, uploadModal.month); }} />
              <div style={{ fontSize: 44, marginBottom: 8 }}>📕</div>
              <div style={{ fontWeight: 600, color: COLORS.leaf, fontSize: 15 }}>PDF auswählen</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 6 }}>oder PDF hier hineinziehen</div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
              <button style={S.btnGhost} onClick={() => setUploadModal(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* View PDF modal */}
      {viewModal && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width: 600, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, color: COLORS.leaf }}>📕 {viewModal.month === "jahresabschluss" ? `Jahresabschluss ${viewModal.year}` : `${MONATE_NAMEN[viewModal.month]} ${viewModal.year}`}</h3>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: COLORS.textMuted }}>{viewModal.file.name} · {viewModal.file.size}</p>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <a href={viewModal.file.data} download={viewModal.file.name}
                  style={{ ...S.btnSmall, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
                  ⬇️ Download
                </a>
                <button style={S.btnDanger} onClick={() => { setViewModal(null); setTimeout(() => setConfirmDel({ year: viewModal.year, month: viewModal.month }), 50); }}>🗑️</button>
                <button style={S.btnGhost} onClick={() => setViewModal(null)}>✕</button>
              </div>
            </div>
            <iframe src={viewModal.file.data} style={{ flex: 1, minHeight: 480, border: "none", borderRadius: 8 }} title="Kontoauszug" />
          </div>
        </div>
      )}

      {/* Delete confirm – rendered last so it's always on top */}
      {confirmDel && (
        <div style={{ ...S.overlay, zIndex: 1000 }}>
          <div style={S.modal}>
            <h3 style={{ color: COLORS.danger, marginBottom: 8 }}>Kontoauszug löschen</h3>
            <p style={{ color: COLORS.textMuted, marginBottom: 24 }}>
              Soll der Kontoauszug für <strong>{confirmDel.month === "jahresabschluss" ? `Jahresabschluss ${confirmDel.year}` : `${MONATE_NAMEN[confirmDel.month]} ${confirmDel.year}`}</strong> wirklich gelöscht werden?
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button style={S.btnGhost} onClick={() => setConfirmDel(null)}>Abbrechen</button>
              <button style={S.btnDanger} onClick={() => deleteAuszug(confirmDel.year, confirmDel.month)}>Löschen</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>Kontoauszüge</h1>
          <p style={S.subtitle}>{yearCount} Auszug{yearCount !== 1 ? "e" : ""} für {selectedYear}</p>
        </div>
      </div>

      {/* Year tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {years.map(y => (
          <button key={y} style={{ ...S.filterBtn, ...(selectedYear === y ? S.filterBtnActive : {}), fontSize: 15, padding: "8px 20px" }}
            onClick={() => setSelectedYear(y)}>{y}</button>
        ))}
      </div>

      {/* Month grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>

        {/* Jahresabschluss Vorjahr */}
        {(() => {
          const vjKey = getKey(selectedYear - 1, "jahresabschluss");
          const vj = auszuege[vjKey] || null;
          return (
            <div
              style={{ background: "var(--cardBg)", borderRadius: 12, border: `1.5px solid ${vj ? COLORS.gold : COLORS.border}`, padding: 18, cursor: "pointer", position: "relative", gridColumn: "1" }}
              onClick={() => vj ? setViewModal({ year: selectedYear - 1, month: "jahresabschluss", file: vj }) : setUploadModal({ year: selectedYear - 1, month: "jahresabschluss" })}
              onDragOver={e => { e.preventDefault(); setDragOver(vjKey); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={e => { e.preventDefault(); setDragOver(null); const f = e.dataTransfer.files?.[0]; if(f) handleFile(f, selectedYear - 1, "jahresabschluss"); }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.text }}>Jahresabschluss</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>Vorjahr {selectedYear - 1}</div>
                </div>
                <div style={{ fontSize: 20 }}>{vj ? "📗" : "📋"}</div>
              </div>
              {vj ? (
                <div>
                  <div style={{ background: "var(--cream)", borderRadius: 8, padding: "8px 10px", border: `1px solid ${COLORS.gold}` }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#7D6608", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>✓ {vj.name}</div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{vj.size} · {vj.datum ? new Date(vj.datum+"T12:00:00").toLocaleDateString("de-DE") : ""}</div>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 8, textAlign: "center" }}>Klicken zum Öffnen</div>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>Kein Abschluss</div>
                  <div style={{ fontSize: 11, color: COLORS.gold, marginTop: 4, fontWeight: 600 }}>+ PDF hinterlegen</div>
                </div>
              )}
              {dragOver === vjKey && (
                <div style={{ position: "absolute", inset: 0, background: "rgba(200,168,75,0.1)", borderRadius: 12, border: `2px dashed ${COLORS.gold}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontWeight: 700, color: COLORS.gold }}>Loslassen</span>
                </div>
              )}
            </div>
          );
        })()}

        {MONATE_NAMEN.map((monat, idx) => {
          const auszug = getAuszug(selectedYear, idx);
          const isPast = new Date(selectedYear, idx + 1, 0) < new Date();
          return (
            <div key={idx}
              style={{ background: "var(--cardBg)", borderRadius: 12, border: `1.5px solid ${auszug ? COLORS.leafLight : COLORS.border}`, padding: 18, cursor: "pointer", transition: "all 0.15s", position: "relative" }}
              onClick={() => auszug ? setViewModal({ year: selectedYear, month: idx, file: auszug }) : setUploadModal({ year: selectedYear, month: idx })}
              onDragOver={e => { e.preventDefault(); setDragOver(idx); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={e => { e.preventDefault(); setDragOver(null); const f = e.dataTransfer.files?.[0]; if(f) handleFile(f, selectedYear, idx); }}
            >
              {/* Month header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.text }}>{monat}</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{selectedYear}</div>
                </div>
                <div style={{ fontSize: 20 }}>{auszug ? "📕" : isPast ? "📭" : "📅"}</div>
              </div>

              {auszug ? (
                <div>
                  <div style={{ background: COLORS.leafPale, borderRadius: 8, padding: "8px 10px" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.leaf, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>✓ {auszug.name}</div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{auszug.size} · {auszug.datum ? new Date(auszug.datum+"T12:00:00").toLocaleDateString("de-DE") : ""}</div>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 8, textAlign: "center" }}>Klicken zum Öffnen</div>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>Kein Auszug</div>
                  <div style={{ fontSize: 11, color: COLORS.leafLight, marginTop: 4, fontWeight: 600 }}>+ PDF hinterlegen</div>
                </div>
              )}

              {dragOver === idx && (
                <div style={{ position: "absolute", inset: 0, background: "rgba(45,90,39,0.1)", borderRadius: 12, border: `2px dashed ${COLORS.leaf}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontWeight: 700, color: COLORS.leaf }}>Loslassen</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}



function detectKategorie(name, beschreibung) {
  const n = (name || "").toLowerCase();
  const b = (beschreibung || "").toLowerCase();
  const combined = n + " " + b;
  if (combined.includes("energie") || combined.includes("energy") || combined.includes("sachsenenergie") || combined.includes("strom") || combined.includes("enso") || combined.includes("eprimo")) return "Strom";
  if (combined.includes("wasser") || combined.includes("wasserwerk") || combined.includes("zweckverband")) return "Wasser";
  if (combined.includes("ergo") || combined.includes("versicherung") || combined.includes("allianz") || combined.includes("huk") || combined.includes("axa") || combined.includes("zurich")) return "Versicherung";
  if (combined.includes("pacht") || combined.includes("miete")) return "Pacht";
  if (combined.includes("spende") || combined.includes("donation")) return "Spende";
  if (combined.includes("fest") || combined.includes("veranstaltung") || combined.includes("feier")) return "Veranstaltung";
  if (combined.includes("reparatur") || combined.includes("instandhaltung") || combined.includes("wartung") || combined.includes("baumarkt")) return "Instandhaltung";
  // Check if name matches a member (nachname)
  const members = JSON.parse(localStorage.getItem("gv_members") || "[]");
  const isMember = members.some(m => m.nachname && n.includes(m.nachname.toLowerCase()));
  if (isMember) return "Mitgliedsbeitrag";
  return "Sonstiges";
}

function FotoDraggableModal({ url, typ, gartenName, jahr, onDelete, onClose }) {
  const [pos,   setPos]   = useState({ x: window.innerWidth/2 - 200, y: window.innerHeight/2 - 250 });
  const [size,  setSize]  = useState({ w: 400, h: 500 });
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [start, setStart] = useState(null);
  const color = typ === "wasser" ? "#2E86C1" : "#D4AC0D";

  function onDragStart(e) {
    e.preventDefault();
    setDragging(true);
    setStart({ mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y });
  }
  function onResizeStart(e) {
    e.preventDefault(); e.stopPropagation();
    setResizing(true);
    setStart({ mx: e.clientX, my: e.clientY, w: size.w, h: size.h });
  }
  function onMove(e) {
    if (dragging && start) setPos({ x: start.px + e.clientX - start.mx, y: start.py + e.clientY - start.my });
    if (resizing && start) setSize({ w: Math.max(250, start.w + e.clientX - start.mx), h: Math.max(200, start.h + e.clientY - start.my) });
  }
  function onUp() { setDragging(false); setResizing(false); setStart(null); }

  React.useEffect(() => {
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [dragging, resizing, start]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9998, pointerEvents: "none" }}>
      <div style={{ position: "absolute", left: pos.x, top: pos.y, width: size.w, height: size.h, background: "var(--cardBg)", borderRadius: 12, boxShadow: "0 8px 40px rgba(0,0,0,0.6)", border: `2px solid ${color}`, display: "flex", flexDirection: "column", pointerEvents: "all", userSelect: "none" }}>
        <div onMouseDown={onDragStart}
          style={{ background: color, borderRadius: "10px 10px 0 0", padding: "8px 12px", cursor: dragging ? "grabbing" : "grab", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
            {typ === "wasser" ? "💧 Wasserzähler" : "⚡ Stromzähler"} — {gartenName} {jahr}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={onDelete} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", borderRadius: 5, padding: "3px 8px", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>🗑️</button>
            <button onClick={onClose}  style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", borderRadius: 5, padding: "3px 8px", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>✕</button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: "hidden", borderRadius: "0 0 10px 10px", position: "relative" }}>
          <img src={url} alt="Zähler" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
          <div onMouseDown={onResizeStart}
            style={{ position: "absolute", right: 0, bottom: 0, width: 22, height: 22, cursor: "se-resize", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.4)", borderRadius: "8px 0 8px 0", color: "#fff", fontSize: 13 }}>
            ↘
          </div>
        </div>
      </div>
    </div>
  );
}

function FotoAnzeige({ gartenName, gartenId, jahr, nur = null }) {
  const [fotos, setFotos]         = useState({ wasser: null, strom: null });
  const [loading, setLoading]     = useState(true);
  const [uploading, setUploading] = useState(null);
  const [fotoModal, setFotoModal] = useState(null);
  const wasserRef       = React.useRef();
  const stromRef        = React.useRef();
  const wasserKameraRef = React.useRef();
  const stromKameraRef  = React.useRef();
  const safeGarten = (gartenName || "").replace(/\s+/g, "_");
  const isMobile = useMobile();

  const typen = nur ? [nur] : ["wasser", "strom"];

  function ladeFotos() {
    setLoading(true);
    Promise.all(typen.map(async typ => {
      try {
        const res  = await fetch(`${PDF_SERVER_URL}/api/foto/${jahr}/${safeGarten}/${typ}`);
        const data = await res.json();
        return { typ, url: data.url ? `${PDF_SERVER_URL}${data.url}?t=${Date.now()}` : null };
      } catch(e) { return { typ, url: null }; }
    })).then(results => {
      const f = { ...fotos };
      results.forEach(r => f[r.typ] = r.url);
      setFotos(f);
      setLoading(false);
    });
  }

  React.useEffect(() => { ladeFotos(); }, [gartenName, jahr]);

  async function handleUpload(e, typ) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(typ);
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX = 800;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) { if(w>h){h=Math.round(h*MAX/w);w=MAX;}else{w=Math.round(w*MAX/h);h=MAX;} }
        canvas.width=w; canvas.height=h;
        canvas.getContext("2d").drawImage(img,0,0,w,h);
        canvas.toBlob(async blob => {
          const formData = new FormData();
          formData.append("foto", blob, `${typ}.jpg`);
          formData.append("jahr", String(jahr));
          formData.append("garten", safeGarten);
          formData.append("typ", typ);
          try {
            await fetch(`${PDF_SERVER_URL}/api/foto/upload`, { method: "POST", body: formData });
            ladeFotos();
          } catch(err) { console.error(err); }
          setUploading(null);
        }, "image/jpeg", 0.82);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  async function loescheFoto(typ) {
    await fetch(`${PDF_SERVER_URL}/api/foto/${jahr}/${safeGarten}/${typ}`, { method: "DELETE" });
    setFotoModal(null);
    ladeFotos();
  }

  if (loading) return null;

  return (
    <>
      {fotoModal && (
        <FotoDraggableModal
          url={fotoModal.url}
          typ={fotoModal.typ}
          gartenName={gartenName}
          jahr={jahr}
          onDelete={() => loescheFoto(fotoModal.typ)}
          onClose={() => setFotoModal(null)}
        />
      )}

      <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {typen.map(typ => {
          const color  = typ === "wasser" ? "#2E86C1" : "#D4AC0D";
          const bg     = typ === "wasser" ? "var(--leafPale)" : "var(--cream)";
          const border = typ === "wasser" ? "#AED6F1" : "#F9E79F";
          const galRef = typ === "wasser" ? wasserRef : stromRef;
          const camRef = typ === "wasser" ? wasserKameraRef : stromKameraRef;
          const hatFoto = !!fotos[typ];
          return (
            <div key={typ} style={{ display: "flex", gap: 6 }}>
              {hatFoto ? (
                <button onClick={() => setFotoModal({ url: fotos[typ], typ })}
                  style={{ padding: "5px 11px", borderRadius: 7, border: `1.5px solid ${color}`, background: bg, color, fontWeight: 600, fontSize: 11, cursor: "pointer" }}>
                  📷 {nur ? "Foto ansehen" : `${typ === "wasser" ? "Wasser" : "Strom"}-Foto`}
                </button>
              ) : isMobile ? (
                /* Mobile: getrennte Buttons → echte Wahl zwischen Kamera und Galerie */
                <>
                  <button onClick={() => camRef.current?.click()}
                    style={{ padding: "5px 11px", borderRadius: 7, border: `1.5px solid ${color}`, background: "var(--cardBg)", color, fontWeight: 600, fontSize: 11, cursor: "pointer" }}>
                    {uploading === typ ? "⏳" : "📸 Kamera"}
                  </button>
                  <button onClick={() => galRef.current?.click()}
                    style={{ padding: "5px 11px", borderRadius: 7, border: `1.5px dashed ${border}`, background: "var(--cardBg)", color: COLORS.textMuted, fontWeight: 600, fontSize: 11, cursor: "pointer" }}>
                    🖼️ Galerie
                  </button>
                </>
              ) : (
                <button onClick={() => galRef.current?.click()}
                  style={{ padding: "5px 11px", borderRadius: 7, border: `1.5px dashed ${border}`, background: "var(--cardBg)", color: COLORS.textMuted, fontWeight: 600, fontSize: 11, cursor: "pointer" }}>
                  {uploading === typ ? "⏳" : `📷 ${nur ? "Foto hinzufügen" : (typ === "wasser" ? "Wasser" : "Strom") + "-Foto hinzufügen"}`}
                </button>
              )}
              {/* Galerie: ohne capture → Auswahl-Dialog; Kamera: capture=environment → direkt Kamera */}
              <input ref={galRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => handleUpload(e, typ)} />
              <input ref={camRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={e => handleUpload(e, typ)} />
            </div>
          );
        })}
      </div>
    </>
  );
}

function FotoMiniBtn({ gartenName, jahr, typ }) {
  const [fotoUrl, setFotoUrl]   = useState(null);
  const [showModal, setShowModal] = useState(false);
  const safeGarten = (gartenName || "").replace(/\s+/g, "_");
  const color = typ === "wasser" ? "#2E86C1" : "#D4AC0D";

  React.useEffect(() => {
    let active = true;
    fetch(`${PDF_SERVER_URL}/api/foto/${jahr}/${safeGarten}/${typ}`)
      .then(r => r.json())
      .then(d => { if (active && d.url) setFotoUrl(`${PDF_SERVER_URL}${d.url}?t=${Date.now()}`); })
      .catch(() => {});
    return () => { active = false; };
  }, [gartenName, jahr, typ]);

  if (!fotoUrl) return null;

  return (
    <>
      <button onClick={() => setShowModal(true)}
        style={{ display: "block", marginTop: 4, padding: "2px 8px", borderRadius: 5, border: `1px solid ${color}`, background: "transparent", color, fontWeight: 600, fontSize: 10, cursor: "pointer" }}>
        📷 Foto
      </button>
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => setShowModal(false)}>
          <div style={{ position: "relative", maxWidth: "90vw" }} onClick={e => e.stopPropagation()}>
            <img src={fotoUrl} alt="Zähler" style={{ maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain", borderRadius: 12, display: "block" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
              <span style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>
                {typ === "wasser" ? "💧 Wasserzähler" : "⚡ Stromzähler"} — {gartenName} {jahr}
              </span>
              <button onClick={() => setShowModal(false)}
                style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                ✕ Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function AblesungenTabelle({ filteredMembers, ablesungen, selectedYear, gaerten, members, onGartenClick }) {
  var hauptAblRaw = "{}";
  try { hauptAblRaw = localStorage.getItem("gv_hauptzaehler") || "{}"; } catch(e) {}
  var hauptAbl = {};
  try { hauptAbl = JSON.parse(hauptAblRaw); } catch(e) {}

  var getHz = function(id, field) { return hauptAbl[id + "_" + field + "__" + selectedYear] || ""; };

  var wege = ["Unterer Weg", "Mittlerer Weg", "Oberer Weg"];
  var stromIds = { "Unterer Weg": "strom_unterer_weg", "Mittlerer Weg": "strom_mittlerer_weg", "Oberer Weg": "strom_oberer_weg" };

  var rowVerbrauch = function(m, typ) {
    var ablId = m._gartenId || m.id;
    var inaktiv = ablesungen[ablId + "__" + typ + "__" + selectedYear + "__inaktiv"] === "1";
    if (inaktiv) return null;
    var cur = ablesungen[ablId + "__" + typ + "__" + selectedYear + "__Anfang"];
    var prevB = ablesungen[ablId + "__" + typ + "__" + (selectedYear-1) + "__EndeB"];
    var A = cur || prevB || ablesungen[ablId + "__" + typ + "__" + (selectedYear-1) + "__Ende"] || "";
    var E = ablesungen[ablId + "__" + typ + "__" + selectedYear + "__Ende"] || "";
    var EB = ablesungen[ablId + "__" + typ + "__" + selectedYear + "__EndeB"] || "";
    var vA = A && E ? Math.round(parseFloat(E) - parseFloat(A)) : null;
    var vB = EB ? Math.round(parseFloat(EB)) : null;
    if (vA !== null && vB !== null) return vA + vB;
    return vA;
  };

  var alleGaertenRows = gaerten.map(function(g) {
    var mitglied = members.find(function(m) {
      return (m.garten === g.name || (m.zusatzgaerten||[]).includes(g.name)) && m.status !== "Ehemaliges Mitglied";
    });
    return { id: g.id, _rowGarten: g.name, _gartenId: g.id, vorname: mitglied ? mitglied.vorname || "" : "", nachname: mitglied ? mitglied.nachname || "" : "", garten: g.name };
  });

  var wHE = parseFloat(getHz("wasser_haupt", "ende")) || 0;
  var wHV = parseFloat(hauptAbl["wasser_haupt_ende__" + (selectedYear-1)]) || 0;
  var wHVerbr = (wHE && wHV) ? wHE - wHV : null;
  var wSumme = alleGaertenRows.reduce(function(s, m) { var v = rowVerbrauch(m, "wasser"); return s + (v || 0); }, 0);
  var wDiff = wHVerbr !== null ? wHVerbr - wSumme : null;
  var wOk = wDiff !== null && Math.abs(wDiff) < 1;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ padding: "20px 24px", background: "var(--leafPale)", border: "1.5px solid #AED6F1", borderRadius: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#2E86C1", marginBottom: 12 }}>💧 Haupt-Wasserzähler — Vergleich alle Wege</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 16 }}>
            <div style={{ background: "var(--cardBg)", borderRadius: 8, padding: "10px 14px", border: "1px solid #AED6F1" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 4 }}>Ablesewert</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#2E86C1" }}>{wHE ? wHE.toFixed(2) + " m³" : "—"}</div>
            </div>
            <div style={{ background: "var(--cardBg)", borderRadius: 8, padding: "10px 14px", border: "1px solid #AED6F1" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 4 }}>Verbrauch Hauptzähler</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#2E86C1" }}>{wHVerbr !== null ? wHVerbr.toFixed(2) + " m³" : "—"}</div>
            </div>
            <div style={{ background: "var(--cardBg)", borderRadius: 8, padding: "10px 14px", border: "1px solid #AED6F1" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 4 }}>Summe Gärten</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text }}>{wSumme.toFixed(2) + " m³"}</div>
            </div>
            <div style={{ background: "var(--cardBg)", borderRadius: 8, padding: "10px 14px", border: "1px solid #AED6F1" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 4 }}>Differenz</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: wDiff !== null ? (wOk ? "#27AE60" : "#C0392B") : COLORS.textMuted }}>{wDiff !== null ? wDiff.toFixed(2) + " m³" : "—"}</div>
            </div>
            <div style={{ background: "var(--cardBg)", borderRadius: 8, padding: "10px 14px", border: "1px solid #AED6F1" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 4 }}>Status</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: wDiff !== null ? (wOk ? "#27AE60" : "#C0392B") : COLORS.textMuted }}>{wDiff !== null ? (wOk ? "✅ Passt" : "⚠️ Abweichung") : "—"}</div>
            </div>
          </div>
        </div>
        {wege.map(function(weg) {
          var sId = stromIds[weg];
          var sHE = parseFloat(getHz(sId, "ende")) || 0;
          var sHV = parseFloat(hauptAbl[sId + "_ende__" + (selectedYear-1)]) || 0;
          var sHVerbr = (sHE && sHV) ? sHE - sHV : null;
          var wegRowsS = alleGaertenRows.filter(function(m) { return categorizeGarten(m._rowGarten || m.garten) === weg; });
          var sSumme = wegRowsS.reduce(function(s, m) { var v = rowVerbrauch(m, "strom"); return s + (v || 0); }, 0);
          var sDiff = sHVerbr !== null ? sHVerbr - sSumme : null;
          var sOk = sDiff !== null && Math.abs(sDiff) < 1;
          return (
            <div key={weg} style={{ padding: "16px 20px", background: "var(--cream)", border: "1.5px solid #F9E79F", borderRadius: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#D4AC0D", marginBottom: 10 }}>⚡ Hauptstromzähler — {weg}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 16 }}>
                <div style={{ background: "var(--cardBg)", borderRadius: 7, padding: "7px 12px", border: "1px solid #F9E79F" }}><div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 3 }}>Ablesewert</div><div style={{ fontSize: 13, fontWeight: 800, color: "#D4AC0D" }}>{sHE ? sHE.toFixed(2) + " kWh" : "—"}</div></div>
                <div style={{ background: "var(--cardBg)", borderRadius: 7, padding: "7px 12px", border: "1px solid #F9E79F" }}><div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 3 }}>Verbrauch Hauptzähler</div><div style={{ fontSize: 13, fontWeight: 800, color: "#D4AC0D" }}>{sHVerbr !== null ? sHVerbr.toFixed(2) + " kWh" : "—"}</div></div>
                <div style={{ background: "var(--cardBg)", borderRadius: 7, padding: "7px 12px", border: "1px solid #F9E79F" }}><div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 3 }}>Summe Gärten</div><div style={{ fontSize: 13, fontWeight: 800, color: COLORS.text }}>{sSumme.toFixed(2) + " kWh"}</div></div>
                <div style={{ background: "var(--cardBg)", borderRadius: 7, padding: "7px 12px", border: "1px solid #F9E79F" }}><div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 3 }}>Differenz</div><div style={{ fontSize: 13, fontWeight: 800, color: sDiff !== null ? (sOk ? "#27AE60" : "#C0392B") : COLORS.textMuted }}>{sDiff !== null ? sDiff.toFixed(2) + " kWh" : "—"}</div></div>
                <div style={{ background: "var(--cardBg)", borderRadius: 7, padding: "7px 12px", border: "1px solid #F9E79F" }}><div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 3 }}>Status</div><div style={{ fontSize: 13, fontWeight: 800, color: sDiff !== null ? (sOk ? "#27AE60" : "#C0392B") : COLORS.textMuted }}>{sDiff !== null ? (sOk ? "✅ Passt" : "⚠️ Abweichung") : "—"}</div></div>
              </div>
            </div>
          );
        })}
      </div>
      {wege.map(function(weg) {
        var wegRows = alleGaertenRows.filter(function(m) { return categorizeGarten(m._rowGarten || m.garten) === weg; });
        var sSumWeg = wegRows.reduce(function(s, m) { var v = rowVerbrauch(m, "strom"); return s + (v || 0); }, 0);
        var wSumWeg = wegRows.reduce(function(s, m) { var v = rowVerbrauch(m, "wasser"); return s + (v || 0); }, 0);
        return (
          <div key={weg}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 8, paddingLeft: 4 }}>{weg}</div>
            <div style={{ overflowX: "auto", background: "var(--cardBg)", borderRadius: 12, border: "1px solid " + COLORS.border }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                <thead>
                  <tr>
                    <th style={S.th} rowSpan={2}>Garten</th>
                    <th style={S.th} rowSpan={2}>Mitglied</th>
                    <th colSpan={4} style={{ ...S.th, background: "#D6EAF8", textAlign: "center", borderBottom: "1px solid #AED6F1" }}>💧 Wasserzähler</th>
                    <th colSpan={4} style={{ ...S.th, background: "#FCF3CF", textAlign: "center", borderBottom: "1px solid #F0C000" }}>⚡ Stromzähler</th>
                  </tr>
                  <tr>
                    <th style={{ ...S.th, background: "var(--leafPale)", whiteSpace: "nowrap" }}>Vorjahr Verbr.</th>
                    <th style={{ ...S.th, background: "var(--leafPale)", whiteSpace: "nowrap" }}>Vorjahr Ables.</th>
                    <th style={{ ...S.th, background: "var(--leafPale)", whiteSpace: "nowrap", borderLeft: "2px solid #AED6F1" }}>Ablesewert</th>
                    <th style={{ ...S.th, background: "var(--leafPale)", whiteSpace: "nowrap" }}>Verbrauch</th>
                    <th style={{ ...S.th, background: "var(--cream)", whiteSpace: "nowrap" }}>Vorjahr Verbr.</th>
                    <th style={{ ...S.th, background: "var(--cream)", whiteSpace: "nowrap" }}>Vorjahr Ables.</th>
                    <th style={{ ...S.th, background: "var(--cream)", whiteSpace: "nowrap", borderLeft: "2px solid #F0C000" }}>Ablesewert</th>
                    <th style={{ ...S.th, background: "var(--cream)", whiteSpace: "nowrap" }}>Verbrauch</th>
                  </tr>
                </thead>
                <tbody>
                  {wegRows.map(function(m) {
                    var rg = m._rowGarten || m.garten;
                    var aid = m._gartenId || m.id;
                    var gV = function(typ, p) { return ablesungen[aid + "__" + typ + "__" + selectedYear + "__" + p] || "–"; };
                    var getA = function(typ) {
                      var c = ablesungen[aid + "__" + typ + "__" + selectedYear + "__Anfang"];
                      if (c) return c;
                      var pb = ablesungen[aid + "__" + typ + "__" + (selectedYear-1) + "__EndeB"];
                      if (pb) return pb;
                      return ablesungen[aid + "__" + typ + "__" + (selectedYear-1) + "__Ende"] || "–";
                    };
                    var wI = ablesungen[aid + "__wasser__" + selectedYear + "__inaktiv"] === "1";
                    var sI = ablesungen[aid + "__strom__" + selectedYear + "__inaktiv"] === "1";
                    var wA = getA("wasser"), wE = gV("wasser","Ende"), wEB = gV("wasser","EndeB");
                    var wVa = wA !== "–" && wE !== "–" ? Math.round(parseFloat(wE)-parseFloat(wA)) : null;
                    var wVb = wEB !== "–" ? Math.round(parseFloat(wEB)) : null;
                    var wV = wVa !== null && wVb !== null ? wVa + wVb : wVa !== null ? wVa : "–";
                    var sA = getA("strom"), sE = gV("strom","Ende"), sEB = gV("strom","EndeB");
                    var sVa = sA !== "–" && sE !== "–" ? Math.round(parseFloat(sE)-parseFloat(sA)) : null;
                    var sVb = sEB !== "–" ? Math.round(parseFloat(sEB)) : null;
                    var sV = sVa !== null && sVb !== null ? sVa + sVb : sVa !== null ? sVa : "–";
                    var pwA = ablesungen[aid + "__wasser__" + (selectedYear-1) + "__Anfang"] || ablesungen[aid + "__wasser__" + (selectedYear-2) + "__Ende"] || "";
                    var pwE = ablesungen[aid + "__wasser__" + (selectedYear-1) + "__Ende"] || "";
                    var wPV = pwA && pwE ? Math.round(parseFloat(pwE)-parseFloat(pwA)) + " m³" : "–";
                    var psA = ablesungen[aid + "__strom__" + (selectedYear-1) + "__Anfang"] || ablesungen[aid + "__strom__" + (selectedYear-2) + "__Ende"] || "";
                    var psE = ablesungen[aid + "__strom__" + (selectedYear-1) + "__Ende"] || "";
                    var sPV = psA && psE ? Math.round(parseFloat(psE)-parseFloat(psA)) + " kWh" : "–";
                    return (
                      <tr key={rg} style={{ cursor: onGartenClick ? "pointer" : "default" }}
                        onClick={onGartenClick ? () => {
                          // Baue das richtige Objekt für das Modal
                          const g = gaerten.find(g => g.name === rg);
                          const mitgl = members.find(mem => (mem.garten === rg || (mem.zusatzgaerten||[]).includes(rg)) && mem.status !== "Ehemaliges Mitglied");
                          if (g) onGartenClick({
                            id: g.id,
                            garten: g.name,
                            isMember: !!mitgl,
                            vorname: mitgl?.vorname || "",
                            nachname: mitgl?.nachname || "",
                          });
                        } : undefined}>
                        <td style={S.td}><span style={{ ...S.gartenBadge, textDecoration: onGartenClick ? "underline dotted" : "none" }}>{rg||"–"}</span></td>
                        <td style={S.td}>{(m.vorname||m.nachname) ? <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={S.avatar}>{m.vorname?m.vorname[0]:""}{m.nachname?m.nachname[0]:""}</div><span style={{ fontWeight: 600, fontSize: 13 }}>{m.vorname} {m.nachname}</span></div> : <span style={{ fontSize: 12, color: COLORS.textMuted }}>—</span>}</td>
                        <td style={{ ...S.td, background: "var(--leafPale)", color: COLORS.textMuted }}>{wPV}</td>
                        <td style={{ ...S.td, background: "var(--leafPale)" }}>{wA}</td>
                        <td style={{ ...S.td, background: "var(--leafPale)", fontWeight: 600, borderLeft: "2px solid #AED6F1" }}>
                          {wI ? <span style={{ fontSize: 11, color: COLORS.textMuted }}>inaktiv</span>
                            : wEB !== "–" ? <span title={`Wechsel: ${wE} m³ → Ende: ${wEB} m³`}>{wE}<span style={{ fontSize: 10, color: COLORS.textMuted }}> / {wEB}</span></span>
                            : wE}
                        </td>
                        <td style={{ ...S.td, background: "var(--leafPale)", fontWeight: 700, color: wI ? COLORS.textMuted : "#2E86C1" }}>
                          {wI ? "–" : wV !== "–" ? wV + " m³" : "–"}
                          <FotoMiniBtn gartenName={rg} jahr={selectedYear} typ="wasser" />
                        </td>
                        <td style={{ ...S.td, background: "var(--cream)", color: COLORS.textMuted }}>{sPV}</td>
                        <td style={{ ...S.td, background: "var(--cream)" }}>{sA}</td>
                        <td style={{ ...S.td, background: "var(--cream)", fontWeight: 600, borderLeft: "2px solid #F0C000" }}>
                          {sI ? <span style={{ fontSize: 11, color: COLORS.textMuted }}>inaktiv</span>
                            : sEB !== "–" ? <span title={`Wechsel: ${sE} kWh → Ende: ${sEB} kWh`}>{sE}<span style={{ fontSize: 10, color: COLORS.textMuted }}> / {sEB}</span></span>
                            : sE}
                        </td>
                        <td style={{ ...S.td, background: "var(--cream)", fontWeight: 700, color: sI ? COLORS.textMuted : "#D4AC0D" }}>
                          {sI ? "–" : sV !== "–" ? sV + " kWh" : "–"}
                          <FotoMiniBtn gartenName={rg} jahr={selectedYear} typ="strom" />
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ background: "var(--cardBg)" }}>
                    <td colSpan={2} style={{ ...S.td, fontWeight: 700, color: COLORS.textMuted, fontSize: 12 }}>SUMME {weg}</td>
                    <td style={{ ...S.td, background: "var(--leafPale)" }}></td><td style={{ ...S.td, background: "var(--leafPale)" }}></td>
                    <td style={{ ...S.td, background: "var(--leafPale)", borderLeft: "2px solid #AED6F1" }}></td>
                    <td style={{ ...S.td, background: "var(--leafPale)", fontWeight: 800, color: "#2E86C1" }}>{wSumWeg.toFixed(0)} m³</td>
                    <td style={{ ...S.td, background: "var(--cream)" }}></td><td style={{ ...S.td, background: "var(--cream)" }}></td>
                    <td style={{ ...S.td, background: "var(--cream)", borderLeft: "2px solid #F0C000" }}></td>
                    <td style={{ ...S.td, background: "var(--cream)", fontWeight: 800, color: "#D4AC0D" }}>{sSumWeg.toFixed(0)} kWh</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FinanzenWrapper({ activeTab, onTabChange, members }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: `2px solid ${COLORS.border}`, padding: "0 40px", background: "var(--cardBg)" }}>
        {[
          { id: "finanzen",             label: "💶 Budget" },
          { id: "erstellte-rechnungen", label: "🧾 Erstellte Rechnungen" },
          { id: "kontoauszuege",        label: "🗂️ Kontoauszüge" },
          { id: "bilanzen",             label: "📊 Bilanzen" },
        ].map(tab => (
          <button key={tab.id}
            style={{
              padding: "14px 24px", border: "none", background: "transparent",
              fontSize: 14, fontWeight: activeTab === tab.id ? 700 : 400,
              color: activeTab === tab.id ? COLORS.leaf : COLORS.textMuted,
              cursor: "pointer", fontFamily: "inherit",
              borderBottom: activeTab === tab.id ? `3px solid ${COLORS.leaf}` : "3px solid transparent",
              marginBottom: -2, transition: "all 0.15s",
            }}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {/* Content */}
      {activeTab === "finanzen"             && <Finanzen />}
      {activeTab === "erstellte-rechnungen" && <ErstellteRechnungen members={members} />}
      {activeTab === "kontoauszuege"        && <Kontoauszuege />}
      {activeTab === "bilanzen"             && <Bilanzen members={members} />}
    </div>
  );
}

// ─── PDF auf dem Server generieren (jsPDF + jsdom) ───────────────────────────
async function downloadAsPdf(htmlContent, filename) {
  // Dateiname bereinigen: Leerzeichen zu _, trailing _ weg, genau eine .pdf Endung
  const safeName = (() => {
    let n = filename.replace(/\.html$/, ".pdf").replace(/\s+/g, "_").trim();
    if (!n.toLowerCase().endsWith(".pdf")) n += ".pdf";
    return n.replace(/(_+)(\.pdf)$/i, "$2");
  })();

  const loader = document.createElement("div");
  loader.id = "pdf-loader";
  loader.style.cssText = "position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.6);display:flex;align-items:center;justify-content:center;z-index:99999;";
  loader.innerHTML = '<div style="background:#fff;padding:32px 48px;border-radius:12px;text-align:center;font-family:Arial,sans-serif;box-shadow:0 8px 32px rgba(0,0,0,0.3);"><div style="font-size:32px;margin-bottom:12px;">📄</div><div id="pdf-status" style="font-size:16px;font-weight:700;color:#2D5A27;">PDF wird auf Server erstellt...</div><div style="font-size:13px;color:#666;margin-top:8px;">Bitte warten</div></div>';
  document.body.appendChild(loader);

  try {
    const res = await fetch(`${PDF_SERVER_URL}/api/rechnung/generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ html: htmlContent, filename: safeName, save: true })
    });
    if (!res.ok) {
      const err = await res.text();
      throw new Error("Server " + res.status + ": " + err);
    }
    const data = await res.json();
    console.log("✅ PDF erstellt:", data.url);
    document.body.removeChild(loader);
    return data;
  } catch(err) {
    const l = document.getElementById("pdf-loader");
    if (l) document.body.removeChild(l);
    console.error("PDF Fehler:", err);
    alert("❌ PDF-Fehler: " + err.message);
    return null;
  }
}

// ─── Rechnungs-Anzeige Modal ──────────────────────────────────────────────────
function RechnungsAnzeigeModal({ rechnungData, onClose }) {
  if (!rechnungData) return null;

  // Rechnung nicht vorhanden → Frage ob erstellen
  if (rechnungData.nichtVorhanden) {
    return (
      <div style={{ ...S.overlay, zIndex: 2000 }}>
        <div style={{ ...S.modal, width: 420, padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>📄</div>
          <h3 style={{ margin: "0 0 10px", color: COLORS.text, fontSize: 17 }}>Rechnung nicht erstellt</h3>
          <p style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 24, lineHeight: 1.6 }}>
            Die Rechnung wurde noch nicht erstellt.<br/>
            Möchten Sie die Rechnung jetzt erstellen?
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button style={{ ...S.btnGhost, fontSize: 13, padding: "9px 20px" }}
              onClick={onClose}>Abbrechen</button>
            <button style={{ ...S.btnPrimary, fontSize: 13, padding: "9px 20px" }}
              onClick={() => { onClose(); rechnungData.onErstellen && rechnungData.onErstellen(); }}>
              🖨️ Jetzt erstellen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Wenn PDF auf Server vorhanden → direkt als iframe-src anzeigen
  const pdfUrl = rechnungData.pdfUrl
    ? `${PDF_SERVER_URL}${rechnungData.pdfUrl}`
    : null;

  // Fallback: altes gespeichertes HTML
  const htmlContent = !pdfUrl && typeof rechnungData === "string"
    ? decodeURIComponent(rechnungData.replace("data:text/html;charset=utf-8,", ""))
    : null;

  return (
    <div style={{ ...S.overlay, zIndex: 2000 }}>
      <div style={{ background: "var(--cardBg)", borderRadius: 12, width: "min(98vw, 900px)", maxHeight: "95vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderBottom: `1px solid ${COLORS.border}` }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: COLORS.leaf }}>🧾 Jahresrechnung Vorschau</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {pdfUrl && (
              <a href={pdfUrl} download target="_blank" rel="noreferrer"
                style={{ ...S.btnSmall, background: COLORS.leaf, color: "#fff", border: "none", textDecoration: "none", fontSize: 12, padding: "5px 12px" }}>
                ⬇ PDF
              </a>
            )}
            <button style={S.btnGhost} onClick={onClose}>✕ Schließen</button>
          </div>
        </div>
        {pdfUrl ? (
          <iframe src={pdfUrl} style={{ flex: 1, border: "none", minHeight: "80vh" }} title="Jahresrechnung PDF" />
        ) : htmlContent ? (
          <iframe srcDoc={htmlContent} style={{ flex: 1, border: "none", minHeight: "80vh" }} title="Jahresrechnung" />
        ) : null}
      </div>
    </div>
  );
}

// Generiert frisches Rechnungs-HTML für ein Mitglied (für ErstellteRechnungen)
// ─── Erstellte Rechnungen ─────────────────────────────────────────────────────
function ErstellteRechnungen({ members }) {
  const currentYear = new Date().getFullYear();
  const [openYear, setOpenYear] = useState(currentYear);
  const [refreshKey, setRefreshKey] = useState(0);

  // Nur Rechnungen anzeigen die wirklich erstellt wurden (haben name UND rechnungsnummer)
  const allRechnungen = React.useMemo(() => {
    const stored = (() => { try { return JSON.parse(localStorage.getItem("gv_members") || "[]"); } catch(e) { return []; } })();
    const byYear = {};
    stored.forEach(m => {
      (m.rechnungen || []).filter(r => r.rechnungsnummer && r.name).forEach(r => {
        const yr = r.jahr || "–";
        if (!byYear[yr]) byYear[yr] = [];
        byYear[yr].push({ ...r, mitglied: m });
      });
    });
    return byYear;
  }, [members, refreshKey]);

  const toggleBezahlt = (r) => {
    const stored = JSON.parse(localStorage.getItem("gv_members") || "[]");
    const updated = stored.map(m => {
      if (m.id !== r.mitglied.id) return m;
      return { ...m, rechnungen: (m.rechnungen||[]).map(rec =>
        rec.id === r.id ? { ...rec, bezahlt: !rec.bezahlt } : rec
      )};
    });
    localStorage.setItem("gv_members", JSON.stringify(updated));
    setRefreshKey(k => k + 1);
  };

  const jahre = Object.keys(allRechnungen).sort((a, b) => b - a);
  const gesamtAnzahl = Object.values(allRechnungen).reduce((s, arr) => s + arr.length, 0);
  const gesamtBezahlt = Object.values(allRechnungen).flat().filter(r => r.bezahlt).length;

  return (
    <div className="gv-page" style={S.page}>
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>Erstellte Rechnungen</h1>
          <p style={S.subtitle}>{gesamtAnzahl} Rechnungen · {gesamtBezahlt} bezahlt · {gesamtAnzahl - gesamtBezahlt} offen</p>
        </div>
      </div>

      {jahre.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 0", color: COLORS.textMuted }}>
          <div style={{ fontSize: 48 }}>🧾</div>
          <p style={{ marginTop: 12, fontSize: 15 }}>Noch keine Rechnungen erstellt.</p>
          <p style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 6 }}>Gehe zu „Mitglieder-Jahresrechnungen" und erstelle die Rechnungen.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {jahre.map(yr => {
            const rechnungen = allRechnungen[yr].sort((a, b) => {
              const na = parseInt((a.mitglied.garten||"").replace(/\D/g,"")) || 0;
              const nb = parseInt((b.mitglied.garten||"").replace(/\D/g,"")) || 0;
              return na - nb;
            });
            const bezahlt    = rechnungen.filter(r => r.bezahlt).length;
            const offen      = rechnungen.length - bezahlt;
            const gesamt     = rechnungen.reduce((s, r) => s + (parseFloat((r.betrag||"0").replace(",",".")) || 0), 0);
            const bezahltSum = rechnungen.filter(r => r.bezahlt).reduce((s, r) => s + (parseFloat((r.betrag||"0").replace(",",".")) || 0), 0);
            const isOpen     = openYear === Number(yr) || openYear === yr;

            return (
              <div key={yr} style={{ ...S.card, padding: 0, overflow: "hidden" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", background: isOpen ? COLORS.leafPale : "#fff", cursor:"pointer", borderBottom: isOpen ? `1px solid ${COLORS.border}` : "none" }}
                  onClick={() => setOpenYear(isOpen ? null : Number(yr))}>
                  <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                    <span style={{ fontSize:20, fontWeight:800, color:COLORS.leaf }}>{yr}</span>
                    <span style={{ fontSize:13, color:COLORS.textMuted }}>{rechnungen.length} Rechnungen</span>
                    {offen > 0 && <span style={{ fontSize:12, padding:"2px 10px", borderRadius:12, background:"#FEF3F2", color:"#C0392B", fontWeight:600 }}>⏳ {offen} offen</span>}
                    {bezahlt > 0 && <span style={{ fontSize:12, padding:"2px 10px", borderRadius:12, background:COLORS.leafPale, color:"#2D5A27", fontWeight:600 }}>✓ {bezahlt} bezahlt</span>}
                    <span style={{ fontSize:13, fontWeight:700, color:COLORS.leaf }}>{gesamt.toFixed(2).replace(".",",")} €</span>
                    {bezahlt > 0 && <span style={{ fontSize:12, color:COLORS.textMuted }}>davon eingegangen: {bezahltSum.toFixed(2).replace(".",",")} €</span>}
                  </div>
                  <span style={{ fontSize:18, color:COLORS.textMuted, transform: isOpen ? "rotate(180deg)" : "none", transition:"transform 0.2s" }}>⌄</span>
                </div>

                {isOpen && (
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead>
                        <tr style={{ background:"var(--cardBg)" }}>
                          <th style={S.th}>Garten</th>
                          <th style={S.th}>Mitglied</th>
                          <th style={S.th}>Rech.-Nr.</th>
                          <th style={S.th}>Datum</th>
                          <th style={S.th}>Betrag</th>
                          <th style={S.th}>Status</th>
                          <th style={S.th}>Buchungstag Konto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rechnungen.map((r, i) => (
                          <tr key={i} style={{ ...S.tr, background: r.bezahlt ? "#f6fdf6" : undefined }}>
                            <td style={S.td}><span style={S.gartenBadge}>{r.mitglied.garten}</span></td>
                            <td style={S.td}>
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <div style={{ ...S.avatar, width:28, height:28, fontSize:11 }}>{r.mitglied.vorname?.[0]}{r.mitglied.nachname?.[0]}</div>
                                <span style={{ fontWeight:600, fontSize:13 }}>{r.mitglied.vorname} {r.mitglied.nachname}</span>
                              </div>
                            </td>
                            <td style={{ ...S.td, fontFamily:"monospace", fontSize:12, color:COLORS.textMuted }}>{r.rechnungsnummer}</td>
                            <td style={S.td}>{r.datum ? (r.datum.includes("-") ? r.datum.split("-").reverse().join(".") : r.datum) : "–"}</td>
                            <td style={{ ...S.td, fontWeight:700, color:COLORS.leaf }}>{r.betrag ? r.betrag+" €" : "–"}</td>
                            <td style={S.td}>
                              <button
                                style={{ fontSize:12, fontWeight:700, padding:"5px 14px", borderRadius:20, border:"none", cursor:"pointer", background: r.bezahlt ? "#d4edda" : "#FEF3F2", color: r.bezahlt ? "#2D5A27" : "#C0392B" }}
                                onClick={() => toggleBezahlt(r)}>
                                {r.bezahlt ? "✓ Bezahlt" : "⏳ Offen"}
                              </button>
                            </td>
                            <td style={{ ...S.td, color:COLORS.textMuted, fontSize:12 }}>
                              {r.buchungstag || "–"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background:COLORS.leafPale, fontWeight:700 }}>
                          <td colSpan={4} style={{ ...S.td, fontSize:13, color:COLORS.leaf }}>Gesamt {yr}</td>
                          <td style={{ ...S.td, fontWeight:800, color:COLORS.leaf, fontSize:14 }}>{gesamt.toFixed(2).replace(".",",")} €</td>
                          <td style={{ ...S.td, fontSize:12, color:COLORS.textMuted }}>{bezahlt > 0 ? `${bezahltSum.toFixed(2).replace(".",",")} € eingegangen` : ""}</td>
                          <td style={S.td}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ─── Jahresrechnungenstellt HTML-String für Rechnung ─────────────────────────
function buildPdfHtml(m, rechnungsnummer, gebP, ablP, gaertenListP, yearP, BETRAG_MAP_P) {
  const gebX         = gebP  || {};
  const ablX         = ablP  || {};
  const gaertenListX = gaertenListP || [];
  const yearX        = yearP || new Date().getFullYear();
  const BETRAG_MAP_X = BETRAG_MAP_P || {};

  const alleGaerten = [m.garten, ...(m.zusatzgaerten||[])].filter(Boolean);
  const aktivePos   = Object.entries(BETRAG_MAP_X).filter(([key]) => m[key]);
  const anrede      = m.anrede === "Frau" ? "Frau" : m.anrede === "Herr" ? "Herrn" : "";

  const wasserPreis = parseFloat((gebX.wasserpreis_m3 || "2,09").replace(",",".")) || 2.09;
  const stromPreis  = parseFloat((gebX.strompreis_kwh || "0,37").replace(",",".")) || 0.37;

  let posHTML = "";
  let total   = 0;
  let rowIdx  = 0;
  aktivePos.forEach(([, b]) => {
    const betrag = parseFloat((gebX[b.gkey] || b.def).replace(",",".")) || 0;
    total += betrag;
    const bg = rowIdx % 2 === 0 ? "#f8fdf6" : "#ffffff";
    posHTML += `<tr style="background:${bg}">
      <td style="padding:10px 16px;border-bottom:1px solid #e8f0e5;color:#333;font-size:10.5pt">${b.label}</td>
      <td style="padding:10px 16px;border-bottom:1px solid #e8f0e5;text-align:right;font-weight:600;color:#1a1a1a;font-size:10.5pt;white-space:nowrap">${betrag.toFixed(2).replace(".",",")} €</td>
    </tr>`;
    rowIdx++;
  });

  // Verbrauchskosten
  let ablHTML = "";
  alleGaerten.forEach(gartenName => {
    const g   = gaertenListX.find(x => x.name === gartenName);
    const gId = g ? g.id : gartenName;
    const wInaktiv = ablX[`${gId}__wasser__${yearX}__inaktiv`] === "1";
    const sInaktiv = ablX[`${gId}__strom__${yearX}__inaktiv`]  === "1";
    const wA  = ablX[`${gId}__wasser__${yearX}__Anfang`]  || ablX[`${gId}__wasser__${yearX-1}__EndeB`] || ablX[`${gId}__wasser__${yearX-1}__Ende`] || "";
    const wE  = ablX[`${gId}__wasser__${yearX}__Ende`]   || "";
    const wEB = ablX[`${gId}__wasser__${yearX}__EndeB`]  || "";
    const sA  = ablX[`${gId}__strom__${yearX}__Anfang`]   || ablX[`${gId}__strom__${yearX-1}__EndeB`]  || ablX[`${gId}__strom__${yearX-1}__Ende`]  || "";
    const sE  = ablX[`${gId}__strom__${yearX}__Ende`]    || "";
    const sEB = ablX[`${gId}__strom__${yearX}__EndeB`]   || "";
    const wVnum = (!wInaktiv && wA && wE) ? Math.round(parseFloat(normAbl(wE))-parseFloat(normAbl(wA))) + (wEB ? Math.round(parseFloat(normAbl(wEB))) : 0) : 0;
    const sVnum = (!sInaktiv && sA && sE) ? Math.round(parseFloat(normAbl(sE))-parseFloat(normAbl(sA))) + (sEB ? Math.round(parseFloat(normAbl(sEB))) : 0) : 0;
    const wK = wVnum * wasserPreis;
    const sK = sVnum * stromPreis;

    if (m.abr_wasseranschluss && !wInaktiv && wVnum > 0) {
      const bg = rowIdx % 2 === 0 ? "#f8fdf6" : "#ffffff";
      posHTML += `<tr style="background:${bg}">
        <td style="padding:10px 16px;border-bottom:1px solid #e8f0e5;color:#333;font-size:10.5pt">
          Wasserverbrauch ${gartenName} <span style="color:#888;font-size:9pt">(${wVnum} m³ × ${wasserPreis.toFixed(2).replace(".",",")} €/m³)</span>
        </td>
        <td style="padding:10px 16px;border-bottom:1px solid #e8f0e5;text-align:right;font-weight:600;color:#1a1a1a;font-size:10.5pt;white-space:nowrap">${wK.toFixed(2).replace(".",",")} €</td>
      </tr>`;
      total += wK; rowIdx++;
    }
    if (m.abr_stromanschluss && !sInaktiv && sVnum > 0) {
      const bg = rowIdx % 2 === 0 ? "#f8fdf6" : "#ffffff";
      posHTML += `<tr style="background:${bg}">
        <td style="padding:10px 16px;border-bottom:1px solid #e8f0e5;color:#333;font-size:10.5pt">
          Stromverbrauch ${gartenName} <span style="color:#888;font-size:9pt">(${sVnum} kWh × ${stromPreis.toFixed(4).replace(".",",")} €/kWh)</span>
        </td>
        <td style="padding:10px 16px;border-bottom:1px solid #e8f0e5;text-align:right;font-weight:600;color:#1a1a1a;font-size:10.5pt;white-space:nowrap">${sK.toFixed(2).replace(".",",")} €</td>
      </tr>`;
      total += sK; rowIdx++;
    }

    if ((m.abr_wasseranschluss || m.abr_stromanschluss) && (wVnum > 0 || sVnum > 0)) {
      ablHTML += `<tr>
        <td colspan="5" style="padding:8px 12px;background:#f0f8ed;font-weight:700;font-size:10pt;color:#2D5A27;border-bottom:1px solid #d4e8cc">${gartenName}</td>
      </tr>`;
      if (m.abr_wasseranschluss && !wInaktiv) {
        ablHTML += `<tr>
          <td style="padding:6px 12px;font-size:9.5pt;color:#555">💧 Wasser</td>
          <td style="padding:6px 12px;font-size:9.5pt;color:#555">${wA||"–"} m³</td>
          <td style="padding:6px 12px;font-size:9.5pt;color:#555">${wE||"–"}${wEB?" / "+wEB:""} m³</td>
          <td style="padding:6px 12px;font-size:9.5pt;font-weight:600">${wVnum} m³</td>
          <td style="padding:6px 12px;font-size:9.5pt;font-weight:600;color:#2D5A27">${wK.toFixed(2).replace(".",",")} €</td>
        </tr>`;
      }
      if (m.abr_stromanschluss && !sInaktiv) {
        ablHTML += `<tr>
          <td style="padding:6px 12px;font-size:9.5pt;color:#555">⚡ Strom</td>
          <td style="padding:6px 12px;font-size:9.5pt;color:#555">${sA||"–"} kWh</td>
          <td style="padding:6px 12px;font-size:9.5pt;color:#555">${sE||"–"}${sEB?" / "+sEB:""} kWh</td>
          <td style="padding:6px 12px;font-size:9.5pt;font-weight:600">${sVnum} kWh</td>
          <td style="padding:6px 12px;font-size:9.5pt;font-weight:600;color:#D4AC0D">${sK.toFixed(2).replace(".",",")} €</td>
        </tr>`;
      }
    }
  });

  const datumHeute = new Date().toLocaleDateString("de-DE", { day:"2-digit", month:"2-digit", year:"numeric" });

  return `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="utf-8">
<title>Rechnung ${rechnungsnummer}</title>
<style>
  @page { size:A4; margin:0; }
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:"Segoe UI",Arial,sans-serif; color:#1a1a1a; background:#fff; max-width:210mm; margin:0 auto; }

  /* ── Dekoratives Top-Banner ── */
  .top-stripe { height:6px; background:linear-gradient(90deg,#2D5A27 0%,#5a9e50 40%,#C8A84B 70%,#e8c86d 100%); }

  /* ── Header ── */
  .header { padding:20mm 18mm 14mm; display:flex; justify-content:space-between; align-items:flex-start; }
  .logo-area { display:flex; align-items:center; gap:14px; }
  .logo-icon { width:56px; height:56px; border-radius:50%; background:linear-gradient(135deg,#2D5A27,#5a9e50); display:flex; align-items:center; justify-content:center; font-size:26px; flex-shrink:0; }
  .org-name { font-size:17pt; font-weight:700; color:#2D5A27; letter-spacing:-0.5px; line-height:1.2; }
  .org-tagline { font-size:8.5pt; color:#888; margin-top:3px; }
  .header-meta { text-align:right; font-size:8.5pt; color:#666; line-height:1.8; }
  .header-meta strong { color:#2D5A27; font-size:9pt; }

  /* ── Trennlinie mit Blatt ── */
  .divider { margin:0 18mm; border:none; border-top:1.5px solid #e0eed9; position:relative; }
  .divider::after { content:"🌿"; position:absolute; top:-10px; left:50%; transform:translateX(-50%); background:#fff; padding:0 8px; font-size:14px; }

  /* ── Adress + Info Bereich ── */
  .addr-info-row { display:flex; justify-content:space-between; padding:10mm 18mm 8mm; gap:20mm; }
  .addr-block { font-size:10.5pt; line-height:1.9; }
  .addr-label { font-size:7pt; font-weight:700; text-transform:uppercase; letter-spacing:1px; color:#aaa; margin-bottom:6px; }

  /* Info-Karten */
  .info-cards { display:flex; flex-direction:column; gap:6px; min-width:52mm; }
  .info-card { background:#f8fdf6; border:1px solid #d4e8cc; border-radius:8px; padding:6px 12px; }
  .info-card-label { font-size:7pt; font-weight:700; text-transform:uppercase; letter-spacing:0.8px; color:#5a9e50; }
  .info-card-value { font-size:11pt; font-weight:700; color:#1a1a1a; margin-top:1px; }

  /* ── Titel ── */
  .invoice-title-area { padding:4mm 18mm 6mm; }
  .invoice-title { font-size:22pt; font-weight:800; color:#1a1a1a; letter-spacing:-1px; }
  .invoice-subtitle { font-size:10pt; color:#888; margin-top:3px; }

  /* ── Anrede ── */
  .intro { padding:2mm 18mm 6mm; font-size:10.5pt; color:#333; line-height:1.7; }

  /* ── Positionen ── */
  .positions-wrap { margin:0 18mm 6mm; border-radius:10px; overflow:hidden; border:1px solid #e0eed9; }
  .pos-header { display:flex; justify-content:space-between; background:linear-gradient(90deg,#2D5A27,#4a8c42); padding:10px 16px; }
  .pos-header span { font-size:8.5pt; font-weight:700; text-transform:uppercase; letter-spacing:1px; color:#fff; }
  .positions-wrap table { width:100%; border-collapse:collapse; }

  /* ── Gesamt ── */
  .total-bar { margin:0 18mm 6mm; background:linear-gradient(90deg,#1e3d1a,#2D5A27); border-radius:10px; padding:14px 20px; display:flex; justify-content:space-between; align-items:center; }
  .total-label { font-size:12pt; font-weight:700; color:rgba(255,255,255,0.9); }
  .total-amount { font-size:18pt; font-weight:800; color:#fff; letter-spacing:-0.5px; }
  .total-sub { font-size:8.5pt; color:rgba(255,255,255,0.6); margin-top:2px; }

  /* ── Zahlung ── */
  .payment-box { margin:0 18mm 8mm; display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .pay-card { background:#fafdf8; border:1px solid #e0eed9; border-radius:8px; padding:10px 14px; }
  .pay-card-title { font-size:7.5pt; font-weight:700; text-transform:uppercase; letter-spacing:0.8px; color:#5a9e50; margin-bottom:6px; }
  .pay-card-content { font-size:9.5pt; color:#333; line-height:1.7; }
  .pay-card-content strong { color:#1a1a1a; }

  /* ── Ablesungen ── */
  .abl-section { margin:0 18mm 8mm; }
  .abl-title { font-size:10pt; font-weight:700; color:#2D5A27; border-bottom:2px solid #c8e0c0; padding-bottom:6px; margin-bottom:8px; display:flex; align-items:center; gap:8px; }
  .abl-table { width:100%; border-collapse:collapse; font-size:9.5pt; }
  .abl-table th { background:#f0f8ed; padding:6px 12px; font-size:8pt; font-weight:700; text-transform:uppercase; letter-spacing:0.5px; color:#2D5A27; text-align:left; border-bottom:2px solid #c8e0c0; }
  .abl-table td { padding:6px 12px; border-bottom:1px solid #eef5eb; }

  /* ── Footer ── */
  .footer { border-top:3px solid #e0eed9; margin:6mm 0 0; padding:6mm 18mm; display:flex; justify-content:space-between; font-size:7.5pt; color:#888; line-height:1.7; background:#fafdf8; }
  .footer strong { display:block; color:#2D5A27; font-size:8pt; margin-bottom:2px; }
  .footer-accent { color:#C8A84B; }

  /* Print */
  .no-print { display:block; }
  @media print { .no-print { display:none !important; } }
  .print-btn { display:block; margin:8mm auto 0; padding:10px 36px; background:#2D5A27; color:#fff; border:none; border-radius:8px; font-size:12pt; cursor:pointer; font-family:inherit; font-weight:600; letter-spacing:0.3px; }
  .print-btn:hover { background:#3d7a35; }
</style>
</head>
<body>

<button class="print-btn no-print" onclick="window.print()">📄 Drucken / Als PDF speichern</button>

<!-- TOP STRIPE -->
<div class="top-stripe"></div>

<!-- HEADER -->
<div class="header">
  <div class="logo-area">
    <div class="logo-icon">🌱</div>
    <div>
      <div class="org-name">Freunde der Gartenanlage<br>Rödern e.V.</div>
      <div class="org-tagline">Gartenfreunde Rödern · Gemeinschaft &amp; Natur seit 1990</div>
    </div>
  </div>
  <div class="header-meta">
    <strong>Kontakt</strong><br>
    Kirchgasse 4<br>
    01561 Ebersbach OT Rödern<br>
    info@gartenfreunde-roedern.de<br>
    www.gartenfreunde-roedern.de
  </div>
</div>

<hr class="divider">

<!-- ADRESSE + INFO-KARTEN -->
<div class="addr-info-row">
  <div class="addr-block">
    <div class="addr-label">Rechnungsempfänger</div>
    ${anrede ? `<div style="color:#888;font-size:10pt">${anrede}</div>` : ""}
    <div style="font-size:12pt;font-weight:700">${m.vorname} ${m.nachname}</div>
    ${m.partner_name ? `<div>${m.partner_name}</div>` : ""}
    ${m.strasse ? `<div>${m.strasse}</div>` : ""}
    ${m.plz || m.ort ? `<div>${m.plz||""} ${m.ort||""}</div>` : ""}
  </div>
  <div class="info-cards">
    <div class="info-card">
      <div class="info-card-label">Rechnungsnummer</div>
      <div class="info-card-value" style="font-family:monospace;letter-spacing:0.5px">${rechnungsnummer}</div>
    </div>
    <div class="info-card">
      <div class="info-card-label">Mitgliedsnummer</div>
      <div class="info-card-value">${m.mitgliedsnummer || m.id || "–"}</div>
    </div>
    <div class="info-card">
      <div class="info-card-label">Rechnungsdatum</div>
      <div class="info-card-value">${datumHeute}</div>
    </div>
  </div>
</div>

<!-- TITEL -->
<div class="invoice-title-area">
  <div class="invoice-title">Rechnung</div>
  <div class="invoice-subtitle">Jahresabrechnung ${yearX} · ${m.vorname} ${m.nachname}</div>
</div>

<!-- ANREDE -->
<div class="intro">
  ${m.anrede === "Frau" ? "Sehr geehrte Frau" : m.anrede === "Herr" ? "Sehr geehrter Herr" : "Sehr geehrte/r"} <strong>${m.nachname}</strong>,<br>
  wir erlauben uns lt. abgestimmter Satzung vom 16.08.2025, die Jahresgebühren <strong>${yearX}</strong>
  und den Verbrauch von Nov.${yearX-1} bis Nov.${yearX} in Rechnung zu stellen:
</div>

<!-- POSITIONEN -->
<div class="positions-wrap">
  <div class="pos-header">
    <span>Leistungsbeschreibung</span>
    <span>Betrag</span>
  </div>
  <table><tbody>${posHTML}</tbody></table>
</div>

<!-- GESAMT -->
<div class="total-bar">
  <div>
    <div class="total-label">Zu zahlender Betrag</div>
    <div class="total-sub">inkl. aller Gebühren und Verbrauchskosten</div>
  </div>
  <div class="total-amount">${total.toFixed(2).replace(".",",")} €</div>
</div>

<!-- ZAHLUNGSINFO -->
<div class="payment-box">
  <div class="pay-card">
    <div class="pay-card-title">💳 Bankverbindung</div>
    <div class="pay-card-content">
      <strong>Deutsche Skatbank</strong><br>
      IBAN: DE79 8306 5408 0004 7907 15<br>
      BIC: GENODEF1SLR
    </div>
  </div>
  <div class="pay-card">
    <div class="pay-card-title">📋 Zahlungsdetails</div>
    <div class="pay-card-content">
      <strong>Verwendungszweck:</strong> ${rechnungsnummer}<br>
      <strong>Zahlungsfrist:</strong> 30 Tage nach Erhalt<br>
      <strong>Betrag:</strong> ${total.toFixed(2).replace(".",",")} €
    </div>
  </div>
</div>

<!-- ABLESUNGEN -->
${ablHTML ? `
<div class="abl-section">
  <div class="abl-title">📊 Anhang: Zählerablesungen ${yearX}</div>
  <table class="abl-table">
    <thead><tr>
      <th>Zähler</th><th>Vorjahreswert</th><th>Ablesewert</th><th>Verbrauch</th><th>Kosten</th>
    </tr></thead>
    <tbody>${ablHTML}</tbody>
  </table>
</div>` : ""}

<!-- FOOTER -->
<div class="footer">
  <div>
    <strong>Vereinsvorstand</strong>
    Vorsitzende: Christian Marx<br>
    Stellv. Vorstand: Ronny Junghanns
  </div>
  <div>
    <strong>Registergericht</strong>
    Amtsgericht Dresden<br>
    Vereinsregister: VR 5887
  </div>
  <div>
    <strong>Bankverbindung</strong>
    Deutsche Skatbank<br>
    IBAN: DE79 8306 5408 0004 7907 15<br>
    BIC: <span class="footer-accent">GENODEF1SLR</span>
  </div>
</div>

<script>
  window.onload = function() { setTimeout(function() { window.print(); }, 500); };
<\/script>
</body>
</html>`;
}

// ─── Jahresrechnungen ─────────────────────────────────────────────────────────
function Jahresrechnungen({ members, initialPreviewMember, onClosePreview }) {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMember, setSelectedMember] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [gebuehrenOpen, setGebuehrenOpen] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(null);
  const [einzelRechnungModal, setEinzelRechnungModal] = useState(null);
  const [jahrDatumModal, setJahrDatumModal] = useState(false);
  const [jahrDatumWert, setJahrDatumWert] = useState("");
  const [jahrRefreshKey, setJahrRefreshKey] = useState(0);
  const [einzelDatumModal, setEinzelDatumModal] = useState(null); // { member, rechnung }
  const [einzelDatumWert, setEinzelDatumWert] = useState("");

  const aktualisiereRechnungsnummerJahr = (alteNr, neuesDatum) => {
    // Datum (erste 8 Ziffern) ersetzen, Gartenteil + Suffix behalten
    const d = neuesDatum.replace(/-/g, "");
    const rest = String(alteNr || "").slice(8); // alles nach Datum: GartenPart + laufende Nr
    return d + (rest || "001");
  };

  const aktualisiereAlleJahrDaten = async (datum) => {
    if (!datum) return;
    const stored = JSON.parse(localStorage.getItem("gv_members") || "[]");
    const datumFormatiert = datum.split("-").reverse().join(".");
    const datePart = datum.replace(/-/g,"");

    const updated = stored.map(m => {
      const istAktiv = m.status === "Aktives Mitglied" || m.status === "Passives Mitglied";
      if (!istAktiv) return m;

      const hatRechnungFuerJahr = (m.rechnungen||[]).some(r => r.jahr === selectedYear);

      if (hatRechnungFuerJahr) {
        // Bestehende Rechnung aktualisieren
        return {
          ...m,
          rechnungen: (m.rechnungen||[]).map(r => {
            if (r.jahr !== selectedYear) return r;
            const neueNr = aktualisiereRechnungsnummerJahr(r.rechnungsnummer, datum);
            return { ...r, rechnungsnummer: neueNr, datum: datumFormatiert, name: (r.name||"").replace(/FGR_RE_[^_]+/, `FGR_RE_${neueNr}`) };
          })
        };
      } else {
        // Kein Eintrag für dieses Jahr → Platzhalter mit Datum + Rechnungsnummer anlegen
        const gartenPart = (String(m.garten||"").replace(/\D/g,"").padStart(2,"0")) + (m.zusatzgaerten||[]).map(g => String(g).replace(/\D/g,"").padStart(2,"0")).join("");
        const prefix = datePart + gartenPart;
        const alleR = stored.flatMap(x => x.rechnungen||[]);
        const vorh = alleR.filter(r => (r.rechnungsnummer||"").startsWith(prefix)).map(r => parseInt((r.rechnungsnummer||"").slice(prefix.length)) || 0);
        const laufend = vorh.length > 0 ? Math.max(...vorh) + 1 : 1;
        const neueNr = `${prefix}${String(laufend).padStart(3,"0")}`;
        return {
          ...m,
          rechnungen: [...(m.rechnungen||[]), {
            id: `r_${m.id}_${selectedYear}`,
            rechnungsnummer: neueNr,
            datum: datumFormatiert,
            jahr: selectedYear,
            titel: `Jahresrechnung ${selectedYear}`,
            betrag: "0,00",
            bezahlt: false,
            name: ""
          }]
        };
      }
    });

    localStorage.setItem("gv_members", JSON.stringify(updated));
    try {
      await fetch(`${PDF_SERVER_URL}/api/data/gv_members`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value: JSON.stringify(updated) })
      });
    } catch(e) {}
    setJahrRefreshKey(k => k + 1);
  };

  // Wenn ein Mitglied von außen übergeben wird, direkt Vorschau öffnen
  React.useEffect(() => {
    if (initialPreviewMember) {
      generatePreviewForMember(initialPreviewMember, currentYear);
    }
  }, [initialPreviewMember]);

  // Load/save fee config per year
  const loadGebuehren = (year) => { try { const all = JSON.parse(localStorage.getItem("gv_gebuehren") || "{}"); return all[year] || {}; } catch(e) { return {}; } };
  const saveGebuehren = (year, data) => { const all = JSON.parse(localStorage.getItem("gv_gebuehren") || "{}"); all[year] = data; localStorage.setItem("gv_gebuehren", JSON.stringify(all)); };
  const [gebuehren, setGebuehren] = useState(() => loadGebuehren(currentYear));

  const setGeb = (key, val) => { const updated = { ...gebuehren, [key]: val }; setGebuehren(updated); saveGebuehren(selectedYear, updated); };

  const GEB_FELDER = [
    { key: "aufnahmegebuehr",      label: "Aufnahmegebühr",                   turnus: "einmalig",           default: "50,00" },
    { key: "beitrag_person1",      label: "Mitgliedsbeitrag",       turnus: "jährlich",           default: "20,00" },
    { key: "beitrag_person2",      label: "Mitgliedsbeitrag Partner",       turnus: "jährlich",           default: "1,00" },
    { key: "grundgeb_elektro",     label: "Grundgebühr Elektro (pro Zähler)", turnus: "jährlich",           default: "3,64",  anzahlKey: "anzahl_elektro_zaehler",  anzahlLabel: "Gesamtkosten Hauptzähler" },
    { key: "strompreis_kwh",       label: "Strompreis je ct/kWh",             turnus: "jährlich",           default: "0,37" },
    { key: "grundgeb_wasser",      label: "Grundgebühr Wasser (pro Anschluss)", turnus: "jährlich",         default: "10,85", anzahlKey: "anzahl_wasser_anschluss", anzahlLabel: "Gesamtkosten Hauptanschluss" },
    { key: "wasserpreis_m3",       label: "Wasserpreis je m³",                turnus: "jährlich",           default: "2,09" },
    { key: "ruecklage_medien",     label: "Rücklage Medien-Versorgung",       turnus: "jährlich",           default: "10,00" },
    { key: "umlage_projekte",      label: "Umlage Projekte",                  turnus: "jährlich",           default: "0,00" },
    { key: "verwaltung_mitglied",  label: "Verwaltung Mitglieder",            turnus: "jährlich",           default: "5,00" },
    { key: "verwaltung_nichtmitglied", label: "Verwaltung Nichtmitglieder",   turnus: "jährlich",           default: "25,00" },
    { key: "verzugsgebuehr",       label: "Verzugsgebühr",                    turnus: "nach Fall",          default: "2,50" },
    { key: "mahngebuehr",          label: "Mahngebühr",                       turnus: "nach Fall",          default: "5,00" },
    { key: "porto",                label: "Porto",                            turnus: "nach Fall",          default: "aktueller Preis" },
    { key: "post_ruecklaeufer",    label: "Post-Rückläufer",                  turnus: "nach Fall",          default: "5,00" },
  ];

  const years = [currentYear - 1, currentYear, currentYear + 1];
  // Immer frisch aus localStorage laden damit Datums-Änderungen sofort sichtbar sind
  const aktiveMembers = React.useMemo(() => {
    const stored = (() => { try { return JSON.parse(localStorage.getItem("gv_members") || "[]"); } catch(e) { return members; } })();
    return (stored.length ? stored : members)
      .filter(m => m.status === "Aktives Mitglied" || m.status === "Passives Mitglied")
      .sort((a, b) => {
        const na = parseInt((a.garten||"").replace(/\D/g,"")) || 0;
        const nb = parseInt((b.garten||"").replace(/\D/g,"")) || 0;
        return na - nb;
      });
  }, [members, jahrRefreshKey]);

  const ABR_ITEMS = [
    { key: "abr_wasseranschluss",      label: "Wasseranschluss" },
    { key: "abr_stromanschluss",       label: "Stromanschluss" },
    { key: "abr_ruecklage_medien",     label: "Rücklage Medien-Versorgung" },
    { key: "abr_vereinsbeitrag",       label: "Vereinsbeitrag" },
    { key: "abr_gebuehr_verwaltung",   label: "Gebühr Verwaltung" },
    { key: "abr_gebuehr_nichtmitglied",label: "Gebühr Verwaltung als Nichtmitglied" },
  ];

  const getAblesungVal = (memberId, typ, period) => {
    const abl = (() => { try { return JSON.parse(localStorage.getItem("gv_ablesungen") || "{}"); } catch(e) { return {}; } })();
    const key = `${memberId}__${typ}__${selectedYear}__${period}`;
    const val = abl[key] || "";
    if (val) return val;
    if (period === "Anfang") {
      const prevB = abl[`${memberId}__${typ}__${selectedYear - 1}__EndeB`];
      if (prevB) return prevB;
      return abl[`${memberId}__${typ}__${selectedYear - 1}__Ende`] || "";
    }
    if (period === "Zaehlernummer") {
      const prevB = abl[`${memberId}__${typ}__${selectedYear - 1}__ZaehlernummerB`];
      if (prevB && prevB.trim()) return prevB;
      return abl[`${memberId}__${typ}__${selectedYear - 1}__Zaehlernummer`] || "";
    }
    if (period === "Eichdatum") {
      const prevB = abl[`${memberId}__${typ}__${selectedYear - 1}__EichdatumB`];
      if (prevB && prevB.trim()) return prevB;
      return abl[`${memberId}__${typ}__${selectedYear - 1}__Eichdatum`] || "";
    }
    return "";
  };

  const getTotalVerbrauch = (memberId, typ) => {
    const abl = (() => { try { return JSON.parse(localStorage.getItem("gv_ablesungen") || "{}"); } catch(e) { return {}; } })();
    const anfang = getAblesungVal(memberId, typ, "Anfang");
    const ende   = abl[`${memberId}__${typ}__${selectedYear}__Ende`] || "";
    const verA   = anfang && ende ? Math.round(parseFloat(normAbl(ende)) - parseFloat(normAbl(anfang))) : null;
    const endeB  = abl[`${memberId}__${typ}__${selectedYear}__EndeB`] || "";
    const verB   = endeB ? Math.round(parseFloat(normAbl(endeB))) : null;
    if (verA !== null && verB !== null) return verA + verB;
    return verA;
  };

  const generatePreviewForMember = (member, year) => {
    const yr = year || selectedYear;
    const items = ABR_ITEMS.filter(i => member[i.key]);
    const abl = (() => { try { return JSON.parse(localStorage.getItem("gv_ablesungen") || "{}"); } catch(e) { return {}; } })();
    const gaertenList = getGaerten();

    const getAbl = (gId, typ, period) => {
      const val = abl[`${gId}__${typ}__${yr}__${period}`] || "";
      if (val) return val;
      if (period === "Anfang") {
        const prevB = abl[`${gId}__${typ}__${yr-1}__EndeB`];
        if (prevB) return prevB;
        return abl[`${gId}__${typ}__${yr-1}__Ende`] || "";
      }
      return "";
    };

    // Hauptgarten für Abwärtskompatibilität
    const g = gaertenList.find(x => x.name === member.garten);
    const gId = g ? g.id : member.id;

    const wAnfang  = getAbl(gId, "wasser", "Anfang");
    const wEnde    = abl[`${gId}__wasser__${yr}__Ende`] || "";
    const wEndeB   = abl[`${gId}__wasser__${yr}__EndeB`] || "";
    const wVerbrA  = wAnfang && wEnde ? Math.round(parseFloat(normAbl(wEnde)) - parseFloat(normAbl(wAnfang))) : null;
    const wVerbrB  = wEndeB ? Math.round(parseFloat(normAbl(wEndeB))) : null;
    const wVerbr   = wVerbrA !== null && wVerbrB !== null ? (wVerbrA + wVerbrB).toString() : wVerbrA !== null ? wVerbrA.toString() : "–";
    const wZaehler = getAbl(gId, "wasser", "Zaehlernummer");
    const wEich    = getAbl(gId, "wasser", "Eichdatum");
    const sAnfang  = getAbl(gId, "strom", "Anfang");
    const sEnde    = abl[`${gId}__strom__${yr}__Ende`] || "";
    const sEndeB   = abl[`${gId}__strom__${yr}__EndeB`] || "";
    const sVerbrA  = sAnfang && sEnde ? Math.round(parseFloat(normAbl(sEnde)) - parseFloat(normAbl(sAnfang))) : null;
    const sVerbrB  = sEndeB ? Math.round(parseFloat(normAbl(sEndeB))) : null;
    const sVerbr   = sVerbrA !== null && sVerbrB !== null ? (sVerbrA + sVerbrB).toString() : sVerbrA !== null ? sVerbrA.toString() : "–";
    const sZaehler = getAbl(gId, "strom", "Zaehlernummer");
    const sEich    = getAbl(gId, "strom", "Eichdatum");
    setPreviewModal({ member, items, wAnfang, wEnde, wVerbr, wZaehler, wEich, sAnfang, sEnde, sVerbr, sZaehler, sEich });
  };

  const generatePreview = (member) => generatePreviewForMember(member, selectedYear);

  // Einzelne Rechnung für ein Mitglied erstellen
  const erstelleEinzelRechnung = async (m, neueNummer) => {
    const geb         = (() => { try { return JSON.parse(localStorage.getItem("gv_gebuehren") || "{}")[selectedYear] || {}; } catch(e) { return {}; } })();
    const abl         = (() => { try { return JSON.parse(localStorage.getItem("gv_ablesungen") || "{}"); } catch(e) { return {}; } })();
    const gaertenList = getGaerten();
    const stored      = JSON.parse(localStorage.getItem("gv_members") || "[]");

    const BETRAG_MAP = {
      abr_wasseranschluss:       { gkey: "grundgeb_wasser",          def: "10,85", label: "Wasseranschluss" },
      abr_stromanschluss:        { gkey: "grundgeb_elektro",         def: "3,64",  label: "Stromanschluss" },
      abr_ruecklage_medien:      { gkey: "ruecklage_medien",         def: "10,00", label: "Rücklage Medien-Versorgung" },
      abr_vereinsbeitrag:        { gkey: "beitrag_person1",          def: "20,00", label: "Vereinsbeitrag" },
      abr_gebuehr_verwaltung:    { gkey: "verwaltung_mitglied",      def: "5,00",  label: "Gebühr Verwaltung" },
      abr_gebuehr_nichtmitglied: { gkey: "verwaltung_nichtmitglied", def: "25,00", label: "Gebühr Verwaltung (Nichtmitglied)" },
    };

    // Rechnungsnummer bestimmen
    let rechnungsnummer;
    // Immer vorhandene Nummer behalten (Platzhalter vom 📅-Button oder bestehende Rechnung)
    const vorhandeneNr = (stored.find(x => x.id === m.id)?.rechnungen || []).find(r => r.jahr === selectedYear)?.rechnungsnummer;
    if (!neueNummer && vorhandeneNr) {
      // Bestehende/gesetzte Nummer behalten
      rechnungsnummer = vorhandeneNr;
    }
    if (!rechnungsnummer) {
      // Neue Nummer generieren — eigenen Platzhalter-Eintrag dabei ausschließen
      const heute     = new Date();
      const datePart  = `${heute.getFullYear()}${String(heute.getMonth()+1).padStart(2,"0")}${String(heute.getDate()).padStart(2,"0")}`;
      const gartenPart = (String(m.garten||"").replace(/\D/g,"").padStart(2,"0")) + (m.zusatzgaerten||[]).map(g => String(g).replace(/\D/g,"").padStart(2,"0")).join("");
      const prefix    = datePart + gartenPart;
      // Eigenen Eintrag ausschließen damit er nicht doppelt zählt
      const alleAbl   = stored.flatMap(x => x.id === m.id ? [] : (x.rechnungen || []));
      const heuteNrn  = alleAbl.filter(r => (r.rechnungsnummer||"").startsWith(prefix)).map(r => parseInt((r.rechnungsnummer||"").slice(prefix.length)) || 0);
      const laufend   = heuteNrn.length > 0 ? Math.max(...heuteNrn) + 1 : 1;
      rechnungsnummer = `${prefix}${String(laufend).padStart(3,"0")}`;
    }

    const aktivePos = Object.entries(BETRAG_MAP).filter(([key]) => m[key]);
    const total     = aktivePos.reduce((sum, [, b]) => sum + (parseFloat((geb[b.gkey] || b.def).replace(",", ".")) || 0), 0);
    const html      = buildPdfHtml(m, rechnungsnummer, geb, abl, gaertenList, selectedYear, BETRAG_MAP);
    const filename  = `FGR_RE_${rechnungsnummer}_${(m.nachname||'').trim()}_${(m.vorname||'').trim()}.pdf`.replace(/\s+/g,'_').replace(/_+\.pdf$/,'.pdf').replace(/_+/g,'_');
    const dataUrl   = "data:text/html;charset=utf-8," + encodeURIComponent(html);

    const neueRechnung = {
      id: `r_${m.id}_${selectedYear}`,
      rechnungsnummer,
      titel: `Jahresrechnung ${selectedYear}`,
      datum: (() => {
        // Gesetztes Datum der Rechnung verwenden (falls vorhanden), sonst aktuelles Datum
        const vorhandene = (stored.find(x => x.id === m.id)?.rechnungen||[]).find(r => r.jahr === selectedYear);
        if (vorhandene?.datum) return vorhandene.datum;
        const h = new Date();
        return `${String(h.getDate()).padStart(2,"0")}.${String(h.getMonth()+1).padStart(2,"0")}.${h.getFullYear()}`;
      })(),
      betrag: total.toFixed(2).replace(".", ","),
      bezahlt: false,
      jahr: selectedYear,
      name: filename,
      data: dataUrl,
      typ: "Rechnung"
    };

    // In localStorage speichern
    const updated = stored.map(x => {
      if (x.id !== m.id) return x;
      const existing = (x.rechnungen || []).filter(r => r.jahr !== selectedYear);
      return { ...x, rechnungen: [...existing, neueRechnung] };
    });
    localStorage.setItem("gv_members", JSON.stringify(updated));
    setJahrRefreshKey(k => k + 1); // Tabelle neu rendern → grüner Hintergrund

    // PDF auf Server generieren mit aktueller Vorlage
    const res2 = await fetch(`${PDF_SERVER_URL}/api/rechnung/erstellen`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ member: m, ablesungen: abl, gebuehren: geb, gaerten: gaertenList, jahr: selectedYear, rechnungsnummer })
    });
    const result = res2.ok ? await res2.json() : null;
    if (result) {
      const toast = document.createElement("div");
      toast.style.cssText = "position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#2D5A27;color:#fff;padding:12px 24px;border-radius:10px;font-size:14px;font-weight:600;font-family:inherit;box-shadow:0 4px 16px rgba(0,0,0,0.3);z-index:99999;display:flex;align-items:center;gap:10px;";
      toast.innerHTML = `<span style="font-size:20px">✅</span> Rechnung <strong>${rechnungsnummer}</strong> wurde erfolgreich erstellt!`;
      document.body.appendChild(toast);
      setTimeout(() => { toast.style.transition = "opacity 0.5s"; toast.style.opacity = "0"; setTimeout(() => document.body.removeChild(toast), 500); }, 3500);
    } else { alert("❌ Fehler beim Erstellen der Rechnung"); }
  };

  return (
    <div className="gv-page" style={S.page}>
      {confirmOverwrite && (
        <div style={{ ...S.overlay, zIndex: 2000 }}>
          <div style={{ ...S.modal, width: 440, padding: 28 }}>
            <div style={{ fontSize: 32, textAlign: "center", marginBottom: 12 }}>⚠️</div>
            <h3 style={{ margin: "0 0 12px", textAlign: "center", color: COLORS.text }}>Rechnungen bereits vorhanden</h3>
            <p style={{ fontSize: 13, color: COLORS.textMuted, textAlign: "center", lineHeight: 1.6 }}>
              Für <strong>{confirmOverwrite.count} Mitglied{confirmOverwrite.count !== 1 ? "er" : ""}</strong> existieren bereits Rechnungen für <strong>{selectedYear}</strong>:
            </p>
            <p style={{ fontSize: 13, background: "var(--cream)", border: "1px solid #F9E79F", borderRadius: 8, padding: "10px 14px", margin: "12px 0", color: COLORS.text }}>
              {confirmOverwrite.names}{confirmOverwrite.mehr}
            </p>
            <p style={{ fontSize: 13, color: COLORS.textMuted, textAlign: "center" }}>Sollen diese überschrieben werden?</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20 }}>
              <button style={S.btnGhost} onClick={() => setConfirmOverwrite(null)}>Abbrechen</button>
              <button style={S.btnPrimary} onClick={() => { confirmOverwrite.doCreate(); setConfirmOverwrite(null); }}>
                Ja, überschreiben
              </button>
            </div>
          </div>
        </div>
      )}
      {einzelRechnungModal && (() => {
        const m = einzelRechnungModal.member;
        const localEintrag = (m.rechnungen || []).find(r => r.jahr === selectedYear);
        // Nur "vorhanden" wenn Eintrag da UND PDF wirklich auf dem Server existiert
        const hatRechnung = !!einzelRechnungModal.pdfExistiertAufServer;
        const aktuelleNr  = localEintrag?.rechnungsnummer || null;
        return (
          <div style={{ ...S.overlay, zIndex: 2000 }}>
            <div style={{ ...S.modal, width: 460, padding: 28 }}>
              <div style={{ fontSize: 28, textAlign: "center", marginBottom: 12 }}>📄</div>
              <h3 style={{ margin: "0 0 8px", textAlign: "center", color: COLORS.text }}>
                Rechnung erstellen
              </h3>
              <p style={{ fontSize: 13, color: COLORS.textMuted, textAlign: "center", marginBottom: 16 }}>
                <strong>{m.vorname} {m.nachname}</strong> — {m.garten}
              </p>

              {hatRechnung ? (
                <>
                  <div style={{ background: "var(--cream)", border: "1px solid #F9E79F", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 13 }}>
                    <div style={{ fontWeight: 700, color: "#D4AC0D", marginBottom: 4 }}>⚠️ Rechnung bereits vorhanden</div>
                    <div style={{ color: COLORS.textMuted }}>RechnungsNr.: <strong>{aktuelleNr}</strong></div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                    <button style={{ ...S.btnPrimary, justifyContent: "center" }}
                      onClick={async () => {
                        setEinzelRechnungModal(null);
                        if (!jahrDatumWert && !(m.rechnungen||[]).find(r=>r.jahr===selectedYear)?.datum) {
                          alert("⚠️ Bitte zuerst ein Rechnungsdatum setzen (Button '📅 Rechnungsdatum für alle').");
                          return;
                        }
                        await erstelleEinzelRechnung(m, false);
                      }}>
                      🔄 Überschreiben (gleiche Nummer behalten)
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ fontSize: 13, color: COLORS.textMuted, textAlign: "center", marginBottom: 20 }}>
                    Es wird eine neue Rechnung für {selectedYear} erstellt.
                  </p>
                  <button style={{ ...S.btnPrimary, width: "100%", justifyContent: "center", marginBottom: 10 }}
                    onClick={async () => {
                      setEinzelRechnungModal(null);
                      const vorhandenesD = (JSON.parse(localStorage.getItem("gv_members")||"[]").find(x=>x.id===m.id)?.rechnungen||[]).find(r=>r.jahr===selectedYear)?.datum;
                      if (!vorhandenesD && !jahrDatumWert) {
                        alert("⚠️ Bitte zuerst ein Rechnungsdatum setzen (Button '📅 Rechnungsdatum für alle').");
                        return;
                      }
                      await erstelleEinzelRechnung(m, true);
                    }}>
                    ✅ Rechnung erstellen
                  </button>
                </>
              )}
              <button style={{ ...S.btnGhost, width: "100%", justifyContent: "center" }}
                onClick={() => setEinzelRechnungModal(null)}>Abbrechen</button>
            </div>
          </div>
        );
      })()}

      {previewModal && (
        <div style={{ ...S.overlay, zIndex: 1000 }}>
          <div style={{ ...S.modal, width: "min(98vw, 580px)", maxHeight: "96vh", overflowY: "auto", padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: COLORS.leaf, fontSize: 18 }}>🧾 Jahresrechnung {selectedYear}</h3>
              <button style={S.btnGhost} onClick={() => setPreviewModal(null)}>✕</button>
            </div>
            {/* Header */}
            <div style={{ background: COLORS.leaf, borderRadius: 10, padding: "16px 20px", color: "#fff", marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Gartenfreunde Rödern e.V.</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>Jahresrechnung {selectedYear}</div>
            </div>
            {/* 1-Spalten Layout: alles untereinander */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Mitglied */}
              <div style={S.card}>
                <h4 style={{ ...S.cardTitle, margin: "0 0 10px" }}>Mitglied</h4>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{previewModal.member.vorname} {previewModal.member.nachname}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                  <span style={{ background: COLORS.leafPale, border: `1px solid ${COLORS.leafLight}`, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700, color: COLORS.leaf }}>🏡 {previewModal.member.garten}</span>
                  {(previewModal.member.zusatzgaerten || []).map(g => (
                    <span key={g} style={{ background: "#F0F0F0", border: `1px solid #CCC`, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600, color: COLORS.textMuted }}>🏡 {g}</span>
                  ))}
                </div>
                {previewModal.member.strasse && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 6 }}>{previewModal.member.strasse}<br/>{previewModal.member.plz} {previewModal.member.ort}</div>}
              </div>

              {/* Ablesungen */}
              <div style={S.card}>
                <h4 style={{ ...S.cardTitle, margin: "0 0 10px" }}>Ablesungen {selectedYear}</h4>
                {(() => {
                  const abl = (() => { try { return JSON.parse(localStorage.getItem("gv_ablesungen") || "{}"); } catch(e) { return {}; } })();
                  const gaertenList = getGaerten();
                  const alleGaerten = [previewModal.member.garten, ...(previewModal.member.zusatzgaerten || [])];

                  return (
                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${alleGaerten.length}, 1fr)`, gap: 10, alignItems: "stretch" }}>
                    {alleGaerten.map(gartenName => {
                    const g = gaertenList.find(x => x.name === gartenName);
                    const gId = g ? g.id : gartenName;

                    const getA = (typ, period) => {
                      const key = `${gId}__${typ}__${selectedYear}__${period}`;
                      const val = abl[key] || "";
                      if (val) return val;
                      if (period === "Anfang") {
                        const prevB = abl[`${gId}__${typ}__${selectedYear-1}__EndeB`];
                        if (prevB) return prevB;
                        return abl[`${gId}__${typ}__${selectedYear-1}__Ende`] || "";
                      }
                      return "";
                    };

                    return (
                      <div key={gartenName} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 8, display: "flex", flexDirection: "column" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.leaf, marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
                          <span>🏡</span> {gartenName}
                        </div>

                        {/* Wasser */}
                        {(() => {
                          const wInaktiv = abl[`${gId}__wasser__${selectedYear}__inaktiv`] === "1";
                          const wAnfang = getA("wasser", "Anfang");
                          const wEnde = abl[`${gId}__wasser__${selectedYear}__Ende`] || "";
                          const wEndeB = abl[`${gId}__wasser__${selectedYear}__EndeB`] || "";
                          const wZaehler = getA("wasser", "Zaehlernummer");
                          const wEich = getA("wasser", "Eichdatum");
                          const hasWB = !!abl[`${gId}__wasser__${selectedYear}__ZaehlernummerB`];
                          const wVA = wAnfang && wEnde ? Math.round(parseFloat(normAbl(wEnde)) - parseFloat(normAbl(wAnfang))) : null;
                          const wVB = wEndeB ? Math.round(parseFloat(normAbl(wEndeB))) : null;
                          return (
                            <div style={{ background: "var(--leafPale)", borderRadius: 8, padding: "8px 12px", marginBottom: 8, opacity: wInaktiv ? 0.5 : 1 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: "#2E86C1", marginBottom: 6 }}>
                                💧 Wasserzähler {wInaktiv && <span style={{ fontSize: 10, color: "#C0392B", marginLeft: 6 }}>INAKTIV</span>}
                                {wZaehler && <span style={{ fontWeight: 400, color: COLORS.textMuted, marginLeft: 8 }}>Nr. {wZaehler}</span>}
                                {wEich && <span style={{ fontWeight: 400, color: COLORS.textMuted, marginLeft: 8 }}>Eich M {wEich}</span>}
                              </div>
                              <div style={{ fontSize: 11, display: "flex", flexDirection: "column", gap: 2 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, whiteSpace: "nowrap" }}><span style={{ color: COLORS.textMuted }}>Vorjahr</span><span>{wAnfang || "–"} m³</span></div>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, whiteSpace: "nowrap" }}><span style={{ color: COLORS.textMuted }}>Ablesewert</span><span>{wEnde || "–"} m³</span></div>
                                {hasWB ? (
                                  <>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, whiteSpace: "nowrap", fontWeight: 600 }}><span>Verbr. A</span><span>{wVA !== null ? `${wVA} m³` : "–"}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, whiteSpace: "nowrap" }}><span style={{ color: COLORS.textMuted }}>Abl. B</span><span>{wEndeB || "–"} m³</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, whiteSpace: "nowrap", fontWeight: 600 }}><span>Verbr. B</span><span>{wVB !== null ? `${wVB} m³` : "–"}</span></div>
                                    {wVA !== null && wVB !== null && <div style={{ display: "flex", justifyContent: "space-between", gap: 8, whiteSpace: "nowrap", fontWeight: 800, color: "#2E86C1", borderTop: `1px solid #AED6F1`, paddingTop: 2 }}><span>Gesamt</span><span>{wVA + wVB} m³</span></div>}
                                  </>
                                ) : (
                                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, whiteSpace: "nowrap", fontWeight: 700, color: "#2E86C1" }}><span>Verbrauch</span><span>{wVA !== null ? `${wVA} m³` : "–"}</span></div>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Strom */}
                        {(() => {
                          const sInaktiv = abl[`${gId}__strom__${selectedYear}__inaktiv`] === "1";
                          const sAnfang = getA("strom", "Anfang");
                          const sEnde = abl[`${gId}__strom__${selectedYear}__Ende`] || "";
                          const sEndeB = abl[`${gId}__strom__${selectedYear}__EndeB`] || "";
                          const sZaehler = getA("strom", "Zaehlernummer");
                          const sEich = getA("strom", "Eichdatum");
                          const hasSB = !!abl[`${gId}__strom__${selectedYear}__ZaehlernummerB`];
                          const sVA = sAnfang && sEnde ? Math.round(parseFloat(normAbl(sEnde)) - parseFloat(normAbl(sAnfang))) : null;
                          const sVB = sEndeB ? Math.round(parseFloat(normAbl(sEndeB))) : null;
                          return (
                            <div style={{ background: "var(--cream)", borderRadius: 8, padding: "8px 12px", opacity: sInaktiv ? 0.5 : 1 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: "#D4AC0D", marginBottom: 6 }}>
                                ⚡ Stromzähler {sInaktiv && <span style={{ fontSize: 10, color: "#C0392B", marginLeft: 6 }}>INAKTIV</span>}
                                {sZaehler && <span style={{ fontWeight: 400, color: COLORS.textMuted, marginLeft: 8 }}>Nr. {sZaehler}</span>}
                                {sEich && <span style={{ fontWeight: 400, color: COLORS.textMuted, marginLeft: 8 }}>Eich M {sEich}</span>}
                              </div>
                              <div style={{ fontSize: 11, display: "flex", flexDirection: "column", gap: 2 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, whiteSpace: "nowrap" }}><span style={{ color: COLORS.textMuted }}>Vorjahr</span><span>{sAnfang || "–"} kWh</span></div>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, whiteSpace: "nowrap" }}><span style={{ color: COLORS.textMuted }}>Ablesewert</span><span>{sEnde || "–"} kWh</span></div>
                                {hasSB ? (
                                  <>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, whiteSpace: "nowrap", fontWeight: 600 }}><span>Verbr. A</span><span>{sVA !== null ? `${sVA} kWh` : "–"}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, whiteSpace: "nowrap" }}><span style={{ color: COLORS.textMuted }}>Abl. B</span><span>{sEndeB || "–"} kWh</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, whiteSpace: "nowrap", fontWeight: 600 }}><span>Verbr. B</span><span>{sVB !== null ? `${sVB} kWh` : "–"}</span></div>
                                    {sVA !== null && sVB !== null && <div style={{ display: "flex", justifyContent: "space-between", gap: 8, whiteSpace: "nowrap", fontWeight: 800, color: "#D4AC0D", borderTop: `1px solid #F9E79F`, paddingTop: 2 }}><span>Gesamt</span><span>{sVA + sVB} kWh</span></div>}
                                  </>
                                ) : (
                                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, whiteSpace: "nowrap", fontWeight: 700, color: "#D4AC0D" }}><span>Verbrauch</span><span>{sVA !== null ? `${sVA} kWh` : "–"}</span></div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                    </div>
                  );
                })()}
              </div>

              {/* Abrechnungspositionen */}
              <div style={S.card}>
                <h4 style={{ ...S.cardTitle, margin: "0 0 12px" }}>Abrechnungspositionen</h4>
              {(() => {
                const gebuehren = (() => { try { return JSON.parse(localStorage.getItem("gv_gebuehren") || "{}")[selectedYear] || {}; } catch(e) { return {}; } })();
                const BETRAG_MAP = {
                  abr_wasseranschluss:       { gkey: "grundgeb_wasser",           def: "10,85" },
                  abr_stromanschluss:        { gkey: "grundgeb_elektro",          def: "3,64"  },
                  abr_ruecklage_medien:      { gkey: "ruecklage_medien",          def: "10,00" },
                  abr_vereinsbeitrag:        { gkey: "beitrag_person1",           def: "20,00" },
                  abr_gebuehr_verwaltung:    { gkey: "verwaltung_mitglied",       def: "5,00"  },
                  abr_gebuehr_nichtmitglied: { gkey: "verwaltung_nichtmitglied",  def: "25,00" },
                };

                // Calculate water and power consumption costs
                const wasserVerbr = previewModal.wVerbr !== "–" ? parseFloat(previewModal.wVerbr) : null;
                const wasserPreis = parseFloat((gebuehren.wasserpreis_m3 || "2,09").replace(",", ".")) || 0;
                const wasserKosten = wasserVerbr !== null ? wasserVerbr * wasserPreis : null;

                const stromVerbr = previewModal.sVerbr !== "–" ? parseFloat(previewModal.sVerbr) : null;
                const stromPreis = parseFloat((gebuehren.strompreis_kwh || "0,37").replace(",", ".")) || 0;
                const stromKosten = stromVerbr !== null ? stromVerbr * stromPreis : null;

                const baseTotal = previewModal.items.reduce((sum, item) => {
                  const bm = BETRAG_MAP[item.key];
                  const val = bm ? parseFloat((gebuehren[bm.gkey] || bm.def).replace(",", ".")) || 0 : 0;
                  return sum + val;
                }, 0);
                const total = baseTotal + (wasserKosten || 0) + (stromKosten || 0);

                // Build extra rows for water/power consumption
                const extraRows = [];
                if (previewModal.items.some(i => i.key === "abr_wasseranschluss")) {
                  extraRows.push({ after: "abr_wasseranschluss", label: "Wasserverbrauch", betrag: wasserKosten !== null ? wasserKosten.toFixed(2).replace(".", ",") : "–", sub: wasserVerbr !== null ? `${previewModal.wVerbr} m³ × ${(gebuehren.wasserpreis_m3 || "2,09")} €/m³` : "Kein Verbrauch eingetragen", color: "#2E86C1" });
                }
                if (previewModal.items.some(i => i.key === "abr_stromanschluss")) {
                  extraRows.push({ after: "abr_stromanschluss", label: "Stromverbrauch", betrag: stromKosten !== null ? stromKosten.toFixed(2).replace(".", ",") : "–", sub: stromVerbr !== null ? `${previewModal.sVerbr} kWh × ${(gebuehren.strompreis_kwh || "0,37")} €/kWh` : "Kein Verbrauch eingetragen", color: "#D4AC0D" });
                }

                return previewModal.items.length === 0 ? (
                  <p style={{ color: COLORS.textMuted, fontSize: 13 }}>Keine Abrechnungspositionen hinterlegt.</p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {previewModal.items.map(item => {
                      const bm = BETRAG_MAP[item.key];
                      const betrag = bm ? (gebuehren[bm.gkey] || bm.def) : "–";
                      const extra = extraRows.find(e => e.after === item.key);
                      return (
                        <React.Fragment key={item.key}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: COLORS.leafPale, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>✓ {item.label}</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.leaf }}>{betrag} €</span>
                          </div>
                          {extra && (
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px 8px 24px", background: "#F0F8FF", borderRadius: 8, border: `1px solid ${extra.color}44` }}>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>↳ {extra.label}</div>
                                <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{extra.sub}</div>
                              </div>
                              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{extra.betrag} €</span>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: COLORS.leaf, borderRadius: 8, marginTop: 4 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Gesamt</span>
                      <span style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>{total.toFixed(2).replace(".", ",")} €</span>
                    </div>
                  </div>
                );
              })()}
              </div>{/* Ende Abrechnungspositionen */}
            </div>{/* Ende flex-Spalte */}
            <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={S.btnGhost} onClick={() => { setPreviewModal(null); if (onClosePreview) onClosePreview(); }}>Schließen</button>
            </div>
          </div>
        </div>
      )}

      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>Mitglieder-Jahresrechnungen</h1>
          <p style={S.subtitle}>{aktiveMembers.length} Mitglieder · Abrechnungsjahr {selectedYear}</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ ...S.btnPrimary, fontSize: 13, padding: "8px 16px" }}
            onClick={() => { setJahrDatumWert(new Date().toISOString().split("T")[0]); setJahrDatumModal(true); }}>
            📅 Rechnungsdatum für alle
          </button>
          <button style={{ ...S.btnPrimary, display: "flex", alignItems: "center", gap: 8 }}
          onClick={() => {
            const stored = JSON.parse(localStorage.getItem("gv_members") || "[]");
            const bereitsErstellt = stored.filter(m =>
              (m.status === "Aktives Mitglied" || m.status === "Passives Mitglied") &&
              (m.rechnungen || []).some(r => r.jahr === selectedYear)
            );

            const doCreate = async () => {
              const geb = (() => { try { return JSON.parse(localStorage.getItem("gv_gebuehren") || "{}")[selectedYear] || {}; } catch(e) { return {}; } })();
              const abl = (() => { try { return JSON.parse(localStorage.getItem("gv_ablesungen") || "{}"); } catch(e) { return {}; } })();
            const gaertenList = getGaerten();
            const BETRAG_MAP = {
              abr_wasseranschluss:       { gkey: "grundgeb_wasser",          def: "10,85", label: "Wasseranschluss" },
              abr_stromanschluss:        { gkey: "grundgeb_elektro",         def: "3,64",  label: "Stromanschluss" },
              abr_ruecklage_medien:      { gkey: "ruecklage_medien",         def: "10,00", label: "Rücklage Medien-Versorgung" },
              abr_vereinsbeitrag:        { gkey: "beitrag_person1",          def: "20,00", label: "Vereinsbeitrag" },
              abr_gebuehr_verwaltung:    { gkey: "verwaltung_mitglied",      def: "5,00",  label: "Gebühr Verwaltung" },
              abr_gebuehr_nichtmitglied: { gkey: "verwaltung_nichtmitglied", def: "25,00", label: "Gebühr Verwaltung (Nichtmitglied)" },
            };


            // Ermittle höchste bestehende laufende Nummer für heute

            // Ermittle höchste bestehende laufende Nummer für heute
            const heute = new Date();
            const datePart = `${heute.getFullYear()}${String(heute.getMonth()+1).padStart(2,"0")}${String(heute.getDate()).padStart(2,"0")}`;

            const updated = stored.map(m => {
              if (m.status !== "Aktives Mitglied" && m.status !== "Passives Mitglied") return m;
              const gartenPart2 = (String(m.garten||"").replace(/\D/g,"").padStart(2,"0")) + (m.zusatzgaerten||[]).map(g => String(g).replace(/\D/g,"").padStart(2,"0")).join("");
              const prefix2 = datePart + gartenPart2;
              const alleR = stored.flatMap(x => x.rechnungen || []);
              const vorh2 = alleR.filter(r => (r.rechnungsnummer||"").startsWith(prefix2)).map(r => parseInt((r.rechnungsnummer||"").slice(prefix2.length)) || 0);
              const laufend2 = vorh2.length > 0 ? Math.max(...vorh2) + 1 : 1;
              const rechnungsnummer = `${prefix2}${String(laufend2).padStart(3,"0")}`;
              const aktivePos = Object.entries(BETRAG_MAP).filter(([key]) => m[key]);
              const total = aktivePos.reduce((sum, [, b]) => sum + (parseFloat((geb[b.gkey] || b.def).replace(",", ".")) || 0), 0);
              const htmlContent = buildPdfHtml(m, rechnungsnummer, geb, abl, gaertenList, selectedYear, BETRAG_MAP);
              const dataUrl = "data:text/html;charset=utf-8," + encodeURIComponent(htmlContent);
              const neueRechnung = {
                id: `r_${m.id}_${selectedYear}`,
                rechnungsnummer,
                titel: `Jahresrechnung ${selectedYear}`,
                datum: new Date().toISOString().split("T")[0],
                betrag: total.toFixed(2).replace(".", ","),
                bezahlt: false,
                jahr: selectedYear,
                name: `FGR_RE_${rechnungsnummer}_${(m.nachname||'').trim()}_${(m.vorname||'').trim()}.pdf`.replace(/\s+/g,'_').replace(/_+\.pdf$/,'.pdf').replace(/_+/g,'_'),
                data: dataUrl,
                typ: "Rechnung"
              };
              const existing = (m.rechnungen || []).filter(r => r.jahr !== selectedYear);
              return { ...m, rechnungen: [...existing, neueRechnung] };
            });
            localStorage.setItem("gv_members", JSON.stringify(updated));

            // PDFs sequentiell zum Server hochladen
            const zuUploaden = updated.filter(m =>
              (m.status === "Aktives Mitglied" || m.status === "Passives Mitglied") &&
              (m.rechnungen || []).some(r => r.jahr === selectedYear)
            );

            // Fortschritts-Modal anzeigen
            const progressModal = document.createElement("div");
            progressModal.style.cssText = "position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.6);display:flex;align-items:center;justify-content:center;z-index:99999;";
            progressModal.innerHTML = `<div style="background:#fff;padding:32px 48px;border-radius:12px;text-align:center;font-family:Georgia,serif;min-width:350px;box-shadow:0 8px 32px rgba(0,0,0,0.3);">
              <div style="font-size:32px;margin-bottom:16px;">📄</div>
              <div style="font-size:16px;font-weight:700;color:#2D5A27;margin-bottom:8px;" id="progress-title">PDFs werden erstellt...</div>
              <div style="font-size:13px;color:#666;margin-bottom:16px;" id="progress-name"></div>
              <div style="background:#E8F5E2;border-radius:8px;height:12px;overflow:hidden;">
                <div id="progress-bar" style="background:#2D5A27;height:100%;width:0%;transition:width 0.3s;border-radius:8px;"></div>
              </div>
              <div style="font-size:12px;color:#666;margin-top:8px;" id="progress-count">0 / ${zuUploaden.length}</div>
            </div>`;
            document.body.appendChild(progressModal);

            let erfolgreich = 0;
            let fehlgeschlagen = 0;

            for (let i = 0; i < zuUploaden.length; i++) {
              const m = zuUploaden[i];
              const rechnung = (m.rechnungen || []).find(r => r.jahr === selectedYear);
              if (!rechnung) continue;

              document.getElementById("progress-name").textContent = `${m.vorname} ${m.nachname}`;
              document.getElementById("progress-count").textContent = `${i + 1} / ${zuUploaden.length}`;
              document.getElementById("progress-bar").style.width = `${((i + 1) / zuUploaden.length) * 100}%`;

              try {
                const htmlContent = decodeURIComponent(rechnung.data.replace("data:text/html;charset=utf-8,", ""));
                const filename = rechnung.name || `FGR_RE_${rechnung.rechnungsnummer}_${(m.nachname||'').trim()}_${(m.vorname||'').trim()}.pdf`.replace(/\s+/g,'_').replace(/_+\.pdf$/,'.pdf').replace(/_+/g,'_');
                await downloadAsPdf(htmlContent, filename);
                erfolgreich++;
              } catch(e) {
                console.error("Fehler bei", m.nachname, e);
                fehlgeschlagen++;
              }
            }

            document.body.removeChild(progressModal);

            if (fehlgeschlagen > 0) {
              alert(`✅ ${erfolgreich} Rechnungen erstellt.\n⚠️ ${fehlgeschlagen} fehlgeschlagen.`);
            }
          };

            if (bereitsErstellt.length > 0) {
              const names = bereitsErstellt.slice(0, 3).map(m => `${m.vorname} ${m.nachname}`).join(", ");
              const mehr = bereitsErstellt.length > 3 ? ` und ${bereitsErstellt.length - 3} weitere` : "";
              setConfirmOverwrite({ count: bereitsErstellt.length, names, mehr, doCreate });
            } else {
              doCreate();
            }
          }}>
          🧾 Alle Rechnungen erstellen ({selectedYear})
        </button>
        </div>
      </div>

      {/* Year tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {years.map(y => (
          <button key={y} style={{ ...S.filterBtn, ...(selectedYear === y ? S.filterBtnActive : {}), fontSize: 15, padding: "8px 20px" }}
            onClick={() => { setSelectedYear(y); setGebuehren(loadGebuehren(y)); }}>{y}</button>
        ))}
      </div>

      {/* Expandable fee config */}
      <div style={{ ...S.card, marginBottom: 24, border: gebuehrenOpen ? `1.5px solid ${COLORS.leafLight}` : `1.5px solid ${COLORS.border}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
          onClick={() => setGebuehrenOpen(v => !v)}>
          <div>
            <h3 style={{ ...S.cardTitle, margin: 0 }}>💰 Gebühren & Beträge {selectedYear}</h3>
            {!gebuehrenOpen && <p style={{ fontSize: 12, color: COLORS.textMuted, margin: "4px 0 0" }}>Klicken zum Bearbeiten der Jahresbeträge</p>}
          </div>
          <span style={{ fontSize: 20, color: COLORS.textMuted, transition: "transform 0.2s", transform: gebuehrenOpen ? "rotate(180deg)" : "rotate(0deg)" }}>⌄</span>
        </div>

        {gebuehrenOpen && (
          <div style={{ marginTop: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 0, background: COLORS.leafPale, borderRadius: "8px 8px 0 0", padding: "8px 12px", border: `1px solid ${COLORS.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: COLORS.textMuted, letterSpacing: 0.7 }}>Bezeichnung</div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: COLORS.textMuted, letterSpacing: 0.7 }}>Turnus</div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: COLORS.textMuted, letterSpacing: 0.7 }}>Betrag (€)</div>
            </div>
            <div style={{ border: `1px solid ${COLORS.border}`, borderTop: "none", borderRadius: "0 0 8px 8px", overflow: "hidden" }}>
              {GEB_FELDER.map((f, i) => (
                <div key={f.key} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 0, padding: "10px 12px", background: i % 2 === 0 ? COLORS.white : "#FAFAFA", borderBottom: i < GEB_FELDER.length - 1 ? `1px solid ${COLORS.border}` : "none", alignItems: "center" }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: COLORS.text }}>
                    {f.label}
                    {f.anzahlKey && (() => {
                      const gesamtkosten = parseFloat((gebuehren[f.anzahlKey] || "0").replace(",",".")) || 0;
                      // Anzahl aktiver Zähler berechnen
                      const abl = (() => { try { return JSON.parse(localStorage.getItem("gv_ablesungen") || "{}"); } catch(e) { return {}; } })();
                      const allMembers = (() => { try { return JSON.parse(localStorage.getItem("gv_members") || "[]"); } catch(e) { return []; } })();
                      const gaertenList = getGaerten();
                      const typ = f.anzahlKey === "anzahl_elektro_zaehler" ? "strom" : "wasser";
                      let anzahlAktiv = 0;
                      allMembers.filter(m => m.status === "Aktives Mitglied" || m.status === "Passives Mitglied").forEach(m => {
                        const hatAnschluss = typ === "strom" ? m.abr_stromanschluss : m.abr_wasseranschluss;
                        if (!hatAnschluss) return;
                        const alleGaerten = [m.garten, ...(m.zusatzgaerten||[])];
                        alleGaerten.forEach(gartenName => {
                          const g = gaertenList.find(x => x.name === gartenName);
                          const gId = g ? g.id : gartenName;
                          const inaktiv = abl[`${gId}__${typ}__${selectedYear}__inaktiv`] === "1";
                          if (!inaktiv) anzahlAktiv++;
                        });
                      });
                      const proZaehler = anzahlAktiv > 0 && gesamtkosten > 0 ? (gesamtkosten / anzahlAktiv) : 0;
                      return (
                        <div style={{ marginTop: 5 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 11, color: COLORS.danger, fontWeight: 700, whiteSpace: "nowrap" }}>{f.anzahlLabel}:</span>
                            <input
                              style={{ ...S.input, fontSize: 12, padding: "3px 8px", width: 80, textAlign: "right", MozAppearance: "textfield", WebkitAppearance: "none" }}
                              type="number" min="0"
                              onWheel={e => e.target.blur()}
                              value={gebuehren[f.anzahlKey] !== undefined ? gebuehren[f.anzahlKey] : ""}
                              onChange={e => setGeb(f.anzahlKey, e.target.value)}
                              placeholder="0,00"
                            />
                            <span style={{ fontSize: 12, color: COLORS.textMuted }}>€</span>
                          </div>
                          {gesamtkosten > 0 && anzahlAktiv > 0 && (
                            <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 11, color: COLORS.textMuted }}>
                                {gesamtkosten.toFixed(2).replace(".",",")} € ÷ {anzahlAktiv} aktive Zähler = <strong style={{ color: COLORS.leaf }}>{proZaehler.toFixed(2).replace(".",",")} €</strong> pro Zähler
                              </span>
                              <button
                                style={{ fontSize: 10, padding: "2px 8px", borderRadius: 5, background: COLORS.leaf, color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, whiteSpace: "nowrap" }}
                                onClick={() => setGeb(f.key, proZaehler.toFixed(2).replace(".",","))}>
                                ↑ Übernehmen
                              </button>
                            </div>
                          )}
                          {gesamtkosten > 0 && anzahlAktiv === 0 && (
                            <div style={{ marginTop: 4, fontSize: 11, color: "#E67E22" }}>
                              ⚠ Keine aktiven Zähler gefunden
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>{f.turnus}</div>
                  <div>
                    <input
                      style={{ ...S.input, fontSize: 13, padding: "5px 10px", textAlign: "right" }}
                      value={gebuehren[f.key] !== undefined ? gebuehren[f.key] : f.default}
                      onChange={e => setGeb(f.key, e.target.value)}
                      placeholder={f.default}
                    />
                  </div>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 8, textAlign: "right" }}>Werte werden automatisch gespeichert</p>
          </div>
        )}
      </div>

      {/* Einzelrechnungs-Datum Modal */}
      {einzelDatumModal && (
        <div style={{ ...S.overlay, zIndex: 2200 }}>
          <div style={{ ...S.modal, width: 420, padding: 28 }}>
            <h3 style={{ margin: "0 0 6px", fontSize: 16, color: COLORS.text }}>📅 Datum ändern</h3>
            <p style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 16, lineHeight: 1.6 }}>
              Rechnung <strong>{einzelDatumModal.rechnung.rechnungsnummer}</strong> für <strong>{einzelDatumModal.member.vorname} {einzelDatumModal.member.nachname}</strong>.<br/>
              Die Rechnungsnummer wird automatisch angepasst (Suffix bleibt).
            </p>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: COLORS.textMuted, display: "block", marginBottom: 6, fontWeight: 600 }}>Neues Rechnungsdatum</label>
              <input type="date" value={einzelDatumWert} onChange={e => setEinzelDatumWert(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, background: "var(--cardBg)", color: COLORS.text }} />
              {einzelDatumWert && (
                <div style={{ marginTop: 8, padding: "8px 12px", background: "#f0f8ed", borderRadius: 6, fontSize: 12, color: COLORS.leaf }}>
                  Datum: <strong>{einzelDatumWert.split("-").reverse().join(".")}</strong><br/>
                  {einzelDatumModal.rechnung.rechnungsnummer ? (
                    <>Neue Rechnungsnummer: <strong>{aktualisiereRechnungsnummerJahr(einzelDatumModal.rechnung.rechnungsnummer, einzelDatumWert)}</strong></>
                  ) : (
                    <>Rechnungsnummer wird generiert: <strong style={{ fontFamily:"monospace" }}>
                    {einzelDatumWert.replace(/-/g,"")}{(String(einzelDatumModal.member.garten||"").replace(/\D/g,"").padStart(2,"0"))}{(einzelDatumModal.member.zusatzgaerten||[]).map(g=>String(g).replace(/\D/g,"").padStart(2,"0")).join("")}001
                  </strong></>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={{ ...S.btnGhost, fontSize: 13 }} onClick={() => setEinzelDatumModal(null)}>Abbrechen</button>
              <button style={{ ...S.btnPrimary, fontSize: 13 }}
                onClick={() => {
                  if (!einzelDatumWert) return;
                  const { member: mem, rechnung: rec } = einzelDatumModal;
                  const stored2 = JSON.parse(localStorage.getItem("gv_members") || "[]");
                  const datumF = einzelDatumWert.split("-").reverse().join(".");

                  if (!rec.rechnungsnummer) {
                    // Neue Rechnungsnummer aus Datum generieren
                    const datePart = einzelDatumWert.replace(/-/g,""); // JJJJMMTT
                    const gartenPart3 = (String(mem.garten||"").replace(/\D/g,"").padStart(2,"0")) + (mem.zusatzgaerten||[]).map(g => String(g).replace(/\D/g,"").padStart(2,"0")).join("");
                    const prefix3 = datePart + gartenPart3;
                    const alleAbl = stored2.flatMap(x => x.rechnungen||[]);
                    const vorhandeneNrn = alleAbl
                      .filter(r2 => (r2.rechnungsnummer||"").startsWith(prefix3))
                      .map(r2 => parseInt((r2.rechnungsnummer||"").slice(prefix3.length)) || 0);
                    const laufend = vorhandeneNrn.length > 0 ? Math.max(...vorhandeneNrn) + 1 : 1;
                    const neueNr = `${prefix3}${String(laufend).padStart(3,"0")}`;
                    const upd = stored2.map(x => {
                      if (x.id !== mem.id) return x;
                      const existing = (x.rechnungen||[]).filter(r2 => r2.jahr !== rec.jahr);
                      return { ...x, rechnungen: [...existing, { ...rec, rechnungsnummer: neueNr, datum: datumF }] };
                    });
                    localStorage.setItem("gv_members", JSON.stringify(upd));
                  } else {
                    const neueNr = aktualisiereRechnungsnummerJahr(rec.rechnungsnummer, einzelDatumWert);
                    const upd = stored2.map(x => {
                      if (x.id !== mem.id) return x;
                      return { ...x, rechnungen: (x.rechnungen||[]).map(r2 =>
                        r2.id === rec.id ? { ...r2, rechnungsnummer: neueNr, datum: datumF, name: (r2.name||"").replace(/FGR_RE_[^_]+/, `FGR_RE_${neueNr}`) } : r2
                      )};
                    });
                    localStorage.setItem("gv_members", JSON.stringify(upd));
                  }
                  setEinzelDatumModal(null);
                  setJahrRefreshKey(k => k + 1);
                }}>
                ✅ Übernehmen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rechnungsdatum Modal */}
      {jahrDatumModal && (
        <div style={{ ...S.overlay, zIndex: 2100 }}>
          <div style={{ ...S.modal, width: 400, padding: 28 }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 16, color: COLORS.text }}>📅 Rechnungsdatum für alle ({selectedYear})</h3>
            <p style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 16, lineHeight: 1.6 }}>
              Ändert das Datum aller Rechnungen für {selectedYear}. Die Rechnungsnummer wird automatisch angepasst.
            </p>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: COLORS.textMuted, display: "block", marginBottom: 6, fontWeight: 600 }}>Neues Rechnungsdatum</label>
              <input type="date" value={jahrDatumWert} onChange={e => setJahrDatumWert(e.target.value)}
                style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, background: "var(--cardBg)", color: COLORS.text }} />
              {jahrDatumWert && (
                <div style={{ marginTop: 8, padding: "8px 12px", background: "#f0f8ed", borderRadius: 6, fontSize: 12, color: COLORS.leaf }}>
                  Datum: <strong>{jahrDatumWert.split("-").reverse().join(".")}</strong> · Nummern-Datum wird auf <strong>{jahrDatumWert.replace(/-/g,"")}</strong> gesetzt
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={{ ...S.btnGhost, fontSize: 13 }} onClick={() => setJahrDatumModal(false)}>Abbrechen</button>
              <button style={{ ...S.btnPrimary, fontSize: 13 }}
                onClick={async () => { setJahrDatumModal(false); await aktualisiereAlleJahrDaten(jahrDatumWert); }}>
                ✅ Übernehmen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Member list */}
      <div style={S.tableWrap}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["Garten","Mitglied","Status","Abrechnungspositionen","Rech.-Nr.","Datum","Aktionen"].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {aktiveMembers.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: "48px", color: COLORS.textMuted }}>Keine aktiven Mitglieder</td></tr>
            ) : aktiveMembers.map(m => {
              const sc = STATUS_COLORS[m.status] || STATUS_COLORS["Aktives Mitglied"];
              const aktivePositionen = ABR_ITEMS.filter(i => m[i.key]);
              return (
                <tr key={m.id} style={{ ...S.tr, background: (m.rechnungen||[]).find(rec => rec.jahr === selectedYear && rec.name) ? "#f0faf0" : undefined, transition: "background 0.3s" }}>
                  <td style={S.td}>
                    <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                      <span style={S.gartenBadge}>{m.garten ? `Garten ${m.garten.replace(/\D/g,"")||m.garten}` : "–"}</span>
                      {(m.zusatzgaerten||[]).map(g => (
                        <span key={g} style={{ ...S.gartenBadge, fontSize:10, opacity:0.8 }}>Garten {g.replace(/\D/g,"")||g}</span>
                      ))}
                    </div>
                  </td>
                  <td style={S.td}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={S.avatar}>{m.vorname?.[0]}{m.nachname?.[0]}</div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{m.vorname} {m.nachname}</div>
                        {m.ort && <div style={{ fontSize: 12, color: COLORS.textMuted }}>{m.ort}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={S.td}>
                    <span style={{ ...S.badge, background: sc.bg, color: sc.text }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: sc.dot }} />{m.status}
                    </span>
                  </td>
                  <td style={S.td}>
                    {aktivePositionen.length === 0 ? (
                      <span style={{ fontSize: 12, color: COLORS.textMuted }}>Keine Positionen</span>
                    ) : (
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {aktivePositionen.map(p => (
                          <span key={p.key} style={{ background: COLORS.leafPale, color: COLORS.leaf, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600, border: `1px solid ${COLORS.border}` }}>
                            {p.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ ...S.td, fontFamily: "monospace", fontSize: 11 }}>
                    {(() => {
                      const r = (m.rechnungen||[]).find(rec => rec.jahr === selectedYear);
                      return r ? <span style={{ fontWeight: 600, color: COLORS.text }}>{r.rechnungsnummer}</span>
                               : <span style={{ color: COLORS.textMuted }}>–</span>;
                    })()}
                  </td>
                  <td style={S.td}>
                    {(() => {
                      const r = (m.rechnungen||[]).find(rec => rec.jahr === selectedYear);
                      if (!r) return <span style={{ fontSize: 11, color: COLORS.textMuted }}>–</span>;
                      const datum = r.datum ? (r.datum.includes("-") ? r.datum.split("-").reverse().join(".") : r.datum) : "";
                      return datum
                        ? <span style={{ fontSize: 12, color: COLORS.text }}>{datum}</span>
                        : <span style={{ fontSize: 11, color: "#E67E22", fontWeight: 600 }}>⚠ kein Datum</span>;
                    })()}
                  </td>
                  <td style={S.td}>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                      <button style={S.btnSmall} onClick={() => generatePreview(m)}>🧾 Vorschau</button>
                      <button style={{ ...S.btnSmall, background: COLORS.leaf, color: "#fff", border: "none" }}
                        onClick={async () => {
                          // Prüfen ob Datum gesetzt
                          const vorh = (m.rechnungen||[]).find(r => r.jahr === selectedYear);
                          const hatDatum = vorh?.datum || jahrDatumWert;
                          if (!hatDatum) {
                            alert("⚠️ Bitte zuerst ein Rechnungsdatum setzen.\n\nNutze den Button '📅 Rechnungsdatum für alle' oder den 📅-Button in der Zeile.");
                            return;
                          }
                          // HEAD-Check: Existiert PDF wirklich auf dem Server?
                          let pdfExistiertAufServer = false;
                          const rNr = vorh?.rechnungsnummer || "";
                          if (rNr) {
                            try {
                              // Dateinamen aus gespeichertem name oder aus Rechnungsnummer ableiten
                              const fname = vorh?.name
                                ? normalizeRechnungName(vorh.name)
                                : normalizeRechnungName(`FGR_RE_${rNr}_${(m.nachname||"").trim()}_${(m.vorname||"").trim()}.pdf`.replace(/\s+/g,"_").replace(/_+\.pdf$/,".pdf").replace(/_+/g,"_"));
                              const check = await fetch(`${PDF_SERVER_URL}/rechnungen/${vorh.jahr || selectedYear}/${fname}`, { method: "HEAD" });
                              pdfExistiertAufServer = check.ok;
                            } catch(e) { pdfExistiertAufServer = false; }
                          }
                          setEinzelRechnungModal({ member: m, pdfExistiertAufServer });
                        }}>
                        📄 Rechnung erstellen
                      </button>
                      {/* 📅 Button immer sichtbar - auch ohne Rechnung */}
                      <button style={{ ...S.btnSmall, fontSize: 11, background: "#7B5EA7", color: "#fff", border: "none" }}
                        title="Rechnungsdatum setzen"
                        onClick={() => {
                          const r = (m.rechnungen||[]).find(rec => rec.jahr === selectedYear);
                          const heute = new Date().toISOString().split("T")[0];
                          if (r) {
                            setEinzelDatumWert(r.datum ? (r.datum.includes(".")
                              ? r.datum.split(".").reverse().join("-")
                              : r.datum) : heute);
                            setEinzelDatumModal({ member: m, rechnung: r });
                          } else {
                            // Noch keine Rechnung: Datum als Vorbelegung speichern
                            setEinzelDatumWert(heute);
                            setEinzelDatumModal({ member: m, rechnung: { id: `r_${m.id}_${selectedYear}`, rechnungsnummer: "", jahr: selectedYear, datum: "" } });
                          }
                        }}>
                        📅
                      </button>
                      {(() => {
                        const r = (m.rechnungen||[]).find(rec => rec.jahr === selectedYear);
                        if (!r) return null;
                        const fname = normalizeRechnungName(r.name || `FGR_RE_${r.rechnungsnummer}.pdf`);
                        const pdfUrl = `${PDF_SERVER_URL}/rechnungen/${r.jahr}/${fname}`;
                        return (<>
                          <button style={{ ...S.btnSmall, fontSize: 11 }}
                            onClick={async () => {
                              try {
                                const check = await fetch(pdfUrl, { method: "HEAD" });
                                if (check.ok) {
                                  const modal = document.createElement("div");
                                  modal.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:9999;display:flex;align-items:center;justify-content:center";
                                  modal.innerHTML = `<div style="background:#fff;border-radius:12px;width:min(95vw,860px);height:90vh;display:flex;flex-direction:column;overflow:hidden">
                                    <div style="padding:12px 20px;border-bottom:1px solid #eee;display:flex;justify-content:space-between;align-items:center">
                                      <span style="font-weight:700;font-size:15px">🧾 Rechnung ${r.rechnungsnummer}</span>
                                      <button onclick="this.closest('[style*=fixed]').remove()" style="background:none;border:none;font-size:20px;cursor:pointer">✕</button>
                                    </div>
                                    <iframe src="${pdfUrl}" style="flex:1;border:none" title="Rechnung"></iframe>
                                  </div>`;
                                  document.body.appendChild(modal);
                                } else {
                                  alert("Rechnung noch nicht erstellt. Bitte zuerst Rechnung erstellen.");
                                }
                              } catch(e) { alert("Fehler beim Öffnen"); }
                            }}>📄 Öffnen</button>
                          <a href={pdfUrl} download={fname} target="_blank" rel="noreferrer"
                            style={{ ...S.btnSmall, background: COLORS.leaf, color: "#fff", border: "none", textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>⬇</a>
                          <button style={{ ...S.btnSmall, fontSize: 11, background: "#C0392B", color: "#fff", border: "none" }}
                            title="Rechnung löschen"
                            onClick={async () => {
                              if (!window.confirm(`Rechnung ${r.rechnungsnummer} für ${m.vorname} ${m.nachname} wirklich löschen?\n\nDies löscht:\n• Den Rechnungseintrag\n• Die PDF-Datei auf dem Server`)) return;
                              // 1. PDF auf Server löschen
                              try {
                                await fetch(`${PDF_SERVER_URL}/api/rechnung/loeschen`, {
                                  method: "DELETE", headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({ filename: fname, jahr: r.jahr })
                                });
                              } catch(e) { console.warn("PDF-Löschen fehlgeschlagen:", e); }
                              // 2. Eintrag aus localStorage entfernen
                              const stored3 = JSON.parse(localStorage.getItem("gv_members") || "[]");
                              const upd3 = stored3.map(x => {
                                if (x.id !== m.id) return x;
                                return { ...x, rechnungen: (x.rechnungen||[]).filter(rec => rec.id !== r.id) };
                              });
                              localStorage.setItem("gv_members", JSON.stringify(upd3));
                              // 3. Server-Sync
                              try {
                                await fetch(`${PDF_SERVER_URL}/api/data/gv_members`, {
                                  method: "POST", headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({ value: JSON.stringify(upd3) })
                                });
                              } catch(e) {}
                              setJahrRefreshKey(k => k + 1);
                              const toast = document.createElement("div");
                              toast.style.cssText = "position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#C0392B;color:#fff;padding:12px 24px;border-radius:10px;font-size:14px;font-weight:600;z-index:99999;box-shadow:0 4px 16px rgba(0,0,0,0.3)";
                              toast.textContent = `🗑 Rechnung ${r.rechnungsnummer} gelöscht`;
                              document.body.appendChild(toast);
                              setTimeout(() => { toast.style.opacity="0"; toast.style.transition="opacity 0.5s"; setTimeout(()=>document.body.removeChild(toast),500); }, 3000);
                            }}>
                            🗑 Löschen
                          </button>
                        </>);
                      })()}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─── Mitgliederversammlung ────────────────────────────────────────────────────
function loadMGV() { try { return JSON.parse(localStorage.getItem("gv_mgv") || "[]"); } catch(e) { return []; } }
function saveMGV(d) { localStorage.setItem("gv_mgv", JSON.stringify(d)); }

function Mitgliederversammlung({ members }) {
  const [versammlungen, setVersammlungen] = useState(loadMGV);
  const [openId, setOpenId] = useState(null);
  const [newModal, setNewModal] = useState(false);
  const [newForm, setNewForm] = useState({ datum: new Date().toISOString().slice(0,10), uhrzeit: "19:00", ort: "", titel: `Jahreshauptversammlung ${new Date().getFullYear()}` });
  const [topModal, setTopModal] = useState(null); // { vId, top }
  const [confirmDelId, setConfirmDelId] = useState(null);

  const aktiveMembers = members
    .filter(m => m.status === "Aktives Mitglied" || m.status === "Passives Mitglied")
    .sort((a,b) => (parseInt((a.garten||"").replace(/\D/g,""))||0) - (parseInt((b.garten||"").replace(/\D/g,""))||0));

  function createVersammlung() {
    if (!newForm.datum) return;
    const v = {
      id: "mgv" + Date.now(),
      datum: newForm.datum,
      uhrzeit: newForm.uhrzeit,
      ort: newForm.ort,
      titel: newForm.titel || "Mitgliederversammlung",
      anwesend: {},
      tops: [],
      bericht: "",
    };
    const updated = [v, ...versammlungen];
    saveMGV(updated);
    setVersammlungen(updated);
    setNewModal(false);
    setOpenId(v.id);
  }

  function updateV(id, changes) {
    const updated = versammlungen.map(v => v.id === id ? { ...v, ...changes } : v);
    saveMGV(updated);
    setVersammlungen(updated);
  }

  function toggleAnwesend(vId, memberId, status) {
    const v = versammlungen.find(x => x.id === vId);
    const anwesend = { ...(v.anwesend||{}), [memberId]: status };
    updateV(vId, { anwesend });
  }

  function addTop(vId) {
    const v = versammlungen.find(x => x.id === vId);
    const tops = [...(v.tops||[]), { id: "top"+Date.now(), titel: "", protokoll: "", abstimmungen: [] }];
    updateV(vId, { tops });
  }

  function updateTop(vId, topId, changes) {
    const v = versammlungen.find(x => x.id === vId);
    const tops = v.tops.map(t => t.id === topId ? { ...t, ...changes } : t);
    updateV(vId, { tops });
  }

  function deleteTop(vId, topId) {
    const v = versammlungen.find(x => x.id === vId);
    updateV(vId, { tops: v.tops.filter(t => t.id !== topId) });
  }

  function addAbstimmung(vId, topId) {
    const v = versammlungen.find(x => x.id === vId);
    const tops = v.tops.map(t => t.id === topId ? {
      ...t, abstimmungen: [...(t.abstimmungen||[]), { id: "abs"+Date.now(), frage: "", ja: 0, nein: 0, enthaltung: 0 }]
    } : t);
    updateV(vId, { tops });
  }

  function updateAbstimmung(vId, topId, absId, changes) {
    const v = versammlungen.find(x => x.id === vId);
    const tops = v.tops.map(t => t.id === topId ? {
      ...t, abstimmungen: t.abstimmungen.map(a => a.id === absId ? { ...a, ...changes } : a)
    } : t);
    updateV(vId, { tops });
  }

  function exportPDF(v) {
    const anwesendCount = Object.values(v.anwesend||{}).filter(s => s === "anwesend").length;
    const entschuldigt = Object.values(v.anwesend||{}).filter(s => s === "entschuldigt").length;
    const beschlussfaehig = anwesendCount > aktiveMembers.length / 2;

    const anwesendListe = aktiveMembers
      .filter(m => (v.anwesend||{})[m.id] === "anwesend")
      .map(m => `<li>${m.vorname} ${m.nachname} (${m.garten})</li>`).join("");

    const topHTML = (v.tops||[]).map((top, i) => `
      <h3>TOP ${i+1}: ${top.titel || "–"}</h3>
      <p>${(top.protokoll||"").replace(/\n/g,"<br>")}</p>
      ${(top.abstimmungen||[]).map(a => `
        <div style="background:#f5f5f5;padding:10px;border-radius:6px;margin:8px 0;">
          <strong>${a.frage}</strong><br>
          Ja: ${a.ja} | Nein: ${a.nein} | Enthaltung: ${a.enthaltung} →
          <strong>${parseInt(a.ja) > parseInt(a.nein) ? "✅ Angenommen" : "❌ Abgelehnt"}</strong>
        </div>
      `).join("")}
    `).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Protokoll ${v.titel}</title>
    <style>body{font-family:Georgia,serif;max-width:750px;margin:40px auto;color:#1C2B1A;}
    h1,h2,h3{color:#2D5A27;} .header{background:#2D5A27;color:white;padding:20px 24px;border-radius:8px;margin-bottom:24px;}
    ul{columns:2;} li{margin-bottom:4px;}</style></head><body>
    <div class="header"><h2 style="margin:0">${v.titel}</h2><p style="margin:4px 0 0;opacity:0.8">${new Date(v.datum+"T12:00:00").toLocaleDateString("de-DE")} · ${v.uhrzeit} Uhr · ${v.ort||"–"}</p></div>
    <h2>Anwesenheit</h2>
    <p><strong>${anwesendCount}</strong> von <strong>${aktiveMembers.length}</strong> Mitgliedern anwesend · ${entschuldigt} entschuldigt<br>
    Beschlussfähigkeit: <strong>${beschlussfaehig ? "✅ Ja" : "❌ Nein"}</strong></p>
    <ul>${anwesendListe}</ul>
    <h2>Tagesordnung & Protokoll</h2>${topHTML}
    </body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const printWindow = window.open("", "_blank");
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  }

  return (
    <div className="gv-page" style={S.page}>
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>Mitgliederversammlung</h1>
          <p style={S.subtitle}>{versammlungen.length} Versammlung{versammlungen.length !== 1 ? "en" : ""}</p>
        </div>
        <button style={S.btnPrimary} onClick={() => { setNewForm({ datum: new Date().toISOString().slice(0,10), uhrzeit: "19:00", ort: "", titel: `Jahreshauptversammlung ${new Date().getFullYear()}` }); setNewModal(true); }}>
          + Neue Versammlung
        </button>
      </div>

      {/* Confirm delete */}
      {confirmDelId && (
        <div style={{ ...S.overlay, zIndex: 1000 }}>
          <div style={S.modal}>
            <h3 style={{ color: COLORS.danger, marginBottom: 8 }}>Versammlung löschen</h3>
            <p style={{ color: COLORS.textMuted, marginBottom: 24 }}>Soll diese Versammlung wirklich gelöscht werden? Alle Protokolle und Abstimmungen gehen verloren.</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button style={S.btnGhost} onClick={() => setConfirmDelId(null)}>Abbrechen</button>
              <button style={S.btnDanger} onClick={() => { const u = versammlungen.filter(x => x.id !== confirmDelId); saveMGV(u); setVersammlungen(u); setOpenId(null); setConfirmDelId(null); }}>Löschen</button>
            </div>
          </div>
        </div>
      )}

      {/* New modal */}
      {newModal && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width: 460 }}>
            <h3 style={{ margin: "0 0 20px", color: COLORS.leaf }}>🏛️ Neue Versammlung</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div><label style={S.fieldLabel}>Datum</label><input type="date" style={S.input} value={newForm.datum} onChange={e => setNewForm(f => ({...f, datum: e.target.value}))} /></div>
              <div><label style={S.fieldLabel}>Uhrzeit</label><input type="time" style={S.input} value={newForm.uhrzeit} onChange={e => setNewForm(f => ({...f, uhrzeit: e.target.value}))} /></div>
              <div style={{ gridColumn: "span 2" }}><label style={S.fieldLabel}>Titel</label><input style={S.input} value={newForm.titel} onChange={e => setNewForm(f => ({...f, titel: e.target.value}))} placeholder="z.B. Jahreshauptversammlung 2026" /></div>
              <div style={{ gridColumn: "span 2" }}><label style={S.fieldLabel}>Ort</label><input style={S.input} value={newForm.ort} onChange={e => setNewForm(f => ({...f, ort: e.target.value}))} placeholder="z.B. Vereinsheim Rödern" /></div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={S.btnGhost} onClick={() => setNewModal(false)}>Abbrechen</button>
              <button style={S.btnPrimary} onClick={createVersammlung}>Erstellen</button>
            </div>
          </div>
        </div>
      )}

      {/* Versammlungen */}
      {versammlungen.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px", color: COLORS.textMuted }}>
          <span style={{ fontSize: 40 }}>🏛️</span>
          <p>Noch keine Versammlungen eingetragen</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {versammlungen.map(v => {
            const isOpen = openId === v.id;
            const anwesendCount = Object.values(v.anwesend||{}).filter(s => s === "anwesend").length;
            const beschlussfaehig = anwesendCount > aktiveMembers.length / 2;

            return (
              <div key={v.id} style={{ ...S.card, border: isOpen ? `1.5px solid ${COLORS.leaf}` : `1.5px solid ${COLORS.border}` }}>
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                  onClick={() => setOpenId(isOpen ? null : v.id)}>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 12, background: COLORS.leafPale, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🏛️</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 16 }}>{v.titel}</div>
                      <div style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 2 }}>
                        {new Date(v.datum+"T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })} · {v.uhrzeit} Uhr{v.ort ? ` · ${v.ort}` : ""}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {anwesendCount > 0 && (
                      <span style={{ ...S.badge, background: beschlussfaehig ? COLORS.leafPale : "var(--cardBg)", color: beschlussfaehig ? COLORS.leaf : COLORS.danger }}>
                        {anwesendCount}/{aktiveMembers.length} · {beschlussfaehig ? "Beschlussfähig ✓" : "Nicht beschlussfähig"}
                      </span>
                    )}
                    <span style={{ fontSize: 20, color: COLORS.textMuted, transform: isOpen ? "rotate(180deg)" : "none", transition: "0.2s" }}>⌄</span>
                  </div>
                </div>

                {/* Content */}
                {isOpen && (
                  <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 24 }}>

                    {/* Anwesenheit */}
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <h3 style={{ ...S.cardTitle, margin: 0 }}>✅ Anwesenheit</h3>
                        <div style={{ fontSize: 13, color: COLORS.textMuted }}>
                          <span style={{ fontWeight: 700, color: COLORS.leaf }}>{anwesendCount}</span> anwesend ·
                          <span style={{ fontWeight: 700, color: "#E67E22", marginLeft: 6 }}>{Object.values(v.anwesend||{}).filter(s=>s==="entschuldigt").length}</span> entschuldigt ·
                          <span style={{ marginLeft: 6 }}>{aktiveMembers.length - anwesendCount} fehlend</span>
                        </div>
                      </div>

                      {/* Vorstand-Sektion */}
                      {(() => {
                        const vorstand = getVorstandNamen().length > 0 ? getVorstandNamen() : VORSTAND_FALLBACK;
                        if (vorstand.length === 0) return null;
                        return (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Vorstand</div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 }}>
                              {vorstand.map(name => {
                                const key = "vorstand__" + name;
                                const status = (v.anwesend||{})[key] || "offen";
                                return (
                                  <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 9, background: status === "anwesend" ? "var(--leafPale)" : status === "entschuldigt" ? "var(--cream)" : "var(--tableStripe)", border: `1px solid ${status === "anwesend" ? COLORS.leaf : status === "entschuldigt" ? "#E67E22" : COLORS.border}` }}>
                                    <div style={{ ...S.avatar, background: COLORS.leaf, color: "#fff", flexShrink: 0 }}>{name[0]}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                                      <div style={{ fontSize: 11, color: COLORS.textMuted }}>Vorstand</div>
                                    </div>
                                    <div style={{ display: "flex", gap: 4 }}>
                                      {[
                                        { val: "anwesend",     icon: "✓", color: COLORS.leaf,     bg: COLORS.leafPale },
                                        { val: "entschuldigt", icon: "E", color: "#E67E22",        bg: "var(--cream)" },
                                        { val: "offen",        icon: "–", color: COLORS.textMuted, bg: "var(--tableStripe)" },
                                      ].map(opt => (
                                        <button key={opt.val}
                                          style={{ width: 28, height: 28, borderRadius: 6, border: `1.5px solid ${status === opt.val ? opt.color : COLORS.border}`, background: status === opt.val ? opt.bg : "var(--cardBg)", color: opt.color, fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                                          onClick={() => toggleAnwesend(v.id, key, opt.val)}
                                        >{opt.icon}</button>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <div style={{ height: 1, background: COLORS.border, margin: "12px 0" }} />
                            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Mitglieder</div>
                          </div>
                        );
                      })()}

                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 }}>
                        {aktiveMembers.map(m => {
                          const status = (v.anwesend||{})[m.id] || "offen";
                          return (
                            <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 9, background: status === "anwesend" ? "var(--leafPale)" : status === "entschuldigt" ? "var(--cream)" : "var(--tableStripe)", border: `1px solid ${status === "anwesend" ? COLORS.leaf : status === "entschuldigt" ? "#E67E22" : COLORS.border}` }}>
                              <div style={S.avatar}>{m.vorname?.[0]}{m.nachname?.[0]}</div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.vorname} {m.nachname}</div>
                                <div style={{ fontSize: 11, color: COLORS.textMuted }}>{m.garten}</div>
                              </div>
                              <div style={{ display: "flex", gap: 4 }}>
                                {[
                                  { val: "anwesend",    icon: "✓", color: COLORS.leaf,    bg: COLORS.leafPale },
                                  { val: "entschuldigt",icon: "E", color: "#E67E22",       bg: "var(--cream)" },
                                  { val: "offen",       icon: "–", color: COLORS.textMuted,bg: "var(--tableStripe)" },
                                ].map(opt => (
                                  <button key={opt.val}
                                    style={{ width: 28, height: 28, borderRadius: 6, border: `1.5px solid ${status === opt.val ? opt.color : COLORS.border}`, background: status === opt.val ? opt.bg : COLORS.white, color: opt.color, fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                                    onClick={() => toggleAnwesend(v.id, m.id, opt.val)}
                                  >{opt.icon}</button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Tagesordnung */}
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <h3 style={{ ...S.cardTitle, margin: 0 }}>📋 Tagesordnung & Protokoll</h3>
                        <button style={S.btnSmall} onClick={() => addTop(v.id)}>+ TOP hinzufügen</button>
                      </div>
                      {(v.tops||[]).length === 0 ? (
                        <p style={{ color: COLORS.textMuted, fontSize: 13 }}>Noch keine Tagesordnungspunkte</p>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                          {(v.tops||[]).map((top, idx) => (
                            <div key={top.id} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: COLORS.leafPale }}>
                                <span style={{ fontWeight: 800, color: COLORS.leaf, fontSize: 14, flexShrink: 0 }}>TOP {idx+1}</span>
                                <input
                                  style={{ ...S.input, flex: 1, background: "transparent", border: `1px solid ${COLORS.border}`, padding: "5px 10px" }}
                                  value={top.titel}
                                  onChange={e => updateTop(v.id, top.id, { titel: e.target.value })}
                                  placeholder="Titel des Tagesordnungspunkts…"
                                />
                                <button style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.textMuted, fontSize: 16 }}
                                  onClick={() => deleteTop(v.id, top.id)}>🗑️</button>
                              </div>
                              <div style={{ padding: "12px 16px" }}>
                                <label style={{ ...S.fieldLabel, marginBottom: 6 }}>Protokoll</label>
                                <textarea
                                  style={{ ...S.input, height: 80, resize: "vertical", width: "100%", boxSizing: "border-box" }}
                                  value={top.protokoll||""}
                                  onChange={e => updateTop(v.id, top.id, { protokoll: e.target.value })}
                                  placeholder="Protokoll zu diesem Tagesordnungspunkt…"
                                />
                                {/* Abstimmungen */}
                                <div style={{ marginTop: 10 }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.7 }}>🗳️ Abstimmungen</span>
                                    <button style={{ ...S.btnSmall, fontSize: 11 }} onClick={() => addAbstimmung(v.id, top.id)}>+ Abstimmung</button>
                                  </div>
                                  {(top.abstimmungen||[]).map(abs => {
                                    const ja = parseInt(abs.ja)||0;
                                    const nein = parseInt(abs.nein)||0;
                                    const enth = parseInt(abs.enthaltung)||0;
                                    const gesamt = ja + nein + enth;
                                    const angenommen = ja > nein;
                                    return (
                                      <div key={abs.id} style={{ background: "#F8F9FA", borderRadius: 8, padding: 12, marginBottom: 8, border: `1px solid ${COLORS.border}` }}>
                                        <input
                                          style={{ ...S.input, marginBottom: 8, fontSize: 13 }}
                                          value={abs.frage}
                                          onChange={e => updateAbstimmung(v.id, top.id, abs.id, { frage: e.target.value })}
                                          placeholder="Abstimmungsfrage…"
                                        />
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, alignItems: "center" }}>
                                          {[
                                            { key: "ja",         label: "Ja",          color: COLORS.leaf },
                                            { key: "nein",       label: "Nein",        color: COLORS.danger },
                                            { key: "enthaltung", label: "Enthaltung",  color: COLORS.textMuted },
                                          ].map(opt => (
                                            <div key={opt.key}>
                                              <div style={{ fontSize: 10, fontWeight: 700, color: opt.color, textTransform: "uppercase", marginBottom: 3 }}>{opt.label}</div>
                                              <input
                                                type="number" min="0"
                                                style={{ ...S.input, fontSize: 14, fontWeight: 700, textAlign: "center", color: opt.color }}
                                                value={abs[opt.key]||0}
                                                onChange={e => updateAbstimmung(v.id, top.id, abs.id, { [opt.key]: e.target.value })}
                                              />
                                            </div>
                                          ))}
                                          <div style={{ textAlign: "center" }}>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 3 }}>Ergebnis</div>
                                            <div style={{ padding: "8px 4px", borderRadius: 7, background: gesamt > 0 ? (angenommen ? COLORS.leafPale : "var(--cardBg)") : "var(--tableStripe)", fontSize: 11, fontWeight: 800, color: gesamt > 0 ? (angenommen ? COLORS.leaf : COLORS.danger) : COLORS.textMuted }}>
                                              {gesamt > 0 ? (angenommen ? "✅ Ja" : "❌ Nein") : "–"}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Aktionen */}
                    <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 8, borderTop: `1px solid ${COLORS.border}` }}>
                      <button style={{ ...S.btnGhost, color: COLORS.danger, borderColor: COLORS.danger }}
                        onClick={() => setConfirmDelId(v.id)}>
                        🗑️ Löschen
                      </button>
                      <button style={S.btnPrimary} onClick={() => exportPDF(v)}>📄 Protokoll exportieren</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}




// ─── Mitglieder Archiv ────────────────────────────────────────────────────────
function MitgliederArchiv({ members, onRestore }) {
  const [searchTerm, setSearchTerm] = React.useState("");
  
  const archived = members.filter(m => m.status === "Ehemaliges Mitglied");
  
  // Filter by search term (name or garten)
  const filtered = archived.filter(m => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const name = `${m.vorname} ${m.nachname}`.toLowerCase();
    const garten = (m.garten || "").toLowerCase();
    return name.includes(term) || garten.includes(term);
  });

  // Group by garten
  const byGarten = {};
  filtered.forEach(m => {
    const g = m.garten || "Ohne Garten";
    if (!byGarten[g]) byGarten[g] = [];
    byGarten[g].push(m);
  });

  const allGaerten = Object.keys(byGarten).sort((a, b) => {
    const aNum = parseInt(a.replace(/\D/g, "")) || 9999;
    const bNum = parseInt(b.replace(/\D/g, "")) || 9999;
    return aNum - bNum;
  });

  return (
    <div className="gv-page" style={S.page}>
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>Mitglieder-Archiv</h1>
          <p style={S.subtitle}>{archived.length} ehemalige{archived.length !== 1 ? "" : "s"} Mitglied{archived.length !== 1 ? "er" : ""}</p>
        </div>
        <input 
          type="text" 
          placeholder="Suche nach Name oder Garten..." 
          style={{ ...S.input, width: 280 }} 
          value={searchTerm} 
          onChange={e => setSearchTerm(e.target.value)} 
        />
      </div>

      {archived.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: COLORS.textMuted }}>
          <span style={{ fontSize: 48 }}>📦</span>
          <p style={{ marginTop: 16 }}>Keine archivierten Mitglieder</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: COLORS.textMuted }}>
          <span style={{ fontSize: 48 }}>🔍</span>
          <p style={{ marginTop: 16 }}>Keine Ergebnisse für "{searchTerm}"</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 20 }}>
          {allGaerten.map(garten => (
            <div key={garten} style={S.card}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, paddingBottom: 12, borderBottom: `2px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 32 }}>🏡</div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{garten}</h3>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
                    {byGarten[garten].length} {byGarten[garten].length === 1 ? "ehemaliges Mitglied" : "ehemalige Mitglieder"}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {byGarten[garten].sort((a, b) => {
                  const aDate = a.austrittsdatum || "";
                  const bDate = b.austrittsdatum || "";
                  return bDate.localeCompare(aDate);
                }).map(m => (
                  <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, background: "var(--cardBg)", borderRadius: 8, border: `1px solid ${COLORS.border}`, cursor: "pointer", transition: "background 0.2s" }}
                    onClick={() => onRestore(m.id)}
                    onMouseEnter={e => e.currentTarget.style.background = "#F0F0F0"}
                    onMouseLeave={e => e.currentTarget.style.background = "var(--tableStripe)"}>
                    <div style={{ ...S.avatar, width: 32, height: 32, fontSize: 12 }}>{m.vorname?.[0]}{m.nachname?.[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{m.vorname} {m.nachname}</div>
                      <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                        {m.eintrittsdatum && new Date(m.eintrittsdatum + "T12:00:00").toLocaleDateString("de-DE", { year: "numeric", month: "2-digit" })} 
                        {m.eintrittsdatum && m.austrittsdatum && " – "}
                        {m.austrittsdatum && new Date(m.austrittsdatum + "T12:00:00").toLocaleDateString("de-DE", { year: "numeric", month: "2-digit" })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Gärten Verwaltung ────────────────────────────────────────────────────────
// All gardens in the Gartenverein
const ALLE_GAERTEN = {
  "Unterer Weg": [8, 16, 18, 20, 21, 22, 23, 27, 29],
  "Mittlerer Weg": [30, 31, 32, 33, 34, 35, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76],
  "Oberer Weg": [78, 79, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, "111a", "111b", 112, 113, 114, 115, 117, 118, 119, 120, 121, 122, 123, 124, 125]
};

function getGaerten() {
  try {
    return JSON.parse(localStorage.getItem("gv_gaerten") || "[]");
  } catch(e) {
    return [];
  }
}

function saveGaerten(data) {
  localStorage.setItem("gv_gaerten", JSON.stringify(data));
}

function GartenList({ onSelect }) {
  const [gaerten, setGaerten] = React.useState(getGaerten());
  const [members, setMembers] = React.useState(() => JSON.parse(localStorage.getItem("gv_members") || "[]"));

  // Aktualisiere Members wenn localStorage sich ändert (z.B. nach Zusatzgarten-Zuweisung)
  React.useEffect(() => {
    const handler = () => setMembers(JSON.parse(localStorage.getItem("gv_members") || "[]"));
    window.addEventListener("storage", handler);
    // Auch beim Fokus neu laden (gleiche Tab-Änderungen)
    window.addEventListener("focus", handler);
    return () => { window.removeEventListener("storage", handler); window.removeEventListener("focus", handler); };
  }, []);
  
  // Initialize all gardens from ALLE_GAERTEN
  React.useEffect(() => {
    const existingIds = new Set(gaerten.map(g => g.id));
    const newGaerten = [...gaerten];
    
    // Create all gardens from ALLE_GAERTEN
    Object.entries(ALLE_GAERTEN).forEach(([weg, nummern]) => {
      nummern.forEach(num => {
        const gartenName = `Garten ${num}`;
        const gartenId = `g${num}`;
        
        if (!existingIds.has(gartenId)) {
          newGaerten.push({
            id: gartenId,
            name: gartenName,
            weg: weg,
            typ: "Pachtgarten",
            groesse: 0
          });
        } else {
          // Update weg if missing
          const existing = newGaerten.find(g => g.id === gartenId);
          if (existing && !existing.weg) {
            existing.weg = weg;
          }
        }
      });
    });
    
    if (newGaerten.length > gaerten.length || newGaerten.some(g => !g.weg)) {
      // Sort by weg order and then by number
      const wegOrder = ["Unterer Weg", "Mittlerer Weg", "Oberer Weg"];
      newGaerten.sort((a, b) => {
        const wegA = wegOrder.indexOf(a.weg || "Sonstige");
        const wegB = wegOrder.indexOf(b.weg || "Sonstige");
        if (wegA !== wegB) return wegA - wegB;
        
        const numA = parseInt(a.name.replace(/[^\d]/g, "")) || 0;
        const numB = parseInt(b.name.replace(/[^\d]/g, "")) || 0;
        return numA - numB;
      });
      
      saveGaerten(newGaerten);
      setGaerten(newGaerten);
    }
  }, []);

  const gartenMitglied = (gartenName) => {
    return members.find(m => 
      m.status !== "Ehemaliges Mitglied" && (
        m.garten === gartenName || 
        (m.zusatzgaerten || []).includes(gartenName)
      )
    );
  };

  const istZusatzgarten = (gartenName) => {
    return members.some(m =>
      m.status !== "Ehemaliges Mitglied" &&
      (m.zusatzgaerten || []).includes(gartenName)
    );
  };

  // Group by weg
  const byWeg = {};
  gaerten.forEach(g => {
    const weg = g.weg || "Sonstige";
    if (!byWeg[weg]) byWeg[weg] = [];
    byWeg[weg].push(g);
  });

  const wegOrder = ["Unterer Weg", "Mittlerer Weg", "Oberer Weg"];

  return (
    <div className="gv-page" style={S.page}>
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>Gärten</h1>
          <p style={S.subtitle}>{gaerten.length} Gärten in 3 Wegen</p>
        </div>
      </div>

      {wegOrder.map(weg => {
        if (!byWeg[weg] || byWeg[weg].length === 0) return null;
        
        return (
          <div key={weg} style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.text, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                <defs>
                  <pattern id="cobbles" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
                    <rect x="0.5" y="0.5" width="2" height="2" fill="#D5D5D5" stroke="#999" strokeWidth="0.3" rx="0.3"/>
                    <rect x="3" y="0.5" width="2.5" height="2" fill="#C8C8C8" stroke="#999" strokeWidth="0.3" rx="0.3"/>
                    <rect x="0.5" y="3" width="2.5" height="2.5" fill="#CFCFCF" stroke="#999" strokeWidth="0.3" rx="0.3"/>
                    <rect x="3.5" y="3" width="2" height="2.5" fill="#D8D8D8" stroke="#999" strokeWidth="0.3" rx="0.3"/>
                  </pattern>
                </defs>
                <path d="M 2 8 Q 6 9, 10 8 T 18 8 L 22 8 L 22 17 Q 18 16, 14 17 T 6 17 L 2 17 Z" fill="url(#cobbles)" stroke="#AAA" strokeWidth="0.5"/>
              </svg>
              {weg} 
              <span style={{ fontSize: 13, fontWeight: 400, color: COLORS.textMuted }}>({byWeg[weg].length} Gärten)</span>
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
              {byWeg[weg].map(g => {
                const mitglied = gartenMitglied(g.name);
                const isZusatz = mitglied && istZusatzgarten(g.name);
                const bgColor = mitglied ? "var(--leafPale)" : "var(--cardBg)";
                return (
                  <div key={g.id} style={{ ...S.card, background: bgColor, cursor: "pointer", transition: "transform 0.2s, box-shadow 0.2s" }} 
                    onClick={() => onSelect(g)}
                    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                      <div style={{ fontSize: 32 }}>🏡</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{g.name}</h3>
                          {isZusatz && <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 10, background: "var(--leafPale)", color: COLORS.leaf, border: `1px solid ${COLORS.leafLight}` }}>Zusatz</span>}
                        </div>
                        <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
                          {g.typ} {g.groesse > 0 ? `· ${g.groesse} m²` : ""}
                        </div>
                      </div>
                    </div>
                    {mitglied ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "var(--cardBg)", borderRadius: 8, fontSize: 13, border: `1px solid ${COLORS.border}` }}>
                        <div style={S.avatar}>{mitglied.vorname?.[0]}{mitglied.nachname?.[0]}</div>
                        <span>{mitglied.vorname} {mitglied.nachname}</span>
                      </div>
                    ) : (
                      <div style={{ padding: "8px 10px", background: "var(--cardBg)", borderRadius: 8, fontSize: 13, color: COLORS.textMuted, textAlign: "center", border: "1px solid #FFE0E0" }}>
                        Kein Mitglied zugeordnet
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GartenDetail({ garten, members, onBack, onEdit }) {
  const isMobile = localStorage.getItem("gv_viewmode") === "mobile";
  const [g, setG] = React.useState(garten);
  const [year, setYear] = React.useState(new Date().getFullYear());
  const [ablState, setAblState] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem("gv_ablesungen") || "{}"); } catch(e) { return {}; }
  });

  const mitglied = members.find(m => m.garten === g.name && m.status !== "Ehemaliges Mitglied");

  function saveGarten(updates) {
    const gaerten = getGaerten();
    const updated = gaerten.map(x => x.id === g.id ? { ...x, ...updates } : x);
    saveGaerten(updated);
    setG({ ...g, ...updates });
  }

  function getV(typ, period, yr = year) {
    return ablState[`${g.id}__${typ}__${yr}__${period}`] || "";
  }

  function setV(typ, period, val, yr = year) {
    const key = `${g.id}__${typ}__${yr}__${period}`;
    const updated = { ...ablState, [key]: val };
    localStorage.setItem("gv_ablesungen", JSON.stringify(updated));
    setAblState(updated);
  }

  function delV(typ, period, yr = year) {
    const key = `${g.id}__${typ}__${yr}__${period}`;
    const updated = { ...ablState };
    delete updated[key];
    localStorage.setItem("gv_ablesungen", JSON.stringify(updated));
    setAblState(updated);
  }

  function delVMulti(typ, periods, yr = year) {
    const updated = { ...ablState };
    periods.forEach(p => { delete updated[`${g.id}__${typ}__${yr}__${p}`]; });
    localStorage.setItem("gv_ablesungen", JSON.stringify(updated));
    setAblState(updated);
  }

  function checkEich(val, typ) {
    if (!val || val === "–") return null;
    const eichJahr = 2000 + parseInt(val);
    const gueltig = typ === "wasser" ? eichJahr + 6 : eichJahr + 16;
    const cur = new Date().getFullYear();
    if (gueltig < cur) return "abgelaufen";
    if (gueltig === cur) return "laeuft_ab";
    if (gueltig === cur + 1) return "bald";
    return null;
  }

  function eichColor(w) {
    if (w === "abgelaufen") return COLORS.danger;
    if (w === "laeuft_ab") return "#E67E22";
    if (w === "bald") return "#D4AC0D";
    return null;
  }

  function eichIcon(w) {
    if (w === "abgelaufen") return "⚠️";
    if (w === "laeuft_ab") return "⚠️";
    if (w === "bald") return "⏰";
    return "";
  }

  const years = [year - 1, year, year + 1];

  return (
    <div className="gv-page" style={S.page}>
      <div style={S.pageHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button style={S.backBtn} onClick={onBack}>← Zurück</button>
          <div>
            <h1 style={S.h1}>{g.name}</h1>
            <p style={S.subtitle}>{g.typ} {g.groesse > 0 ? `· ${g.groesse} m²` : ""}</p>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "360px 1fr", gap: 20, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
          <div style={S.card}>
            <h3 style={S.cardTitle}>Garten-Informationen</h3>
            <div style={{ display: "grid", gap: 16 }}>
              <div>
                <label style={S.fieldLabel}>Gartentyp</label>
                <select style={S.input} value={g.typ || ""} onChange={e => saveGarten({ typ: e.target.value })}>
                  <option value="" disabled>— Bitte wählen —</option>
                  <option value="Pachtgarten">Pachtgarten</option>
                  <option value="Privatgarten">Privatgarten</option>
                </select>
              </div>
              <div>
                <label style={S.fieldLabel}>Größe (m²)</label>
                <input type="number" style={S.input} value={g.groesse || ""} onChange={e => saveGarten({ groesse: parseInt(e.target.value) || 0 })} placeholder="0" />
              </div>
            </div>
          </div>

          <div style={S.card}>
            <h3 style={S.cardTitle}>Aktuelles Mitglied</h3>
            {mitglied ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={S.avatar}>{mitglied.vorname?.[0]}{mitglied.nachname?.[0]}</div>
                <div>
                  <div style={{ fontWeight: 700 }}>{mitglied.vorname} {mitglied.nachname}</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>Seit {mitglied.eintrittsdatum ? new Date(mitglied.eintrittsdatum).toLocaleDateString("de-DE") : "—"}</div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: 20, color: COLORS.textMuted }}>
                Kein Mitglied zugeordnet
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
          <div style={{ ...S.card, minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ ...S.cardTitle, margin: 0 }}>Zählerstände {year}</h3>
              <div style={{ display: "flex", gap: 8 }}>
                {years.map(y => (
                  <button key={y} style={{ ...S.btnGhost, ...(y === year ? { background: COLORS.leaf, color: "#fff", borderColor: COLORS.leaf } : {}), padding: "4px 12px", fontSize: 13 }} onClick={() => setYear(y)}>{y}</button>
                ))}
              </div>
            </div>

            {["wasser", "strom"].map(typ => {
              const isWasser   = typ === "wasser";
              const color      = isWasser ? "#2E86C1" : "#D4AC0D";
              const bg         = isWasser ? "var(--leafPale)" : "var(--cream)";
              const border     = isWasser ? "#AED6F1" : "#F9E79F";
              const unit       = isWasser ? "m³" : "kWh";

              // Zähler A (Hauptzähler)
              const inaktiv    = getV(typ, "inaktiv") === "1";
              const zaehlernr  = getV(typ, "Zaehlernummer");
              const eichdatum  = getV(typ, "Eichdatum");
              const anfang     = getV(typ, "Anfang") || getV(typ, "Ende", year - 1) || "";
              const ende       = getV(typ, "Ende");
              const eichStatus = checkEich(eichdatum, typ);
              const eColor     = eichColor(eichStatus);

              // Zähler B (nach Wechsel)
              const hasWechsel  = !!getV(typ, "ZaehlernummerB");
              const zaehlernrB  = getV(typ, "ZaehlernummerB");
              const eichdatumB  = getV(typ, "EichdatumB");
              const wechseldatum = getV(typ, "WechseldatumB");
              const endeB       = getV(typ, "EndeB");
              const eichStatusB = checkEich(eichdatumB, typ);
              const eColorB     = eichColor(eichStatusB);

              // Verbrauch
              const verbrA = anfang && ende ? Math.max(0, parseFloat(normAbl(ende)) - parseFloat(normAbl(anfang))) : null;
              const verbrB = endeB ? Math.max(0, parseFloat(normAbl(endeB))) : null;
              // Bei Zählerwechsel: wenn kein Anfang aber Ende vorhanden, verbrA = 0
              const verbrAeff = hasWechsel && ende && verbrA === null ? Math.max(0, parseFloat(normAbl(ende)) - 0) : verbrA;
              const verbrGesamt = hasWechsel
                ? (verbrAeff !== null || verbrB !== null ? (verbrAeff || 0) + (verbrB || 0) : null)
                : verbrA !== null ? verbrA : null;

              // Im Folgejahr: wenn Wechsel im Vorjahr, Anfang = EndeB des Vorjahres
              const prevHasWechsel = !!getV(typ, "ZaehlernummerB", year - 1);
              const anfangAuto = prevHasWechsel
                ? getV(typ, "EndeB", year - 1)
                : getV(typ, "Ende", year - 1);

              const fieldStyle = (extra = {}) => ({
                ...S.input, fontSize: 13,
                boxSizing: "border-box", minWidth: 0, width: "100%",
                ...(inaktiv && !hasWechsel ? { opacity: 0.4, pointerEvents: "none", background: "#eee" } : {}),
                ...extra
              });

              return (
                <div key={typ} style={{ marginBottom: 20, padding: 16, background: bg, border: `1.5px solid ${border}`, borderRadius: 10, opacity: inaktiv && !hasWechsel ? 0.75 : 1, width: "100%", boxSizing: "border-box" }}>

                  {/* Header */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 20 }}>{isWasser ? "💧" : "⚡"}</span>
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, textTransform: "uppercase", color: inaktiv && !hasWechsel ? COLORS.textMuted : color }}>
                      {isWasser ? "Wasserzähler" : "Stromzähler"}
                      {inaktiv && !hasWechsel && <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 8, color: COLORS.textMuted }}>(inaktiv)</span>}
                    </h4>
                    <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {/* Zählerwechsel-Button (nur wenn kein Wechsel & nicht inaktiv) */}
                      {!hasWechsel && !inaktiv && (
                        <button
                          style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, border: `1px solid ${color}`, background: "transparent", color, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
                          onClick={() => setV(typ, "ZaehlernummerB", " ")}
                        >🔄 Zählerwechsel</button>
                      )}
                      {/* Inaktiv/Aktiv toggle */}
                      {!hasWechsel && (
                        <button
                          style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, border: `1px solid ${inaktiv ? COLORS.leaf : COLORS.danger}`, background: "transparent", color: inaktiv ? COLORS.leaf : COLORS.danger, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
                          onClick={() => inaktiv ? delV(typ, "inaktiv") : setV(typ, "inaktiv", "1")}
                        >{inaktiv ? "✓ Aktivieren" : "⊘ Inaktiv"}</button>
                      )}
                    </div>
                  </div>

                  {/* Zähler A Felder */}
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, ...(inaktiv && !hasWechsel ? { opacity: 0.45 } : {}) }}>
                    <div>
                      <label style={{ ...S.fieldLabel, fontSize: 11 }}>{hasWechsel ? "Zählernummer (alt)" : "Zählernummer"}</label>
                      <input style={fieldStyle()} value={zaehlernr} onChange={e => setV(typ, "Zaehlernummer", e.target.value)} placeholder="—" disabled={inaktiv && !hasWechsel} />
                    </div>
                    <div>
                      <label style={{ ...S.fieldLabel, fontSize: 11, ...(eColor ? { color: eColor } : {}) }}>Eichdatum (M YY)</label>
                      <input style={fieldStyle(eColor ? { borderColor: eColor, background: eColor + "11" } : {})} value={eichdatum} onChange={e => setV(typ, "Eichdatum", e.target.value)} placeholder="M 26" disabled={inaktiv && !hasWechsel} />
                    </div>
                    <div>
                      <label style={{ ...S.fieldLabel, fontSize: 11 }}>Vorjahr ({unit})</label>
                      <input type="number" style={{ ...fieldStyle(), background: "var(--tableStripe)", color: COLORS.textMuted }} value={anfangAuto || anfang} readOnly placeholder="0" />
                    </div>
                    <div>
                      <label style={{ ...S.fieldLabel, fontSize: 11 }}>{hasWechsel ? `Ablesewert bei Wechsel (${unit})` : `Ende (${unit})`}</label>
                      <input type="text" inputMode="decimal" style={fieldStyle()} value={ende} onChange={e => setV(typ, "Ende", sanitizeAblesewert(e.target.value))} placeholder="0" disabled={inaktiv && !hasWechsel} />
                    </div>
                  </div>

                  {/* Zähler B (nach Wechsel) */}
                  {hasWechsel && (
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: `2px dashed ${color}66` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color, background: `${color}22`, padding: "3px 10px", borderRadius: 5 }}>🆕 Neuer Zähler</span>
                          {eichStatusB && <span style={{ fontSize: 11, color: eColorB, fontWeight: 700 }}>{eichIcon(eichStatusB)}</span>}
                        </div>
                        <button
                          style={{ fontSize: 11, padding: "3px 8px", borderRadius: 5, border: `1px solid ${endeB ? "#CCC" : COLORS.danger}`, background: "transparent", color: endeB ? "#CCC" : COLORS.danger, cursor: endeB ? "not-allowed" : "pointer", fontFamily: "inherit" }}
                          title={endeB ? "Wechsel kann nicht entfernt werden — Ablesewert bereits eingetragen" : "Zählerwechsel entfernen"}
                          onClick={() => {
                            if (endeB) return;
                            if (window.confirm("Zählerwechsel wirklich entfernen?")) {
                              delVMulti(typ, ["ZaehlernummerB","EichdatumB","WechseldatumB","EndeB"]);
                            }
                          }}
                        >✕ Wechsel entfernen</button>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
                        <div>
                          <label style={{ ...S.fieldLabel, fontSize: 11 }}>Neue Zählernummer</label>
                          <input style={{ ...S.input, fontSize: 13, boxSizing: "border-box", width: "100%", minWidth: 0 }} value={zaehlernrB.trim()} onChange={e => setV(typ, "ZaehlernummerB", e.target.value)} placeholder="—" />
                        </div>
                        <div>
                          <label style={{ ...S.fieldLabel, fontSize: 11, ...(eColorB ? { color: eColorB } : {}) }}>Eichdatum (M YY)</label>
                          <input style={{ ...S.input, fontSize: 13, boxSizing: "border-box", width: "100%", minWidth: 0, ...(eColorB ? { borderColor: eColorB, background: eColorB + "11" } : {}) }} value={eichdatumB} onChange={e => setV(typ, "EichdatumB", e.target.value)} placeholder="M 26" />
                        </div>
                        <div>
                          <label style={{ ...S.fieldLabel, fontSize: 11 }}>Wechseldatum</label>
                          <input type="date" style={{ ...S.input, fontSize: 13, boxSizing: "border-box", width: "100%", minWidth: 0 }} value={wechseldatum} onChange={e => setV(typ, "WechseldatumB", e.target.value)} />
                        </div>
                        <div>
                          <label style={{ ...S.fieldLabel, fontSize: 11 }}>Vorjahr ({unit})</label>
                          <input style={{ ...S.input, fontSize: 13, boxSizing: "border-box", width: "100%", minWidth: 0, background: "var(--tableStripe)", color: COLORS.textMuted }} value="0 (neuer Zähler)" readOnly />
                        </div>
                        <div style={{ gridColumn: "1 / -1" }}>
                          <input type="text" inputMode="decimal" style={{ ...S.input, fontSize: 13, boxSizing: "border-box", width: "100%", minWidth: 0, maxWidth: 160 }} maxLength={10} value={endeB} onChange={e => setV(typ, "EndeB", sanitizeAblesewert(e.target.value))} placeholder={`Ende Jahr (${unit})`} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Verbrauch-Zusammenfassung */}
                  {verbrGesamt !== null && (
                    <div style={{ marginTop: 12, padding: "8px 12px", background: color, borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>
                        {hasWechsel ? `Gesamtverbrauch ${year}` : `Verbrauch ${year}`}
                      </span>
                      <span style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>
                        {verbrGesamt.toFixed(2)} {unit}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Nachrichtencenter ────────────────────────────────────────────────────────
// ─── Hilfsfunktionen Protokoll ─────────────────────────────────────────────
function detectDevice(ua) {
  if (!ua) return { icon: "💻", name: "Unbekannt" };
  if (/iPad/.test(ua)) return { icon: "📱", name: "iPad" };
  if (/iPhone/.test(ua)) return { icon: "📱", name: "iPhone" };
  if (/Android/.test(ua)) return { icon: "📱", name: "Android" };
  if (/Mac/.test(ua)) return { icon: "💻", name: "Mac" };
  if (/Windows/.test(ua)) return { icon: "💻", name: "Windows" };
  if (/Linux/.test(ua)) return { icon: "💻", name: "Linux" };
  return { icon: "🌐", name: "Browser" };
}
function detectBrowser(ua) {
  if (!ua) return "";
  if (/Edg\//.test(ua)) return "Edge";
  if (/OPR\//.test(ua)) return "Opera";
  if (/Chrome/.test(ua)) return "Chrome";
  if (/Firefox/.test(ua)) return "Firefox";
  if (/Safari/.test(ua)) return "Safari";
  return "";
}

// ─── Portal-Protokolle (Anmeldungen) ────────────────────────────────────────
function PortalProtokolle({ members }) {
  const [logs, setLogs] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem("gv_portal_logs") || "[]"); } catch(e) { return []; }
  });
  const [suche, setSuche] = React.useState("");

  function clearLogs() {
    if (!window.confirm("Alle Anmelde-Protokoll-Einträge wirklich löschen?")) return;
    localStorage.setItem("gv_portal_logs", "[]");
    setLogs([]);
  }

  const memberMap = {};
  members.forEach(m => { memberMap[m.id] = m; });

  const gefiltert = logs.filter(l => {
    if (!suche) return true;
    const s = suche.toLowerCase();
    return (
      l.nachname?.toLowerCase().includes(s) ||
      l.vorname?.toLowerCase().includes(s) ||
      l.garten?.toLowerCase().includes(s)
    );
  });

  return (
    <div style={{ padding: 32, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={S.h1}>🔑 Login-Protokoll</h1>
          <p style={S.subtitle}>{logs.length} Anmeldungen aufgezeichnet (nur Mitglieder)</p>
        </div>
        <button style={{ ...S.btnGhost, color: "#C0392B", borderColor: "#C0392B" }} onClick={clearLogs}>🗑 Löschen</button>
      </div>
      <div style={{ marginBottom: 18 }}>
        <input type="text" placeholder="🔍 Suche nach Name oder Garten..." value={suche} onChange={e => setSuche(e.target.value)}
          style={{ ...S.input, maxWidth: 380 }} />
      </div>
      {gefiltert.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 48, color: COLORS.textMuted }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <div>Keine Anmeldungen gefunden</div>
        </div>
      ) : (
        <div style={{ background: "var(--cardBg)", borderRadius: 12, border: `1px solid ${COLORS.border}`, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: COLORS.leafPale, borderBottom: `2px solid ${COLORS.border}` }}>
                {["Zeitpunkt", "Nachname", "Garten", "Endgerät"].map(h => (
                  <th key={h} style={{ ...S.th, textAlign: "left", padding: "10px 14px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gefiltert.map((log, idx) => {
                const member = log.memberId ? memberMap[log.memberId] : null;
                const dev = detectDevice(log.userAgent);
                const browser = detectBrowser(log.userAgent);
                const datum = new Date(log.timestamp);
                const isToday = datum.toDateString() === new Date().toDateString();
                const nachname = log.nachname || member?.nachname || "–";
                const vorname = log.vorname || member?.vorname || "–";
                const garten = log.garten || member?.garten || "–";
                return (
                  <tr key={log.id} style={{ borderBottom: `1px solid ${COLORS.border}`, background: idx % 2 ? "var(--tableStripe)" : "transparent" }}>
                    <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                      <div style={{ fontWeight: 600, color: isToday ? COLORS.leaf : "var(--text)" }}>
                        {datum.toLocaleDateString("de-DE")}
                      </div>
                      <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                        {datum.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                      </div>
                    </td>
                    <td style={{ ...S.td, fontWeight: 600 }}>{nachname}</td>
                    <td style={S.td}>
                      <span style={{ background: COLORS.leafPale, color: COLORS.leaf, padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                        {garten}
                      </span>
                    </td>
                    <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                      {dev.icon} {dev.name}{browser ? ` · ${browser}` : ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Datenänderungs-Protokoll ────────────────────────────────────────────────
function DatenAenderungsProtokoll({ members }) {
  const [logs, setLogs] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem("gv_daten_aenderungen") || "[]"); } catch(e) { return []; }
  });
  const [suche, setSuche] = React.useState("");

  function clearLogs() {
    if (!window.confirm("Alle Datenänderungs-Einträge wirklich löschen?")) return;
    localStorage.setItem("gv_daten_aenderungen", "[]");
    setLogs([]);
  }

  const memberMap = {};
  members.forEach(m => { memberMap[m.id] = m; });

  const gefiltert = logs.filter(l => {
    if (suche) {
      const s = suche.toLowerCase();
      return l.nachname?.toLowerCase().includes(s) || l.garten?.toLowerCase().includes(s) || l.vorname?.toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div style={{ padding: 32, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={S.h1}>✏️ Mitglieder-Änderungen</h1>
          <p style={S.subtitle}>{logs.length} Änderungen aufgezeichnet · durch Mitglieder selbst vorgenommen</p>
        </div>
        <button style={{ ...S.btnGhost, color: "#C0392B", borderColor: "#C0392B" }} onClick={clearLogs}>🗑 Löschen</button>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        <input type="text" placeholder="🔍 Suche nach Name oder Garten..." value={suche} onChange={e => setSuche(e.target.value)}
          style={{ ...S.input, maxWidth: 320 }} />
      </div>

      {gefiltert.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 48, color: COLORS.textMuted }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <div>Keine Datenänderungen gefunden</div>
        </div>
      ) : (
        <div style={{ background: "var(--cardBg)", borderRadius: 12, border: `1px solid ${COLORS.border}`, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: COLORS.leafPale, borderBottom: `2px solid ${COLORS.border}` }}>
                {["Zeitpunkt", "Nachname", "Garten", "Alter Wert", "Neuer Wert"].map(h => (
                  <th key={h} style={{ ...S.th, textAlign: "left", padding: "10px 14px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gefiltert.map((log, idx) => {
                const datum = new Date(log.timestamp);
                const isToday = datum.toDateString() === new Date().toDateString();
                return (
                  <tr key={log.id} style={{ borderBottom: `1px solid ${COLORS.border}`, background: idx % 2 ? "var(--tableStripe)" : "transparent" }}>
                    <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                      <div style={{ fontWeight: 600, color: isToday ? COLORS.leaf : "var(--text)" }}>
                        {datum.toLocaleDateString("de-DE")}
                      </div>
                      <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                        {datum.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </td>
                    <td style={{ ...S.td, fontWeight: 600 }}>
                      {log.nachname}
                      <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 400 }}>{log.vorname}</div>
                    </td>
                    <td style={S.td}>
                      <span style={{ background: COLORS.leafPale, color: COLORS.leaf, padding: "2px 8px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                        {log.garten || "–"}
                      </span>
                    </td>
                    <td style={{ ...S.td, color: "#C0392B", maxWidth: 200 }}>
                      <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, background: "#FADBD8", fontSize: 12, wordBreak: "break-all" }}>
                        {log.altWert || "–"}
                      </span>
                    </td>
                    <td style={{ ...S.td, color: "#1E8449", maxWidth: 200 }}>
                      <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, background: "#D5F5E3", fontSize: 12, wordBreak: "break-all" }}>
                        {log.neuWert || "–"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Nachrichtencenter({ members }) {
  const NACHRICHTEN_KEY = "gv_nachrichten";
  const [nachrichten, setNachrichten] = useState(() => {
    try { return JSON.parse(localStorage.getItem(NACHRICHTEN_KEY) || "[]"); } catch(e) { return []; }
  });
  const [modal, setModal] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // id
  const [smsModal, setSmsModal] = useState(null); // nachricht
  const [smsStatus, setSmsStatus] = useState(null); // { gateway, guthaben, konfiguriert }
  const [smsErgebnis, setSmsErgebnis] = useState(null);
  const [testModus, setTestModus] = useState(false);

  // SMS-Gateway Status beim Laden prüfen
  React.useEffect(() => {
    fetch(`${PDF_SERVER_URL}/api/sms/status`)
      .then(r => r.json()).then(d => setSmsStatus(d)).catch(() => setSmsStatus(null));
  }, []);

  async function sendeSMS(nachricht, test = false) {
    const empf = empfaengerListe(nachricht);
    const mitTelefon = empf.filter(m => m.telefon);
    if (!mitTelefon.length) return;
    setSmsErgebnis({ loading: true });
    try {
      const res = await fetch(`${PDF_SERVER_URL}/api/sms/senden`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nummern: mitTelefon.map(m => m.telefon), text: nachricht.text, test_modus: test })
      });
      const data = await res.json();
      setSmsErgebnis(data);
      if (!test && data.erfolgreich > 0) {
        statusAendern(nachricht.id, "Versandt");
      }
    } catch(e) {
      setSmsErgebnis({ error: e.message });
    }
  }
  const [form, setForm] = useState({
    betreff: "", text: "", empfaenger: "alle",
    einzeln: [], gruppe: "alle", geplant: "", status: "Entwurf"
  });
  const [filter, setFilter] = useState("Alle");
  const [empfaengerSuche, setEmpfaengerSuche] = useState("");

  const aktiveMembers = members
    .filter(m => m.status === "Aktives Mitglied" || m.status === "Passives Mitglied")
    .sort((a,b) => (parseInt((a.garten||"").replace(/\D/g,""))||0) - (parseInt((b.garten||"").replace(/\D/g,""))||0));

  function save(arr) {
    setNachrichten(arr);
    localStorage.setItem(NACHRICHTEN_KEY, JSON.stringify(arr));
  }

  function empfaengerLabel(n) {
    if (n.empfaenger === "alle") return `Alle Mitglieder (${aktiveMembers.length})`;
    if (n.empfaenger === "einzeln") return `${(n.einzeln||[]).length} Mitglied${n.einzeln?.length !== 1 ? "er" : ""}`;
    return n.empfaenger;
  }

  function empfaengerListe(n) {
    if (n.empfaenger === "alle") return aktiveMembers;
    if (n.empfaenger === "einzeln") return aktiveMembers.filter(m => (n.einzeln||[]).includes(m.id));
    return aktiveMembers;
  }

  function erstelleNachricht() {
    if (!form.betreff || !form.text) return;
    const neu = {
      id: "msg" + Date.now(),
      betreff: form.betreff,
      text: form.text,
      empfaenger: form.empfaenger,
      einzeln: form.einzeln,
      erstellt: new Date().toISOString(),
      geplant: form.geplant || null,
      status: "Entwurf",
      versandt: null,
      anzahlEmpfaenger: form.empfaenger === "alle" ? aktiveMembers.length : form.einzeln.length,
    };
    save([neu, ...nachrichten]);
    setModal(false);
    setForm({ betreff: "", text: "", empfaenger: "alle", einzeln: [], geplant: "", status: "Entwurf" });
  }

  function loeschen(id) {
    setConfirmDelete(id);
  }

  function statusAendern(id, status) {
    save(nachrichten.map(n => n.id === id ? { ...n, status, versandt: status === "Versandt" ? new Date().toISOString() : n.versandt } : n));
  }

  const filtered = filter === "Alle" ? nachrichten : nachrichten.filter(n => n.status === filter);

  const STATUS_FARBEN = {
    "Entwurf":  { bg: "#F2F3F4", color: "#717D7E", icon: "📝" },
    "Bereit":   { bg: "#EBF5FB", color: "#1A5276", icon: "✅" },
    "Versandt": { bg: "#f0f8ed", color: "#2D5A27", icon: "📤" },
    "Geplant":  { bg: "#FEF9E7", color: "#B7950B", icon: "⏰" },
  };

  const SMS_CHAR_LIMIT = 160;
  const zeichenRest = SMS_CHAR_LIMIT - (form.text?.length || 0);
  const smsAnzahl = Math.ceil((form.text?.length || 0) / SMS_CHAR_LIMIT) || 1;

  const empfaengerGefiltert = aktiveMembers.filter(m =>
    empfaengerSuche === "" ||
    `${m.vorname} ${m.nachname} ${m.garten}`.toLowerCase().includes(empfaengerSuche.toLowerCase())
  );

  return (
    <div className="gv-page" style={S.page}>
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>💬 Nachrichtencenter</h1>
          <p style={S.subtitle}>{nachrichten.length} Nachricht{nachrichten.length !== 1 ? "en" : ""} · {nachrichten.filter(n=>n.status==="Versandt").length} versandt</p>
        </div>
        <button style={S.btnPrimary} onClick={() => { setForm({ betreff: "", text: "", empfaenger: "alle", einzeln: [], geplant: "" }); setModal(true); }}>
          + Neue Nachricht
        </button>
      </div>

      {/* Statistiken */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
        {[
          { label: "Gesamt", value: nachrichten.length, icon: "💬", color: COLORS.leaf, bg: COLORS.leafPale },
          { label: "Entwürfe", value: nachrichten.filter(n=>n.status==="Entwurf").length, icon: "📝", color: "#717D7E", bg: "#F2F3F4" },
          { label: "Bereit", value: nachrichten.filter(n=>n.status==="Bereit").length, icon: "✅", color: "#1A5276", bg: "#EBF5FB" },
          { label: "Versandt", value: nachrichten.filter(n=>n.status==="Versandt").length, icon: "📤", color: "#2D5A27", bg: "#f0f8ed" },
        ].map(k => (
          <div key={k.label} style={{ background: k.bg, borderRadius: 12, padding: "14px 20px", border: `1.5px solid ${k.color}22`, textAlign: "center" }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{k.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {["Alle","Entwurf","Bereit","Geplant","Versandt"].map(f => (
          <button key={f} style={{ ...S.filterBtn, ...(filter===f?S.filterBtnActive:{}) }} onClick={() => setFilter(f)}>{f}</button>
        ))}
      </div>

      {/* Liste */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: COLORS.textMuted }}>
          <div style={{ fontSize: 48 }}>💬</div>
          <p style={{ marginTop: 12, fontSize: 15 }}>Noch keine Nachrichten</p>
          <p style={{ fontSize: 13, marginTop: 6 }}>Klicke auf „+ Neue Nachricht" um loszulegen</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map(n => {
            const sf = STATUS_FARBEN[n.status] || STATUS_FARBEN["Entwurf"];
            const empf = empfaengerListe(n);
            return (
              <div key={n.id} style={{ ...S.card, padding: "16px 20px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: COLORS.text }}>{n.betreff}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 10, background: sf.bg, color: sf.color }}>
                        {sf.icon} {n.status}
                      </span>
                    </div>
                    <p style={{ margin: "0 0 8px", fontSize: 13, color: COLORS.textMuted, lineHeight: 1.5,
                      overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                      {n.text}
                    </p>
                    <div style={{ display: "flex", gap: 16, fontSize: 11, color: COLORS.textMuted }}>
                      <span>👥 {empfaengerLabel(n)}</span>
                      <span>📅 {new Date(n.erstellt).toLocaleDateString("de-DE")}</span>
                      {n.versandt && <span>📤 Versandt: {new Date(n.versandt).toLocaleDateString("de-DE")}</span>}
                      {n.geplant && <span>⏰ Geplant: {new Date(n.geplant).toLocaleDateString("de-DE", {day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"})}</span>}
                      <span>💬 {Math.ceil(n.text.length/160)} SMS · {n.text.length} Zeichen</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end", maxWidth: 220 }}>
                    <button style={{ ...S.btnSmall, fontSize: 11 }} onClick={() => setDetailModal(n)}>🔍 Details</button>
                    {n.status !== "Versandt" && (
                      <button style={{ ...S.btnSmall, fontSize: 11, background: "#1A5276", color: "#fff", border: "none" }}
                        onClick={() => statusAendern(n.id, "Bereit")}>✅ Bereit</button>
                    )}
                    {n.status === "Bereit" && (
                      <button style={{ ...S.btnSmall, fontSize: 11, background: COLORS.leaf, color: "#fff", border: "none" }}
                        onClick={() => { setSmsErgebnis(null); setTestModus(false); setSmsModal(n); }}>
                        📱 SMS senden
                      </button>
                    )}
                    <button style={{ ...S.btnSmall, fontSize: 11, background: "#C0392B", color: "#fff", border: "none" }}
                      onClick={() => setConfirmDelete(n.id)}>🗑</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* SMS Senden Modal */}
      {smsModal && (
        <div style={{ ...S.overlay, zIndex: 2300 }}>
          <div style={{ ...S.modal, width: "min(96vw, 640px)", maxHeight: "90vh", overflow: "auto", padding: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 17, color: COLORS.leaf }}>📱 SMS versenden</h3>
              <button style={S.btnGhost} onClick={() => { setSmsModal(null); setSmsErgebnis(null); }}>✕</button>
            </div>

            {/* Gateway Status */}
            <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 12,
              background: smsStatus?.konfiguriert ? "#f0f8ed" : "#FEF3F2",
              border: `1px solid ${smsStatus?.konfiguriert ? "#b8ddb0" : "#f5c6cb"}` }}>
              {smsStatus?.konfiguriert ? (
                <span style={{ color: "#2D5A27", fontWeight: 600 }}>
                  ✅ {smsStatus.gateway} verbunden{smsStatus.guthaben ? ` · Guthaben: ${parseFloat(smsStatus.guthaben).toFixed(2)} €` : ""}
                </span>
              ) : (
                <div>
                  <span style={{ color: "#C0392B", fontWeight: 600 }}>⚠ Kein SMS-Gateway konfiguriert</span>
                  <p style={{ margin: "6px 0 0", color: COLORS.textMuted }}>
                    Trage <code>SMS_API_KEY=dein-key</code> in die <code>.env</code> Datei auf dem Server ein.<br/>
                    Empfohlen: <a href="https://www.seven.io" target="_blank" rel="noreferrer" style={{ color: COLORS.leaf }}>seven.io</a> (ab 0,075€/SMS) oder <a href="https://www.twilio.com" target="_blank" rel="noreferrer" style={{ color: COLORS.leaf }}>Twilio</a>
                  </p>
                </div>
              )}
            </div>

            {/* Nachricht Vorschau */}
            <div style={{ background: COLORS.leafPale, borderRadius: 10, padding: "12px 16px", marginBottom: 16, border: `1px solid ${COLORS.border}` }}>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4, fontWeight: 600 }}>NACHRICHT ({smsModal.text.length} Zeichen · {Math.ceil(smsModal.text.length/160)} SMS)</div>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{smsModal.text}</p>
            </div>

            {/* Empfänger */}
            {(() => {
              const empf = empfaengerListe(smsModal);
              const mitTel = empf.filter(m => m.telefon);
              const ohneTel = empf.filter(m => !m.telefon);
              return (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>
                    👥 {empf.length} Empfänger — {mitTel.length} mit Nummer, {ohneTel.length} ohne
                  </div>
                  <div style={{ maxHeight: 150, overflowY: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
                    {mitTel.map(m => (
                      <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", borderBottom: `1px solid ${COLORS.border}`, fontSize: 12 }}>
                        <span style={{ ...S.gartenBadge, fontSize: 10 }}>{m.garten}</span>
                        <span>{m.vorname} {m.nachname}</span>
                        <span style={{ marginLeft: "auto", color: COLORS.leaf, fontWeight: 600 }}>📱 {m.telefon}</span>
                        {smsErgebnis && smsErgebnis.details && (() => {
                          const d = smsErgebnis.details.find(x => x.nummer.replace(/\s/g,"") === m.telefon.replace(/[\s\-\/\(\)]/g,"").replace(/^0/,"+49"));
                          if (!d) return null;
                          return <span style={{ fontSize: 11, fontWeight: 700, color: d.status==="ok"||d.status==="test_ok"?"#2D5A27":"#C0392B" }}>
                            {d.status==="ok"?"✓":d.status==="test_ok"?"✓ Test":"✗"}
                          </span>;
                        })()}
                      </div>
                    ))}
                  </div>
                  {ohneTel.length > 0 && (
                    <div style={{ marginTop: 6, fontSize: 11, color: "#E67E22" }}>
                      ⚠ {ohneTel.map(m => `${m.vorname} ${m.nachname}`).join(", ")} — keine Nummer hinterlegt
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Test-Modus Toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: "10px 14px", background: "var(--tableStripe)", borderRadius: 8 }}>
              <input type="checkbox" id="testModus" checked={testModus} onChange={e => setTestModus(e.target.checked)} />
              <label htmlFor="testModus" style={{ fontSize: 13, cursor: "pointer", userSelect: "none" }}>
                <strong>Test-Modus</strong> — keine echten SMS senden, nur prüfen
              </label>
            </div>

            {/* Ergebnis */}
            {smsErgebnis && !smsErgebnis.loading && (
              <div style={{ padding: "12px 16px", borderRadius: 8, marginBottom: 16,
                background: smsErgebnis.error ? "#FEF3F2" : smsErgebnis.test ? "#EBF5FB" : "#f0f8ed",
                border: `1px solid ${smsErgebnis.error ? "#f5c6cb" : smsErgebnis.test ? "#AED6F1" : "#b8ddb0"}` }}>
                {smsErgebnis.error ? (
                  <span style={{ color: "#C0392B", fontWeight: 700 }}>❌ Fehler: {smsErgebnis.error}</span>
                ) : smsErgebnis.test ? (
                  <span style={{ color: "#1A5276", fontWeight: 700 }}>✅ Test erfolgreich — {smsErgebnis.anzahl} SMS würden versendet</span>
                ) : (
                  <span style={{ color: "#2D5A27", fontWeight: 700 }}>
                    📤 {smsErgebnis.erfolgreich} versandt{smsErgebnis.fehlgeschlagen > 0 ? `, ${smsErgebnis.fehlgeschlagen} fehlgeschlagen` : ""}
                  </span>
                )}
              </div>
            )}
            {smsErgebnis?.loading && (
              <div style={{ textAlign: "center", padding: "12px", color: COLORS.textMuted, fontSize: 13 }}>⏳ Wird gesendet…</div>
            )}

            {/* Versand-Optionen erklären */}
            <div style={{ padding: "12px 14px", borderRadius: 8, marginBottom: 16, background: "#EBF5FB", border: "1px solid #AED6F1", fontSize: 12, color: "#1A5276" }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>📨 Zwei Versand-Wege:</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div><strong>📲 Im Handy öffnen</strong> — SMS-App des Handys öffnen, kostenlos über deine SIM (im Mobilfunktarif enthalten)</div>
                <div><strong>📱 Gateway (seven.io)</strong> — automatischer Versand über Server, ca. 0,075€ pro SMS</div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "space-between", borderTop: `1px solid ${COLORS.border}`, paddingTop: 16, flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 10 }}>
                <button style={{ ...S.btnPrimary, background: "#0E6655", border: "none" }}
                  onClick={() => {
                    const empf = empfaengerListe(smsModal).filter(m => m.telefon);
                    if (!empf.length) { alert("Keine Empfänger mit Telefonnummer"); return; }
                    // Nummern formatieren (entfernt Leerzeichen, Bindestriche etc.)
                    const nummern = empf.map(m => m.telefon.replace(/[\s\-/()]/g,"")).join(",");
                    // sms: Link öffnen — body wird auf iOS mit ? und auf Android mit ? unterstützt
                    const text = encodeURIComponent(smsModal.text);
                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                    const link = isIOS ? `sms:${nummern}&body=${text}` : `sms:${nummern}?body=${text}`;
                    window.location.href = link;
                    statusAendern(smsModal.id, "Versandt");
                  }}>
                  📲 Im Handy öffnen
                </button>
                <button style={{ ...S.btnGhost }}
                  onClick={() => {
                    const empf = empfaengerListe(smsModal).filter(m => m.telefon);
                    if (!empf.length) { alert("Keine Empfänger mit Telefonnummer"); return; }
                    const text = encodeURIComponent(smsModal.text);
                    // WhatsApp: erste Nummer öffnen
                    const nr = empf[0].telefon.replace(/[\s\-/()]/g,"").replace(/^0/,"49");
                    window.open(`https://wa.me/${nr}?text=${text}`, "_blank");
                  }}>
                  💬 WhatsApp
                </button>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button style={S.btnGhost} onClick={() => { setSmsModal(null); setSmsErgebnis(null); }}>Schließen</button>
                <button style={{ ...S.btnPrimary, background: testModus ? "#1A5276" : COLORS.leaf, border: "none" }}
                  onClick={() => sendeSMS(smsModal, testModus)}
                  disabled={smsErgebnis?.loading}>
                  {testModus ? "🧪 Test (Gateway)" : "📱 Gateway senden"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Löschen-Bestätigung */}
      {confirmDelete && (
        <div style={{ ...S.overlay, zIndex: 2300 }}>
          <div style={{ ...S.modal, width: 360, padding: 28 }}>
            <h3 style={{ margin: "0 0 12px", color: "#C0392B", fontSize: 16 }}>🗑 Nachricht löschen</h3>
            <p style={{ color: COLORS.textMuted, marginBottom: 24, fontSize: 13 }}>
              Soll diese Nachricht wirklich gelöscht werden?
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={S.btnGhost} onClick={() => setConfirmDelete(null)}>Abbrechen</button>
              <button style={{ ...S.btnPrimary, background: "#C0392B", border: "none" }}
                onClick={() => { save(nachrichten.filter(n => n.id !== confirmDelete)); setConfirmDelete(null); }}>
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Neue Nachricht Modal */}
      {modal && (
        <div style={{ ...S.overlay, zIndex: 2100 }}>
          <div style={{ ...S.modal, width: "min(96vw, 760px)", maxHeight: "90vh", overflow: "auto", padding: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 17, color: COLORS.leaf }}>💬 Neue Nachricht</h3>
              <button style={S.btnGhost} onClick={() => setModal(false)}>✕</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Betreff */}
              <div>
                <label style={S.label}>Betreff (intern)</label>
                <input style={S.input} placeholder="z.B. Einladung Jahreshauptversammlung 2026"
                  value={form.betreff} onChange={e => setForm(f=>({...f,betreff:e.target.value}))} />
              </div>

              {/* Nachrichtentext */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <label style={S.label}>SMS-Text</label>
                  <span style={{ fontSize: 11, color: zeichenRest < 0 ? "#C0392B" : zeichenRest < 20 ? "#E67E22" : COLORS.textMuted, fontWeight: 600 }}>
                    {form.text.length} Zeichen · {smsAnzahl} SMS{smsAnzahl > 1 ? ` (à 153 Z.)` : ""}
                    {zeichenRest >= 0 ? ` · noch ${zeichenRest}` : ` · ${Math.abs(zeichenRest)} zu viel`}
                  </span>
                </div>
                <textarea style={{ ...S.input, minHeight: 120, resize: "vertical", fontFamily: "inherit", lineHeight: 1.6 }}
                  placeholder="Sehr geehrte/r [Name],&#10;&#10;wir laden Sie herzlich ein zur...&#10;&#10;Mit freundlichen Grüßen&#10;Vorstand Gartenfreunde Rödern"
                  value={form.text} onChange={e => setForm(f=>({...f,text:e.target.value}))} />
                {zeichenRest < 0 && (
                  <div style={{ fontSize: 11, color: "#C0392B", marginTop: 4 }}>
                    ⚠ Nachricht wird auf {smsAnzahl} SMS aufgeteilt (Mehrkosten möglich)
                  </div>
                )}
              </div>

              {/* Empfänger */}
              <div>
                <label style={S.label}>Empfänger</label>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  {["alle","einzeln"].map(t => (
                    <button key={t} style={{ ...S.filterBtn, ...(form.empfaenger===t?S.filterBtnActive:{}), fontSize: 13 }}
                      onClick={() => setForm(f=>({...f,empfaenger:t,einzeln:[]}))}>
                      {t === "alle" ? `👥 Alle (${aktiveMembers.length})` : "👤 Einzeln auswählen"}
                    </button>
                  ))}
                </div>

                {form.empfaenger === "einzeln" && (
                  <div>
                    <input style={{ ...S.input, marginBottom: 8 }} placeholder="🔍 Mitglied suchen..."
                      value={empfaengerSuche} onChange={e => setEmpfaengerSuche(e.target.value)} />
                    <div style={{ maxHeight: 200, overflowY: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
                      {empfaengerGefiltert.map(m => {
                        const selected = form.einzeln.includes(m.id);
                        return (
                          <div key={m.id}
                            style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", cursor: "pointer",
                              background: selected ? COLORS.leafPale : "transparent",
                              borderBottom: `1px solid ${COLORS.border}` }}
                            onClick={() => setForm(f => ({
                              ...f,
                              einzeln: selected ? f.einzeln.filter(id => id !== m.id) : [...f.einzeln, m.id]
                            }))}>
                            <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${selected?COLORS.leaf:COLORS.border}`,
                              background: selected?COLORS.leaf:"transparent", display:"flex",alignItems:"center",justifyContent:"center", flexShrink: 0 }}>
                              {selected && <span style={{color:"#fff",fontSize:12,fontWeight:700}}>✓</span>}
                            </div>
                            <span style={{ ...S.gartenBadge, fontSize: 11 }}>{m.garten}</span>
                            <span style={{ fontSize: 13 }}>{m.vorname} {m.nachname}</span>
                            {m.telefon && <span style={{ fontSize: 11, color: COLORS.textMuted, marginLeft: "auto" }}>📱 {m.telefon}</span>}
                            {!m.telefon && <span style={{ fontSize: 11, color: "#C0392B", marginLeft: "auto" }}>⚠ keine Nummer</span>}
                          </div>
                        );
                      })}
                    </div>
                    {form.einzeln.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: 12, color: COLORS.leaf, fontWeight: 600 }}>
                        {form.einzeln.length} Mitglied{form.einzeln.length !== 1 ? "er" : ""} ausgewählt
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Zeitplanung */}
              <div>
                <label style={S.label}>Zeitplanung (optional)</label>
                <input type="datetime-local" style={S.input} value={form.geplant}
                  onChange={e => setForm(f=>({...f,geplant:e.target.value}))} />
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
                  Hinweis: Der automatische Versand erfordert ein SMS-Gateway (z.B. Twilio). Ohne Gateway dient dies als Erinnerung.
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24, borderTop: `1px solid ${COLORS.border}`, paddingTop: 16 }}>
              <button style={S.btnGhost} onClick={() => setModal(false)}>Abbrechen</button>
              <button style={{ ...S.btnPrimary, opacity: (!form.betreff || !form.text) ? 0.5 : 1 }}
                onClick={erstelleNachricht} disabled={!form.betreff || !form.text}>
                💾 Als Entwurf speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail-Modal */}
      {detailModal && (() => {
        const n = detailModal;
        const empf = empfaengerListe(n);
        const sf = STATUS_FARBEN[n.status] || STATUS_FARBEN["Entwurf"];
        const mitTelefon = empf.filter(m => m.telefon);
        const ohneTelefon = empf.filter(m => !m.telefon);
        return (
          <div style={{ ...S.overlay, zIndex: 2200 }}>
            <div style={{ ...S.modal, width: "min(96vw, 700px)", maxHeight: "90vh", overflow: "auto", padding: 28 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h3 style={{ margin: 0, fontSize: 17, color: COLORS.leaf }}>🔍 {n.betreff}</h3>
                <button style={S.btnGhost} onClick={() => setDetailModal(null)}>✕</button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
                {[
                  { label: "Status", value: `${sf.icon} ${n.status}`, color: sf.color, bg: sf.bg },
                  { label: "Empfänger", value: `${empf.length} gesamt`, color: COLORS.leaf, bg: COLORS.leafPale },
                  { label: "SMS", value: `${Math.ceil(n.text.length/160)} × ${empf.length}`, color: "#1A5276", bg: "#EBF5FB" },
                ].map(k => (
                  <div key={k.label} style={{ background: k.bg, borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
                    <div style={{ fontSize: 11, color: COLORS.textMuted }}>{k.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: k.color, marginTop: 2 }}>{k.value}</div>
                  </div>
                ))}
              </div>

              {/* Nachrichtentext */}
              <div style={{ background: COLORS.leafPale, borderRadius: 10, padding: "14px 16px", marginBottom: 20, border: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6, fontWeight: 600 }}>SMS-TEXT ({n.text.length} Zeichen)</div>
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{n.text}</p>
              </div>

              {/* Empfängerliste */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: COLORS.text }}>
                  📱 Empfänger mit Telefonnummer ({mitTelefon.length})
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {mitTelefon.map(m => (
                    <div key={m.id} style={{ fontSize: 12, padding: "4px 10px", borderRadius: 8, background: COLORS.leafPale, border: `1px solid ${COLORS.border}` }}>
                      <span style={S.gartenBadge}>{m.garten}</span> {m.vorname} {m.nachname} · <span style={{ color: COLORS.leaf }}>{m.telefon}</span>
                    </div>
                  ))}
                  {mitTelefon.length === 0 && <span style={{ fontSize: 12, color: COLORS.textMuted }}>Keine</span>}
                </div>
              </div>

              {ohneTelefon.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: "#C0392B" }}>
                    ⚠ Ohne Telefonnummer ({ohneTelefon.length}) — werden nicht erreicht
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {ohneTelefon.map(m => (
                      <div key={m.id} style={{ fontSize: 12, padding: "4px 10px", borderRadius: 8, background: "#FDEDEC", border: "1px solid #f5c6cb" }}>
                        <span style={S.gartenBadge}>{m.garten}</span> {m.vorname} {m.nachname}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SMS exportieren */}
              <div style={{ background: "#EBF5FB", borderRadius: 10, padding: "14px 16px", border: "1px solid #AED6F1" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1A5276", marginBottom: 8 }}>📋 SMS-Liste exportieren</div>
                <p style={{ fontSize: 12, color: COLORS.textMuted, margin: "0 0 10px", lineHeight: 1.5 }}>
                  Kopiere die Nummern und den Text für manuellen Versand oder importiere in ein SMS-Gateway.
                </p>
                <button style={{ ...S.btnSmall, background: "#1A5276", color: "#fff", border: "none", fontSize: 12 }}
                  onClick={() => {
                    const nummern = mitTelefon.map(m => m.telefon).join("\n");
                    const export_text = `EMPFÄNGER:\n${nummern}\n\nNACHRICHT:\n${n.text}`;
                    navigator.clipboard.writeText(export_text).then(() => alert("✅ In Zwischenablage kopiert!"));
                  }}>
                  📋 Nummern + Text kopieren
                </button>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                <button style={S.btnGhost} onClick={() => setDetailModal(null)}>Schließen</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Feste ───────────────────────────────────────────────────────────────────
const FESTE_TYPES = ["Sommerfest", "Herbstfest", "Sonstiges"];
const LADEN_TYPES = ["Lidl", "Aldi", "Rewe", "Netto", "Getränkemarkt", "Metzger", "Bäcker", "Sonstiges"];

// ─── Feste: einzelne Datensätze pro Fest ─────────────────────────────────────
function getFestKey(id) { return "gv_fest_" + id; }

// ─── Feste: einzelne Liste in einem Key ──────────────────────────────────────
// WICHTIG: Das alte System mit gv_fest_* Einzelkeys + localStorage.key(i)
// funktioniert NICHT mit _gvStore (nativer localStorage.key sieht _gvStore nicht).
// Lösung: alle Feste als eine Liste unter einem Key — genau wie Mitglieder etc.
const GV_FESTE_LIST_KEY = "gv_feste_list";

// IDs der alten hardcodierten Default-Feste — werden nie angezeigt
const DEFAULT_FEST_IDS = new Set(["f1", "f2", "f3", "f4"]);
const DEFAULT_FEST_NAMES = new Set(["Wasser anstellen", "Sommerfest", "Herbstfest", "Wasser abstellen"]);

function loadFeste() {
  try {
    const raw = localStorage.getItem(GV_FESTE_LIST_KEY) || "[]";
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    // Default-IDs (f1-f4) immer rausfiltern — können nie vom Nutzer angelegt worden sein
    return arr
      .filter(f => f && f.id && !DEFAULT_FEST_IDS.has(f.id))
      .sort((a,b) => (b.datum||"").localeCompare(a.datum||""));
  } catch(e) { return []; }
}

function saveFest(fest) {
  const feste = loadFeste();
  const idx = feste.findIndex(f => f.id === fest.id);
  if (idx >= 0) feste[idx] = fest; else feste.unshift(fest);
  localStorage.setItem(GV_FESTE_LIST_KEY, JSON.stringify(feste));
}

function deleteFest(id) {
  const feste = loadFeste().filter(f => f.id !== id);
  localStorage.setItem(GV_FESTE_LIST_KEY, JSON.stringify(feste));
  // Sofort zum Server senden (kein Debounce) damit Reload nicht zurücksetzt
  const PDF_SERVER_URL = window._PDF_SERVER_URL || "https://fgr-gartenfreunde.myds.me:3443";
  fetch(`${PDF_SERVER_URL}/api/data/${GV_FESTE_LIST_KEY}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ value: JSON.stringify(feste) }),
  }).catch(e => console.warn("Sofort-Sync deleteFest fehlgeschlagen:", e));
}

function saveFeste(arr) {
  localStorage.setItem(GV_FESTE_LIST_KEY, JSON.stringify(arr));
}


function Feste({ members }) {
  const [feste, setFeste] = useState(loadFeste);
  const [tab, setTab] = useState("uebersicht");
  const [selectedFest, setSelectedFest] = useState(null);
  const [neuesFestModal, setNeuesFestModal] = useState(false);
  const [festForm, setFestForm] = useState({ name: "Sommerfest", datum: "", jahr: new Date().getFullYear(), beschreibung: "" });

  // Nach Server-Sync Feste neu laden
  useEffect(() => {
    const refresh = () => setFeste(loadFeste());
    window.addEventListener("gv-data-refreshed", refresh);
    return () => window.removeEventListener("gv-data-refreshed", refresh);
  }, []);

  function save(arr) {
    setFeste(arr);
    saveFeste(arr);
  }
  function updateFest(id, updates) {
    const updated = { ...feste.find(f => f.id === id), ...updates };
    saveFest(updated);
    const arr = feste.map(f => f.id === id ? updated : f);
    setFeste(arr);
    if (selectedFest?.id === id) setSelectedFest(updated);
  }

  function erstelleFest() {
    if (!festForm.datum) return;
    const neu = {
      id: "fest" + Date.now(), name: festForm.name, datum: festForm.datum,
      jahr: new Date(festForm.datum).getFullYear(), beschreibung: festForm.beschreibung,
      einkaufsliste: [], kassenbons: [], kassenEintraege: [], status: "Planung",
      erstellt: new Date().toISOString()
    };
    saveFest(neu);
    setFeste([neu, ...feste]);
    setNeuesFestModal(false);
    setSelectedFest(neu);
    setTab("planung");
  }

  const aktiveFeste = feste.filter(f => !selectedFest || f.id === selectedFest?.id);

  return (
    <div className="gv-page" style={S.page}>
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>🎉 Feste</h1>
          <p style={S.subtitle}>{feste.length} Fest{feste.length !== 1 ? "e" : ""} geplant</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {selectedFest && (
            <>
              <button style={S.btnGhost} onClick={() => { setSelectedFest(null); setTab("uebersicht"); }}>
                ← Alle Feste
              </button>
              <button style={{ ...S.btnGhost, fontSize:12 }} title="Zur mobilen Kasse wechseln"
                onClick={() => window.__setViewGlobal && window.__setViewGlobal("kasse")}>
                🛒 → Kasse (Mobil)
              </button>
              {selectedFest.status !== "Abgeschlossen" ? (
                <button style={{ ...S.btnGhost, color:"#E67E22", border:"1.5px solid #E67E22" }}
                  onClick={() => {
                    if (window.confirm(`Fest "${selectedFest.name} ${selectedFest.jahr}" wirklich beenden?

Das Fest wird als Abgeschlossen markiert und ist in der Kasse nicht mehr verfügbar.`)) {
                      const updated = feste.map(f => f.id === selectedFest.id ? {...f, status:"Abgeschlossen"} : f);
                      save(updated);
                    }
                  }}>
                  🏁 Fest beenden
                </button>
              ) : (
                <span style={{ padding:"8px 14px", borderRadius:8, background:COLORS.leafPale, color:COLORS.leaf, fontSize:13, fontWeight:700 }}>
                  ✅ Abgeschlossen
                </span>
              )}
            </>
          )}
          <button style={S.btnPrimary} onClick={() => setNeuesFestModal(true)}>+ Neues Fest</button>
        </div>
      </div>

      {!selectedFest ? (
        /* ── Feste-Liste ── */
        <div>
          {feste.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: COLORS.textMuted }}>
              <div style={{ fontSize: 48 }}>🎉</div>
              <p style={{ marginTop: 12, fontSize: 15 }}>Noch keine Feste geplant</p>
              <button style={{ ...S.btnPrimary, marginTop: 16 }} onClick={() => setNeuesFestModal(true)}>+ Erstes Fest anlegen</button>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
              {feste.map(f => {
                const ausgaben = (f.einkaufsliste || []).filter(e => e.gekauft).reduce((s, e) => s + (parseFloat(e.preis) || 0), 0);
                const einnahmen = (f.kassenEintraege || []).filter(k => k.bezahlt).reduce((s, k) => s + (k.summe || 0), 0);
                const gewinn = einnahmen - ausgaben;
                const artikelOffen = (f.einkaufsliste || []).filter(e => !e.inKasse).length;
                const kasseOffen = (f.kassenEintraege || []).filter(k => !k.bezahlt).length;
                return (
                  <div key={f.id} style={{ ...S.card, cursor: "pointer", padding: 20 }}
                    onClick={() => { setSelectedFest(f); setTab("planung"); }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.text }}>{f.name} {f.jahr}</div>
                        <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
                          📅 {f.datum ? new Date(f.datum).toLocaleDateString("de-DE") : "–"}
                        </div>
                      </div>
                      {f.status === "Abgeschlossen" && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 10, background: COLORS.leafPale, color: COLORS.leaf }}>
                          ✅ Abgeschlossen
                        </span>
                      )}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
                      {[
                        { label: "Ausgaben", val: `-${ausgaben.toFixed(2).replace(".",",")} €`, color: "#C0392B" },
                        { label: "Einnahmen", val: `+${einnahmen.toFixed(2).replace(".",",")} €`, color: COLORS.leaf },
                        { label: "Gewinn", val: `${gewinn >= 0 ? "+" : ""}${gewinn.toFixed(2).replace(".",",")} €`, color: gewinn >= 0 ? COLORS.leaf : "#C0392B" },
                      ].map(k => (
                        <div key={k.label} style={{ textAlign: "center", padding: "6px 8px", background: "var(--tableStripe)", borderRadius: 8 }}>
                          <div style={{ fontSize: 10, color: COLORS.textMuted }}>{k.label}</div>
                          <div style={{ fontSize: 13, fontWeight: 800, color: k.color }}>{k.val}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {f.status === "Abgeschlossen" ? (
                          <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.leaf }}>✅ Fest abgeschlossen</span>
                        ) : (() => {
                          const nochZuKaufen = (f.einkaufsliste||[]).filter(e => !e.gekauft).length;
                          const laeden = [...new Set((f.einkaufsliste||[]).map(e => e.laden).filter(Boolean))];
                          const fehlendeBons = laeden.filter(l => !(f.kassenbons||[]).some(b => b.laden === l && b.url)).length;
                          const offeneZahlungen = (f.kassenEintraege||[]).filter(k => !k.bezahlt).length;
                          const hints = [];
                          if (nochZuKaufen > 0) hints.push({ text: `🛒 ${nochZuKaufen} noch zu kaufen`, color: "#E67E22" });
                          if (fehlendeBons > 0) hints.push({ text: `🧾 ${fehlendeBons} Bon${fehlendeBons>1?"s":""} fehlen`, color: "#C0392B" });
                          if (offeneZahlungen > 0) hints.push({ text: `💰 ${offeneZahlungen} unbezahlt`, color: "#E67E22" });
                          if (hints.length === 0) hints.push({ text: "✓ Bereit zum Beenden", color: COLORS.leaf });
                          return hints.map((h,i) => (
                            <span key={i} style={{ fontSize: 11, fontWeight: 700, color: h.color }}>{h.text}</span>
                          ));
                        })()}
                      </div>
                      <button style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, border: "1px solid #C0392B", background: "transparent", color: "#C0392B", cursor: "pointer", fontWeight: 700, fontFamily: "inherit" }}
                        onClick={async e => {
                          e.stopPropagation();
                          if (window.confirm(`Fest "${f.name} ${f.jahr}" wirklich löschen?\n\nAlle zugehörigen Buchungen werden ebenfalls gelöscht.`)) {
                            deleteFest(f.id);
                            setFeste(feste.filter(x => x.id !== f.id));
                            if (selectedFest?.id === f.id) setSelectedFest(null);
                            // Zugehörige Kassen-Buchungen löschen
                            try {
                              const alleBuchungen = JSON.parse(localStorage.getItem("gv_kasse_buchungen") || "[]");
                              const gefiltert = alleBuchungen.filter(b => b.festId !== f.id);
                              localStorage.setItem("gv_kasse_buchungen", JSON.stringify(gefiltert));
                              // Server-Sync automatisch über patched localStorage.setItem
                            } catch(e2) {}
                          }
                        }}>
                        🗑 Löschen
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* ── Fest-Detail ── */
        <div>
          {/* Tabs */}
          <div style={{ display: "flex", gap: 4, marginBottom: 20, background: "var(--tableStripe)", padding: 4, borderRadius: 10, width: "fit-content" }}>
            {[
              { id: "planung", label: "🛒 Einkauf", icon: "🛒" },
              { id: "auswertung", label: "📊 Auswertung", icon: "📊" },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, fontFamily: "inherit",
                  background: tab === t.id ? COLORS.leaf : "transparent",
                  color: tab === t.id ? "#fff" : COLORS.textMuted }}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === "planung" && <FestEinkauf fest={selectedFest} updateFest={updateFest} />}

          {tab === "auswertung" && <FestAuswertung fest={selectedFest} />}
        </div>
      )}

      {/* Neues Fest Modal */}
      {neuesFestModal && (
        <div style={{ ...S.overlay, zIndex: 2100 }}>
          <div style={{ ...S.modal, width: 440, padding: 28 }}>
            <h3 style={{ margin: "0 0 20px", color: COLORS.leaf }}>🎉 Neues Fest anlegen</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={S.label}>Art des Festes</label>
                <select style={S.input} value={festForm.name} onChange={e => setFestForm(f => ({...f, name: e.target.value}))}>
                  {FESTE_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Datum</label>
                <input type="date" style={S.input} value={festForm.datum} onChange={e => setFestForm(f => ({...f, datum: e.target.value}))} />
              </div>
              <div>
                <label style={S.label}>Beschreibung (optional)</label>
                <input style={S.input} placeholder="z.B. Grillfest am Vereinsheim" value={festForm.beschreibung} onChange={e => setFestForm(f => ({...f, beschreibung: e.target.value}))} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
              <button style={S.btnGhost} onClick={() => setNeuesFestModal(false)}>Abbrechen</button>
              <button style={{ ...S.btnPrimary, opacity: !festForm.datum ? 0.5 : 1 }} onClick={erstelleFest} disabled={!festForm.datum}>
                🎉 Anlegen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Fest Einkauf ────────────────────────────────────────────────────────────
function FestEinkauf({ fest, updateFest }) {
  const [neuerArtikel, setNeuerArtikel] = useState({ artikel: "", menge: "1", einheit: "Stk", preis: "", kategorie: "Essen" });
  const [showForm, setShowForm] = useState(true);
  const [bonModal, setBonModal]   = useState(null); // { laden, bonId? }
  const [filterLaden, setFilterLaden] = useState("Alle");

  const einkaufsliste = fest.einkaufsliste || [];
  // Kassenbons: [{id, laden, url, notiz, betrag, datum}] — mehrere pro Laden erlaubt
  const kassenbons    = fest.kassenbons   || [];

  // Alle genutzten Läden (inkl. "—" für unzugewiesene)
  const laeden = ["Alle", "—", ...new Set(einkaufsliste.map(e => e.laden).filter(l => l && l !== "—"))];
  const ladFilter = [...new Set(einkaufsliste.map(e => e.laden || "—"))];

  function addArtikel() {
    if (!neuerArtikel.artikel.trim()) return;
    const neu = { id: "art" + Date.now(), ...neuerArtikel, laden: "—", preis: parseFloat(neuerArtikel.preis) || 0, gekauft: false, inKasse: false };
    updateFest(fest.id, { einkaufsliste: [...einkaufsliste, neu] });
    setNeuerArtikel(f => ({ ...f, artikel: "", preis: "" }));
  }

  function updateArtikel(id, changes) {
    updateFest(fest.id, { einkaufsliste: einkaufsliste.map(e => e.id === id ? { ...e, ...changes } : e) });
  }

  function moveArtikel(id, dir) {
    const arr = [...einkaufsliste];
    const idx = arr.findIndex(e => e.id === id);
    const to  = idx + dir;
    if (to < 0 || to >= arr.length) return;
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    updateFest(fest.id, { einkaufsliste: arr });
  }

  function delArtikel(id) {
    updateFest(fest.id, { einkaufsliste: einkaufsliste.filter(e => e.id !== id) });
  }

  const gesamtActual = einkaufsliste.filter(e => e.gekauft).reduce((s, e) => s + (parseFloat(e.preis) || 0), 0);

  // Artikel gruppieren: zuerst nach Laden (kein Laden = "—" ganz oben), dann innerhalb der Reihenfolge beibehalten
  const gruppen = {};
  einkaufsliste
    .filter(e => filterLaden === "Alle" || (e.laden || "—") === filterLaden)
    .forEach(e => {
      const l = e.laden || "—";
      if (!gruppen[l]) gruppen[l] = [];
      gruppen[l].push(e);
    });
  // Sortierung: "—" zuerst, dann alphabetisch nach Laden
  const gruppenKeys = Object.keys(gruppen).sort((a, b) => {
    if (a === "—") return -1; if (b === "—") return 1;
    return a.localeCompare(b);
  });

  // Bons pro Laden
  function bonsVonLaden(laden) { return kassenbons.filter(b => b.laden === laden); }

  const inputSt = { boxSizing:"border-box", height:38, padding:"0 10px", border:"1.5px solid var(--border)", borderRadius:8, fontSize:13, fontFamily:"inherit", background:"var(--cardBg)", color:"var(--text)", width:"100%" };

  return (
    <div>
      {/* Kosten-Banner */}
      <div style={{ marginBottom:16, background:"#FDEDEC", borderRadius:10, padding:"10px 20px", border:"1px solid #f5c6cb", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <span style={{ fontSize:13, color:"#C0392B", fontWeight:600 }}>💰 Ausgaben gesamt (gekaufte Artikel)</span>
        <span style={{ fontSize:20, fontWeight:800, color:"#C0392B" }}>{gesamtActual.toFixed(2).replace(".",",")} €</span>
      </div>

      {/* Laden-Filter */}
      <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
        {["Alle", "—", ...new Set(einkaufsliste.map(e=>e.laden).filter(l=>l&&l!=="—"))].map(l => (
          <button key={l} style={{ ...S.filterBtn, ...(filterLaden===l?S.filterBtnActive:{}), position:"relative" }}
            onClick={() => setFilterLaden(l)}>
            {l === "—" ? "🛒 Nicht zugewiesen" : l}
            {l !== "Alle" && l !== "—" && bonsVonLaden(l).length > 0 &&
              <span style={{ position:"absolute", top:-4, right:-4, fontSize:9, background:COLORS.leaf, color:"#fff", borderRadius:"50%", width:14, height:14, display:"flex", alignItems:"center", justifyContent:"center" }}>{bonsVonLaden(l).length}</span>}
          </button>
        ))}
      </div>

      {/* Artikel hinzufügen */}
      <div style={{ ...S.card, marginBottom:16, padding:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:showForm?14:0 }}>
          <span style={{ fontWeight:700, fontSize:14, color:COLORS.text }}>+ Artikel hinzufügen</span>
          <button style={S.btnGhost} onClick={() => setShowForm(v=>!v)}>{showForm?"▲":"▼"}</button>
        </div>
        {showForm && (
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr auto", gap:12, alignItems:"end" }}>
            <div>
              <label style={S.label}>Artikel *</label>
              <input style={inputSt} placeholder="z.B. Bratwürste" value={neuerArtikel.artikel}
                onChange={e => setNeuerArtikel(f=>({...f,artikel:e.target.value}))}
                onKeyDown={e => e.key==="Enter" && addArtikel()} autoFocus />
            </div>
            <div>
              <label style={S.label}>Kategorie</label>
              <select style={inputSt} value={neuerArtikel.kategorie} onChange={e => setNeuerArtikel(f=>({...f,kategorie:e.target.value}))}>
                {["Essen","Drinks","Sonstiges"].map(k=><option key={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Menge</label>
              <input style={inputSt} placeholder="1" type="number" min="0" value={neuerArtikel.menge}
                onChange={e => setNeuerArtikel(f=>({...f,menge:e.target.value}))} />
            </div>
            <div>
              <label style={S.label}>Einheit</label>
              <select style={inputSt} value={neuerArtikel.einheit} onChange={e => setNeuerArtikel(f=>({...f,einheit:e.target.value}))}>
                {["Stk","kg","g","l","ml","Pkg","Flasche","Kasten","Dose"].map(e=><option key={e}>{e}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Kosten (€)</label>
              <input style={inputSt} placeholder="0,00" type="number" step="0.01" value={neuerArtikel.preis}
                onChange={e => setNeuerArtikel(f=>({...f,preis:e.target.value}))} />
            </div>
            <button style={{ ...S.btnPrimary, height:38, padding:"0 18px", alignSelf:"end" }} onClick={addArtikel}>+</button>
          </div>
        )}
      </div>

      {/* Artikel-Gruppen nach Laden */}
      {gruppenKeys.length === 0 && (
        <div style={{ textAlign:"center", padding:40, color:COLORS.textMuted }}>Noch keine Artikel. Füge oben Artikel hinzu.</div>
      )}
      {gruppenKeys.map(laden => {
        const artikel = gruppen[laden];
        const ladenSumme = artikel.filter(e=>e.gekauft).reduce((s,e)=>s+(parseFloat(e.preis)||0),0);
        const bons = bonsVonLaden(laden);
        const istUnzugewiesen = laden === "—";

        return (
          <div key={laden} style={{ ...S.card, marginBottom:14, padding:0, overflow:"hidden" }}>
            {/* Laden-Header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 16px", background: istUnzugewiesen ? "var(--tableStripe)" : COLORS.leafPale, borderBottom:`1px solid ${COLORS.border}` }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:15, fontWeight:800, color: istUnzugewiesen ? COLORS.textMuted : COLORS.leaf }}>
                  {istUnzugewiesen ? "🛒 Noch kein Laden zugewiesen" : `🏪 ${laden}`}
                </span>
                <span style={{ fontSize:11, color:COLORS.textMuted }}>{artikel.length} Artikel</span>
                {!istUnzugewiesen && <span style={{ fontSize:11, fontWeight:700, color:COLORS.leaf }}>· {ladenSumme.toFixed(2).replace(".",",")} €</span>}
              </div>
              {/* Kassenbon-Buttons (nur wenn Laden zugewiesen) */}
              {!istUnzugewiesen && (
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  {bons.map((bon, i) => (
                    <button key={bon.id} style={{ ...S.btnSmall, fontSize:11, background:COLORS.leaf, color:"#fff", border:"none" }}
                      onClick={() => setBonModal({ laden, bonId: bon.id })}>
                      ✅ Bon {bons.length>1 ? `#${i+1}` : ""}
                    </button>
                  ))}
                  <button style={{ ...S.btnSmall, fontSize:11, background:"#E67E22", color:"#fff", border:"none" }}
                    onClick={() => setBonModal({ laden, bonId: null })}>
                    📷 {bons.length > 0 ? "Neuer Bon" : "Kassenbon"}
                  </button>
                </div>
              )}
            </div>

            {/* Artikel-Tabelle */}
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ background:"var(--cardBg)" }}>
                  <th style={{ ...S.th, width:54 }}>↕</th>
                  <th style={S.th}>Artikel</th>
                  <th style={S.th}>Kat.</th>
                  <th style={S.th}>Menge</th>
                  <th style={{ ...S.th, minWidth:120 }}>Laden</th>
                  <th style={S.th}>Preis €</th>
                  <th style={{ ...S.th, textAlign:"center" }}>In Kasse</th>
                  <th style={{ ...S.th, textAlign:"center" }}>Gekauft</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {artikel.map((e, idx) => {
                  const globalIdx = einkaufsliste.findIndex(x => x.id === e.id);
                  return (
                    <tr key={e.id} style={{ ...S.tr, background: e.gekauft ? "#f0f8ed" : undefined }}>
                      {/* Sortier-Buttons */}
                      <td style={{ ...S.td, textAlign:"center", padding:"4px 6px" }}>
                        <div style={{ display:"flex", flexDirection:"column", gap:1 }}>
                          <button style={{ background:"none", border:"1px solid var(--border)", borderRadius:4, width:22, height:19, cursor:"pointer", fontSize:10, color:COLORS.textMuted, lineHeight:1 }}
                            onClick={() => moveArtikel(e.id, -1)} disabled={globalIdx===0}>▲</button>
                          <button style={{ background:"none", border:"1px solid var(--border)", borderRadius:4, width:22, height:19, cursor:"pointer", fontSize:10, color:COLORS.textMuted, lineHeight:1 }}
                            onClick={() => moveArtikel(e.id, 1)} disabled={globalIdx===einkaufsliste.length-1}>▼</button>
                        </div>
                      </td>
                      <td style={{ ...S.td, fontWeight:600, textDecoration:e.gekauft?"line-through":"none", color:e.gekauft?COLORS.textMuted:COLORS.text }}>{e.artikel}</td>
                      <td style={S.td}><span style={{ fontSize:11, padding:"2px 6px", borderRadius:6, background:"var(--tableStripe)" }}>{e.kategorie}</span></td>
                      <td style={S.td}>{e.menge} {e.einheit}</td>
                      {/* Laden-Dropdown inline */}
                      <td style={S.td}>
                        <select
                          value={e.laden || "—"}
                          onChange={ev => updateArtikel(e.id, { laden: ev.target.value })}
                          style={{ fontSize:12, padding:"3px 6px", borderRadius:6, border:`1px solid ${COLORS.border}`, fontFamily:"inherit", background:"var(--cardBg)", color:COLORS.text, maxWidth:130 }}>
                          <option value="—">— kein Laden</option>
                          {LADEN_TYPES.map(l=><option key={l} value={l}>{l}</option>)}
                        </select>
                      </td>
                      <td style={S.td}>
                        <input type="number" step="0.01" placeholder="0,00"
                          style={{ width:74, padding:"3px 6px", borderRadius:6, border:`1px solid ${COLORS.border}`, fontSize:12, fontFamily:"inherit", background:"var(--cardBg)", color:COLORS.text }}
                          value={e.preis || ""}
                          onChange={ev => updateArtikel(e.id, { preis: parseFloat(ev.target.value)||0 })} />
                      </td>
                      <td style={{ ...S.td, textAlign:"center" }}>
                        <button disabled={!e.gekauft}
                          onClick={() => !e.gekauft || updateArtikel(e.id, { inKasse: !e.inKasse })}
                          style={{ padding:"4px 10px", borderRadius:6, border:"none", fontSize:12, fontWeight:700, fontFamily:"inherit",
                            cursor:e.gekauft?"pointer":"not-allowed", opacity:e.gekauft?1:0.35,
                            background:e.inKasse?"#1A5276":"var(--tableStripe)", color:e.inKasse?"#fff":COLORS.textMuted }}>
                          {e.inKasse?"✓ Ja":"Nein"}
                        </button>
                      </td>
                      <td style={{ ...S.td, textAlign:"center" }}>
                        <button onClick={() => updateArtikel(e.id, { gekauft: !e.gekauft })}
                          style={{ padding:"4px 10px", borderRadius:6, border:"none", cursor:"pointer", fontSize:12, fontWeight:700, fontFamily:"inherit",
                            background:e.gekauft?COLORS.leaf:"var(--tableStripe)", color:e.gekauft?"#fff":COLORS.textMuted }}>
                          {e.gekauft?"✓ Gekauft":"Kaufen"}
                        </button>
                      </td>
                      <td style={S.td}>
                        <button style={{ ...S.btnSmall, color:"#C0392B", fontSize:12 }} onClick={() => delArtikel(e.id)}>🗑</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}

      {bonModal && (
        <BonModal laden={bonModal.laden} bonId={bonModal.bonId} fest={fest} updateFest={updateFest} onClose={() => setBonModal(null)} />
      )}
    </div>
  );
}

// ─── Kassenbon Modal ─────────────────────────────────────────────────────────
// Unterstützt mehrere Bons pro Laden (bonId=null → neuer Bon, bonId=string → editieren)
function BonModal({ laden, bonId, fest, updateFest, onClose }) {
  const kassenbons = fest.kassenbons || [];
  const bestehend  = bonId ? kassenbons.find(b => b.id === bonId) : null;

  const [bonUrl,    setBonUrl   ] = useState(bestehend?.url    || "");
  const [bonNotiz,  setBonNotiz ] = useState(bestehend?.notiz  || "");
  const [bonBetrag, setBonBetrag] = useState(bestehend?.betrag || "");
  const [loading,   setLoading  ] = useState(false);
  const fileRef = React.useRef();

  const PDF_SERVER_URL = window._PDF_SERVER_URL || "https://fgr-gartenfreunde.myds.me:3443";

  async function uploadBon(file) {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("bon", file);
      formData.append("jahr",     fest.jahr || new Date().getFullYear());
      formData.append("festName", fest.name || "Fest");
      formData.append("laden",    laden);
      const res  = await fetch(`${PDF_SERVER_URL}/api/feste/bon/upload`, { method:"POST", body:formData });
      const data = await res.json();
      if (data.url) setBonUrl(data.url);
    } catch(e) { alert("Upload fehlgeschlagen: " + e.message); }
    setLoading(false);
  }

  function speichern() {
    const id  = bonId || ("bon" + Date.now());
    const neu = { id, laden, url: bonUrl, notiz: bonNotiz, betrag: parseFloat(bonBetrag)||0, datum: new Date().toISOString() };
    // Vorhandenen ersetzen oder neuen anhängen
    const rest = kassenbons.filter(b => b.id !== id);
    updateFest(fest.id, { kassenbons: [...rest, neu] });
    onClose();
  }

  function loeschen() {
    if (!bonId) return;
    updateFest(fest.id, { kassenbons: kassenbons.filter(b => b.id !== bonId) });
    onClose();
  }

  const bonsDesLadens = kassenbons.filter(b => b.laden === laden);
  const bonNummer     = bonId ? bonsDesLadens.findIndex(b => b.id === bonId) + 1 : bonsDesLadens.length + 1;

  return (
    <div style={{ ...S.overlay, zIndex:2200 }}>
      <div style={{ ...S.modal, width:420, padding:24 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <h3 style={{ margin:0, color:COLORS.leaf, fontSize:15 }}>
            🧾 Kassenbon — {laden} {bonsDesLadens.length > 0 || bonId ? `(Einkauf #${bonNummer})` : ""}
          </h3>
          <button style={S.btnGhost} onClick={onClose}>✕</button>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            <button style={{ ...S.btnPrimary, fontSize:13 }} onClick={() => fileRef.current?.click()} disabled={loading}>
              {loading ? "⏳ Lädt…" : "📷 Datei hochladen"}
            </button>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display:"none" }}
              onChange={e => { const f=e.target.files?.[0]; if(f) uploadBon(f); e.target.value=""; }} />
            {bonUrl && <a href={`${PDF_SERVER_URL}${bonUrl}`} target="_blank" rel="noreferrer" style={{ fontSize:12, color:COLORS.leaf, textDecoration:"underline" }}>📄 Bon ansehen</a>}
          </div>
          <div>
            <label style={S.label}>Betrag (€)</label>
            <input type="number" step="0.01" placeholder="0,00" style={S.input} value={bonBetrag} onChange={e=>setBonBetrag(e.target.value)} />
          </div>
          <div>
            <label style={S.label}>Notiz</label>
            <input type="text" placeholder="z.B. Rewe Speyer, 2. Einkauf" style={S.input} value={bonNotiz} onChange={e=>setBonNotiz(e.target.value)} />
          </div>
        </div>
        <div style={{ display:"flex", gap:10, justifyContent:"space-between", marginTop:20 }}>
          <div>
            {bonId && <button style={{ ...S.btnGhost, color:"#C0392B", borderColor:"#C0392B", fontSize:12 }} onClick={loeschen}>🗑 Bon löschen</button>}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button style={S.btnGhost} onClick={onClose}>Abbrechen</button>
            <button style={S.btnPrimary} onClick={speichern}>💾 Speichern</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Fest Kasse ──────────────────────────────────────────────────────────────
function FestKasse({ fest, updateFest, members }) {
  const [aktiverGarten, setAktiverGarten] = useState(null);
  const [warenkorb, setWarenkorb]         = useState([]);
  const [katFilter, setKatFilter]         = useState("Alle");
  const [gartenSuche, setGartenSuche]     = useState("");
  const [confirmBezahl, setConfirmBezahl] = useState(false);
  const [artikelModal, setArtikelModal]   = useState(false);
  const [artForm, setArtForm]             = useState({ name:"", kategorie:"Getränke", preis:"", einheit:"" });

  // Artikel aus globalem Kassen-Artikelstamm
  const [artikel, setArtikel] = useState(loadKasseArtikel);
  function saveArt(arr) {
    setArtikel(arr);
    const val = JSON.stringify(arr);
    localStorage.setItem(KASSE_ARTIKEL_KEY, val); // → _gvStore + Server automatisch
  }

  const kassenEintraege = fest.kassenEintraege || [];

  const aktiveMembers = members
    .filter(m => m.status === "Aktives Mitglied" || m.status === "Passives Mitglied")
    .sort((a,b) => (parseInt((a.garten||"").replace(/\D/g,""))||0) - (parseInt((b.garten||"").replace(/\D/g,""))||0));

  const gefilterteMembers = gartenSuche
    ? aktiveMembers.filter(m => (m.garten||"").replace(/\D/g,"").startsWith(gartenSuche.replace(/\D/g,"")) ||
        `${m.vorname} ${m.nachname}`.toLowerCase().includes(gartenSuche.toLowerCase()))
    : aktiveMembers;

  // FestKasse zeigt alle Artikel (vonFest oder manuell)
  const gefilterteArtikel = katFilter === "Alle" ? artikel : artikel.filter(a => a.kategorie === katFilter);

  function addToCart(artId) {
    // Gleiche Artikel (gleicher Name) zusammenfassen
    const artName = artikel.find(a => a.id === artId)?.name?.toLowerCase().trim();
    setWarenkorb(w => {
      const ex = w.find(x => {
        if (x.artikelId === artId) return true;
        const xName = artikel.find(a => a.id === x.artikelId)?.name?.toLowerCase().trim();
        return xName === artName;
      });
      if (ex) return w.map(x => x.artikelId === ex.artikelId ? {...x, anzahl: x.anzahl+1} : x);
      return [...w, {artikelId: artId, anzahl: 1}];
    });
  }
  function removeFromCart(artId) {
    setWarenkorb(w => { const ex = w.find(x => x.artikelId === artId); if (!ex) return w; return ex.anzahl <= 1 ? w.filter(x => x.artikelId !== artId) : w.map(x => x.artikelId === artId ? {...x, anzahl: x.anzahl-1} : x); });
  }
  function clearCart() { setWarenkorb([]); }

  const gesamtBetrag = warenkorb.reduce((s, w) => { const art = artikel.find(a => a.id === w.artikelId); return s + (art ? parseFloat(art.preis) * w.anzahl : 0); }, 0);

  const gesamtEinnahmen = kassenEintraege.reduce((s,k) => s+k.summe, 0);
  const gesamtOffen = kassenEintraege.filter(k => !k.bezahlt).reduce((s,k) => s+k.summe, 0);

  function bezahlen() {
    if (!aktiverGarten || warenkorb.length === 0) return;
    const eintrag = {
      id: "kas" + Date.now(),
      gartenNr: (aktiverGarten.garten||"").replace(/\D/g,""),
      mitglied: `${aktiverGarten.vorname} ${aktiverGarten.nachname}`,
      mitgliedId: aktiverGarten.id,
      artikel: warenkorb.map(w => { const art = artikel.find(a => a.id === w.artikelId); return { name: art?.name, menge: w.anzahl, preis: parseFloat(art?.preis||0) }; }),
      summe: gesamtBetrag, bezahlt: true, zeitpunkt: new Date().toISOString()
    };
    updateFest(fest.id, { kassenEintraege: [...kassenEintraege, eintrag] });
    setAktiverGarten(null); clearCart(); setConfirmBezahl(false);
  }

  function toggleBezahlt(id) {
    updateFest(fest.id, { kassenEintraege: kassenEintraege.map(k => k.id === id ? {...k, bezahlt: !k.bezahlt} : k) });
  }

  function loescheEintrag(id) {
    updateFest(fest.id, { kassenEintraege: kassenEintraege.filter(k => k.id !== id) });
  }

  return (
    <div style={{ display:"flex", height:"calc(100vh - 220px)", minHeight:500, border:`1px solid ${COLORS.border}`, borderRadius:12, overflow:"hidden" }}>

      {/* Links: Gartenauswahl */}
      <div style={{ width:190, background:"#f5fbf3", borderRight:`1px solid ${COLORS.border}`, display:"flex", flexDirection:"column", overflow:"hidden" }}>
        <div style={{ padding:"10px 8px", borderBottom:`1px solid ${COLORS.border}` }}>
          <div style={{ fontSize:10, color:COLORS.textMuted, fontWeight:700, letterSpacing:1, marginBottom:5 }}>GARTEN WÄHLEN</div>
          <input style={{ ...S.input, fontSize:13, padding:"6px 8px" }} placeholder="Nr. suchen..."
            value={gartenSuche} onChange={e => setGartenSuche(e.target.value)} />
        </div>
        <div style={{ flex:1, overflowY:"auto" }}>
          {gefilterteMembers.map(m => {
            const gNr = (m.garten||"").replace(/\D/g,"");
            const istAktiv = aktiverGarten?.id === m.id;
            const hatBuchung = kassenEintraege.some(k => k.mitgliedId === m.id);
            return (
              <div key={m.id}
                style={{ padding:"8px 10px", cursor:"pointer", borderBottom:`1px solid ${COLORS.border}`,
                  background: istAktiv ? COLORS.leafPale : "transparent",
                  borderLeft: `3px solid ${istAktiv ? COLORS.leaf : "transparent"}` }}
                onClick={() => { setAktiverGarten(m); clearCart(); }}>
                <div style={{ fontSize:15, fontWeight:800, color:COLORS.leaf }}>{gNr}</div>
                <div style={{ fontSize:11, color:COLORS.textMuted }}>{m.vorname} {m.nachname}</div>
                {hatBuchung && <div style={{ fontSize:10, color:COLORS.leaf }}>✓ gebucht</div>}
              </div>
            );
          })}
        </div>
        {/* Einnahmen-Summary */}
        <div style={{ padding:"8px 10px", borderTop:`1px solid ${COLORS.border}`, background:"#f0f8ed" }}>
          <div style={{ fontSize:10, color:COLORS.textMuted }}>Einnahmen</div>
          <div style={{ fontSize:16, fontWeight:800, color:COLORS.leaf }}>{gesamtEinnahmen.toFixed(2).replace(".",",")} €</div>
          {gesamtOffen > 0 && <div style={{ fontSize:10, color:"#E67E22" }}>⏳ {gesamtOffen.toFixed(2).replace(".",",")} € offen</div>}
        </div>
      </div>

      {/* Mitte: Artikel-Grid */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", background:"var(--mainBg)" }}>
        {/* Kategorie-Tabs */}
        <div style={{ display:"flex", gap:2, padding:"6px 8px", background:"#f0f8ed", borderBottom:`1px solid ${COLORS.border}`, overflowX:"auto" }}>
          {["Alle",...KASSE_KATEGORIEN].map(k => (
            <button key={k}
              style={{ padding:"6px 12px", borderRadius:6, border:"none", cursor:"pointer", fontSize:12, fontWeight:700, fontFamily:"inherit", whiteSpace:"nowrap",
                background: katFilter===k ? COLORS.leaf : "transparent", color: katFilter===k ? "#fff" : COLORS.textMuted }}
              onClick={() => setKatFilter(k)}>{k}</button>
          ))}
          <button style={{ marginLeft:"auto", padding:"4px 10px", borderRadius:6, border:`1px solid ${COLORS.border}`, cursor:"pointer", fontSize:11, fontFamily:"inherit", background:"transparent", color:COLORS.textMuted }}
            onClick={() => { setArtForm({name:"",kategorie:"Getränke",preis:"",einheit:""}); setArtikelModal(true); }}>+ Artikel</button>
        </div>
        {/* Artikel */}
        <div style={{ flex:1, overflowY:"auto", padding:8, display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(120px, 1fr))", gap:8, alignContent:"start" }}>
          {gefilterteArtikel.length === 0 ? (
            <div style={{ gridColumn:"1/-1", textAlign:"center", padding:40, color:COLORS.textMuted }}>
              <div style={{ fontSize:13 }}>Keine Artikel — klicke „+ Artikel"</div>
            </div>
          ) : gefilterteArtikel.map(art => {
            const imWarenkorb = warenkorb.find(w => w.artikelId === art.id);
            return (
              <button key={art.id}
                style={{ padding:"12px 8px", background: imWarenkorb ? "#f0f8ed" : "#fff",
                  border:`2px solid ${imWarenkorb ? COLORS.leaf : COLORS.border}`, borderRadius:10,
                  cursor: aktiverGarten ? "pointer" : "not-allowed", textAlign:"center",
                  opacity: aktiverGarten ? 1 : 0.5, transition:"all 0.15s", position:"relative",
                  display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:6, fontFamily:"inherit",
                  boxShadow: imWarenkorb ? "0 2px 8px rgba(0,0,0,0.2)" : "0 1px 4px rgba(0,0,0,0.1)", minHeight:120, width:"100%" }}
                onClick={() => aktiverGarten && addToCart(art.id)}
                disabled={!aktiverGarten}>
                {imWarenkorb && (
                  <div style={{ position:"absolute", top:-7, right:-7, background:COLORS.leaf, color:"#fff", borderRadius:"50%", width:20, height:20, fontSize:11, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    {imWarenkorb.anzahl}
                  </div>
                )}
                <div style={{ fontSize:13, fontWeight:700, lineHeight:1.3, color:"#fff" }}>{art.name}</div>
                <div style={{ fontSize:15, fontWeight:800, color:"rgba(255,255,255,0.9)" }}>{parseFloat(art.preis).toFixed(2).replace(".",",")} €</div>
                {art.einheit && <div style={{ fontSize:10, color:COLORS.textMuted }}>{art.einheit}</div>}
              </button>
            );
          })}
        </div>
        {!aktiverGarten && (
          <div style={{ padding:"7px 12px", background:"#fffbe6", borderTop:`1px solid #f0d060`, textAlign:"center", fontSize:12, color:"#8a7000" }}>
            ← Bitte zuerst einen Garten auswählen
          </div>
        )}
      </div>

      {/* Rechts: Bon + Kassenbuch */}
      <div style={{ width:280, background:"#f5fbf3", borderLeft:`1px solid ${COLORS.border}`, display:"flex", flexDirection:"column" }}>
        {/* Garten-Info */}
        <div style={{ padding:"10px 12px", borderBottom:`1px solid ${COLORS.border}`, minHeight:52 }}>
          {aktiverGarten ? (
            <div>
              <div style={{ fontSize:18, fontWeight:800, color:COLORS.leaf }}>Garten {(aktiverGarten.garten||"").replace(/\D/g,"")}</div>
              <div style={{ fontSize:11, color:COLORS.textMuted }}>{aktiverGarten.vorname} {aktiverGarten.nachname}</div>
            </div>
          ) : <div style={{ fontSize:12, color:COLORS.textMuted, padding:"4px 0" }}>Kein Garten gewählt</div>}
        </div>

        {/* Warenkorb */}
        <div style={{ flex:1, overflowY:"auto", padding:"6px 0" }}>
          {warenkorb.length === 0 ? (
            <div style={{ textAlign:"center", padding:"20px 12px", color:COLORS.textMuted, fontSize:12 }}>Warenkorb leer</div>
          ) : warenkorb.map(w => {
            const art = artikel.find(a => a.id === w.artikelId);
            if (!art) return null;
            return (
              <div key={w.artikelId} style={{ display:"flex", alignItems:"center", padding:"6px 10px", borderBottom:`1px solid ${COLORS.border}` }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, fontWeight:700 }}>{art.name}</div>
                  <div style={{ fontSize:11, color:COLORS.textMuted }}>{w.anzahl} × {parseFloat(art.preis).toFixed(2).replace(".",",")} €</div>
                </div>
                <div style={{ fontSize:13, fontWeight:800, color:COLORS.leaf, marginRight:8 }}>
                  {(w.anzahl * parseFloat(art.preis)).toFixed(2).replace(".",",")} €
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                  <button style={{ width:22, height:22, background:COLORS.leaf, border:"none", borderRadius:4, color:"#fff", fontWeight:800, cursor:"pointer", fontSize:14, lineHeight:1 }} onClick={() => addToCart(w.artikelId)}>+</button>
                  <button style={{ width:22, height:22, background:"#C0392B", border:"none", borderRadius:4, color:"#fff", fontWeight:800, cursor:"pointer", fontSize:14, lineHeight:1 }} onClick={() => removeFromCart(w.artikelId)}>−</button>
                </div>
              </div>
            );
          })}
          {/* Bisherige Buchungen für diesen Garten */}
          {aktiverGarten && kassenEintraege.filter(k => k.mitgliedId === aktiverGarten.id).length > 0 && (
            <div style={{ padding:"8px 10px", borderTop:`2px solid ${COLORS.border}`, marginTop:4 }}>
              <div style={{ fontSize:10, color:COLORS.textMuted, fontWeight:700, letterSpacing:1, marginBottom:6 }}>FRÜHERE BUCHUNGEN</div>
              {kassenEintraege.filter(k => k.mitgliedId === aktiverGarten.id).map(k => (
                <div key={k.id} style={{ display:"flex", justifyContent:"space-between", fontSize:11, padding:"3px 0", borderBottom:`1px solid ${COLORS.border}` }}>
                  <div style={{ color:COLORS.textMuted }}>{k.artikel?.map(a => `${a.name}×${a.menge}`).join(", ")}</div>
                  <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                    <span style={{ fontWeight:700, color: k.bezahlt ? COLORS.leaf : "#C0392B" }}>{k.summe.toFixed(2).replace(".",",")} €</span>
                    <button style={{ fontSize:9, padding:"1px 5px", borderRadius:4, border:"none", cursor:"pointer", background: k.bezahlt ? "#d4edda" : "#FEF3F2", color: k.bezahlt ? "#2D5A27" : "#C0392B", fontWeight:700 }}
                      onClick={() => toggleBezahlt(k.id)}>{k.bezahlt ? "✓" : "⏳"}</button>
                    <button style={{ fontSize:9, padding:"1px 4px", borderRadius:4, border:"none", cursor:"pointer", background:"#FDEDEC", color:"#C0392B" }} onClick={() => loescheEintrag(k.id)}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Gesamt + Bezahlen */}
        <div style={{ borderTop:`2px solid ${COLORS.border}`, padding:10 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, padding:"4px 0" }}>
            <span style={{ fontSize:13, color:COLORS.textMuted, fontWeight:700 }}>GESAMT</span>
            <span style={{ fontSize:24, fontWeight:800, color:COLORS.leaf }}>{gesamtBetrag.toFixed(2).replace(".",",")} €</span>
          </div>
          <button
            style={{ width:"100%", padding:"12px 0", background: (aktiverGarten && warenkorb.length > 0) ? COLORS.leaf : "#ddd",
              border:"none", borderRadius:10, color:"#fff", fontSize:15, fontWeight:800, cursor: (aktiverGarten && warenkorb.length > 0) ? "pointer" : "not-allowed", fontFamily:"inherit" }}
            disabled={!aktiverGarten || warenkorb.length === 0}
            onClick={() => setConfirmBezahl(true)}>
            🧾 BON ERSTELLEN
          </button>
          {warenkorb.length > 0 && (
            <button style={{ width:"100%", marginTop:6, padding:"7px 0", background:"transparent", border:`1px solid #C0392B`, borderRadius:8, color:"#C0392B", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}
              onClick={clearCart}>🗑 Bon löschen</button>
          )}
        </div>
      </div>

      {/* Bezahl-Bestätigung */}
      {confirmBezahl && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999 }}>
          <div style={{ background:"#fff", border:`2px solid ${COLORS.leaf}`, borderRadius:16, padding:28, width:320, textAlign:"center", boxShadow:"0 8px 32px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize:14, color:COLORS.textMuted, marginBottom:4 }}>Garten {(aktiverGarten?.garten||"").replace(/\D/g,"")}</div>
            <div style={{ fontSize:34, fontWeight:800, color:COLORS.leaf, marginBottom:20 }}>{gesamtBetrag.toFixed(2).replace(".",",")} €</div>
            <div style={{ display:"flex", gap:10 }}>
              <button style={{ flex:1, padding:"11px 0", background:"var(--tableStripe)", border:"none", borderRadius:10, color:"var(--textMuted)", fontSize:14, cursor:"pointer", fontFamily:"inherit", fontWeight:700 }} onClick={() => setConfirmBezahl(false)}>Zurück</button>
              <button style={{ flex:2, padding:"11px 0", background:COLORS.leaf, border:"none", borderRadius:10, color:"#fff", fontSize:15, cursor:"pointer", fontFamily:"inherit", fontWeight:800 }} onClick={bezahlen}>✅ Bestätigen</button>
            </div>
          </div>
        </div>
      )}

      {/* Artikel hinzufügen Modal */}
      {artikelModal && (
        <div style={{ ...S.overlay, zIndex:2200 }}>
          <div style={{ ...S.modal, width:380, padding:24 }}>
            <h3 style={{ margin:"0 0 16px", color:COLORS.leaf, fontSize:15 }}>Artikel hinzufügen</h3>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div><label style={S.label}>Name</label><input style={S.input} value={artForm.name} onChange={e=>setArtForm(f=>({...f,name:e.target.value}))} placeholder="z.B. Cola 0,5l" /></div>
              <div><label style={S.label}>Kategorie</label><select style={S.input} value={artForm.kategorie} onChange={e=>setArtForm(f=>({...f,kategorie:e.target.value}))}>{KASSE_KATEGORIEN.map(k=><option key={k}>{k}</option>)}</select></div>
              <div><label style={S.label}>Preis (€)</label><input type="number" step="0.10" style={S.input} value={artForm.preis} onChange={e=>setArtForm(f=>({...f,preis:e.target.value}))} placeholder="1,50" /></div>
              <div><label style={S.label}>Einheit</label><input style={S.input} value={artForm.einheit} onChange={e=>setArtForm(f=>({...f,einheit:e.target.value}))} placeholder="z.B. 0,5l" /></div>
              <div>
                <label style={S.label}>Farbe</label>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:4 }}>
                  {["#2D5A27","#1A5276","#922B21","#BA4A00","#6C3483","#0E6655","#1C2833","#9A7D0A"].map(c => (
                    <div key={c} onClick={() => setArtForm(f=>({...f,farbe:c}))}
                      style={{ width:34, height:34, borderRadius:8, background:c, cursor:"pointer",
                        outline: (artForm.farbe||"#2D5A27")===c ? `3px solid ${c}` : "none",
                        outlineOffset:2, boxShadow: (artForm.farbe||"#2D5A27")===c ? "0 0 0 1px white inset" : "none",
                        display:"flex", alignItems:"center", justifyContent:"center" }}>
                      {(artForm.farbe||"#2D5A27")===c && <span style={{ color:"#fff", fontSize:16, fontWeight:800 }}>✓</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
              <button style={S.btnGhost} onClick={() => setArtikelModal(false)}>Abbrechen</button>
              <button style={{ ...S.btnPrimary, opacity:(!artForm.name||!artForm.preis)?0.5:1 }}
                disabled={!artForm.name||!artForm.preis}
                onClick={() => { saveArt([...artikel, {...artForm, id:"art"+Date.now(), preis:parseFloat(artForm.preis)||0, farbe:artForm.farbe||"#2D5A27"}]); setArtikelModal(false); }}>
                💾 Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Fest Auswertung ─────────────────────────────────────────────────────────
function FestAuswertung({ fest }) {
  const einkaufsliste = fest.einkaufsliste || [];
  const kassenEintraege = fest.kassenEintraege || [];
  const kassenbons = fest.kassenbons || [];

  const ausgaben = einkaufsliste.filter(e => e.gekauft).reduce((s,e) => s+(parseFloat(e.preis)||0), 0);
  const einnahmen = kassenEintraege.filter(k => k.bezahlt).reduce((s,k) => s+k.summe, 0);
  const einnahmenGesamt = kassenEintraege.reduce((s,k) => s+k.summe, 0);
  const gewinn = einnahmen - ausgaben;
  const offeneZahlungen = kassenEintraege.filter(k => !k.bezahlt).reduce((s,k) => s+k.summe, 0);

  // Ausgaben nach Laden
  const ladenStats = {};
  einkaufsliste.forEach(e => {
    if (!ladenStats[e.laden]) ladenStats[e.laden] = { geplant: 0, actual: 0, bon: false };
    ladenStats[e.laden].geplant += e.kosten?.geplant || 0;
    ladenStats[e.laden].actual += e.kosten?.actual || 0;
    ladenStats[e.laden].bon = !!kassenbons.find(b => b.laden === e.laden);
  });

  return (
    <div>
      {/* Haupt-Kennzahlen */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
        {[
          { label: "Ausgaben", val: ausgaben, suffix: " €", color: "#C0392B", bg: "#FDEDEC", icon: "📉" },
          { label: "Einnahmen (bezahlt)", val: einnahmen, suffix: " €", color: COLORS.leaf, bg: COLORS.leafPale, icon: "📈" },
          { label: "Gewinn / Verlust", val: gewinn, suffix: " €", color: gewinn >= 0 ? COLORS.leaf : "#C0392B", bg: gewinn >= 0 ? COLORS.leafPale : "#FDEDEC", icon: gewinn >= 0 ? "🏆" : "📉" },
          { label: "Noch ausstehend", val: offeneZahlungen, suffix: " €", color: "#E67E22", bg: "#FEF9E7", icon: "⏳" },
        ].map(k => (
          <div key={k.label} style={{ background: k.bg, borderRadius: 12, padding: "16px 20px", textAlign: "center", border: `1.5px solid ${k.color}33` }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>{k.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{k.val >= 0 ? "" : ""}{k.val.toFixed(2).replace(".",",")} {k.suffix}</div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>{k.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Ausgaben nach Laden */}
        <div style={S.card}>
          <h3 style={{ ...S.cardTitle, marginBottom: 16 }}>🏪 Ausgaben nach Laden</h3>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: COLORS.leafPale }}>
                <th style={S.th}>Laden</th>
                <th style={{ ...S.th, textAlign: "right" }}>Geplant</th>
                <th style={{ ...S.th, textAlign: "right" }}>Tatsächlich</th>
                <th style={S.th}>Bon</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(ladenStats).map(([laden, stat]) => (
                <tr key={laden} style={S.tr}>
                  <td style={{ ...S.td, fontWeight: 600 }}>{laden}</td>
                  <td style={{ ...S.td, textAlign: "right", fontWeight: 700, color: COLORS.leaf }}>{stat.actual.toFixed(2).replace(".",",")} €</td>
                  <td style={{ ...S.td, textAlign: "center" }}>
                    {stat.bon ? <span style={{ color: COLORS.leaf }}>✅</span> : <span style={{ color: "#C0392B" }}>⚠</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: COLORS.leafPale, fontWeight: 700 }}>
                <td style={S.td}>Gesamt</td>
                <td style={{ ...S.td, textAlign: "right" }}>{Object.values(ladenStats).reduce((s,v)=>s+v.geplant,0).toFixed(2).replace(".",",")} €</td>
                <td style={{ ...S.td, textAlign: "right", color: "#C0392B" }}>{ausgaben.toFixed(2).replace(".",",")} €</td>
                <td style={S.td}></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Einnahmen-Übersicht */}
        <div style={S.card}>
          <h3 style={{ ...S.cardTitle, marginBottom: 16 }}>💰 Einnahmen-Übersicht</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            {[
              { label: "Transaktionen gesamt", val: kassenEintraege.length, unit: "" },
              { label: "Bezahlt", val: kassenEintraege.filter(k=>k.bezahlt).length, unit: "" },
              { label: "Noch offen", val: kassenEintraege.filter(k=>!k.bezahlt).length, unit: "" },
              { label: "Einnahmen gesamt", val: einnahmenGesamt.toFixed(2).replace(".",","), unit: " €" },
              { label: "Davon eingegangen", val: einnahmen.toFixed(2).replace(".",","), unit: " €" },
              { label: "Ausstehend", val: offeneZahlungen.toFixed(2).replace(".",","), unit: " €" },
            ].map(k => (
              <div key={k.label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", background: "var(--tableStripe)", borderRadius: 8 }}>
                <span style={{ fontSize: 13, color: COLORS.textMuted }}>{k.label}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{k.val}{k.unit}</span>
              </div>
            ))}
          </div>

          {/* Fehlende Kassenbons */}
          {Object.entries(ladenStats).filter(([,s]) => !s.bon && s.actual > 0).length > 0 && (
            <div style={{ padding: "10px 14px", background: "#FEF3F2", borderRadius: 8, border: "1px solid #f5c6cb" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#C0392B", marginBottom: 6 }}>⚠ Fehlende Kassenbons:</div>
              {Object.entries(ladenStats).filter(([,s]) => !s.bon && s.actual > 0).map(([l]) => (
                <div key={l} style={{ fontSize: 12, color: "#C0392B" }}>· {l}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Artikel aus Fest berechnen (Top-Level) ─────────────────────────────────
function berechneArtikelAusFest(f, farbenMap) {
  if (!f?.einkaufsliste?.length) return [];
  const alleInKasse = f.einkaufsliste.filter(e => e.artikel && e.inKasse);
  const zusammengefasst = {};
  alleInKasse.forEach(e => {
    const key = e.artikel.toLowerCase().trim();
    const artId = "fest_" + f.id + "_art_" + key.replace(/[^a-z0-9]/gi,"_");
    if (!zusammengefasst[key]) {
      const gesamtMenge = alleInKasse.filter(x => x.artikel.toLowerCase().trim()===key).reduce((s,x)=>s+(parseFloat(x.menge)||0),0);
      const farbe = (farbenMap && farbenMap[key]) || "#2D5A27";
      zusammengefasst[key] = { id: artId, name: e.artikel,
        kategorie: (e.kategorie==="Getränke"||e.kategorie==="Drinks")?"Drinks":"Essen",
        preis: e.preis ? parseFloat(e.preis).toFixed(2) : "0.00",
        einheit: e.einheit||"", vonFest: f.id, menge: gesamtMenge, farbe };
    }
  });
  return Object.values(zusammengefasst);
}

// ─── Kassen-App ──────────────────────────────────────────────────────────────
const KASSE_ARTIKEL_KEY = "gv_kasse_artikel";
const KASSE_BUCHUNGEN_KEY = "gv_kasse_buchungen";

function loadKasseArtikel() {
  try { return JSON.parse(localStorage.getItem(KASSE_ARTIKEL_KEY) || "[]"); } catch(e) { return []; }
}
function loadKasseBuchungen() {
  try { return JSON.parse(localStorage.getItem(KASSE_BUCHUNGEN_KEY) || "[]"); } catch(e) { return []; }
}

const KASSE_KATEGORIEN = ["Essen", "Drinks"];
const KF = {
  bg:       "var(--mainBg, #f5f7f5)",
  card:     "var(--cardBg, #fff)",
  border:   "#d0e4ce",
  green:    "#2D5A27",
  greenLt:  "#3d7a37",
  gold:     "#2D5A27",
  text:     "var(--text, #1a2e1a)",
  muted:    "var(--textMuted, #6a8a68)",
  red:      "#C0392B",
  blue:     "#1A5276",
};

function KassenApp({ members }) {
  const [artikel, setArtikel]       = useState(loadKasseArtikel);

  // Beim Laden: BEIDE Keys frisch vom Server holen + aufräumen
  useEffect(() => {
    const loadFromServer = async () => {
      try {
        // Artikel laden
        const rA = await fetch(`${PDF_SERVER_URL}/api/data/${KASSE_ARTIKEL_KEY}`);
        const dA = await rA.json();
        if (dA?.value) {
          const val = typeof dA.value === "string" ? JSON.parse(dA.value) : dA.value;
          if (Array.isArray(val)) { setArtikel(val); localStorage.setItem(KASSE_ARTIKEL_KEY, JSON.stringify(val)); }
        }
        // Buchungen laden
        const rB = await fetch(`${PDF_SERVER_URL}/api/data/${KASSE_BUCHUNGEN_KEY}`);
        const dB = await rB.json();
        if (dB?.value) {
          const val = typeof dB.value === "string" ? JSON.parse(dB.value) : dB.value;
          if (Array.isArray(val)) { setBuchungen(val); localStorage.setItem(KASSE_BUCHUNGEN_KEY, JSON.stringify(val)); }
        }
      } catch(e) {}
    };
    loadFromServer();
  }, []);
  const [buchungen, setBuchungen]   = useState(loadKasseBuchungen);
  const [modus, setModus]           = useState("kasse"); // kasse | artikel | uebersicht | vollbild
  const [aktiverGarten, setAktiverGarten] = useState(null);
  const [aktiverFest, setAktiverFest]     = useState(null); // { id, name, datum }
  const [feste, setFeste]                 = useState(loadFeste);
  const [festPickerOpen, setFestPickerOpen] = useState(false);
  const [warenkorb, setWarenkorb]   = useState([]);
  const [katFilter, setKatFilter]   = useState("Alle");
  const [gartenSuche, setGartenSuche] = useState("");
  const [artikelModal, setArtikelModal] = useState(null);
  const [artForm, setArtForm]       = useState({ name:"", kategorie:"Getränke", preis:"", einheit:"", farbe:"#2D5A27" });
  const [vollbild, setVollbild]     = useState(false);
  const [confirmBezahl, setConfirmBezahl] = useState(false);
  const [showAbgeschlossen, setShowAbgeschlossen] = useState(false);
  const [gartenEingabe, setGartenEingabe] = useState("");
  const [geparkteBons, setGeparkteBons] = useState([]);

  // Buchung auch im Fest speichern wenn eines gewählt ist
  function buchungImFestSpeichern(buchung) {
    if (!aktiverFest) return;
    const festAlt = loadFeste().find(f => f.id === aktiverFest.id) || aktiverFest;
    const updatedFest = { ...festAlt, kassenEintraege: [...(festAlt.kassenEintraege||[]), {
      id: buchung.id, gartenNr: buchung.gartenNr, mitglied: buchung.mitglied,
      mitgliedId: buchung.mitgliedId,
      artikel: buchung.positionen?.map(p => ({name:p.name, menge:p.anzahl, preis:p.preis})),
      summe: buchung.gesamt, bezahlt: true, zeitpunkt: buchung.zeitpunkt
    }]};
    saveFest(updatedFest);
    setAktiverFest(updatedFest);
  }

  const heute = new Date().toISOString().split("T")[0];

  function saveArtikel(arr) {
    setArtikel(arr);
    const val = JSON.stringify(arr);
    localStorage.setItem(KASSE_ARTIKEL_KEY, val); // → _gvStore + Server automatisch
  }
  function saveBuchungen(arr) { setBuchungen(arr); localStorage.setItem(KASSE_BUCHUNGEN_KEY, JSON.stringify(arr)); }

  // Warenkorb-Operationen
  function addToCart(artId) {
    setWarenkorb(w => {
      const ex = w.find(x => x.artikelId === artId);
      return ex ? w.map(x => x.artikelId === artId ? {...x, anzahl: x.anzahl+1} : x) : [...w, {artikelId: artId, anzahl: 1}];
    });
  }
  function removeFromCart(artId) {
    setWarenkorb(w => {
      const ex = w.find(x => x.artikelId === artId);
      if (!ex) return w;
      return ex.anzahl <= 1 ? w.filter(x => x.artikelId !== artId) : w.map(x => x.artikelId === artId ? {...x, anzahl: x.anzahl-1} : x);
    });
  }
  function clearCart() { setWarenkorb([]); }

  // Gesamtbetrag
  const gesamtBetrag = warenkorb.reduce((s, w) => {
    const art = artikel.find(a => a.id === w.artikelId);
    return s + (art ? parseFloat(art.preis) * w.anzahl : 0);
  }, 0);

  // Verfügbare Restmenge pro Artikel (aus Fest-Einkaufsliste minus bereits gebuchte Mengen)
  // Nur Buchungen des aktuellen Festes anzeigen
  const heuteBuchungen = aktiverFest
    ? buchungen.filter(b => b.festId === aktiverFest.id)
    : buchungen.filter(b => !b.festId && b.datum === heute);
  const tagesUmsatz = heuteBuchungen.reduce((s,b) => s+b.gesamt, 0);
  const gesamtBuchungen = heuteBuchungen.length;

  const verfuegbareMenuge = React.useMemo(() => {
    if (!aktiverFest) return {};
    const einkauf = aktiverFest.einkaufsliste || [];
    // Gesamtmenge aus Einkaufsliste (nach Artikelname)
    const gesamt = {};
    // artikel enthält bereits summierte Gesamtmenge (zusammengefasst nach Name)
    artikel.filter(a => a.vonFest === aktiverFest.id).forEach(a => {
      const key = a.name?.toLowerCase().trim();
      if (!key) return;
      gesamt[key] = parseFloat(a.menge) || 0;
    });
    // Bereits verkaufte Mengen aus Fest-Buchungen
    const verkauft = {};
    heuteBuchungen.forEach(b => {
      (b.positionen || []).forEach(p => {
        const key = p.name?.toLowerCase().trim();
        if (!key) return;
        verkauft[key] = (verkauft[key] || 0) + (p.anzahl || 0);
      });
    });
    // Noch im aktuellen Warenkorb
    const imWarenkorb = {};
    warenkorb.forEach(w => {
      const art = artikel.find(a => a.id === w.artikelId);
      if (!art) return;
      const key = art.name?.toLowerCase().trim();
      imWarenkorb[key] = (imWarenkorb[key] || 0) + w.anzahl;
    });
    // Restmenge = gesamt - verkauft - warenkorb
    const rest = {};
    Object.keys(gesamt).forEach(key => {
      rest[key] = Math.max(0, gesamt[key] - (verkauft[key] || 0) - (imWarenkorb[key] || 0));
    });
    return rest;
  }, [aktiverFest, buchungen, warenkorb, artikel]);

  function getRestmenge(art) {
    if (!aktiverFest) return null;
    const key = art.name?.toLowerCase().trim();
    const val = verfuegbareMenuge[key];
    return val !== undefined ? val : null;
  }

  // Aktive Mitglieder nach Gartennummer sortiert
  const aktiveMembers = members
    .filter(m => m.status === "Aktives Mitglied" || m.status === "Passives Mitglied")
    .sort((a,b) => (parseInt((a.garten||"").replace(/\D/g,""))||0) - (parseInt((b.garten||"").replace(/\D/g,""))||0));

  const gefilterteMembers = gartenSuche
    ? aktiveMembers.filter(m => (m.garten||"").replace(/\D/g,"").startsWith(gartenSuche.replace(/\D/g,"")) ||
        `${m.vorname} ${m.nachname}`.toLowerCase().includes(gartenSuche.toLowerCase()))
    : aktiveMembers;

  const gefilterteArtikel = katFilter === "Alle" ? artikel : artikel.filter(a => a.kategorie === katFilter);

  function bezahlen() {
    if (!aktiverGarten || warenkorb.length === 0) return;
    const buchung = {
      id: "buch" + Date.now(),
      datum: heute,
      mitgliedId: aktiverGarten.id,
      gartenNr: (aktiverGarten.garten||"").replace(/\D/g,""),
      mitglied: `${aktiverGarten.vorname} ${aktiverGarten.nachname}`,
      festId: aktiverFest?.id || null,
      festName: aktiverFest?.name || null,
      positionen: warenkorb.map(w => {
        const art = artikel.find(a => a.id === w.artikelId);
        return { name: art?.name, preis: parseFloat(art?.preis||0), anzahl: w.anzahl, gesamt: parseFloat(art?.preis||0)*w.anzahl };
      }),
      gesamt: gesamtBetrag,
      bezahlt: false,
      zeitpunkt: new Date().toISOString(),
    };
    saveBuchungen([buchung, ...buchungen]);
    buchungImFestSpeichern(buchung);
    // Garten zurücksetzen für neue Bestellung
    setAktiverGarten(null);
    setGartenEingabe("");
    clearCart();
    setConfirmBezahl(false);
  }

  // Tages-Statistiken


  // ── Vollbild-Kasse ──
  const kasseContent = (
    <div style={{ display:"flex", height:"100%", fontFamily:"system-ui,sans-serif", color:"var(--text)", background:"var(--mainBg)", overflow:"hidden" }}>

      {/* Linke Seite: Garten-Eingabe + offene/abgeschlossene Bons */}
      <div style={{ width:220, background:"var(--tableStripe)", borderRight:`2px solid ${KF.border}`, display:"flex", flexDirection:"column", overflow:"hidden" }}>
        {/* Eingabe */}
        <div style={{ padding:"10px 8px", borderBottom:`1px solid ${KF.border}` }}>
          <div style={{ fontSize:11, color:KF.muted, fontWeight:700, letterSpacing:1, marginBottom:6 }}>GARTENNUMMER</div>
          <input
            style={{ width:"100%", background: aktiverFest ? "var(--cardBg)" : "var(--tableStripe)", border:`2px solid ${aktiverGarten ? KF.green : KF.border}`, borderRadius:6, padding:"8px 10px", color:"var(--text)", fontSize:18, fontWeight:800, fontFamily:"inherit", boxSizing:"border-box", textAlign:"center", letterSpacing:2, cursor: aktiverFest ? "text" : "not-allowed" }}
            placeholder={aktiverFest ? "Nr." : "Fest wählen"} value={gartenEingabe} type="number"
            disabled={!aktiverFest}
            onChange={e => {
              const val = e.target.value;
              setGartenEingabe(val);
              const treffer = aktiveMembers.filter(m => (m.garten||"").replace(/\D/g,"") === val.trim());
              if (treffer.length === 1) { setAktiverGarten(treffer[0]); setGartenSuche(val); }
              else setAktiverGarten(null);
            }} />
          {!aktiverFest && (
            <div style={{ fontSize:11, color:"#E67E22", marginTop:4, textAlign:"center", cursor:"pointer", fontWeight:700 }}
              onClick={() => setFestPickerOpen(true)}>
              👆 Fest auswählen
            </div>
          )}
          {gartenEingabe && !aktiverGarten && aktiverFest && (
            <div style={{ fontSize:11, color:"#C0392B", marginTop:4, textAlign:"center" }}>⚠ Kein Garten gefunden</div>
          )}
          {aktiverGarten && (
            <div style={{ marginTop:6, padding:"6px 8px", background:KF.green, borderRadius:6, textAlign:"center" }}>
              <div style={{ fontSize:12, fontWeight:800, color:"#fff" }}>{aktiverGarten.vorname} {aktiverGarten.nachname}</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.8)" }}>Garten {(aktiverGarten.garten||"").replace(/\D/g,"")}</div>
            </div>
          )}
        </div>

        {/* Offene Rechnungen */}
        <div style={{ padding:"6px 8px 3px", borderBottom:`1px solid ${KF.border}` }}>
          <div style={{ fontSize:10, color:KF.muted, fontWeight:700, letterSpacing:1 }}>
            OFFENE RECHNUNGEN ({heuteBuchungen.filter(b => !b.bezahlt).length + geparkteBons.length}{warenkorb.length > 0 && aktiverGarten ? "+1" : ""})
          </div>
        </div>
        <div style={{ flex:1, overflowY:"auto" }}>
          {/* Aktiver Garten mit Warenkorb — sofort anzeigen */}
          {aktiverGarten && warenkorb.length > 0 && (
            <div style={{ padding:"8px 10px", borderBottom:`1px solid ${KF.border}`,
              background:"#f0f8ed", borderLeft:`3px solid ${KF.green}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ cursor:"pointer", flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:800, color:KF.green }}>Garten {(aktiverGarten.garten||"").replace(/\D/g,"")}</div>
                  <div style={{ fontSize:10, color:KF.muted }}>{aktiverGarten.vorname} {aktiverGarten.nachname}</div>
                  <div style={{ fontSize:10, color:KF.muted, marginTop:2 }}>{warenkorb.map(w => { const a=artikel.find(x=>x.id===w.artikelId); return a?`${a.name}×${w.anzahl}`:""; }).filter(Boolean).join(", ")}</div>
                </div>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:3 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:KF.green }}>{gesamtBetrag.toFixed(2).replace(".",",")} €</div>
                  <div style={{ fontSize:10, color:KF.muted, fontStyle:"italic" }}>im Warenkorb</div>
                  <button style={{ fontSize:13, padding:"8px 14px", borderRadius:8, border:"none", cursor:"pointer", background:KF.green, color:"#fff", fontWeight:700, touchAction:"manipulation" }}
                    title="Zu offenen Rechnungen hinzufügen"
                    onClick={ev => {
                      ev.stopPropagation();
                      // Garten zu geparkteBons hinzufügen
                      const bonId = "park_" + aktiverGarten.id + "_" + Date.now();
                      setGeparkteBons(prev => {
                        // Vorhandenen Eintrag für diesen Garten updaten oder neu anlegen
                        const ohneAlt = prev.filter(p => p.mitgliedId !== aktiverGarten.id);
                        return [...ohneAlt, { id: bonId, mitgliedId: aktiverGarten.id, mitglied: `${aktiverGarten.vorname} ${aktiverGarten.nachname}`, gartenNr: (aktiverGarten.garten||"").replace(/\D/g,""), warenkorb: [...warenkorb], gesamt: gesamtBetrag }];
                      });
                      // Garten und Warenkorb zurücksetzen
                      setAktiverGarten(null);
                      setGartenEingabe("");
                      clearCart();
                    }}>
                    + Parken
                  </button>
                  <button style={{ fontSize:13, padding:"8px 14px", borderRadius:8, border:`2px solid #C0392B`, cursor:"pointer", background:"transparent", color:"#C0392B", fontWeight:700, touchAction:"manipulation" }}
                    onClick={() => { clearCart(); }}>
                    🗑
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Aktiver Garten ohne Warenkorb */}
          {aktiverGarten && warenkorb.length === 0 && !heuteBuchungen.some(b => b.mitgliedId === aktiverGarten.id && !b.bezahlt) && (
            <div style={{ padding:"8px 10px", borderBottom:`1px solid ${KF.border}`,
              background:"#f0f8ed", borderLeft:`3px solid ${KF.green}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:800, color:KF.green }}>Garten {(aktiverGarten.garten||"").replace(/\D/g,"")}</div>
                  <div style={{ fontSize:10, color:KF.muted }}>{aktiverGarten.vorname} {aktiverGarten.nachname}</div>
                </div>
                <div style={{ fontSize:10, color:KF.muted, fontStyle:"italic" }}>aktiv</div>
              </div>
            </div>
          )}
          {/* Geparkte Bons (noch nicht gebucht) */}
          {geparkteBons.map(p => (
            <div key={p.id}
              style={{ padding:"8px 10px", borderBottom:`1px solid ${KF.border}`,
                background:"#fffbe6", borderLeft:`3px solid #E67E22`, cursor:"pointer" }}
              onClick={() => {
                const m = aktiveMembers.find(x => x.id === p.mitgliedId);
                if (m) {
                  setAktiverGarten(m);
                  setGartenEingabe(p.gartenNr);
                  setWarenkorb(p.warenkorb);
                  setGeparkteBons(prev => prev.filter(x => x.id !== p.id));
                }
              }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:800, color:"#B7950B" }}>Garten {p.gartenNr}</div>
                  <div style={{ fontSize:10, color:KF.muted }}>{p.mitglied}</div>
                  <div style={{ fontSize:10, color:KF.muted, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {p.warenkorb.map(w => { const a=artikel.find(x=>x.id===w.artikelId); return a?`${a.name}×${w.anzahl}`:""; }).filter(Boolean).join(", ")}
                  </div>
                </div>
                <div style={{ textAlign:"right", flexShrink:0, marginLeft:4 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:"#B7950B" }}>{p.gesamt.toFixed(2).replace(".",",")} €</div>
                  <div style={{ display:"flex", gap:3, marginTop:3, justifyContent:"flex-end" }}>
                    <div style={{ fontSize:9, color:KF.muted, fontStyle:"italic" }}>tippen→laden</div>
                    <button style={{ fontSize:13, padding:"8px 14px", borderRadius:8, border:`2px solid #C0392B`, cursor:"pointer", background:"transparent", color:"#C0392B", fontWeight:700, touchAction:"manipulation" }}
                      onClick={ev => { ev.stopPropagation(); setGeparkteBons(prev => prev.filter(x => x.id !== p.id)); }}>
                      🗑
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Gespeicherte offene Buchungen */}
          {heuteBuchungen.filter(b => !b.bezahlt).length === 0 && geparkteBons.length === 0 && !aktiverGarten ? (
            <div style={{ padding:"12px 8px", fontSize:11, color:KF.muted, textAlign:"center" }}>Keine offenen</div>
          ) : heuteBuchungen.filter(b => !b.bezahlt).map(b => (
            <div key={b.id}
              style={{ padding:"8px 10px", borderBottom:`1px solid ${KF.border}`,
                background: aktiverGarten?.id === b.mitgliedId ? "#f0f8ed" : "transparent",
                borderLeft: `3px solid ${aktiverGarten?.id === b.mitgliedId ? KF.green : "transparent"}`,
                cursor:"pointer" }}
              onClick={() => {
                const m = aktiveMembers.find(x => x.id === b.mitgliedId);
                if (m) { setAktiverGarten(m); setGartenEingabe((m.garten||"").replace(/\D/g,"")); setGartenSuche(""); clearCart(); }
              }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:800, color:KF.green }}>Garten {b.gartenNr}</div>
                  <div style={{ fontSize:10, color:KF.muted }}>{b.mitglied}</div>
                  <div style={{ fontSize:10, color:KF.muted, marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{b.positionen?.map(p=>`${p.name}×${p.anzahl}`).join(", ")}</div>
                </div>
                <div style={{ textAlign:"right", flexShrink:0, marginLeft:4 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:"#C0392B" }}>{b.gesamt.toFixed(2).replace(".",",")} €</div>
                  <div style={{ display:"flex", gap:3, marginTop:3, justifyContent:"flex-end" }}>
                    <button style={{ fontSize:13, padding:"8px 14px", borderRadius:8, border:"none", cursor:"pointer", background:KF.green, color:"#fff", fontWeight:700, touchAction:"manipulation" }}
                      onClick={ev => { ev.stopPropagation(); saveBuchungen(buchungen.map(x => x.id === b.id ? {...x, bezahlt:true} : x)); }}>
                      ✓
                    </button>
                    <button style={{ fontSize:13, padding:"8px 14px", borderRadius:8, border:`2px solid #C0392B`, cursor:"pointer", background:"transparent", color:"#C0392B", fontWeight:700, touchAction:"manipulation" }}
                      onClick={ev => { ev.stopPropagation(); saveBuchungen(buchungen.filter(x => x.id !== b.id)); }}>
                      🗑
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Abgeschlossene */}
        <div style={{ borderTop:`2px solid ${KF.border}` }}>
          <button style={{ width:"100%", padding:"7px 8px", background:"transparent", border:"none", cursor:"pointer", fontSize:11, fontWeight:700, color:KF.muted, fontFamily:"inherit", display:"flex", justifyContent:"space-between", alignItems:"center" }}
            onClick={() => setShowAbgeschlossen(v => !v)}>
            <span>✓ BEZAHLT ({heuteBuchungen.filter(b => b.bezahlt).length})</span>
            <span>{showAbgeschlossen ? "▲" : "▼"}</span>
          </button>
          {showAbgeschlossen && (
            <div style={{ maxHeight:150, overflowY:"auto" }}>
              {heuteBuchungen.filter(b => b.bezahlt).map(b => (
                <div key={b.id} style={{ padding:"6px 10px", borderBottom:`1px solid ${KF.border}`, display:"flex", justifyContent:"space-between", opacity:0.7 }}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:KF.green }}>Garten {b.gartenNr}</div>
                    <div style={{ fontSize:10, color:KF.muted }}>{b.positionen?.map(p=>`${p.name}×${p.anzahl}`).join(", ")}</div>
                  </div>
                  <div style={{ fontSize:12, fontWeight:700, color:COLORS.leaf }}>{b.gesamt.toFixed(2).replace(".",",")} €</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mitte: Artikel-Grid */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
        {/* Kategorie-Tabs */}
        <div style={{ display:"flex", gap:2, padding:"6px 8px", background:"var(--tableStripe)", borderBottom:`1px solid ${KF.border}`, overflowX:"auto" }}>
          {["Alle",...KASSE_KATEGORIEN].map(k => (
            <button key={k}
              style={{ padding:"7px 14px", borderRadius:6, border:"none", cursor:"pointer", fontSize:12, fontWeight:700, fontFamily:"inherit", whiteSpace:"nowrap",
                background: katFilter===k ? KF.green : "transparent", color: katFilter===k ? "#fff" : KF.muted }}
              onClick={() => setKatFilter(k)}>{k}</button>
          ))}
        </div>
        {/* Artikel Buttons */}
        <div style={{ flex:1, overflowY:"auto", padding:8, display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(140px, 1fr))", gap:10, alignContent:"start", background:"var(--mainBg)" }}>
          {gefilterteArtikel.length === 0 ? (
            <div style={{ gridColumn:"1/-1", textAlign:"center", padding:40, color:KF.muted }}>
              <div style={{ fontSize:32, marginBottom:8 }}>🛒</div>
              <div style={{ fontSize:14 }}>Keine Artikel in dieser Kategorie</div>
              <button style={{ marginTop:12, padding:"8px 16px", background:KF.green, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontFamily:"inherit", fontWeight:700 }}
                onClick={() => setModus("artikel")}>+ Artikel verwalten</button>
            </div>
          ) : gefilterteArtikel.map(art => {
            const imWarenkorb = warenkorb.find(w => w.artikelId === art.id);
            return (
              <button key={art.id}
                style={{ padding:"12px 8px", background: art.farbe || KF.green,
                  border:"none", borderRadius:10,
                  cursor: aktiverGarten ? "pointer" : "not-allowed", color:"#fff", textAlign:"center",
                  opacity: aktiverGarten ? 1 : 0.5, transition:"all 0.15s", position:"relative",
                  display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:6, fontFamily:"inherit",
                  minHeight:120, width:"100%",
                  boxShadow: imWarenkorb ? "0 4px 12px rgba(0,0,0,0.25)" : "0 1px 4px rgba(0,0,0,0.1)",
                  transform: imWarenkorb ? "scale(0.97)" : "scale(1)" }}
                onClick={() => aktiverGarten && addToCart(art.id)}
                disabled={!aktiverGarten}>
                {(() => {
                  const rest = getRestmenge(art);
                  if (rest === null) {
                    // Kein Fest: Warenkorb-Badge
                    return imWarenkorb ? (
                      <div style={{ position:"absolute", top:-6, right:-6, background:KF.green, color:"#fff", borderRadius:"50%", width:20, height:20, fontSize:11, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        {imWarenkorb.anzahl}
                      </div>
                    ) : null;
                  }
                  // Mit Fest: Restmenge-Badge
                  return (
                    <div style={{ position:"absolute", top:-6, right:-6, background:"rgba(0,0,0,0.35)", color:"#fff", borderRadius:"50%", width:22, height:22, fontSize:11, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center" }}>
                      {rest}
                    </div>
                  );
                })()}
                <div style={{ fontSize:13, fontWeight:700, lineHeight:1.3, color:"#fff", textShadow:"0 1px 2px rgba(0,0,0,0.2)" }}>{art.name}</div>
                <div style={{ fontSize:16, fontWeight:800, color:"#fff", textShadow:"0 1px 2px rgba(0,0,0,0.2)" }}>{parseFloat(art.preis).toFixed(2).replace(".",",")} €</div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.8)" }}>{art.kategorie}</div>
              </button>
            );
          })}
        </div>
        {!aktiverGarten && (
          <div style={{ padding:"8px 12px", background:"#fffbe6", borderTop:`1px solid #f0d060`, textAlign:"center", fontSize:12, color:"#8a7000" }}>
            ← Bitte zuerst einen Garten auswählen
          </div>
        )}
      </div>

      {/* Rechte Seite: Bon */}
      <div style={{ width:260, background:"var(--tableStripe)", borderLeft:`2px solid ${KF.border}`, display:"flex", flexDirection:"column" }}>
        {/* Garten-Info */}
        <div style={{ padding:"10px 12px", borderBottom:`1px solid var(--border)`, minHeight:56 }}>
          {aktiverGarten ? (
            <div>
              <div style={{ fontSize:20, fontWeight:800, color:KF.green }}>Garten {(aktiverGarten.garten||"").replace(/\D/g,"")}</div>
              <div style={{ fontSize:12, color:KF.muted }}>{aktiverGarten.vorname} {aktiverGarten.nachname}</div>
            </div>
          ) : (
            <div style={{ fontSize:12, color:KF.muted, padding:"6px 0" }}>Kein Garten gewählt</div>
          )}
        </div>

        {/* Bon-Positionen */}
        <div style={{ flex:1, overflowY:"auto", padding:"8px 0" }}>
          {warenkorb.length === 0 ? (
            <div style={{ textAlign:"center", padding:"24px 12px", color:"var(--textMuted)", fontSize:12 }}>Warenkorb leer</div>
          ) : warenkorb.map(w => {
            const art = artikel.find(a => a.id === w.artikelId);
            if (!art) return null;
            return (
              <div key={w.artikelId} style={{ display:"flex", alignItems:"center", padding:"6px 10px", borderBottom:`1px solid ${KF.bg}` }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#fff" }}>{art.name}</div>
                  <div style={{ fontSize:11, color:KF.muted }}>{w.anzahl} × {parseFloat(art.preis).toFixed(2).replace(".",",")} €</div>
                </div>
                <div style={{ fontSize:13, fontWeight:800, color:KF.gold, marginRight:8 }}>
                  {(w.anzahl * parseFloat(art.preis)).toFixed(2).replace(".",",")} €
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                  <button style={{ width:22, height:22, background:KF.green, border:"none", borderRadius:4, color:"#fff", fontWeight:800, cursor:"pointer", fontSize:14, lineHeight:1 }}
                    onClick={() => addToCart(w.artikelId)}>+</button>
                  <button style={{ width:22, height:22, background:KF.red, border:"none", borderRadius:4, color:"#fff", fontWeight:800, cursor:"pointer", fontSize:14, lineHeight:1 }}
                    onClick={() => removeFromCart(w.artikelId)}>−</button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Gesamt + Bezahlen */}
        <div style={{ borderTop:`2px solid ${KF.border}`, padding:10 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10, padding:"8px 4px" }}>
            <span style={{ fontSize:14, color:KF.muted, fontWeight:700 }}>GESAMT</span>
            <span style={{ fontSize:26, fontWeight:800, color:KF.green }}>{gesamtBetrag.toFixed(2).replace(".",",")} €</span>
          </div>
          <button
            style={{ width:"100%", padding:"14px 0", background: (aktiverGarten && warenkorb.length > 0) ? KF.green : "var(--border)",
border:"none", borderRadius:10, color:"var(--text)", fontSize:16, fontWeight:800, cursor: (aktiverGarten && warenkorb.length > 0) ? "pointer" : "not-allowed",
              fontFamily:"inherit", letterSpacing:0.5 }}
            disabled={!aktiverGarten || warenkorb.length === 0}
            onClick={() => setConfirmBezahl(true)}>
            ✅ BEZAHLEN
          </button>
          {warenkorb.length > 0 && (
            <button style={{ width:"100%", marginTop:6, padding:"8px 0", background:"transparent", border:`1px solid #C0392B`, borderRadius:8, color:"#C0392B", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}
              onClick={clearCart}>🗑 Bon löschen</button>
          )}
        </div>
      </div>

      {/* Bezahl-Bestätigung */}
      {confirmBezahl && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999 }}>
          <div style={{ background:"var(--cardBg)", border:`2px solid ${KF.green}`, borderRadius:16, padding:28, width:340, textAlign:"center", boxShadow:"0 8px 32px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize:32, marginBottom:8 }}>💰</div>
            <div style={{ fontSize:14, color:KF.muted, marginBottom:4 }}>Garten {(aktiverGarten?.garten||"").replace(/\D/g,"")}</div>
            <div style={{ fontSize:36, fontWeight:800, color:KF.green, marginBottom:20 }}>{gesamtBetrag.toFixed(2).replace(".",",")} €</div>
            <div style={{ display:"flex", gap:10 }}>
              <button style={{ flex:1, padding:"12px 0", background:"#333", border:"none", borderRadius:10, color:"#aaa", fontSize:14, cursor:"pointer", fontFamily:"inherit", fontWeight:700 }}
                onClick={() => setConfirmBezahl(false)}>Zurück</button>
              <button style={{ flex:2, padding:"12px 0", background:KF.green, border:"none", borderRadius:10, color:"#fff", fontSize:16, cursor:"pointer", fontFamily:"inherit", fontWeight:800 }}
                onClick={bezahlen}>✅ Bestätigen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── Vollbild-Modus ──
  if (vollbild) {
    return (
      <div style={{ position:"fixed", inset:0, zIndex:9000, display:"flex", flexDirection:"column" }}>
        <div style={{ height:40, background:"var(--cardBg)", borderBottom:`2px solid ${KF.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 12px", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <span style={{ fontSize:15, fontWeight:800, color:KF.green }}>🛒 Gartenfreunde Kasse</span>
            <span style={{ fontSize:12, color:KF.muted }}>{new Date().toLocaleDateString("de-DE")}</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:12, color:KF.muted }}>Heute: <strong style={{ color:KF.green }}>{tagesUmsatz.toFixed(2).replace(".",",")} €</strong></span>
            <button style={{ padding:"5px 14px", background:KF.blue, border:"none", borderRadius:8, color:"#fff", fontSize:12, cursor:"pointer", fontFamily:"inherit", fontWeight:700 }}
              onClick={() => { setVollbild(false); setModus("uebersicht"); }}>📊 Übersicht</button>
            <button style={{ padding:"5px 14px", background:KF.green, border:"none", borderRadius:8, color:"#fff", fontSize:12, cursor:"pointer", fontFamily:"inherit", fontWeight:700 }}
              onClick={() => { setVollbild(false); setModus("artikel"); }}>⚙ Artikel</button>
            <button style={{ padding:"5px 12px", background:"#333", border:"none", borderRadius:8, color:"#aaa", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}
              onClick={() => setVollbild(false)}>✕ Beenden</button>
          </div>
        </div>
        <div style={{ flex:1, overflow:"hidden" }}>
          {kasseContent}
        </div>
      </div>
    );
  }

  // ── Normal-Modus ──
  // Kein Fest gewählt → Fest-Auswahl Startseite
  if (!aktiverFest) {
    return (
      <div style={{ ...S.page, background:"var(--mainBg)" }}>
        <div style={S.pageHeader}>
          <div><h1 style={S.h1}>🛒 Kasse</h1><p style={S.subtitle}>Bitte zuerst ein Fest auswählen</p></div>
        </div>
        <div style={{ maxWidth:600, margin:"0 auto" }}>
          {feste.filter(f => f.status !== "Abgeschlossen").length === 0 ? (
            <div style={{ ...S.card, textAlign:"center", padding:48 }}>
              <div style={{ fontSize:48, marginBottom:12 }}>🎉</div>
              <p style={{ fontSize:16, color:COLORS.text, marginBottom:8 }}>
                {feste.length === 0 ? "Noch keine Feste angelegt." : "Alle Feste sind abgeschlossen."}
              </p>
              <p style={{ fontSize:13, color:COLORS.textMuted }}>
                {feste.length === 0 ? "Lege zuerst ein Fest unter Feste an." : "Lege ein neues Fest unter Feste an."}
              </p>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <p style={{ fontSize:14, color:COLORS.textMuted, marginBottom:4 }}>Wähle das Fest für diese Kassensitzung:</p>
              {feste.filter(f => f.status !== "Abgeschlossen").sort((a,b)=>(b.datum||"").localeCompare(a.datum||"")).map(f => {
                const einnahmen = (f.kassenEintraege||[]).reduce((s,k)=>s+k.summe,0);
                const buchAnzahl = buchungen.filter(b=>b.festId===f.id).length;
                return (
                  <button key={f.id}
                    style={{ ...S.card, textAlign:"left", cursor:"pointer", border:`2px solid ${COLORS.border}`, padding:"18px 20px", background:"var(--cardBg)", fontFamily:"inherit", width:"100%" }}
                    onClick={() => {
                      setAktiverFest(f);
                      setModus("kasse");
                      // Artikel direkt aus Fest-Einkaufsliste (kein Cache)
                      setArtikel(berechneArtikelAusFest(f, (() => { try { return JSON.parse(localStorage.getItem("gv_kasse_farben")||"{}"); } catch(e){return{};} })()));
                    }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div>
                        <div style={{ fontSize:18, fontWeight:800, color:COLORS.leaf }}>{f.name} {f.jahr}</div>
                        <div style={{ fontSize:12, color:COLORS.textMuted, marginTop:3 }}>
                          📅 {f.datum ? new Date(f.datum).toLocaleDateString("de-DE") : "–"} · {f.status}
                        </div>
                        <div style={{ fontSize:12, color:COLORS.textMuted, marginTop:2 }}>
                          🛒 {(f.einkaufsliste||[]).length} Artikel in Einkaufsliste · 💰 {einnahmen.toFixed(2).replace(".",",")} € eingenommen
                        </div>
                      </div>
                      <div style={{ fontSize:28, marginLeft:16 }}>▶</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {festPickerOpen && (
          <div style={{ ...S.overlay, zIndex:2300 }}>
            <div style={{ ...S.modal, width:440, padding:28 }}>
              <button style={S.btnGhost} onClick={() => setFestPickerOpen(false)}>✕ Schließen</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ ...S.page, background:"var(--mainBg)" }}>
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.h1}>🛒 Kasse</h1>
          <p style={S.subtitle}>{heuteBuchungen.length} Buchungen · {tagesUmsatz.toFixed(2).replace(".",",")} € Umsatz</p>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <button onClick={() => setAktiverFest(null)}
            style={{ padding:"8px 16px", borderRadius:8, border:`2px solid ${COLORS.leaf}`,
              background: COLORS.leafPale, color: COLORS.leaf, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
            🎉 {aktiverFest.name} {aktiverFest.jahr} ✕
          </button>
          <button style={{ ...S.btnGhost }} onClick={() => setModus(modus === "artikel" ? "kasse" : "artikel")}>
            {modus === "artikel" ? "🛒 Kasse" : "⚙ Artikel"}
          </button>
          <button style={{ ...S.btnGhost }} onClick={() => setModus(modus === "uebersicht" ? "kasse" : "uebersicht")}>
            📊 Übersicht
          </button>
          <button style={{ ...S.btnPrimary }} onClick={() => setVollbild(true)}>
            ⛶ Vollbild
          </button>
        </div>
      </div>

      {modus === "kasse" && (
        <div>
          {aktiverFest && (
            <div style={{ marginBottom:8, padding:"8px 14px", background:COLORS.leafPale, borderRadius:8, border:`1px solid ${COLORS.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontSize:13, fontWeight:700, color:COLORS.leaf }}>🎉 Fest: {aktiverFest.name} {aktiverFest.jahr}</span>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <span style={{ fontSize:12, color:COLORS.textMuted }}>Buchungen werden im Fest gespeichert</span>
                <button style={{ fontSize:11, padding:"4px 10px", borderRadius:6, background:COLORS.leaf, color:"#fff", border:"none", cursor:"pointer", fontFamily:"inherit", fontWeight:700 }}
                  onClick={() => window.__setViewGlobal && window.__setViewGlobal("feste")}>
                  🎉 → Feste
                </button>
              </div>
            </div>
          )}
          <div style={{ height:"calc(100vh - 160px)", borderRadius:12, overflow:"hidden", border:`2px solid ${KF.border}` }}>
            {kasseContent}
          </div>
        </div>
      )}

      {modus === "artikel" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <h2 style={{ margin:0, fontSize:16, color:COLORS.text }}>Artikel & Preise</h2>
            <button style={S.btnPrimary} onClick={() => { setArtForm({name:"",kategorie:"Essen",preis:"",einheit:"",farbe:"#2D5A27"}); setArtikelModal("neu"); }}>
              + Artikel hinzufügen
            </button>
          </div>
          {/* Artikel als Buttons anzeigen — wie in der Kasse */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(140px,1fr))", gap:10 }}>
            {artikel.map(a => {
              const farbe = a.farbe || "#2D5A27";
              return (
                <div key={a.id} style={{ borderRadius:10, border:"none", padding:"12px 10px", background:farbe, position:"relative", display:"flex", flexDirection:"column", alignItems:"center", gap:4, textAlign:"center" }}>
                  <div style={{ position:"absolute", top:-6, right:-6, width:16, height:16, borderRadius:"50%", background:farbe }} />
                  <div style={{ fontSize:13, fontWeight:700, color:"#fff" }}>{a.name}</div>
                  <div style={{ fontSize:13, fontWeight:800, color:"rgba(255,255,255,0.9)" }}>{parseFloat(a.preis||0).toFixed(2).replace(".",",")} €</div>
                  {a.einheit && <div style={{ fontSize:10, color:"rgba(255,255,255,0.7)" }}>{a.kategorie} · {a.einheit}</div>}
                  <div style={{ display:"flex", gap:6, marginTop:4 }}>
                    <button style={{ fontSize:11, padding:"3px 10px", borderRadius:5, border:"1px solid rgba(255,255,255,0.5)", background:"rgba(255,255,255,0.2)", color:"#fff", cursor:"pointer", fontFamily:"inherit", fontWeight:700 }}
                      onClick={() => { setArtForm({...a}); setArtikelModal(a); }}>✏</button>
                    {!a.vonFest && (
                      <button style={{ fontSize:11, padding:"3px 10px", borderRadius:5, border:"1px solid rgba(255,255,255,0.5)", background:"rgba(200,0,0,0.4)", color:"#fff", cursor:"pointer", fontFamily:"inherit", fontWeight:700 }}
                        onClick={() => saveArtikel(artikel.filter(x => x.id !== a.id))}>🗑</button>
                    )}
                  </div>
                </div>
              );
            })}
            {artikel.length === 0 && (
              <div style={{ gridColumn:"1/-1", ...S.card, textAlign:"center", padding:40, color:COLORS.textMuted }}>
                Noch keine Artikel angelegt
              </div>
            )}
          </div>
        </div>
      )}

      {modus === "uebersicht" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:16, marginBottom:24 }}>
            {[
              { label:"Buchungen heute", val:heuteBuchungen.length, color:COLORS.leaf, bg:COLORS.leafPale },
              { label:"Umsatz heute", val:`${tagesUmsatz.toFixed(2).replace(".",",")} €`, color:COLORS.leaf, bg:COLORS.leafPale },
              { label:"Buchungen Fest", val:heuteBuchungen.length, color:"#1A5276", bg:"#EBF5FB" },
              { label:"Umsatz Fest", val:`${heuteBuchungen.reduce((s,b)=>s+b.gesamt,0).toFixed(2).replace(".",",")} €`, color:"#1A5276", bg:"#EBF5FB" },
            ].map(k => (
              <div key={k.label} style={{ background:k.bg, borderRadius:12, padding:"14px 18px", textAlign:"center", border:`1px solid ${k.color}22` }}>
                <div style={{ fontSize:11, color:COLORS.textMuted }}>{k.label}</div>
                <div style={{ fontSize:20, fontWeight:800, color:k.color, marginTop:4 }}>{k.val}</div>
              </div>
            ))}
          </div>
          <div style={S.card}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ background:"var(--cardBg)" }}>
                  <th style={S.th}>Zeit</th>
                  <th style={S.th}>Garten</th>
                  <th style={S.th}>Mitglied</th>
                  <th style={S.th}>Positionen</th>
                  <th style={{ ...S.th, textAlign:"right" }}>Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {heuteBuchungen.length === 0 ? (
                  <tr><td colSpan={5} style={{ ...S.td, textAlign:"center", color:COLORS.textMuted, padding:32 }}>Heute noch keine Buchungen</td></tr>
                ) : heuteBuchungen.map(b => (
                  <tr key={b.id} style={S.tr}>
                    <td style={{ ...S.td, fontSize:12 }}>{new Date(b.zeitpunkt).toLocaleTimeString("de-DE", {hour:"2-digit",minute:"2-digit"})}</td>
                    <td style={S.td}><span style={S.gartenBadge}>Garten {b.gartenNr}</span></td>
                    <td style={S.td}>{b.mitglied}</td>
                    <td style={{ ...S.td, fontSize:12, color:COLORS.textMuted }}>{b.positionen.map(p => `${p.name} ×${p.anzahl}`).join(", ")}</td>
                    <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:COLORS.leaf }}>{b.gesamt.toFixed(2).replace(".",",")} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Fest-Picker Modal */}
      {festPickerOpen && (
        <div style={{ ...S.overlay, zIndex:2300 }}>
          <div style={{ ...S.modal, width:440, padding:28 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <h3 style={{ margin:0, color:COLORS.leaf, fontSize:16 }}>🎉 Fest auswählen</h3>
              <button style={S.btnGhost} onClick={() => setFestPickerOpen(false)}>✕</button>
            </div>
            {feste.length === 0 ? (
              <div style={{ textAlign:"center", padding:32, color:COLORS.textMuted }}>
                <div style={{ fontSize:32, marginBottom:8 }}>🎉</div>
                <p>Noch keine Feste angelegt.</p>
                <p style={{ fontSize:12 }}>Lege zuerst ein Fest unter „Feste" an.</p>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:20 }}>
                <button
                  style={{ padding:"10px 14px", borderRadius:8, border:`1px solid ${COLORS.border}`, cursor:"pointer",
                    background: !aktiverFest ? COLORS.leafPale : "transparent",
                    fontFamily:"inherit", fontWeight: !aktiverFest ? 700 : 400, textAlign:"left",
                    color: !aktiverFest ? COLORS.leaf : COLORS.textMuted }}
                  onClick={() => { setAktiverFest(null); setFestPickerOpen(false); clearCart(); }}>
                  Kein Fest (allgemeine Kasse)
                </button>
                {feste.sort((a,b) => (b.datum||"").localeCompare(a.datum||"")).map(f => {
                  const einnahmen = (f.kassenEintraege||[]).reduce((s,k) => s+k.summe, 0);
                  const istAktiv = aktiverFest?.id === f.id;
                  return (
                    <button key={f.id}
                      style={{ padding:"12px 14px", borderRadius:8, border:`2px solid ${istAktiv ? COLORS.leaf : COLORS.border}`,
                        cursor:"pointer", background: istAktiv ? COLORS.leafPale : "#fff",
                        fontFamily:"inherit", textAlign:"left" }}
                      onClick={() => {
                        setAktiverFest(f);
                        setFestPickerOpen(false);
                        clearCart();
                        setAktiverGarten(null);
                        // Erst Farben vom Server holen, dann Artikel berechnen
                        setBuchungen(loadKasseBuchungen());
                        fetch(`${PDF_SERVER_URL}/api/data/gv_kasse_farben`)
                          .then(r => r.json())
                          .then(d => {
                            let farben = (() => { try { return JSON.parse(localStorage.getItem("gv_kasse_farben")||"{}"); } catch(e){return{};} })();
                            if (d?.value) {
                              const v = typeof d.value === "string" ? JSON.parse(d.value) : d.value;
                              if (v && typeof v === "object") {
                                farben = { ...farben, ...v };
                                localStorage.setItem("gv_kasse_farben", JSON.stringify(farben));
                                localStorage.setItem("gv_kasse_farben", JSON.stringify(farben));
                              }
                            }
                            setArtikel(berechneArtikelAusFest(f, farben));
                          })
                          .catch(() => {
                            setArtikel(berechneArtikelAusFest(f, {}));
                          });
                      }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <div>
                          <div style={{ fontSize:14, fontWeight:800, color: istAktiv ? COLORS.leaf : COLORS.text }}>
                            {istAktiv ? "✓ " : ""}{f.name} {f.jahr}
                          </div>
                          <div style={{ fontSize:12, color:COLORS.textMuted, marginTop:2 }}>
                            📅 {f.datum ? new Date(f.datum).toLocaleDateString("de-DE") : "–"} · Status: {f.status}
                          </div>
                        </div>
                        <div style={{ textAlign:"right" }}>
                          <div style={{ fontSize:13, fontWeight:700, color:COLORS.leaf }}>{einnahmen.toFixed(2).replace(".",",")} €</div>
                          <div style={{ fontSize:11, color:COLORS.textMuted }}>{(f.kassenEintraege||[]).length} Buchungen</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <button style={{ ...S.btnGhost, width:"100%" }} onClick={() => setFestPickerOpen(false)}>Schließen</button>
          </div>
        </div>
      )}

      {/* Artikel Modal */}
      {artikelModal && (
        <div style={{ ...S.overlay, zIndex:2200 }}>
          <div style={{ ...S.modal, width:400, padding:28 }}>
            <h3 style={{ margin:"0 0 20px", color:COLORS.leaf }}>{artikelModal === "neu" ? "Artikel hinzufügen" : "Artikel bearbeiten"}</h3>
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div><label style={S.label}>Name</label><input style={S.input} value={artForm.name} onChange={e=>setArtForm(f=>({...f,name:e.target.value}))} placeholder="z.B. Cola 0,5l" /></div>
              <div><label style={S.label}>Kategorie</label>
                <select style={S.input} value={artForm.kategorie} onChange={e=>setArtForm(f=>({...f,kategorie:e.target.value}))}>
                  {KASSE_KATEGORIEN.map(k=><option key={k}>{k}</option>)}
                </select>
              </div>
              <div><label style={S.label}>Verkaufspreis (€)</label><input type="number" step="0.10" style={S.input} value={artForm.preis} onChange={e=>setArtForm(f=>({...f,preis:e.target.value}))} placeholder="1,50" /></div>
              <div><label style={S.label}>Einheit (optional)</label><input style={S.input} value={artForm.einheit} onChange={e=>setArtForm(f=>({...f,einheit:e.target.value}))} placeholder="z.B. 0,5l" /></div>
              <div>
                <label style={S.label}>Farbe</label>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:4 }}>
                  {["#2D5A27","#1A5276","#922B21","#BA4A00","#6C3483","#0E6655","#1C2833","#9A7D0A"].map(c => (
                    <div key={c} onClick={() => setArtForm(f=>({...f,farbe:c}))}
                      style={{ width:34, height:34, borderRadius:8, background:c, cursor:"pointer",
                        outline: (artForm.farbe||"#2D5A27")===c ? `3px solid ${c}` : "none",
                        outlineOffset:2, boxShadow: (artForm.farbe||"#2D5A27")===c ? "0 0 0 1px white inset" : "none",
                        display:"flex", alignItems:"center", justifyContent:"center" }}>
                      {(artForm.farbe||"#2D5A27")===c && <span style={{ color:"#fff", fontSize:16, fontWeight:800 }}>✓</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:20 }}>
              <button style={S.btnGhost} onClick={() => setArtikelModal(null)}>Abbrechen</button>
              <button style={{ ...S.btnPrimary, opacity:(!artForm.name||!artForm.preis)?0.5:1 }}
                disabled={!artForm.name||!artForm.preis}
                onClick={() => {
                  // Farbe speichern
                  const farbenAlt = (() => { try { return JSON.parse(localStorage.getItem("gv_kasse_farben")||"{}"); } catch(e){return{};} })();
                  const nameKey = (artForm.name||"").toLowerCase().trim();
                  const farbenNeu = { ...farbenAlt, [nameKey]: artForm.farbe||"#2D5A27" };
                  localStorage.setItem("gv_kasse_farben", JSON.stringify(farbenNeu));
                  // Server-Sync automatisch über patched localStorage.setItem
                  // Farbe gespeichert
                  // Artikel-State: Farbe + alle anderen Felder in einem Schritt updaten
                  setArtikel(prev => prev.map(a => {
                    const match = a.name?.toLowerCase().trim() === nameKey;
                    if (!match) return a;
                    return { ...a, farbe: artForm.farbe||"#2D5A27", preis: parseFloat(artForm.preis)||a.preis };
                  }));
                  setArtikelModal(null);
                }}>
                💾 Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
